import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { db, auth, storage } from "../../firebase.init";
import {
    collection, query, where, orderBy, limit, startAfter,
    onSnapshot, addDoc, updateDoc, setDoc, doc,
    serverTimestamp, getDocs, getDoc, increment, arrayUnion, arrayRemove, writeBatch,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, getBlob } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuthState } from "react-firebase-hooks/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus, faSearch, faTimes, faBoxArchive, faDownload,
    faCircleExclamation, faCalendar, faUser, faCopy,
    faArrowRight, faPaperPlane, faClock, faImage, faTrashCan,
    faExclamationTriangle, faCircleCheck, faCheckCircle,
    faPlay, faPause, faRotateLeft, faComment, faChevronDown,
    faLink, faHashtag, faTag, faHistory, faChevronRight,
    faChevronLeft, faUserCheck, faClipboardCheck, faBuilding, faBell, faTrophy,
    faCircleXmark, faHourglassHalf, faGift, faCoins
} from "@fortawesome/free-solid-svg-icons";
import { Spinner, Modal as BootstrapModal, Form, Row, Col, Button } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_red.css";
import { format } from "date-fns";
import "./Tickets.css";
import Review from "../Review/Review";
import { CalendarEventModal, CALENDAR_COLLECTION_PATH, dateUtils as calendarDateUtils } from "../Calendar/Calendar";
import InspectionModal from "../Reports/Dashboard/InspectionModal";
import EstimateModal from "../Reports/DoorDetails/EstimateModal";
import ClipLoader from "react-spinners/ClipLoader";
import ReactModal from "react-modal";
import RewardsPage from "../Rewards/RewardsPage";
import { useLiveRewardSummary } from "../Rewards/rewardClient";
import RewardClassificationPanel from "../Rewards/RewardClassificationPanel";
import TicketRewardIndicator from "../Rewards/TicketRewardIndicator";

const ASSIGNABLE_ROLES = ["admin", "inspection", "coordinator", "reviewer", "remedial", "manager"];

export const STAGE_CHAIN = [
    { key: "inspection", role: "inspection", label: "Inspection", taskType: "inspection" },
    { key: "coordinator_qc", role: "coordinator", label: "Coordinator (Quality Control)", taskType: "quality_control" },
    { key: "reviewer", role: "reviewer", label: "Reviewer", taskType: "review_inspection" },
    { key: "coordinator_review", role: "coordinator", label: "Coordinator (Review)", taskType: "review_report" },
    { key: "remedial", role: "remedial", label: "Remedial", taskType: "check_remedials" },
    { key: "manager", role: "manager", label: "Manager", taskType: "review_carpentry" },
];

const normalizeUserRole = (role) => role === "coordinator2" ? "coordinator" : role;

const stageByKey = (key) => STAGE_CHAIN.find((stage) => stage.key === key) || null;
const stageByTaskType = (taskType) => STAGE_CHAIN.find((stage) => stage.taskType === taskType) || null;

const workflowStageKeyForTicket = (ticket, assigneeRole = null) => {
    if (!ticket) return null;
    if (ticket.workflow_stage && stageByKey(ticket.workflow_stage)) return ticket.workflow_stage;

    const normalizedRole = normalizeUserRole(assigneeRole);
    if (normalizedRole === "coordinator") {
        if (ticket.task_type === "review_report" || ticket.stage_index === 3) return "coordinator_review";
        return "coordinator_qc";
    }

    const byType = stageByTaskType(ticket.task_type);
    if (byType) return byType.key;
    if (typeof ticket.stage_index === "number" && STAGE_CHAIN[ticket.stage_index]) return STAGE_CHAIN[ticket.stage_index].key;
    return STAGE_CHAIN.find((stage) => stage.role === normalizedRole)?.key || null;
};

const encodeAssignmentTarget = (uid, stageKey) => uid ? `${uid}::${stageKey || ""}` : "";
const decodeAssignmentTarget = (value) => {
    const [uid = "", stageKey = ""] = String(value || "").split("::");
    return { uid, stageKey: stageKey || null };
};

const baseMentionHandleForUser = (user) => {
    const raw = user?.username || user?.name || String(user?.email || "").split("@")[0] || "user";
    return String(raw).replace(/[^A-Za-z0-9_.-]+/g, "").slice(0, 34) || "user";
};

const mentionHandleForUser = (user, users = []) => {
    const base = baseMentionHandleForUser(user);
    const collisions = (users || []).filter(
        (candidate) => baseMentionHandleForUser(candidate).toLowerCase() === base.toLowerCase()
    );
    if (collisions.length <= 1) return base;
    const suffix = String(user?.uid || user?.email || "user")
        .replace(/[^A-Za-z0-9]+/g, "")
        .slice(-5) || "user";
    return `${base}_${suffix}`;
};

const mentionedUserIdsFromText = (text, users) => {
    const handles = new Set(
        [...String(text || "").matchAll(/@([A-Za-z0-9_.-]+)/g)]
            .map((match) => match[1].toLowerCase())
    );
    return [...new Set((users || [])
        .filter((u) => handles.has(mentionHandleForUser(u, users).toLowerCase()))
        .map((u) => u.uid)
        .filter(Boolean))];
};

const cleanNotificationType = (value) =>
    String(value || "notification")
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 36) || "notification";

const notificationHashFallback = (value) => {
    const input = String(value || "");
    const seeds = [0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35];

    return seeds.map((seed, seedIndex) => {
        let hash = seed >>> 0;
        for (let i = 0; i < input.length; i += 1) {
            hash ^= input.charCodeAt(i) + seedIndex;
            hash = Math.imul(hash, 0x01000193);
            hash ^= hash >>> 13;
        }
        return (hash >>> 0).toString(16).padStart(8, "0");
    }).join("");
};

const notificationDocId = async (payload = {}) => {
    const type = cleanNotificationType(payload.type);
    const dedupeSource = payload.dedupe_key || payload.event_id || "";

    if (!dedupeSource) return null;

    const source = `${type}|${payload.ticket_id || "task"}|${dedupeSource}`;
    let digestHex = "";

    try {
        const browserCrypto = typeof window !== "undefined" ? window.crypto : null;
        const TextEncoderCtor = typeof window !== "undefined" ? window.TextEncoder : null;

        if (browserCrypto?.subtle && TextEncoderCtor) {
            const digest = await browserCrypto.subtle.digest(
                "SHA-256",
                new TextEncoderCtor().encode(source)
            );
            digestHex = Array.from(new Uint8Array(digest).slice(0, 16))
                .map((byte) => byte.toString(16).padStart(2, "0"))
                .join("");
        }
    } catch (err) {
        console.log("Web Crypto unavailable for notification ID; using deterministic fallback.", err);
    }

    if (!digestHex) digestHex = notificationHashFallback(source);

    return `${type}_${digestHex}`;
};

const notifyUserSafely = async (recipientUid, payload) => {
    if (!recipientUid || recipientUid === payload?.actor_id) return null;

    try {
        const { dedupe_key = null, ...storedPayload } = payload || {};
        const deterministicId = await notificationDocId(payload);
        const notificationsCollection = collection(db, "users", recipientUid, "notifications");

        const generatedRef = doc(notificationsCollection);
        const notificationRef = deterministicId
            ? doc(db, "users", recipientUid, "notifications", deterministicId)
            : doc(db, "users", recipientUid, "notifications", `${cleanNotificationType(payload?.type)}_${generatedRef.id}`);

        if (deterministicId) {
            await setDoc(notificationRef, {
                ...storedPayload,
                created_at: serverTimestamp(),
            }, { merge: true });
        } else {
            await setDoc(notificationRef, {
                ...storedPayload,
                read_at: null,
                created_at: serverTimestamp(),
            }, { merge: false });
        }

        return notificationRef;
    } catch (err) {
        console.error("Failed to create notification:", err);
        return null;
    }
};

const MAX_SCHEDULES_PER_TICKET = 2;

const TASK_TYPES = [
    // { value: "sales", label: "Sales" },
    // { value: "booking", label: "Booking" },
    { value: "inspection", label: "Inspection" },
    { value: "estimate_request", label: "Estimate Request" },
    { value: "review_inspection", label: "Review Inspection" },
    { value: "quality_control", label: "Quality Control" },
    { value: "review_report", label: "Review Report" },
    { value: "check_remedials", label: "Check Remedials" },
    { value: "remedial_booking", label: "Remedial Booking" },
    { value: "carpentry_work", label: "Carpentry Work" },
    { value: "review_carpentry", label: "Review Carpentry" },
    { value: "client_query", label: "Client Query" },
    { value: "certificate", label: "Certificate" },
    { value: "other", label: "Other" },
    // { value: "closed", label: "Closed" },
];

const TASK_TYPE_CFG = {
    // sales: { color: "#ec4899", label: "Sales" },
    // booking: { color: "#8b5cf6", label: "Booking" },
    inspection: { color: "#3b82f6", label: "Inspection" },
    estimate_request: { color: "#ea580c", label: "Estimate Request" },
    review_inspection: { color: "#0891b2", label: "Review Inspection" },
    quality_control: { color: "#059669", label: "Quality Control" },
    review_report: { color: "#49ca0d", label: "Review Report" },
    check_remedials: { color: "#d97706", label: "Check Remedials" },
    remedial_booking: { color: "#7c3aed", label: "Remedial Booking" },
    carpentry_work: { color: "#b45309", label: "Carpentry Work" },
    review_carpentry: { color: "#0d9488", label: "Review Carpentry" },
    client_query: { color: "#dc2626", label: "Client Query" },
    certificate: { color: "#7c3aed", label: "Certificate" },
    other: { color: "#64748b", label: "Other" },
    // closed: { color: "#6b7280", label: "Closed" },
};

const PRIORITY_OPTIONS = ["Low", "Medium", "High"];
const PRIORITY_ORDER = { Low: 0, Medium: 1, High: 2 };

const STATUS_CFG = {
    inbox: { color: "#94a3b8", bg: "rgba(148,163,184,0.15)", label: "Inbox" },
    ready: { color: "#3b82f6", bg: "rgba(59,130,246,0.15)", label: "Ready" },
    active: { color: "#10b981", bg: "rgba(16,185,129,0.15)", label: "Active" },
    waiting: { color: "#f59e0b", bg: "rgba(245,158,11,0.15)", label: "Waiting" },
    completed: { color: "#4b5563", bg: "rgba(107,114,128,0.12)", label: "Completed" },
    cancelled: { color: "#ef4444", bg: "rgba(239,68,68,0.15)", label: "Cancelled" },
};

const PRIORITY_CFG = {
    Low: { color: "#16a34a" },
    Medium: { color: "#ca8a04" },
    High: { color: "#dc2626" },
};

const EVENT_CFG = {
    created: { icon: faPlus, color: "#10b981" },
    started: { icon: faPlay, color: "#3b82f6" },
    completed: { icon: faCircleCheck, color: "#10b981" },
    blocked: { icon: faExclamationTriangle, color: "#f59e0b" },
    unblocked: { icon: faRotateLeft, color: "#3b82f6" },
    paused: { icon: faPause, color: "#d97706" },
    resumed: { icon: faPlay, color: "#10b981" },
    switched: { icon: faArrowRight, color: "#8b5cf6" },
    reassigned: { icon: faUserCheck, color: "#0891b2" },
    archived: { icon: faBoxArchive, color: "#6b7280" },
    comment: { icon: faPaperPlane, color: "#0ea5e9" },
    email_sent: { icon: faPaperPlane, color: "#7c3aed" },
    edited: { icon: faClipboardCheck, color: "#0d9488" },
};

const WAITING_ON_OPTIONS = [
    "Client / Resident",
    "Internal Team",
    "Supplier / Materials",
    "Management Approval",
    "Property Access",
    "Re-Inspection",
    "Tradesperson",
    "Certificate",
    "Other",
];

const ACTIVITY_DISPLAY_LIMIT = 20;
const ACTIVITY_MAX_LIMIT = 200;
const TASK_INITIAL_LOAD = 300;
const TASK_LOAD_MORE_BATCH = 100;
const ACTIVE_TICKET_CACHE_MAX_AGE_MS = 15000;
const NOTIFICATION_FETCH_LIMIT = 200;
const NOTIFICATION_DISPLAY_LIMIT = 50;
const NOTIFICATION_CLEAR_BATCH_SIZE = 400;

const showTicketToast = (message, title = "Notice", options = {}) => {
    const { tone: explicitTone, ...toastOptions } = options || {};
    const normalizedTitle = String(title || "").toLowerCase();
    const tone = explicitTone
        || (/cannot|couldn|failed|failure|error|unable|not cleared|not saved|not updated/.test(normalizedTitle) ? "danger" : null)
        || (/done|saved|updated|cleared|completed|resumed|paused/.test(normalizedTitle) ? "success" : null)
        || "neutral";
    const icon = tone === "danger" ? faCircleXmark : (tone === "success" ? faCircleCheck : faBell);

    toast(
        <div className={`ticket-notification-toast is-${tone}`}>
            <div className="ticket-notification-toast-icon">
                <FontAwesomeIcon icon={icon} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
                <div className="ticket-notification-toast-title">{title}</div>
                <div className="ticket-notification-toast-subtitle">{message}</div>
            </div>
        </div>,
        {
            icon: false,
            autoClose: 4000,
            closeOnClick: true,
            pauseOnHover: true,
            className: `ticket-notification-toast-shell is-${tone}`,
            progressClassName: "ticket-notification-toast-progress",
            ...toastOptions,
        }
    );
};


const HANDOVER_CHECKLIST_FIELDS = [
    { key: "residentsContacted", question: "Have residents been contacted?", inputType: "text", inputLabel: "e.g. 12/08/2026 or details" },
    { key: "accessDetails", question: "Do we have access details?", inputType: "text", inputLabel: "What are the access details?" },
    { key: "residentsAccessCount", question: "How many residents have provided access?", inputType: "text", inputLabel: "Number of residents" },
    { key: "calendarBooked", question: "Has the work been added into the calendar?", inputType: "text", inputLabel: "Enter calendar details" },
    { key: "visitTwice", question: "Are we visiting twice?", inputType: "checkbox", checkedLabel: "Yes — two visits", uncheckedLabel: "No — one visit (default)" },
    { key: "docsNotesMissing", question: "Are documents, notes or decisions still missing?", inputType: "text", inputLabel: "What is missing? If nothing, enter None." },
];

const emptyHandoverChecklist = () =>
    Object.fromEntries(
        HANDOVER_CHECKLIST_FIELDS.map((f) => [
            f.key,
            { value: f.inputType === "checkbox" ? false : "" },
        ])
    );

const cloneHandoverChecklist = (source) =>
    Object.fromEntries(
        HANDOVER_CHECKLIST_FIELDS.map((f) => [
            f.key,
            {
                value: source?.[f.key]?.value
                    ?? (f.inputType === "checkbox" ? false : ""),
            },
        ])
    );

const checklistFromHandoverLog = (ticket) => {
    const log = ticket?.handover_log || [];
    const last = log[log.length - 1];
    const checklist = last ? cloneHandoverChecklist(last.checklist) : emptyHandoverChecklist();

    if (!last) {
        const scheduledVisits = (ticket?.schedule_ids || []).filter(Boolean).length;
        if (scheduledVisits > 0) checklist.calendarBooked = { value: "Yes" };
        checklist.visitTwice = { value: scheduledVisits > 1 };
    }

    return checklist;
};

const isHandoverChecklistComplete = (checklist) =>
    HANDOVER_CHECKLIST_FIELDS.every((f) => {
        if (f.inputType === "checkbox") return true;
        return String(checklist?.[f.key]?.value ?? "").trim() !== "";
    });

const formatHandoverAnswer = (field, entry) => {
    if (!entry) return "—";
    if (field.inputType === "checkbox") return entry.value ? "Twice" : "Once";
    return String(entry.value ?? "").trim() || (entry.na ? "N/A" : "—");
};

const checklistCellStyle = { padding: "6px 8px", textAlign: "left", verticalAlign: "top", whiteSpace: "nowrap" };

const HandoverChecklist = ({ checklist, onChange, previousLog = [] }) => (
    <div className="form-field mb-3">
        <label className="form-lbl">Inspection handover notes <span className="form-opt">(required before handing off)</span></label>
        <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px",
            border: "1px solid var(--gray-200)", borderRadius: 8, padding: "12px 14px",
        }}>
            {HANDOVER_CHECKLIST_FIELDS.map((f) => {
                const entry = checklist[f.key] || { value: f.inputType === "checkbox" ? false : "" };
                return (
                    <div key={f.key} style={{ minWidth: 0 }}>
                        <div style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: "0.8rem", color: "var(--gray-700)", fontWeight: 600, lineHeight: 1.25 }}>{f.question}</span>
                        </div>

                        {f.inputType === "checkbox" ? (
                            <label style={{
                                display: "flex", alignItems: "center", gap: 9, minHeight: 42,
                                border: "1px solid var(--gray-200)", borderRadius: 6,
                                padding: "8px 10px", cursor: "pointer", background: "white",
                            }}>
                                <input
                                    type="checkbox"
                                    checked={Boolean(entry.value)}
                                    onChange={(e) => onChange(f.key, { value: e.target.checked })}
                                    style={{ accentColor: "var(--primary)", width: 12, height: 12, marginBottom: 8, cursor: "pointer" }}
                                />
                                <span style={{ fontSize: "0.82rem", color: "var(--gray-700)" }}>
                                    {entry.value ? f.checkedLabel : f.uncheckedLabel}
                                </span>
                            </label>
                        ) : f.inputType === "select" ? (
                            <select
                                className="form-ctrl"
                                value={entry.value}
                                onChange={(e) => onChange(f.key, { value: e.target.value })}
                            >
                                <option value="">{f.inputLabel}</option>
                                {(f.options || []).map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                className="form-ctrl"
                                placeholder={f.inputLabel}
                                value={entry.value}
                                onChange={(e) => onChange(f.key, { value: e.target.value })}
                            />
                        )}
                    </div>
                );
            })}
        </div>

        <p className="mb-0 mt-2" style={{ fontSize: "0.76rem", color: "var(--gray-500)" }}>
            Visit frequency defaults to one visit. Tick “Are we visiting twice?” only when a second visit is required.
        </p>

        {previousLog.length > 0 && (
            <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--gray-600)", marginBottom: 6 }}>
                    Previous handover answers
                </div>
                <div style={{ overflowX: "auto", border: "1px solid var(--gray-200)", borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem" }}>
                        <thead>
                            <tr style={{ background: "var(--gray-50)" }}>
                                <th style={{ ...checklistCellStyle, fontWeight: 700, color: "var(--gray-500)" }}>Employee</th>
                                <th style={{ ...checklistCellStyle, fontWeight: 700, color: "var(--gray-500)" }}>When</th>
                                {HANDOVER_CHECKLIST_FIELDS.map((f) => (
                                    <th key={f.key} style={{ ...checklistCellStyle, fontWeight: 700, color: "var(--gray-500)", whiteSpace: "normal" }}>{f.question}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {previousLog.map((logEntry, i) => (
                                <tr key={i} style={{ borderTop: "1px solid var(--gray-100)" }}>
                                    <td style={{ ...checklistCellStyle, fontWeight: 600, color: "var(--gray-800)" }}>
                                        {logEntry.assignee_name || "Unknown"}
                                        {logEntry.direction === "back" && (
                                            <span style={{ marginLeft: 5, fontSize: "0.68rem", color: "#b45309" }}>↩ Amendment Required</span>
                                        )}
                                    </td>
                                    <td style={checklistCellStyle}>{logEntry.submitted_at ? fmtTimestamp(new Date(logEntry.submitted_at)) : "—"}</td>
                                    {HANDOVER_CHECKLIST_FIELDS.map((f) => (
                                        <td key={f.key} style={{ ...checklistCellStyle, whiteSpace: "normal", color: "var(--gray-600)" }}>
                                            {formatHandoverAnswer(f, logEntry.checklist?.[f.key])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}
    </div>
);



const reportSelectStyles = {
    control: (provided, { isFocused }) => ({
        ...provided,
        borderRadius: "0.375rem",
        padding: "0 0.5rem",
        borderColor: isFocused ? "#dc2626" : "#e7e5e4",
        boxShadow: isFocused ? "0 0 0 0.2rem rgba(220,38,38,.2)" : "none",
        minHeight: "42px",
        outline: "none",
        "&:hover": { borderColor: "#dc2626" },
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        cursor: "pointer",
        fontSize: "0.9rem",
    }),
    menu: (provided) => ({ ...provided, zIndex: 20 }),
    option: (provided, { isFocused, isSelected }) => ({
        ...provided,
        backgroundColor: isSelected ? "#dc2626" : isFocused ? "#fee2e2" : "white",
        color: isSelected ? "white" : "#292524",
        cursor: "pointer",
        minHeight: "36px",
        display: "flex",
        alignItems: "center",
        fontSize: "0.88rem",
    }),
    singleValue: (provided) => ({ ...provided, color: "#292524" }),
    placeholder: (provided) => ({ ...provided, color: "#a8a29e", fontSize: "0.88rem" }),
    dropdownIndicator: (provided) => ({ ...provided, color: "#dc2626", "&:hover": { color: "#b91c1c" } }),
    clearIndicator: (provided) => ({ ...provided, color: "#dc2626", "&:hover": { color: "#b91c1c" } }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};


const getTaskId = (t) => `#${t.id.slice(0, 6)}`;
const getFullTaskId = (t) => `#${t.id}`;
const getTitle = (t) => t.title || t.text || "(Untitled)";
const getDueAt = (t) => t.due_at || t.dueDate || null;
const getCreatedAt = (t) => t.created_at || t.createdAt || null;

const parseDate = (v) => {
    if (!v) return null;
    if (v.toDate) return v.toDate();
    return new Date(v);
};

const reportTimestampMillis = (value) => {
    if (!value) return 0;

    if (typeof value?.toMillis === "function") {
        const ms = value.toMillis();
        return Number.isFinite(ms) ? ms : 0;
    }

    if (typeof value?.toDate === "function") {
        const ms = value.toDate().getTime();
        return Number.isFinite(ms) ? ms : 0;
    }

    if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
    }

    const raw = String(value || "").trim();
    if (!raw) return 0;

    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(raw)
        ? raw.replace(" ", "T")
        : raw;
    const ms = new Date(normalized).getTime();
    return Number.isFinite(ms) ? ms : 0;
};


const ticketsCol = () => collection(db, "tickets");


const secsSince = (ts) => {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const secs = Math.floor((Date.now() - d.getTime()) / 1000);
    return Number.isFinite(secs) ? Math.max(0, secs) : null;
};


const statusStartOf = (ticket) =>
    ticket?.status_changed_at || ticket?.updated_at || ticket?.created_at || null;

const logEvent = (taskId, actor, event_type, note, extra = {}) => {
    const {
        from_status = null,
        to_status = null,
        duration_seconds = null,
        reward_eligible = null,
        reward_user_id = null,
        work_unit_assignee_id = null,
        work_unit_assigned_at = null,
        work_unit_due_at = null,
        work_unit_workflow_stage = null,
        completion_kind = null,
        next_assignee_id = null,
        ...meta
    } = extra;

    const fullEventWrite = addDoc(collection(db, "tickets", taskId, "events"), {
        user_id: actor.uid,
        event_type,
        from_status,
        to_status,
        duration_seconds: typeof duration_seconds === "number" ? duration_seconds : null,
        ...(typeof reward_eligible === "boolean" ? { reward_eligible } : {}),
        ...(reward_user_id ? { reward_user_id } : {}),
        ...(work_unit_assignee_id ? { work_unit_assignee_id } : {}),
        ...(work_unit_assigned_at ? { work_unit_assigned_at } : {}),
        ...(work_unit_due_at ? { work_unit_due_at } : {}),
        ...(work_unit_workflow_stage ? { work_unit_workflow_stage } : {}),
        ...(completion_kind ? { completion_kind } : {}),
        ...(next_assignee_id ? { next_assignee_id } : {}),
        note,
        meta,
        created_at: serverTimestamp(),
    });

    const embeddedWrite = updateDoc(doc(db, "tickets", taskId), {
        activity_log: arrayUnion({
            user_id: actor.uid,
            event_type,
            note,
            created_at_client: new Date().toISOString(),
        }),
    });

    return Promise.all([fullEventWrite, embeddedWrite]);
};

const markTicketSent = (fromUid, ticketId, toUid) =>
    setDoc(doc(db, "users", fromUid, "sent_tickets", ticketId), {
        ticket_id: ticketId,
        sent_to: toUid,
        sent_at: serverTimestamp(),
    });

const markTicketSentSafely = (fromUid, ticketId, toUid, actorUid) => {
    const uids = [...new Set([fromUid, actorUid].filter(Boolean))];
    return Promise.all(
        uids.map((uid) =>
            markTicketSent(uid, ticketId, toUid).catch((err) =>
                console.error(`Failed to record sent-ticket pointer for ${uid}:`, err)
            )
        )
    );
};


export const createTicket = async ({
    title,
    description = null,
    assigneeId,
    assigneeRole = null,
    assigneeLabel = null,
    assigneeStage = null,
    actorUid,
    creatorRole = null,
    priority = "Medium",
    taskType,
    dueDate,
    image = "",
    files = [],
    buildingId = null,
    buildingName = null,
}) => {
    if (!title?.trim()) throw new Error("A ticket title is required.");
    if (!assigneeId) throw new Error("An assignee is required.");
    if (!dueDate) throw new Error("A due date is required.");
    if (!actorUid) throw new Error("Missing actor uid (who is creating this ticket).");

    const now = new Date().toISOString();
    const normalizedAssigneeRole = normalizeUserRole(assigneeRole);
    const explicitStage = stageByKey(assigneeStage);
    const fallbackStage = normalizedAssigneeRole === "coordinator"
        ? stageByKey("coordinator_qc")
        : STAGE_CHAIN.find((stage) => stage.role === normalizedAssigneeRole);
    const startStage = explicitStage || fallbackStage || STAGE_CHAIN[0];
    const startIdx = STAGE_CHAIN.findIndex((stage) => stage.key === startStage.key);
    const resolvedTaskType = taskType || startStage.taskType || null;

    const docRef = await addDoc(ticketsCol(), {
        title: title.trim(),
        description: description?.trim() || null,
        assignee_id: assigneeId,
        created_by_id: actorUid,
        created_by_role: normalizeUserRole(creatorRole) || null,
        status: "ready",
        priority,
        task_type: resolvedTaskType,
        workflow_stage: startStage.key,
        due_at: dueDate,
        completed_at: null,
        blocked_reason: null, waiting_on_type: null,
        next_follow_up_at: null, waiting_started_at: null,
        archived: false,
        image,
        stage_index: startIdx === -1 ? 0 : startIdx,
        active_by_id: null, activeAt: null, currentSessionId: null,
        is_paused: false, paused_at: null,
        status_changed_at: serverTimestamp(),
        total_time_seconds: 0,
        schedule_ids: [],
        participant_ids: [assigneeId],
        ...(buildingId ? { building_id: buildingId } : {}),
        ...(buildingName ? { building_name: buildingName } : {}),
        assignment_history: [{
            assignee_id: assigneeId,
            assigned_at: now,
            unassigned_at: null,
            assigned_by: actorUid,
            workflow_stage: startStage.key,
            due_at: dueDate,
            work_note: null,
            direction: "start",
            reason: null,
        }],
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        updated_by_id: actorUid,
    });

    const attachments = await uploadTicketFiles(docRef.id, files, "creation");
    const firstImage = attachments.find((attachment) => attachmentIsImage(attachment));

    if (attachments.length > 0) {
        await updateDoc(docRef, {
            attachments,
            ...(firstImage ? { image: firstImage.url } : {}),
            updated_at: serverTimestamp(),
            updated_by_id: actorUid,
        });
    }

    await logEvent(docRef.id, { uid: actorUid }, "created",
        `Created${buildingName ? ` for ${buildingName}` : ""}${assigneeLabel ? ` and assigned to ${assigneeLabel}` : ""}`,
        { to_status: "ready", attachments }
    );


    if (buildingId && buildingName) {
        await addDoc(collection(db, "tickets", docRef.id, "task_links"), {
            entity_type: "building",
            display_label: buildingName,
            building_id: buildingId,
        });
    }

    if (actorUid !== assigneeId) {
        await markTicketSentSafely(actorUid, docRef.id, assigneeId, actorUid);
    }

    await notifyUserSafely(assigneeId, {
        type: "task_assigned",
        ticket_id: docRef.id,
        ticket_title: title.trim(),
        actor_id: actorUid,
        workflow_stage: startStage.key,
        message: `A task was assigned to you: ${title.trim()}`,
        dedupe_key: `created:${now}:${assigneeId}`,
    });

    return docRef;
};


// export const CLIENT_QUERY_ASSIGNEE_UID = "YmbjYY2JTmX7PtTZ6iOoYOC11A33";
export const CLIENT_QUERY_ASSIGNEE_UID = "9cBQOcNhO8Nc1EOhE0gFhcThruE3";
export const CLIENT_QUERY_TASK_TYPE = "client_query";

export const getClientQueryDueDate = () => {
    const due = new Date();
    let workingDaysAdded = 0;

    while (workingDaysAdded < 3) {
        due.setDate(due.getDate() + 1);
        const day = due.getDay();

        if (day !== 0 && day !== 6) {
            workingDaysAdded += 1;
        }
    }

    due.setHours(23, 59, 59, 999);
    return due;
};

export const createClientQueryTicket = async ({
    title,
    description = null,
    actorUid,
    creatorRole = null,
    priority = "Medium",
    files = [],
    buildingId = null,
    buildingName = null,
}) => {
    if (!title?.trim()) throw new Error("A ticket title is required.");
    if (!actorUid) throw new Error("Missing actor uid (who is creating this ticket).");

    const [assigneeSnap, actorSnap] = await Promise.all([
        getDoc(doc(db, "users", CLIENT_QUERY_ASSIGNEE_UID)).catch((err) => {
            console.log("Could not read fixed client-query assignee profile; using the fixed uid only.", err);
            return null;
        }),
        creatorRole
            ? Promise.resolve(null)
            : getDoc(doc(db, "users", actorUid)).catch((err) => {
                console.log("Could not read client-query creator profile; continuing without creator role.", err);
                return null;
            }),
    ]);

    const assigneeData = assigneeSnap?.exists?.() ? assigneeSnap.data() : {};
    const actorData = actorSnap?.exists?.() ? actorSnap.data() : {};
    const assigneeRole = assigneeData?.user_role || null;
    const resolvedCreatorRole = creatorRole || actorData?.user_role || null;
    const assigneeLabel = assigneeData?.name || assigneeData?.email || "Client Query Assignee";

    const docRef = await createTicket({
        title,
        description,
        assigneeId: CLIENT_QUERY_ASSIGNEE_UID,
        assigneeRole,
        assigneeLabel,
        assigneeStage: assigneeRole === "coordinator" ? "coordinator_qc" : null,
        actorUid,
        creatorRole: resolvedCreatorRole,
        priority,
        taskType: CLIENT_QUERY_TASK_TYPE,
        dueDate: getClientQueryDueDate(),
        files,
        buildingId,
        buildingName,
    });

    if (buildingId) {
        try {
            await updateDoc(doc(db, "Reports", buildingId), {
                ticket_id: docRef.id,
                linked_ticket_ids: arrayUnion(docRef.id),
            });
        } catch (err) {
            console.error("Client query ticket was created, but linking it back to the report failed:", err);
        }
    }

    return docRef;
};

const openSession = (taskId, actor) =>
    addDoc(collection(db, "tickets", taskId, "workSessions"), {
        user_id: actor.uid,
        started_at: serverTimestamp(),
        stopped_at: null,
        stop_reason: null,
        duration_seconds: null,
    });

const closeSession = (taskId, sessionId, stop_reason, activeAt, durationOverride = null) => {
    const duration_seconds = Number.isFinite(durationOverride)
        ? Math.max(0, Math.floor(durationOverride))
        : activeAt
            ? Math.max(0, Math.floor(
                (Date.now() - (activeAt.toDate ? activeAt.toDate() : new Date(activeAt)).getTime()) / 1000
            ))
            : 0;
    return Promise.all([
        updateDoc(doc(db, "tickets", taskId, "workSessions", sessionId), {
            stopped_at: serverTimestamp(), stop_reason, duration_seconds,
        }),

        updateDoc(doc(db, "tickets", taskId), {
            total_time_seconds: increment(duration_seconds),
        }),
    ]);
};

const useElapsedTime = (activeAt) => {
    const [elapsed, setElapsed] = useState(0);
    const timerRef = useRef(null);
    useEffect(() => {
        clearInterval(timerRef.current);
        if (!activeAt) { setElapsed(0); return; }
        const start = activeAt.toDate ? activeAt.toDate() : new Date(activeAt);
        const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start.getTime()) / 1000)));
        tick();
        timerRef.current = setInterval(tick, 1000);
        return () => clearInterval(timerRef.current);
    }, [activeAt]);
    return elapsed;
};


const useTicketWorkSessions = (ticketId) => {
    const [sessions, setSessions] = useState([]);
    useEffect(() => {
        if (!ticketId) { setSessions([]); return; }
        return onSnapshot(
            collection(db, "tickets", ticketId, "workSessions"),
            (s) => setSessions(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
            console.error
        );
    }, [ticketId]);
    return sessions;
};

const formatTime = (s) => {
    const h = Math.floor(s / 3600).toString().padStart(2, "0");
    const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${h}:${m}:${sec}`;
};


const formatDuration = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${s}s`;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};


const todayISO = () => {
    const d = new Date();
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

const dateOnlyStartOfDay = (value) => {
    if (!value) return null;
    if (value instanceof Date) {
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);
        return d;
    }
    const raw = String(value).slice(0, 10);
    const d = new Date(`${raw}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
};

const dateOnlyEndOfDay = (value) => {
    const d = dateOnlyStartOfDay(value);
    if (!d) return null;
    d.setHours(23, 59, 59, 999);
    return d;
};

const timestampToMillis = (value, fallback = Infinity) => {
    if (!value) return fallback;
    if (typeof value?.toMillis === "function") return value.toMillis();
    if (typeof value?.toDate === "function") return value.toDate().getTime();
    const millis = new Date(value).getTime();
    return Number.isFinite(millis) ? millis : fallback;
};

const toLocalDateInputValue = (value) => {
    if (!value) return "";
    const d = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 10);
};

const StatusBadge = ({ status }) => {
    const c = STATUS_CFG[status] || STATUS_CFG.ready;
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                padding: "4px 10px",
                borderRadius: "100px",
                fontSize: "0.76rem",
                fontWeight: 600,
                lineHeight: 1.4,
                whiteSpace: "nowrap",
                color: c.color,
                backgroundColor: c.bg,
                border: `1px solid ${c.color}40`,
                boxSizing: "border-box",
            }}
        >
            <span
                style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "inline-block",
                    backgroundColor: c.color,
                }}
            />
            {c.label}
        </span>
    );
};

const PriorityBadge = ({ priority }) => {
    const c = PRIORITY_CFG[priority] || PRIORITY_CFG.Medium;
    return (
        <span
            className="type-badge"
            style={{ color: c.color, backgroundColor: `${c.color}18`, borderColor: `${c.color}40` }}
        >
            {priority}
        </span>
    );
};

const TaskTypeBadge = ({ type }) => {
    const c = TASK_TYPE_CFG[type];
    if (!c) return null;
    return (
        <span
            className="type-badge"
            style={{ color: c.color, backgroundColor: `${c.color}18`, borderColor: `${c.color}30` }}
        >
            {c.label}
        </span>
    );
};

const Avatar = ({ name, small }) => (
    <span className={`tk-avatar${small ? " sm" : ""}`} title={name}>
        {name?.charAt(0)?.toUpperCase() || "?"}
    </span>
);


const ActiveSince = ({ activeAt, paused = false }) => {
    const elapsed = useElapsedTime(paused ? null : activeAt);
    if (paused) {
        return (
            <span className="active-since-tag is-paused" title="Task is paused; timer is stopped">
                <FontAwesomeIcon icon={faPause} size="xs" className="me-1" />
                Paused
            </span>
        );
    }
    if (!activeAt) return null;
    return (
        <span className="active-since-tag" title="Time active">
            <FontAwesomeIcon icon={faClock} size="xs" className="me-1" />
            {formatTime(elapsed)}
        </span>
    );
};


const TotalTimeCell = ({ ticket }) => {
    const base = typeof ticket.total_time_seconds === "number" ? ticket.total_time_seconds : 0;
    const isLive = ticket.status === "active" && !ticket.is_paused && Boolean(ticket.activeAt);
    const liveElapsed = useElapsedTime(isLive ? ticket.activeAt : null);
    const total = base + (isLive ? liveElapsed : 0);
    if (total === 0) return <span className="date-normal">—</span>;
    return (
        <span className={`total-time-cell${isLive ? " live" : ""}${ticket.is_paused ? " paused" : ""}`}>
            {formatDuration(total)}
        </span>
    );
};

const fmtDate = (ts) => {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB");
};

const fmtTimestamp = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB");
};

const fmtNotificationTimestamp = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};


const sanitizeUploadName = (name = "file") =>
    String(name)
        .replace(/[^A-Za-z0-9._() -]+/g, "_")
        .replace(/\s+/g, " ")
        .slice(0, 120) || "file";

const MB = 1024 * 1024;
const MAX_ATTACHMENT_FILES = 10;
const COMMON_ATTACHMENT_ACCEPT = [
    "image/*",
    "video/mp4", "video/webm", "video/quicktime",
    "application/pdf",
    ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx",
    ".csv", ".txt", ".msg", ".eml", ".zip",
].join(",");

const ATTACHMENT_LIMIT_HELP =
    "Images 10 MB · PDF/Office/MSG/EML 25 MB · Video/ZIP 50 MB · CSV/TXT 10 MB · max 10 files";

const fileExtensionOf = (name = "") => {
    const clean = String(name || "").split(/[?#]/)[0];
    const match = clean.match(/\.([A-Za-z0-9]{1,10})$/);
    return match ? `.${match[1].toLowerCase()}` : "";
};

const attachmentUploadPolicyForFile = (file) => {
    const ext = fileExtensionOf(file?.name);
    const type = String(file?.type || "").toLowerCase();

    if (type.startsWith("image/") || [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"].includes(ext))
        return { label: "Image", maxBytes: 10 * MB };
    if (type === "application/pdf" || ext === ".pdf")
        return { label: "PDF", maxBytes: 25 * MB };
    if (type.startsWith("video/") || [".mp4", ".webm", ".mov", ".m4v"].includes(ext))
        return { label: "Video", maxBytes: 50 * MB };
    if ([".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx"].includes(ext))
        return { label: "Office document", maxBytes: 25 * MB };
    if ([".msg", ".eml"].includes(ext) || type === "application/vnd.ms-outlook" || type === "message/rfc822")
        return { label: "Email file", maxBytes: 25 * MB };
    if ([".csv", ".txt"].includes(ext) || type === "text/csv" || type === "text/plain")
        return { label: "Text/data file", maxBytes: 10 * MB };
    if (ext === ".zip" || type === "application/zip" || type === "application/x-zip-compressed")
        return { label: "ZIP archive", maxBytes: 50 * MB };

    return null;
};

const validateAttachmentSelection = (files, existingCount = 0) => {
    const accepted = [];
    const errors = [];

    for (const file of Array.from(files || []).filter(Boolean)) {
        if (existingCount + accepted.length >= MAX_ATTACHMENT_FILES) {
            errors.push(`Maximum ${MAX_ATTACHMENT_FILES} attachments at a time.`);
            break;
        }

        const policy = attachmentUploadPolicyForFile(file);
        if (!policy) {
            errors.push(`${file.name}: this file type is not allowed.`);
            continue;
        }
        if (Number(file.size || 0) > policy.maxBytes) {
            errors.push(`${file.name}: ${policy.label} limit is ${Math.round(policy.maxBytes / MB)} MB.`);
            continue;
        }
        accepted.push(file);
    }

    return { accepted, errors };
};

const showAttachmentValidationErrors = (errors) => {
    if (!errors?.length) return;
    const message = errors.slice(0, 3).join(" ") + (errors.length > 3 ? ` +${errors.length - 3} more.` : "");
    showTicketToast(message, "Attachment not added", { autoClose: 6500 });
};

const uploadTicketFiles = async (ticketId, files, purpose = "attachment") => {
    const uploadList = Array.from(files || []).filter(Boolean);
    if (!ticketId || uploadList.length === 0) return [];

    const { accepted, errors } = validateAttachmentSelection(uploadList, 0);
    if (errors.length || accepted.length !== uploadList.length) {
        throw new Error(errors[0] || "One or more attachments are not allowed.");
    }

    return Promise.all(accepted.map(async (file, index) => {
        const storedName = `${purpose}-${Date.now()}-${index}-${sanitizeUploadName(file.name)}`;
        const storageRef = ref(storage, `tickets/${ticketId}/${storedName}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);

        return {
            name: file.name || storedName,
            url,
            type: file.type || "",
            size: Number(file.size || 0),
        };
    }));
};

const normalizeAttachment = (attachment) => {
    if (!attachment) return null;
    if (typeof attachment === "string") {
        return { name: "Attachment", url: attachment, type: "", size: 0 };
    }

    const url = attachment.url
        || attachment.downloadURL
        || attachment.download_url
        || attachment.file_url
        || attachment.fileUrl
        || attachment.src
        || "";
    const name = attachment.name
        || attachment.fileName
        || attachment.filename
        || attachment.file_name
        || attachment.originalName
        || attachment.original_name
        || "Attachment";
    const type = attachment.type
        || attachment.mimeType
        || attachment.mime_type
        || attachment.contentType
        || attachment.content_type
        || "";
    const size = Number(attachment.size ?? attachment.fileSize ?? attachment.file_size ?? 0);

    return { ...attachment, name, url, type, size };
};

const attachmentDetectionText = (attachment) => {
    const normalized = normalizeAttachment(attachment);
    if (!normalized) return "";
    let decodedUrl = String(normalized.url || "");
    try { decodedUrl = decodeURIComponent(decodedUrl); } catch { }
    return `${normalized.name || ""} ${decodedUrl}`;
};

const attachmentFilenameFromUrl = (url, fallback = "Attachment") => {
    let decodedUrl = String(url || "");
    try { decodedUrl = decodeURIComponent(decodedUrl); } catch { }

    const pathOnly = decodedUrl.split(/[?#]/)[0];
    const candidate = pathOnly.split("/").filter(Boolean).pop() || fallback;
    const cleaned = candidate.replace(/^(?:create|comment|completion|attachment)-\d+-\d+-/i, "");
    return cleaned || fallback;
};

const attachmentExtensionOf = (attachment) => {
    const normalized = normalizeAttachment(attachment);
    if (!normalized) return "";

    const nameExt = fileExtensionOf(normalized.name);
    if (nameExt) return nameExt;

    return fileExtensionOf(attachmentFilenameFromUrl(normalized.url, ""));
};

const IMAGE_ATTACHMENT_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".bmp", ".svg"];
const VIDEO_ATTACHMENT_EXTENSIONS = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];

const attachmentIsImage = (attachment) => {
    const normalized = normalizeAttachment(attachment);
    const ext = attachmentExtensionOf(normalized);

    if (ext) return IMAGE_ATTACHMENT_EXTENSIONS.includes(ext);

    return String(normalized?.type || "").toLowerCase().startsWith("image/");
};

const attachmentIsVideo = (attachment) => {
    const normalized = normalizeAttachment(attachment);
    const ext = attachmentExtensionOf(normalized);
    if (ext) return VIDEO_ATTACHMENT_EXTENSIONS.includes(ext);

    return String(normalized?.type || "").toLowerCase().startsWith("video/");
};

const attachmentIsPdf = (attachment) => {
    const normalized = normalizeAttachment(attachment);
    const ext = attachmentExtensionOf(normalized);
    if (ext) return ext === ".pdf";

    const type = String(normalized?.type || "").toLowerCase();
    return type === "application/pdf" || type.includes("pdf");
};

const formatFileSize = (bytes) => {
    const size = Number(bytes || 0);
    if (!size) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const ClientQueryTicketModal = ({
    show,
    onHide,
    buildingId = null,
    buildingName = null,
    creatorRole = null,
    onCreated = null,
    labels = null,
    sendEmailOnCreate = false,
    reportId = null,
    companyName = null,
    companyEmail = null,
}) => {
    const [user] = useAuthState(auth);
    const [queryTitle, setQueryTitle] = useState("");
    const [queryDescription, setQueryDescription] = useState("");
    const [queryPriority, setQueryPriority] = useState("Medium");
    const [queryAttachments, setQueryAttachments] = useState([]);
    const [queryError, setQueryError] = useState("");
    const [querySuccess, setQuerySuccess] = useState("");
    const [sendingQuery, setSendingQuery] = useState(false);

    const uiLabels = useMemo(() => ({
        modalTitle: "Send us a query",
        modalSubtitle: "Tell us what you need help with and our team will pick it up.",
        propertyLabel: "Property",
        titleLabel: "Query title",
        titlePlaceholder: "What would you like to ask?",
        descriptionLabel: "Query details",
        descriptionPlaceholder: "Add any information that will help us understand your query...",
        typeLabel: "Query type",
        typeValue: "Client Query",
        priorityLabel: "Priority",
        dueDateLabel: "Expected response by",
        dueDateHelp: "Automatically set to three working days from today (weekends excluded).",
        attachmentsLabel: "Supporting files",
        attachmentsHelp: ATTACHMENT_LIMIT_HELP,
        chooseFilesLabel: "Attach files",
        addMoreFilesLabel: "Add more files",
        cancelLabel: "Cancel",
        closeLabel: "Close",
        submitLabel: "Send Query",
        submittingLabel: "Sending query...",
        titleRequiredError: "Please enter a query title.",
        authRequiredError: "You need to be signed in to send a query.",
        successTitle: "Query sent",
        successMessage: "Your query has been sent successfully. Our team can now manage it as a task.",
        failureMessage: "Failed to send your query.",
        ...labels,
    }), [labels]);

    const dueDateLabel = useMemo(
        () => getClientQueryDueDate().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }),
        [show]
    );

    useEffect(() => {
        if (!show) return;
        setQueryError("");
        setQuerySuccess("");
    }, [show]);

    const resetClientQueryForm = () => {
        setQueryTitle("");
        setQueryDescription("");
        setQueryPriority("Medium");
        setQueryAttachments([]);
    };

    const handleClose = () => {
        if (sendingQuery) return;
        setQueryError("");
        setQuerySuccess("");
        resetClientQueryForm();
        onHide?.();
    };

    const handleSubmitQuery = async () => {
        if (!queryTitle.trim()) {
            setQueryError(uiLabels.titleRequiredError);
            return;
        }

        const actorUid = user?.uid || auth.currentUser?.uid;
        if (!actorUid) {
            setQueryError(uiLabels.authRequiredError);
            return;
        }

        setSendingQuery(true);
        setQueryError("");
        setQuerySuccess("");

        try {
            const docRef = await createClientQueryTicket({
                title: queryTitle.trim(),
                description: queryDescription.trim(),
                actorUid,
                creatorRole,
                priority: queryPriority,
                files: queryAttachments,
                buildingId,
                buildingName,
            });

            console.log("Client query ticket created:", docRef.id);

            if (sendEmailOnCreate) {
                try {
                    const sendClientQuery = httpsCallable(getFunctions(), "sendClientQuery");
                    const emailQuery = [
                        queryTitle.trim(),
                        queryDescription.trim(),
                    ]
                        .filter(Boolean)
                        .join("\n\n")
                        .slice(0, 2000);

                    await sendClientQuery({
                        ticketId: docRef.id,
                        buildingName: buildingName || "",
                        reportId: reportId || "",
                        companyName: companyName || "",
                        companyEmail: companyEmail || "",
                        query: emailQuery,
                    });

                    console.log("Client query email sent:", docRef.id);
                } catch (emailError) {
                    console.error(
                        "Client query ticket created, but email notification failed:",
                        emailError
                    );
                }
            }

            setQuerySuccess(uiLabels.successMessage);
            resetClientQueryForm();
            onCreated?.(docRef);
        } catch (error) {
            console.error("Error creating client query ticket:", error);
            setQueryError(error?.message || uiLabels.failureMessage);
        } finally {
            setSendingQuery(false);
        }
    };

    const handlePickedFiles = (event) => {
        const picked = Array.from(event.target.files || []);
        const { accepted, errors } = validateAttachmentSelection(
            picked,
            queryAttachments.length
        );

        if (errors.length) setQueryError(errors.slice(0, 3).join(" "));
        else setQueryError("");

        if (accepted.length) {
            setQueryAttachments((current) => [...current, ...accepted]);
        }

        event.target.value = "";
    };

    return (
        <ReactModal
            isOpen={Boolean(show)}
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={!sendingQuery}
            ariaHideApp={false}
            contentLabel={uiLabels.modalTitle}
            style={{
                overlay: {
                    position: "fixed",
                    inset: 0,
                    zIndex: 12000,
                    background: "rgba(15, 23, 42, 0.58)",
                    backdropFilter: "blur(5px)",
                    WebkitBackdropFilter: "blur(5px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "14px",
                },
                content: {
                    position: "relative",
                    inset: "auto",
                    width: "min(760px, 100%)",
                    maxWidth: "760px",
                    maxHeight: "96vh",
                    margin: 0,
                    padding: 0,
                    overflow: "hidden",
                    border: "1px solid rgba(226, 232, 240, 0.95)",
                    borderRadius: "18px",
                    background: "#ffffff",
                    boxShadow: "0 26px 75px rgba(15, 23, 42, 0.28)",
                },
            }}
        >
            <style>{`
                .client-query-modal-shell {
                    display: flex;
                    flex-direction: column;
                    max-height: 96vh;
                    background: #fff;
                    color: #0f172a;
                }

                .client-query-modal-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 15px;
                    padding: 18px 22px 16px;
                    border-bottom: 1px solid #eef2f7;
                    background: linear-gradient(180deg, #ffffff 0%, #fffafa 100%);
                }

                .client-query-modal-heading {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    min-width: 0;
                }

                .client-query-modal-icon {
                    width: 42px;
                    height: 42px;
                    border-radius: 11px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex: 0 0 auto;
                    color: #dc2626;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    font-size: 1rem;
                }

                .client-query-modal-title {
                    margin: 0;
                    color: #111827;
                    font-size: 1.16rem;
                    line-height: 1.25;
                    font-weight: 800;
                    letter-spacing: -0.015em;
                }

                .client-query-modal-subtitle {
                    margin: 5px 0 0;
                    max-width: 560px;
                    color: #64748b;
                    font-size: .84rem;
                    line-height: 1.4;
                }

                .client-query-modal-close {
                    width: 36px;
                    height: 36px;
                    border: 0;
                    border-radius: 9px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex: 0 0 auto;
                    background: #f8fafc;
                    color: #64748b;
                    cursor: pointer;
                    transition: background .15s ease, color .15s ease;
                }

                .client-query-modal-close:hover:not(:disabled) {
                    background: #f1f5f9;
                    color: #0f172a;
                }

                .client-query-modal-close:disabled {
                    opacity: .5;
                    cursor: not-allowed;
                }

                .client-query-modal-body {
                    padding: 17px 22px 18px;
                    overflow-y: auto;
                    flex: 1 1 auto;
                    min-height: 0;
                }

                .client-query-context {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    padding: 10px 12px;
                    margin-bottom: 13px;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    background: #f8fafc;
                }

                .client-query-context-label {
                    color: #94a3b8;
                    font-size: .7rem;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: .06em;
                }

                .client-query-context-value {
                    min-width: 0;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #334155;
                    font-size: .85rem;
                    font-weight: 700;
                }

                .client-query-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    column-gap: 16px;
                    row-gap: 0;
                }

                .client-query-field {
                    min-width: 0;
                    margin-bottom: 12px;
                }

                .client-query-field.full {
                    grid-column: 1 / -1;
                }

                .client-query-label {
                    display: block;
                    margin-bottom: 6px;
                    color: #334155;
                    font-size: .8rem;
                    font-weight: 800;
                }

                .client-query-optional {
                    color: #94a3b8;
                    font-weight: 600;
                }

                .client-query-control {
                    width: 100%;
                    height: 44px;
                    min-height: 44px;
                    padding: 8px 12px;
                    border: 1px solid #dbe2ea;
                    border-radius: 10px;
                    outline: none;
                    background: #ffffff;
                    color: #0f172a;
                    font-size: .9rem;
                    box-sizing: border-box;
                    transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
                }

                .client-query-control::placeholder {
                    color: #94a3b8;
                }

                .client-query-control:focus {
                    border-color: #ef4444;
                    box-shadow: 0 0 0 3px rgba(239, 68, 68, .09);
                }

                .client-query-control:disabled,
                .client-query-control[readonly] {
                    color: #475569;
                    background: #f8fafc;
                    cursor: default;
                }

                textarea.client-query-control {
                    height: 86px;
                    min-height: 86px;
                    max-height: 86px;
                    padding: 10px 12px;
                    line-height: 1.45;
                    resize: none;
                }

                .client-query-readonly {
                    position: relative;
                }

                .client-query-readonly .client-query-control {
                    padding-right: 36px;
                }

                .client-query-lock {
                    position: absolute;
                    right: 11px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    font-size: .75rem;
                    pointer-events: none;
                }

                .client-query-help {
                    margin-top: 5px;
                    color: #94a3b8;
                    font-size: .7rem;
                    line-height: 1.35;
                }

                .client-query-dropzone {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 9px;
                    width: 100%;
                    min-height: 54px;
                    padding: 11px 14px;
                    border: 1px dashed #cbd5e1;
                    border-radius: 10px;
                    background: #fbfdff;
                    color: #475569;
                    font-size: .83rem;
                    font-weight: 750;
                    cursor: pointer;
                    transition: border-color .15s ease, background .15s ease, color .15s ease;
                    box-sizing: border-box;
                }

                .client-query-dropzone:hover {
                    border-color: #fca5a5;
                    background: #fffafa;
                    color: #b91c1c;
                }

                .client-query-dropzone input {
                    position: absolute;
                    inset: 0;
                    opacity: 0;
                    cursor: pointer;
                }

                .client-query-files {
                    display: grid;
                    gap: 6px;
                    margin-top: 7px;
                    max-height: min(168px, 22vh);
                    overflow-y: auto;
                    padding-right: 4px;
                }

                .client-query-files::-webkit-scrollbar {
                    width: 6px;
                }

                .client-query-files::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 6px;
                }

                .client-query-files::-webkit-scrollbar-track {
                    background: transparent;
                }

                .client-query-file {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                    min-width: 0;
                    padding: 7px 10px;
                    border: 1px solid #e2e8f0;
                    border-radius: 9px;
                    background: #ffffff;
                }

                .client-query-file-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex: 0 0 auto;
                    color: #64748b;
                    background: #f1f5f9;
                }

                .client-query-file-name {
                    min-width: 0;
                    flex: 1;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #334155;
                    font-size: .78rem;
                    font-weight: 700;
                }

                .client-query-file-remove {
                    width: 30px;
                    height: 30px;
                    flex: 0 0 auto;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border: 0;
                    border-radius: 8px;
                    background: transparent;
                    color: #94a3b8;
                    cursor: pointer;
                    transition: background .15s ease, color .15s ease;
                }

                .client-query-file-remove:hover:not(:disabled) {
                    background: #fef2f2;
                    color: #dc2626;
                }

                .client-query-file-remove:disabled {
                    opacity: .45;
                    cursor: not-allowed;
                }

                .client-query-alert {
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                    margin-top: 6px;
                    padding: 9px 11px;
                    border-radius: 9px;
                    font-size: .78rem;
                    line-height: 1.4;
                }

                .client-query-alert.error {
                    color: #991b1b;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                }

                .client-query-success {
                    min-height: 240px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    padding: 28px;
                }

                .client-query-success-icon {
                    width: 54px;
                    height: 54px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 13px;
                    background: #ecfdf5;
                    color: #059669;
                    font-size: 1.4rem;
                }

                .client-query-success h4 {
                    margin: 0 0 7px;
                    color: #0f172a;
                    font-size: 1.08rem;
                    font-weight: 800;
                }

                .client-query-success p {
                    margin: 0;
                    max-width: 440px;
                    color: #64748b;
                    font-size: .86rem;
                    line-height: 1.5;
                }

                .client-query-modal-footer {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 9px;
                    padding: 12px 22px;
                    border-top: 1px solid #eef2f7;
                    background: #fbfdff;
                }

                .client-query-btn {
                    min-height: 42px;
                    padding: 8px 17px;
                    border-radius: 9px;
                    font-size: .84rem;
                    font-weight: 800;
                    cursor: pointer;
                    transition: background .15s ease, border-color .15s ease, box-shadow .15s ease, opacity .15s ease;
                }

                .client-query-btn.secondary {
                    border: 1px solid #dbe2ea;
                    background: #ffffff;
                    color: #475569;
                }

                .client-query-btn.secondary:hover:not(:disabled) {
                    background: #f8fafc;
                }

                .client-query-btn.primary {
                    min-width: 140px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    border: 1px solid #dc2626;
                    background: #dc2626;
                    color: #ffffff;
                    box-shadow: 0 5px 12px rgba(220, 38, 38, .16);
                }

                .client-query-btn.primary:hover:not(:disabled) {
                    background: #c81e1e;
                    border-color: #c81e1e;
                }

                .client-query-btn:disabled {
                    opacity: .55;
                    cursor: not-allowed;
                }

                @media (max-height: 720px) {
                    .client-query-modal-body {
                        overflow-y: auto;
                    }

                    .client-query-modal-shell {
                        max-height: 94vh;
                    }
                }

                @media (max-width: 640px) {
                    .client-query-modal-header {
                        padding: 15px 16px 13px;
                    }

                    .client-query-modal-body {
                        padding: 14px 16px 15px;
                        overflow-y: auto;
                    }

                    .client-query-modal-footer {
                        padding: 11px 16px;
                    }

                    .client-query-grid {
                        grid-template-columns: 1fr;
                    }

                    .client-query-field.full {
                        grid-column: auto;
                    }

                    .client-query-modal-subtitle {
                        font-size: .78rem;
                    }

                    .client-query-context {
                        margin-bottom: 11px;
                    }

                    .client-query-control,
                    textarea.client-query-control {
                        font-size: 16px;
                    }

                    .client-query-modal-footer .client-query-btn {
                        flex: 1;
                    }
                }
            `}</style>

            <div className="client-query-modal-shell">
                <div className="client-query-modal-header">
                    <div className="client-query-modal-heading">
                        <div className="client-query-modal-icon">
                            <FontAwesomeIcon icon={faComment} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <h3 className="client-query-modal-title">{uiLabels.modalTitle}</h3>
                            <p className="client-query-modal-subtitle">{uiLabels.modalSubtitle}</p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="client-query-modal-close"
                        onClick={handleClose}
                        disabled={sendingQuery}
                        aria-label="Close query modal"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {querySuccess ? (
                    <>
                        <div className="client-query-modal-body">
                            <div className="client-query-success">
                                <div className="client-query-success-icon">
                                    <FontAwesomeIcon icon={faCheckCircle} />
                                </div>
                                <h4>{uiLabels.successTitle}</h4>
                                <p>{querySuccess}</p>
                            </div>
                        </div>
                        <div className="client-query-modal-footer">
                            <button
                                type="button"
                                className="client-query-btn primary"
                                onClick={handleClose}
                            >
                                {uiLabels.closeLabel}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="client-query-modal-body">
                            {buildingName && (
                                <div className="client-query-context">
                                    <span className="client-query-context-label">{uiLabels.propertyLabel}</span>
                                    <span className="client-query-context-value" title={buildingName}>{buildingName}</span>
                                </div>
                            )}

                            <div className="client-query-grid">
                                <div className="client-query-field full">
                                    <label className="client-query-label">{uiLabels.titleLabel}</label>
                                    <input
                                        type="text"
                                        className="client-query-control"
                                        placeholder={uiLabels.titlePlaceholder}
                                        value={queryTitle}
                                        onChange={(e) => {
                                            setQueryTitle(e.target.value);
                                            if (queryError) setQueryError("");
                                        }}
                                        autoFocus
                                        disabled={sendingQuery}
                                        maxLength={160}
                                    />
                                </div>

                                <div className="client-query-field full">
                                    <label className="client-query-label">
                                        {uiLabels.descriptionLabel} <span className="client-query-optional">(optional)</span>
                                    </label>
                                    <textarea
                                        className="client-query-control"
                                        placeholder={uiLabels.descriptionPlaceholder}
                                        value={queryDescription}
                                        onChange={(e) => setQueryDescription(e.target.value)}
                                        disabled={sendingQuery}
                                        rows={3}
                                    />
                                </div>

                                <div className="client-query-field">
                                    <label className="client-query-label">{uiLabels.typeLabel}</label>
                                    <div className="client-query-readonly">
                                        <input
                                            type="text"
                                            className="client-query-control"
                                            value={uiLabels.typeValue}
                                            readOnly
                                            tabIndex={-1}
                                        />
                                        <span className="client-query-lock">
                                            <FontAwesomeIcon icon={faTag} />
                                        </span>
                                    </div>
                                </div>

                                <div className="client-query-field">
                                    <label className="client-query-label">{uiLabels.priorityLabel}</label>
                                    <select
                                        className="client-query-control"
                                        value={queryPriority}
                                        onChange={(e) => setQueryPriority(e.target.value)}
                                        disabled={sendingQuery}
                                    >
                                        {PRIORITY_OPTIONS.map((priority) => (
                                            <option key={priority} value={priority}>{priority}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="client-query-field full">
                                    <label className="client-query-label">{uiLabels.dueDateLabel}</label>
                                    <div className="client-query-readonly">
                                        <input
                                            type="text"
                                            className="client-query-control"
                                            value={dueDateLabel}
                                            readOnly
                                            tabIndex={-1}
                                        />
                                        <span className="client-query-lock">
                                            <FontAwesomeIcon icon={faCalendar} />
                                        </span>
                                    </div>
                                    <div className="client-query-help">{uiLabels.dueDateHelp}</div>
                                </div>

                                <div className="client-query-field full" style={{ marginBottom: 0 }}>
                                    <label className="client-query-label">
                                        {uiLabels.attachmentsLabel} <span className="client-query-optional">(optional)</span>
                                    </label>

                                    <label className="client-query-dropzone">
                                        <input
                                            type="file"
                                            multiple
                                            accept={COMMON_ATTACHMENT_ACCEPT}
                                            disabled={sendingQuery}
                                            onChange={handlePickedFiles}
                                        />
                                        <FontAwesomeIcon icon={faLink} />
                                        <span>
                                            {queryAttachments.length
                                                ? uiLabels.addMoreFilesLabel
                                                : uiLabels.chooseFilesLabel}
                                        </span>
                                    </label>

                                    <div className="client-query-help">{uiLabels.attachmentsHelp}</div>

                                    {queryAttachments.length > 0 && (
                                        <div className="client-query-files">
                                            {queryAttachments.map((file, index) => (
                                                <div className="client-query-file" key={`${file.name}-${file.size}-${index}`}>
                                                    <div className="client-query-file-icon">
                                                        <FontAwesomeIcon
                                                            icon={file.type?.startsWith("image/") ? faImage : faDownload}
                                                            size="xs"
                                                        />
                                                    </div>
                                                    <div className="client-query-file-name" title={file.name}>
                                                        {file.name}
                                                        {file.size ? ` · ${formatFileSize(file.size)}` : ""}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="client-query-file-remove"
                                                        onClick={() => setQueryAttachments((current) =>
                                                            current.filter((_, i) => i !== index)
                                                        )}
                                                        disabled={sendingQuery}
                                                        title="Remove attachment"
                                                        aria-label={`Remove ${file.name}`}
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} size="xs" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {queryError && (
                                <div className="client-query-alert error" role="alert">
                                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginTop: 2 }} />
                                    <span>{queryError}</span>
                                </div>
                            )}
                        </div>

                        <div className="client-query-modal-footer">
                            <button
                                type="button"
                                className="client-query-btn secondary"
                                onClick={handleClose}
                                disabled={sendingQuery}
                            >
                                {uiLabels.cancelLabel}
                            </button>
                            <button
                                type="button"
                                className="client-query-btn primary"
                                onClick={handleSubmitQuery}
                                disabled={sendingQuery || !queryTitle.trim()}
                            >
                                {sendingQuery ? (
                                    <>
                                        <Spinner size="sm" animation="border" />
                                        {uiLabels.submittingLabel}
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faPaperPlane} />
                                        {uiLabels.submitLabel}
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </ReactModal>
    );
};


const attachmentCanPreview = (attachment) =>
    attachmentIsPdf(attachment) || attachmentIsImage(attachment) || attachmentIsVideo(attachment);

const triggerBlobDownload = (blob, filename) => {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename || "Attachment";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
};

const downloadAttachmentFile = async (attachment) => {
    const normalized = normalizeAttachment(attachment);
    if (!normalized?.url) return;

    const filename = normalized.name || "Attachment";

    try {
        const blob = await getBlob(ref(storage, normalized.url));
        triggerBlobDownload(blob, filename);
        return;
    } catch (firebaseErr) {
        console.log("Firebase blob download failed; trying normal fetch.", firebaseErr);
    }

    try {
        const response = await fetch(normalized.url);
        if (!response.ok) throw new Error(`Download failed (${response.status})`);
        triggerBlobDownload(await response.blob(), filename);
        return;
    } catch (fetchErr) {
        console.log("Blob download failed; using direct file link.", fetchErr);
    }

    const link = document.createElement("a");
    link.href = normalized.url;
    link.download = filename;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
};

const MediaViewerModal = ({ media, onClose }) => {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
    const touchStartRef = useRef(null);

    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
        setIsDragging(false);
    }, [media?.src]);

    useEffect(() => {
        if (!media) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [media, onClose]);

    if (!media || typeof document === "undefined") return null;

    const minZoom = 1;
    const maxZoom = 5;
    const clampZoom = (value) => Math.min(maxZoom, Math.max(minZoom, value));
    const updateZoom = (next) => {
        const value = clampZoom(next);
        setZoom(value);
        if (value === 1) setPan({ x: 0, y: 0 });
    };
    const zoomInBtn = () => updateZoom(zoom + 0.25);
    const zoomOutBtn = () => updateZoom(zoom - 0.25);
    const resetZoom = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };
    const handleWheelZoom = (event) => {
        event.preventDefault();
        updateZoom(zoom + (event.deltaY < 0 ? 0.15 : -0.15));
    };
    const handleMouseDown = (event) => {
        if (zoom <= 1) return;
        event.preventDefault();
        dragStartRef.current = { x: event.clientX, y: event.clientY, panX: pan.x, panY: pan.y };
        setIsDragging(true);
    };
    const handleMouseMoveDrag = (event) => {
        if (!isDragging || zoom <= 1) return;
        const start = dragStartRef.current;
        setPan({
            x: start.panX + (event.clientX - start.x),
            y: start.panY + (event.clientY - start.y),
        });
    };
    const handleMouseUp = () => setIsDragging(false);
    const handleTouchStart = (event) => {
        if (event.touches.length !== 1 || zoom <= 1) return;
        const touch = event.touches[0];
        touchStartRef.current = { x: touch.clientX, y: touch.clientY, panX: pan.x, panY: pan.y };
        setIsDragging(true);
    };
    const handleTouchMove = (event) => {
        if (!touchStartRef.current || event.touches.length !== 1 || zoom <= 1) return;
        const touch = event.touches[0];
        const start = touchStartRef.current;
        setPan({
            x: start.panX + (touch.clientX - start.x),
            y: start.panY + (touch.clientY - start.y),
        });
    };
    const handleTouchEnd = () => {
        touchStartRef.current = null;
        setIsDragging(false);
    };

    const mediaStyle = {
        width: "100%",
        height: "100%",
        objectFit: "contain",
        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        transition: isDragging ? "none" : "transform 0.1s ease-out",
        transformOrigin: "center center",
        cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
        userSelect: "none",
    };

    return createPortal(
        <div className="media-modal-overlay" onMouseDown={onClose}>
            <div
                className="media-modal-container"
                onMouseDown={(event) => event.stopPropagation()}
                style={{ width: "min(92vw, 1000px)", height: "min(85vh, 700px)" }}
            >
                <div className="image-viewer-container" onWheel={handleWheelZoom}>
                    {media.isVideo ? (
                        <video
                            src={media.src}
                            controls
                            muted
                            autoPlay
                            draggable={false}
                            style={{ ...mediaStyle, cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMoveDrag}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            onDoubleClick={() => (zoom > 1 ? resetZoom() : updateZoom(2))}
                        />
                    ) : (
                        <img
                            src={media.src}
                            alt={media.label || "Image preview"}
                            draggable={false}
                            style={mediaStyle}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMoveDrag}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            onDoubleClick={() => (zoom > 1 ? resetZoom() : updateZoom(2))}
                        />
                    )}

                    <div className="media-viewer-topbar">
                        <div className="media-viewer-title-wrap">
                            <span className="media-viewer-type">{media.isVideo ? "Video" : "Photo"}</span>
                            <span className="media-viewer-title">{media.label || (media.isVideo ? "Video" : "Photo")}</span>
                        </div>
                        <button type="button" className="media-viewer-icon-btn media-viewer-close" onClick={onClose} aria-label="Close media viewer">×</button>
                    </div>

                    <div className="media-viewer-controls">
                        <button type="button" className="media-viewer-icon-btn" onClick={zoomOutBtn} aria-label="Zoom out">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 12H4" /></svg>
                        </button>
                        <span className="media-viewer-zoom">{Math.round(zoom * 100)}%</span>
                        <button type="button" className="media-viewer-icon-btn" onClick={zoomInBtn} aria-label="Zoom in">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v16M20 12H4" /></svg>
                        </button>
                        <button type="button" className="media-viewer-icon-btn" onClick={resetZoom} title="Reset zoom" aria-label="Reset zoom">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

const AttachmentCards = ({ attachments = [], onOpenAttachment }) => {
    const files = Array.isArray(attachments)
        ? attachments.map(normalizeAttachment).filter((item) => item?.url)
        : [];
    if (files.length === 0) return null;

    return (
        <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
            {files.map((attachment, index) => {
                const isPdf = attachmentIsPdf(attachment);
                const isImage = !isPdf && attachmentIsImage(attachment);
                const isVideo = !isPdf && attachmentIsVideo(attachment);
                const canPreview = attachmentCanPreview(attachment);
                const extensionLabel = attachmentExtensionOf(attachment)
                    .replace(/^\./, "")
                    .toUpperCase();
                return (
                    <button
                        type="button"
                        key={`${attachment.url}-${index}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!canPreview) {
                                void downloadAttachmentFile(attachment);
                                return;
                            }
                            if (onOpenAttachment) onOpenAttachment(attachment);
                            else window.open(attachment.url, "_blank", "noopener,noreferrer");
                        }}
                        style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 10,
                            border: "1px solid var(--gray-200)", borderRadius: 8,
                            padding: 8, background: "white", textDecoration: "none",
                            color: "var(--gray-800)", minWidth: 0, textAlign: "left", cursor: "pointer",
                        }}
                    >
                        {isImage ? (
                            <img
                                src={attachment.url}
                                alt={attachment.name || "Attachment"}
                                style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 6, border: "1px solid var(--gray-100)", flexShrink: 0 }}
                            />
                        ) : (
                            <div style={{
                                width: 54, height: 54, borderRadius: 6, flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                background: isPdf ? "rgba(220,38,38,0.08)" : "var(--gray-50)",
                                color: isPdf ? "#dc2626" : "var(--gray-500)",
                                fontWeight: 800, fontSize: "0.72rem",
                            }}>
                                {isPdf ? "PDF" : <FontAwesomeIcon icon={faDownload} />}
                            </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: "0.82rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {attachment.name || "Attachment"}
                            </div>
                            <div style={{ marginTop: 2, fontSize: "0.72rem", color: "var(--gray-500)" }}>
                                {isImage ? "Image" : isVideo ? "Video" : isPdf ? "PDF document" : (extensionLabel || "File")}
                                {formatFileSize(attachment.size) ? ` · ${formatFileSize(attachment.size)}` : ""}
                                {canPreview ? " · Preview" : " · Download"}
                            </div>
                        </div>
                        <FontAwesomeIcon icon={canPreview ? faLink : faDownload} size="xs" style={{ color: "var(--gray-400)", flexShrink: 0 }} />
                    </button>
                );
            })}
        </div>
    );
};

const ExpandableDescription = ({ text, limit = 220, initiallyExpanded = false }) => {
    const [expanded, setExpanded] = useState(Boolean(initiallyExpanded));
    useEffect(() => { setExpanded(Boolean(initiallyExpanded)); }, [text, initiallyExpanded]);
    const value = String(text || "");
    const isLong = value.length > limit;
    const visible = !isLong || expanded ? value : `${value.slice(0, limit).trimEnd()}…`;

    return (
        <div style={{ marginTop: 10 }}>
            <p className="detail-desc" style={{ marginBottom: isLong ? 4 : undefined, whiteSpace: "pre-wrap" }}>
                {visible}
            </p>
            {isLong && (
                <button
                    type="button"
                    onClick={() => setExpanded((current) => !current)}
                    style={{
                        border: 0, background: "transparent", padding: 0,
                        color: "var(--primary)", fontSize: "0.78rem", fontWeight: 700,
                        cursor: "pointer",
                    }}
                >
                    {expanded ? "Show less" : "Show more"}
                </button>
            )}
        </div>
    );
};

const ActiveExpandableDescription = ({ text, limit = 180, onShowMore }) => {
    const value = String(text || "").trim();
    const isLong = value.length > limit;
    const visible = isLong ? `${value.slice(0, limit).trimEnd()}…` : value;

    return (
        <div className="active-description-preview">
            <p className="active-desc">{visible}</p>
            {isLong && onShowMore && (
                <button
                    type="button"
                    className="active-description-more"
                    onClick={onShowMore}
                    aria-label="Open task details"
                >
                    Show more
                </button>
            )}
        </div>
    );
};

const sentAtFor = (ticket, uid) => {
    const history = ticket?.assignment_history || [];
    const candidates = [];
    history.forEach((h) => {
        if (h.assigned_by === uid && h.assigned_at) candidates.push(h.assigned_at);
        if (h.assignee_id === uid && h.unassigned_at) candidates.push(h.unassigned_at);
    });
    if (!candidates.length) return null;
    return candidates.reduce((latest, value) => {
        const time = new Date(value).getTime();
        return !latest || time > latest.time ? { time, value } : latest;
    }, null)?.value || null;
};

const isOverdue = (t) => {
    const due = getDueAt(t);
    if (!due || ["completed", "cancelled"].includes(t.status)) return false;
    const d = due.toDate ? due.toDate() : new Date(due);
    return d.getTime() < Date.now();
};

const isDueToday = (t) => {
    const due = getDueAt(t);
    if (!due) return false;
    const d = due.toDate ? due.toDate() : new Date(due);
    return d.toDateString() === new Date().toDateString();
};

const sortQueue = (tickets) =>
    [...tickets].sort((a, b) => {
        const aDue = getDueAt(a)?.toMillis?.()
            ?? (getDueAt(a) ? new Date(getDueAt(a)).getTime() : Infinity);

        const bDue = getDueAt(b)?.toMillis?.()
            ?? (getDueAt(b) ? new Date(getDueAt(b)).getTime() : Infinity);

        if (aDue !== bDue) return aDue - bDue;

        const priorityDiff =
            (PRIORITY_ORDER[b.priority] ?? -1) -
            (PRIORITY_ORDER[a.priority] ?? -1);

        if (priorityDiff !== 0) return priorityDiff;

        const aCreated = getCreatedAt(a)?.toMillis?.()
            ?? (getCreatedAt(a) ? new Date(getCreatedAt(a)).getTime() : 0);

        const bCreated = getCreatedAt(b)?.toMillis?.()
            ?? (getCreatedAt(b) ? new Date(getCreatedAt(b)).getTime() : 0);

        return aCreated - bCreated;
    });


const summarizeSessions = (sessions, liveUid, liveSeconds) => {
    const totals = {};
    sessions.forEach((s) => {
        if (typeof s.duration_seconds === "number") {
            totals[s.user_id] = (totals[s.user_id] || 0) + s.duration_seconds;
        }
    });
    if (liveUid) {
        totals[liveUid] = (totals[liveUid] || 0) + (liveSeconds || 0);
    }
    const byUser = Object.entries(totals)
        .map(([uid, seconds]) => ({ uid, seconds, isLive: uid === liveUid }))
        .sort((a, b) => b.seconds - a.seconds);
    const total = byUser.reduce((sum, u) => sum + u.seconds, 0);
    return { total, byUser };
};


const TimeSpentPanel = ({ ticket, nameOf, currentUid, isAdmin }) => {
    const sessions = useTicketWorkSessions(ticket?.id);

    const liveSession = ticket?.status === "active"
        ? sessions.find((s) => s.id === ticket.currentSessionId)
        : null;
    const liveElapsed = useElapsedTime(liveSession ? ticket.activeAt : null);
    const { total, byUser } = summarizeSessions(
        sessions.filter((s) => s.id !== liveSession?.id),
        liveSession?.user_id || null,
        liveElapsed
    );

    if (sessions.length === 0 && ticket?.status !== "active") return null;

    const visibleUsers = isAdmin ? byUser : byUser.filter((u) => u.uid === currentUid);
    const visibleTotal = isAdmin ? total : (visibleUsers[0]?.seconds || 0);

    if (!isAdmin && visibleUsers.length === 0) return null;

    return (
        <div className="meta-cell wide">
            <label>{isAdmin ? "Time Spent (all)" : "Your Time on This Task"}</label>
            <div className="time-spent-total">
                <FontAwesomeIcon icon={faClock} size="xs" className="me-1" />
                {formatTime(visibleTotal)}
                {liveSession && (isAdmin || liveSession.user_id === currentUid) && (
                    <span className="time-spent-live">live</span>
                )}
            </div>
            {isAdmin && visibleUsers.length > 1 && (
                <div className="time-spent-breakdown">
                    {visibleUsers.map((u) => (
                        <span key={u.uid} className="time-spent-chip">
                            {u.isLive && <span className="time-spent-live-dot" />}
                            {nameOf(u.uid)}: {formatDuration(u.seconds)}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};


const isDoorFailed = (door) => {
    let trueCount = 0;
    let falseCount = 0;

    const d1 = parseFloat(door.fD1) || 0;
    const d2 = parseFloat(door.fD2) || 0;
    const d3 = parseFloat(door.fD3) || 0;
    const d4 = parseFloat(door.fD4) || 0;

    const condition1 = d1 <= 4 && d2 <= 4 && d3 <= 4;
    const condition2 = d4 <= 8;

    if (door.doorCloseAsIntented === false) falseCount++; else trueCount++;

    if (door.hasOwnProperty('doorStrips')) {
        if (door.doorStrips) trueCount++; else falseCount++;
    }

    if (door.hingesWorking) trueCount++; else falseCount++;
    if (door.hingesFireRating) trueCount++; else falseCount++;

    const damageMultiplesArr = (door.damageMultiples || []).map(item => item.toString());
    if (damageMultiplesArr.includes("None") && damageMultiplesArr.length === 1) {
        trueCount++;
    } else {
        const isValidCondition =
            (damageMultiplesArr.includes("Door Pass") || !damageMultiplesArr.includes("Door")) &&
            (damageMultiplesArr.includes("Frame Pass") || !damageMultiplesArr.includes("Frame")) &&
            (damageMultiplesArr.includes("Glass None") ||
                damageMultiplesArr.includes("Glass Pass") ||
                !damageMultiplesArr.includes("Glass")) &&
            (damageMultiplesArr.includes("Ventilation Gaps None") ||
                damageMultiplesArr.includes("Ventilation Gaps Pass") ||
                !damageMultiplesArr.includes("Ventilation Gaps"));

        if (isValidCondition) trueCount++;
        else falseCount++;
    }

    const doorCertif = door.doorCertif || "";
    if (doorCertif && !doorCertif.includes("No")) trueCount++; else falseCount++;

    if (condition1 && condition2) trueCount++; else falseCount++;

    return falseCount > 0;
};

const doorLabel = (report) =>
    (report.type === "residential" ? "R" : "C") + (report.doorNumber || "");

const estimateUrlFor = (door) =>
    door?.estimatePDF || door?.estimateLink || door?.estimateUrl || door?.estimatePdfLink || door?.estimatePdfUrl || door?.estimate_pdf_url || null;


const hasWorkDoneInfo = (door) =>
    Boolean(door.workDone || door.workDonePhoto || door.workDoneUpdatedAt);


const DOOR_ROLE_CFG = {
    inspection: { title: "Create Building", emptyText: "Add a new building to start inspections.", queueLabel: "Create Building" },
    reviewer: { title: "Doors Awaiting Review", emptyText: "No doors currently need review.", queueLabel: "Review" },
    coordinator_review: { title: "Doors & Reports", emptyText: "No doors found for this building.", queueLabel: "Doors" },
    remedial: { title: "Book Remedial Works", emptyText: "No failed doors for this building.", queueLabel: "Remedial" },
    manager: { title: "Remedial Work Details", emptyText: "No failed doors found for this building.", queueLabel: "Doors" },
};


const remedialInfoCollectionName = (door) =>
    door?.type === "residential" ? "ResidentialReports" : "DoorReports";

const parseDoorNumber = (doorNumber) => {
    const str = String(doorNumber || "").replace(/\s+/g, "");
    const match = str.match(/^([A-Za-z]*)(\d+)?([A-Za-z]*)$/);
    if (!match) return { num: Infinity, category: 3, key: str };

    const [, prefix, digits, suffix] = match;
    const num = digits ? parseInt(digits, 10) : Infinity;

    let category;
    if (!prefix && !suffix) category = 0;
    else if (!prefix && suffix) category = 1;
    else category = 2;

    return { num, category, key: prefix + suffix };
};

const naturalDoorCompare = (numberA, numberB) => {
    const A = parseDoorNumber(numberA);
    const B = parseDoorNumber(numberB);

    if (A.num !== B.num) return A.num - B.num;
    if (A.category !== B.category) return A.category - B.category;
    return A.key.localeCompare(B.key);
};

const useBuildingDoors = (buildingId, role) => {
    const [doors, setDoors] = useState([]);
    const [doorsLoading, setDoorsLoading] = useState(false);

    useEffect(() => {
        if (!buildingId || !DOOR_ROLE_CFG[role]) {
            setDoors([]);
            setDoorsLoading(false);
            return undefined;
        }

        let disposed = false;
        let publishSeq = 0;
        setDoorsLoading(true);

        let reportList = [];
        let reportsReady = false;

        const communalDocsByReport = new Map();
        const residentialDocsByReport = new Map();
        const communalReady = new Set();
        const residentialReady = new Set();

        const communalUnsubs = new Map();
        const residentialUnsubs = new Map();
        let unsubReports = null;

        const publish = () => {
            if (disposed || !reportsReady) return;
            const mySeq = ++publishSeq;

            const sortedReports = [...reportList].sort((a, b) => b.reportDate - a.reportDate);
            const allSubcollectionsLoaded = sortedReports.every(
                (r) => communalReady.has(r.reportUid) && residentialReady.has(r.reportUid)
            );

            const seenCommunal = new Set();
            const seenResidential = new Set();
            const candidates = [];

            sortedReports.forEach(({ reportUid }) => {
                (communalDocsByReport.get(reportUid) || []).forEach(({ id, data }) => {
                    const doorNumber = data.doorNumber?.trim() || id;
                    if (!data.addedBy || data.addedBy.trim() === "") return;

                    if (role === "reviewer") {
                        if (data.requiresReview) {
                            candidates.push({ reportUid, type: "communal", doorUid: id, doorNumber, data });
                        }
                        return;
                    }

                    if (seenCommunal.has(doorNumber)) return;
                    seenCommunal.add(doorNumber);

                    if (data.requiresReview) return;
                    if (data.hasAccess === false) return;
                    if (data.requiresAttention) return;

                    candidates.push({ reportUid, type: "communal", doorUid: id, doorNumber, data });
                });

                (residentialDocsByReport.get(reportUid) || []).forEach(({ id, data }) => {
                    const doorNumber = data.doorNumber?.trim() || id;
                    if (!data.addedBy || data.addedBy.trim() === "") return;

                    if (role === "reviewer") {
                        if (data.requiresReview) {
                            candidates.push({ reportUid, type: "residential", doorUid: id, doorNumber, data });
                        }
                        return;
                    }

                    if (seenResidential.has(doorNumber)) return;
                    seenResidential.add(doorNumber);

                    if (data.requiresReview) return;
                    if (data.hasAccess === false) return;
                    if (data.requiresAttention) return;

                    candidates.push({ reportUid, type: "residential", doorUid: id, doorNumber, data });
                });
            });

            if (disposed || mySeq !== publishSeq) return;

            let merged = candidates.map((c) => ({
                ...c.data,
                id: c.doorUid,
                buildingId: c.reportUid,
                type: c.type,
                doorNumber: c.doorNumber,
            }));

            merged = merged.filter((door) => !door.archived);
            if (role === "remedial") {
                merged = merged.filter(isDoorFailed);
            } else if (role === "manager") {
                merged = merged.filter((door) => isDoorFailed(door) && Boolean(door.workDoneUpdatedAt));
            }

            merged.sort((a, b) => naturalDoorCompare(a.doorNumber, b.doorNumber));
            setDoors(merged);
            setDoorsLoading(!allSubcollectionsLoaded);
        };

        const syncReportListeners = () => {
            if (disposed) return;

            const desiredUids = new Set(reportList.map((r) => r.reportUid));

            [...communalUnsubs.keys()].forEach((uid) => {
                if (desiredUids.has(uid)) return;
                communalUnsubs.get(uid)?.();
                communalUnsubs.delete(uid);
                communalDocsByReport.delete(uid);
                communalReady.delete(uid);
            });
            [...residentialUnsubs.keys()].forEach((uid) => {
                if (desiredUids.has(uid)) return;
                residentialUnsubs.get(uid)?.();
                residentialUnsubs.delete(uid);
                residentialDocsByReport.delete(uid);
                residentialReady.delete(uid);
            });

            reportList.forEach(({ reportUid }) => {
                if (!communalUnsubs.has(reportUid)) {
                    const unsub = onSnapshot(
                        collection(db, "Reports", reportUid, "DoorReports"),
                        (snap) => {
                            communalDocsByReport.set(reportUid, snap.docs.map((d) => ({ id: d.id, data: d.data() })));
                            communalReady.add(reportUid);
                            publish();
                        },
                        (err) => {
                            console.error(`Live communal door listener failed for ${reportUid}:`, err);
                            communalDocsByReport.set(reportUid, []);
                            communalReady.add(reportUid);
                            publish();
                        }
                    );
                    communalUnsubs.set(reportUid, unsub);
                }
                if (!residentialUnsubs.has(reportUid)) {
                    const unsub = onSnapshot(
                        collection(db, "Reports", reportUid, "ResidentialReports"),
                        (snap) => {
                            residentialDocsByReport.set(reportUid, snap.docs.map((d) => ({ id: d.id, data: d.data() })));
                            residentialReady.add(reportUid);
                            publish();
                        },
                        (err) => {
                            console.error(`Live residential door listener failed for ${reportUid}:`, err);
                            residentialDocsByReport.set(reportUid, []);
                            residentialReady.add(reportUid);
                            publish();
                        }
                    );
                    residentialUnsubs.set(reportUid, unsub);
                }
            });

            publish();
        };

        const unsubBuilding = onSnapshot(
            doc(db, "Reports", buildingId),
            (snap) => {
                const buildingName = snap.exists() ? snap.data()?.buildingName : null;
                if (!buildingName) {
                    reportList = [];
                    reportsReady = true;
                    syncReportListeners();
                    return;
                }

                unsubReports?.();
                unsubReports = onSnapshot(
                    query(collection(db, "Reports"), where("buildingName", "==", buildingName)),
                    (reportsSnap) => {
                        reportList = reportsSnap.docs.map((d) => ({
                            reportUid: d.id,
                            reportDate: new Date(d.data().date || 0),
                        }));
                        reportsReady = true;
                        syncReportListeners();
                    },
                    (err) => {
                        console.error("Live reports-by-name listener failed:", err);
                        reportList = [];
                        reportsReady = true;
                        syncReportListeners();
                    }
                );
            },
            (err) => {
                console.error("Live building doc listener failed:", err);
                reportList = [];
                reportsReady = true;
                syncReportListeners();
            }
        );

        return () => {
            disposed = true;
            unsubBuilding();
            unsubReports?.();
            communalUnsubs.forEach((u) => u());
            residentialUnsubs.forEach((u) => u());
        };
    }, [buildingId, role]);

    return { doors, doorsLoading };
};

const useBuildingData = (buildingId) => {
    const [buildingData, setBuildingData] = useState(null);

    useEffect(() => {
        if (!buildingId) {
            setBuildingData(null);
            return;
        }

        return onSnapshot(
            doc(db, "Reports", buildingId),
            (snap) => {
                const data = snap.exists() ? { uid: buildingId, ...snap.data() } : null;
                setBuildingData(data);
            },
            (err) => {
                console.error("useBuildingData: error fetching building data for", buildingId, err);
                setBuildingData(null);
            }
        );
    }, [buildingId]);

    return buildingData;
};

const useActiveTicketDoors = (activeTicket, role, refreshSignal = 0) => {
    const eligible = role !== "inspection"
        && activeTicket?.status === "active"
        && activeTicket?.building_id
        && DOOR_ROLE_CFG[role];
    return useBuildingDoors(eligible ? activeTicket.building_id : null, role, refreshSignal);
};


const BuildingNameWithReportId = ({ buildingId, buildingName, fallback = "View building" }) => {
    const buildingData = useBuildingData(buildingId);
    const resolvedName = buildingName || buildingData?.buildingName || fallback;
    const reportId = String(buildingData?.reportId || "").trim();

    return (
        <>
            {resolvedName}
            {/* {reportId ? ` — ${reportId}` : ""} */}
        </>
    );
};

const TicketReportIdLine = ({ buildingId, className = "" }) => {
    const buildingData = useBuildingData(buildingId);
    const reportId = String(buildingData?.reportId || "").trim();
    if (!buildingId || !reportId) return null;

    return (
        <div
            className={className}
            style={{
                marginTop: 5,
                marginBottom: 7,
                fontSize: "0.78rem",
                lineHeight: 1.35,
                color: "var(--gray-500)",
                fontWeight: 600,
            }}
        >
            {/* <span style={{ color: "var(--gray-400)", fontWeight: 700 }}>Report ID:</span>{" "} */}
            <span style={{ color: "var(--gray-700)" }}>{reportId}</span>
        </div>
    );
};


const useBuildingReportLinks = (buildingId) => {
    const [links, setLinks] = useState({ pdfLink: null, pdfResiLink: null });
    useEffect(() => {
        if (!buildingId) { setLinks({ pdfLink: null, pdfResiLink: null }); return; }
        return onSnapshot(
            doc(db, "Reports", buildingId),
            (snap) => {
                const data = snap.exists() ? snap.data() : {};
                setLinks({ pdfLink: data.pdfLink || null, pdfResiLink: data.pdfResiLink || null });
            },
            (err) => {
                console.error("Error listening for report links:", err);
                setLinks({ pdfLink: null, pdfResiLink: null });
            }
        );
    }, [buildingId]);
    return links;
};

const BuildingReportLinks = ({ buildingId }) => {
    const { pdfLink, pdfResiLink } = useBuildingReportLinks(buildingId);
    if (!pdfLink && !pdfResiLink) return null;
    return (
        <>
            {pdfLink && (
                <a href={pdfLink} target="_blank" rel="noopener noreferrer" className="task-tag" onClick={(e) => e.stopPropagation()}>
                    <FontAwesomeIcon icon={faLink} size="xs" className="me-1" />
                    Communal Report
                </a>
            )}
            {pdfResiLink && (
                <a href={pdfResiLink} target="_blank" rel="noopener noreferrer" className="task-tag" onClick={(e) => e.stopPropagation()}>
                    <FontAwesomeIcon icon={faLink} size="xs" className="me-1" />
                    Residential Report
                </a>
            )}
        </>
    );
};

const DoorCountBadge = ({ buildingId, role }) => {
    const cfg = DOOR_ROLE_CFG[role];
    const { doors, doorsLoading } = useBuildingDoors(buildingId, role);
    if (!cfg || !buildingId) return null;
    if (doorsLoading) {
        return <span className="door-count-badge loading" style={{ fontSize: "0.7rem", color: "var(--gray-400)" }}>…</span>;
    }
    return (
        <span
            title={`${doors.length} door${doors.length === 1 ? "" : "s"} — ${cfg.title}`}
            style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "2px 8px", borderRadius: "100px", fontSize: "0.7rem", fontWeight: 700,
                background: "rgba(220,38,38,0.1)", color: "#dc2626", whiteSpace: "nowrap",
            }}
        >
            {doors.length} {cfg.queueLabel}
        </span>
    );
};

const DoorCard = ({
    door,
    onOpen,
    onEstimate,
    showEstimateAction,
    showRemedialInfoAction,
    showCopyLinkAction,
    onRemedialInfo,
    estimateUrl,
    allowNoAccess = false,
    remedialBooking = null,
}) => {
    if ((!allowNoAccess && door.hasAccess === false) || door.requiresAttention) {
        return null;
    }

    const noAccess = door.hasAccess === false;
    const failed = !noAccess && isDoorFailed(door);

    const handleCopyLink = (e) => {
        e.stopPropagation();
        const url = `${window.location.origin}/book-remedial/${door.buildingId}/${door.id}`;

        navigator.clipboard.writeText(url)
            .then(() => showTicketToast("Link copied to clipboard.", "Done"))
            .catch(() => showTicketToast("Could not copy link.", "Couldn’t complete"));
    };

    return (
        <div
            onClick={() => onOpen?.(door)}
            style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px 12px",
                borderBottom: "1px solid var(--gray-100)",
                cursor: onOpen ? "pointer" : "default",
            }}
        >
            {door.doorPullPhotoLink ? (
                <img
                    src={door.doorPullPhotoLink}
                    alt={doorLabel(door)}
                    style={{
                        width: "56px",
                        height: "56px",
                        objectFit: "cover",
                        borderRadius: "var(--r-sm)",
                        border: "1px solid var(--gray-200)",
                        flexShrink: 0,
                    }}
                />
            ) : (
                <div
                    style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "var(--r-sm)",
                        border: "1px solid var(--gray-200)",
                        background: "var(--gray-50)",
                        color: "var(--gray-300)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <FontAwesomeIcon icon={faImage} />
                </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "10px",
                        flexWrap: "wrap",
                    }}
                >
                    <span
                        style={{
                            fontWeight: 700,
                            fontSize: "0.87rem",
                            color: "var(--gray-800)",
                            flex: "1 1 120px",
                            minWidth: 0,
                        }}
                    >
                        {doorLabel(door)}
                    </span>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "6px",
                            rowGap: "6px",
                            flexWrap: "wrap",
                            flex: "0 1 auto",
                            maxWidth: "100%",
                            minWidth: 0,
                        }}
                    >
                        <span
                            style={{
                                padding: "2px 9px",
                                borderRadius: "100px",
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.03em",
                                whiteSpace: "nowrap",
                                color: noAccess
                                    ? "#d97706"
                                    : failed
                                        ? "#dc2626"
                                        : "#16a34a",
                                backgroundColor: noAccess
                                    ? "rgba(217,119,6,0.1)"
                                    : failed
                                        ? "rgba(220,38,38,0.1)"
                                        : "rgba(22,163,74,0.1)",
                            }}
                        >
                            {noAccess ? "No Access" : failed ? "Failed" : "Passed"}
                        </span>

                        {showEstimateAction && (
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEstimate?.(door);
                                }}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 9px",
                                    borderRadius: "100px",
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.03em",
                                    color: "#2563eb",
                                    backgroundColor: "rgba(37,99,235,0.1)",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faDownload}
                                    className="me-1"
                                    size="sm"
                                />
                                Estimate
                            </span>
                        )}

                        {estimateUrl && (
                            <a
                                href={estimateUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="Open generated estimate PDF"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 9px",
                                    borderRadius: "100px",
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.03em",
                                    color: "#0891b2",
                                    backgroundColor: "rgba(8,145,178,0.1)",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faLink}
                                    className="me-1"
                                    size="sm"
                                />
                                Open
                            </a>
                        )}

                        {showRemedialInfoAction && (
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemedialInfo?.(door);
                                }}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 9px",
                                    borderRadius: "100px",
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.03em",
                                    color: "#7c3aed",
                                    backgroundColor: "rgba(124,58,237,0.1)",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faClipboardCheck}
                                    className="me-1"
                                    size="sm"
                                />
                                Remedial Info
                            </span>
                        )}

                        {showCopyLinkAction && (
                            <span
                                onClick={handleCopyLink}
                                title="Copy booking link"
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "2px 9px",
                                    borderRadius: "100px",
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.03em",
                                    color: "#0891b2",
                                    backgroundColor: "rgba(8,145,178,0.1)",
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                            </span>
                        )}

                        {remedialBooking && (
                            <span
                                title={`Booking ID: ${remedialBooking.booking_id || "—"}`}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "2px 9px",
                                    borderRadius: "100px",
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    color: "#059669",
                                    backgroundColor: "rgba(5,150,105,0.1)",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <FontAwesomeIcon icon={faCalendar} size="xs" />

                                Booked
                                {remedialBooking.date
                                    ? ` · ${remedialBooking.date
                                        .split("-")
                                        .reverse()
                                        .join("/")}`
                                    : ""}
                                {remedialBooking.job_time
                                    ? ` · ${remedialBooking.job_time}`
                                    : ""}
                            </span>
                        )}
                    </div>
                </div>
                {door.date && (
                    <div
                        style={{
                            fontSize: "0.78rem",
                            color: "var(--gray-400)",
                            marginTop: "2px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {fmtDate(door.date)}
                    </div>
                )}
            </div>
        </div>
    );
};

const DoorsReviewPanel = ({
    activeTicket, role, onOpenDoor = null, onCreateBuilding = null, onEstimateDoor = null, onEstimateAllCommunal = null, onRemedialInfoDoor = null, panelHeight = null, refreshSignal = 0, showTitle = true, embedded = false,
}) => {
    const cfg = DOOR_ROLE_CFG[role];
    const { doors, doorsLoading } = useActiveTicketDoors(activeTicket, role, refreshSignal);

    const [remedialInfoByDoor, setRemedialInfoByDoor] = useState({});
    const [doorSearch, setDoorSearch] = useState("");
    const [doorTypeTab, setDoorTypeTab] = useState("communal");
    const communalDoors = useMemo(
        () => doors.filter((d) => d.type === "communal"),
        [doors]
    );
    const residentialDoors = useMemo(
        () => doors.filter((d) => d.type === "residential"),
        [doors]
    );

    const visibleDoors = useMemo(() => {
        if (!doorSearch.trim()) return doors;
        const q = doorSearch.trim().toLowerCase().replace(/\s+/g, "");
        return doors.filter((d) => {
            const label = doorLabel(d).toLowerCase();
            const num = String(d.doorNumber || "").toLowerCase();
            return label.includes(q) || num.includes(q);
        });
    }, [doors, doorSearch]);

    const communalVisibleDoors = useMemo(
        () => visibleDoors.filter((d) => d.type === "communal"),
        [visibleDoors]
    );
    const residentialVisibleDoors = useMemo(
        () => visibleDoors.filter((d) => d.type === "residential"),
        [visibleDoors]
    );
    const selectedVisibleDoors = doorTypeTab === "residential"
        ? residentialVisibleDoors
        : communalVisibleDoors;
    const selectedDoorTypeLabel = doorTypeTab === "residential" ? "Residential" : "Communal";

    useEffect(() => {
        setDoorTypeTab("communal");
    }, [activeTicket?.id, role]);

    useEffect(() => {
        if (role !== "remedial" || !doors?.length) {
            setRemedialInfoByDoor({});
            return undefined;
        }

        let disposed = false;
        const infoByKey = {};

        const unsubs = doors.map((door) => {
            const key = `${door.type}-${door.id}`;
            const collectionName = remedialInfoCollectionName(door);

            return onSnapshot(
                query(
                    collection(
                        db,
                        "Reports",
                        door.buildingId,
                        collectionName,
                        door.id,
                        "remedial-info"
                    ),
                    orderBy("created_at", "desc"),
                    limit(1)
                ),
                (snap) => {
                    if (disposed) return;

                    infoByKey[key] = snap.empty
                        ? null
                        : {
                            id: snap.docs[0].id,
                            ...snap.docs[0].data(),
                        };

                    setRemedialInfoByDoor({ ...infoByKey });
                },
                (err) => {
                    console.error(
                        "Live remedial-info listener failed for door:",
                        door.id,
                        err
                    );

                    if (disposed) return;

                    infoByKey[key] = null;
                    setRemedialInfoByDoor({ ...infoByKey });
                }
            );
        });

        return () => {
            disposed = true;
            unsubs.forEach((u) => u());
        };
    }, [doors, role]);


    if (!cfg) return null;

    const panelStyle = {
        background: "white",
        border: embedded ? "none" : "1px solid var(--gray-200)",
        borderRadius: embedded ? 0 : "var(--r-lg)",
        boxShadow: embedded ? "none" : "var(--sh-sm)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: embedded ? "100%" : (panelHeight ? `${panelHeight}px` : "100%"),
        minHeight: embedded ? 0 : (panelHeight ? `${panelHeight}px` : 0),
        maxHeight: embedded ? undefined : (panelHeight ? `${panelHeight}px` : undefined),
        alignSelf: "stretch",
    };

    if (role === "inspection") {
        return (
            <div className="mywork-grid-report" style={panelStyle}>
                <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--gray-100)", flexShrink: 0 }}>
                    <strong style={{ display: "block", fontSize: "0.94rem", color: "var(--gray-800)" }}>
                        {cfg.title}
                    </strong>
                    <p style={{ margin: "2px 0 0", fontSize: "0.82rem", color: "var(--gray-500)" }}>
                        {cfg.emptyText}
                    </p>
                </div>
                <div style={{ padding: "16px", flex: 1, display: "flex", alignItems: "flex-start" }}>
                    <button
                        className="btn-unblock"
                        onClick={() => onCreateBuilding?.()}
                        style={{
                            background: "#dc2626",
                            borderColor: "#dc2626",
                            color: "white",
                        }}
                    >
                        <FontAwesomeIcon icon={faBuilding} size="xs" className="me-1" />
                        {cfg.queueLabel}
                    </button>
                </div>
            </div>
        );
    }

    const hasActiveBuilding = Boolean(
        activeTicket?.status === "active" && activeTicket?.building_id
    );

    return (
        <div className="mywork-grid-report" style={panelStyle}>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--gray-100)", flexShrink: 0 }}>
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    gap: showTitle && hasActiveBuilding ? 8 : 0,
                }}>
                    {showTitle && (
                        <strong style={{ display: "block", fontSize: "0.94rem", color: "var(--gray-800)" }}>
                            {cfg.title}
                        </strong>
                    )}
                    {hasActiveBuilding && (
                        <div
                            className="side-panel-tabs"
                            style={{
                                margin: 0,
                                marginLeft: 0,
                                alignSelf: "flex-start",
                                borderBottom: 0,
                                width: "auto",
                                maxWidth: "100%",
                                flex: "0 1 auto",
                            }}
                        >
                            <button
                                type="button"
                                className={`side-panel-tab${doorTypeTab === "communal" ? " active" : ""}`}
                                onClick={() => setDoorTypeTab("communal")}
                                style={{ flex: "0 0 auto", padding: "6px 9px", fontSize: "0.72rem", whiteSpace: "nowrap" }}
                            >
                                Communal Doors ({communalDoors.length})
                            </button>
                            <button
                                type="button"
                                className={`side-panel-tab${doorTypeTab === "residential" ? " active" : ""}`}
                                onClick={() => setDoorTypeTab("residential")}
                                style={{ flex: "0 0 auto", padding: "6px 9px", fontSize: "0.72rem", whiteSpace: "nowrap" }}
                            >
                                Residential Doors ({residentialDoors.length})
                            </button>
                        </div>
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: role === "remedial" && hasActiveBuilding ? 10 : 0 }}>
                    {role === "remedial" && hasActiveBuilding && doors.length > 0 && (
                        <input
                            type="text"
                            value={doorSearch}
                            onChange={(e) => setDoorSearch(e.target.value)}
                            placeholder="Search door number..."
                            style={{
                                flex: "1 1 140px",
                                minWidth: 0,
                                maxWidth: 200,
                                padding: "5px 10px",
                                fontSize: "0.78rem",
                                border: "1px solid var(--gray-200)",
                                borderRadius: "var(--r-sm)",
                                outline: "none",
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = "var(--primary)";
                                e.target.style.boxShadow = "0 0 0 3px var(--primary-a)";
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = "var(--gray-200)";
                                e.target.style.boxShadow = "none";
                            }}
                        />
                    )}

                    {role === "remedial" && hasActiveBuilding && communalDoors.length > 0 && (
                        <button
                            type="button"
                            onClick={() => onEstimateAllCommunal?.(communalDoors, activeTicket.building_id)}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "3px 10px",
                                borderRadius: 8,
                                border: "1px solid rgba(37,99,235,0.3)",
                                background: "rgba(37,99,235,0.08)",
                                color: "#2563eb",
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                marginLeft: "auto",
                                flexShrink: 0,
                            }}
                        >
                            <FontAwesomeIcon icon={faDownload} size="xs" className="me-1" />
                            Estimate Communal Doors ({communalDoors.length})
                        </button>
                    )}
                </div>
            </div>


            {
                !hasActiveBuilding ? (
                    <div
                        className="activity-empty"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                        }}
                    >
                        <p style={{ margin: 0 }}>Start a task from your queue to show this building's live door list.</p>
                    </div>
                ) : doorsLoading ? (
                    <div className="tk-loading" style={{ padding: "16px", flex: 1 }}>
                        <Spinner animation="border" style={{ color: "var(--primary)", width: "1.4rem", height: "1.4rem" }} />
                    </div>
                ) : doors.length === 0 ? (
                    <div
                        className="activity-empty"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                        }}
                    >
                        <p style={{ margin: 0 }}>{cfg.emptyText}</p>
                    </div>
                ) : visibleDoors.length === 0 ? (
                    <div className="activity-empty" style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
                        <p style={{ margin: 0 }}>No doors match "{doorSearch}".</p>
                    </div>
                ) : selectedVisibleDoors.length === 0 ? (
                    <div className="activity-empty" style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
                        <p style={{ margin: 0 }}>No {selectedDoorTypeLabel.toLowerCase()} doors found.</p>
                    </div>
                ) : (
                    <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                        {selectedVisibleDoors.map((door) => (
                            <DoorCard
                                key={`${door.type}-${door.id}`}
                                door={door}
                                onOpen={
                                    (role === "reviewer" || role === "manager") && onOpenDoor
                                        ? () => onOpenDoor(door)
                                        : undefined
                                }
                                showEstimateAction={
                                    role === "remedial" && door.type === "residential"
                                }
                                onEstimate={onEstimateDoor}
                                estimateUrl={estimateUrlFor(door)}
                                showRemedialInfoAction={role === "remedial"}
                                onRemedialInfo={onRemedialInfoDoor}
                                showCopyLinkAction={
                                    role === "remedial" &&
                                    Boolean(remedialInfoByDoor[`${door.type}-${door.id}`])
                                }
                                remedialBooking={
                                    remedialInfoByDoor[`${door.type}-${door.id}`]?.remedial_booking || null
                                }
                                allowNoAccess={role === "reviewer"}
                            />
                        ))}
                    </div>
                )
            }
        </div >
    );
};

const InspectionTabsPanel = ({ panelHeight, onCreateBuilding }) => {
    const panelStyle = {
        background: "white",
        border: "1px solid var(--gray-200)",
        borderRadius: "var(--r-lg)",
        boxShadow: "var(--sh-sm)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: panelHeight ? `${panelHeight}px` : "100%",
        minHeight: panelHeight ? `${panelHeight}px` : 0,
        maxHeight: panelHeight ? `${panelHeight}px` : undefined,
        alignSelf: "stretch",
    };

    return (
        <div className="mywork-grid-report" style={panelStyle}>
            <div
                style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid var(--gray-100)",
                    flexShrink: 0,
                }}
            >
                <strong
                    style={{
                        display: "block",
                        fontSize: "0.94rem",
                        color: "var(--gray-800)",
                    }}
                >
                    Create Building
                </strong>

                <p
                    style={{
                        margin: "2px 0 0",
                        fontSize: "0.82rem",
                        color: "var(--gray-500)",
                    }}
                >
                    Add a new building to start inspections.
                </p>
            </div>

            <div
                style={{
                    padding: "16px",
                    flex: 1,
                    display: "flex",
                    alignItems: "flex-start",
                }}
            >
                <button
                    className="btn-unblock"
                    onClick={() => onCreateBuilding?.()}
                    style={{
                        background: "#dc2626",
                        borderColor: "#dc2626",
                        color: "white",
                    }}
                >
                    <FontAwesomeIcon
                        icon={faBuilding}
                        size="xs"
                        className="me-1"
                    />
                    Create Building
                </button>
            </div>
        </div>
    );
};

const ManagerRemedialTabsPanel = ({ activeTicket, panelHeight, onOpenDoor, onEstimateDoor, onEstimateAllCommunal, onRemedialInfoDoor, refreshSignal }) => {
    const [tab, setTab] = useState("details");

    const panelStyle = {
        background: "white",
        border: "1px solid var(--gray-200)",
        borderRadius: "var(--r-lg)",
        boxShadow: "var(--sh-sm)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: panelHeight ? `${panelHeight}px` : "100%",
        minHeight: panelHeight ? `${panelHeight}px` : 0,
        maxHeight: panelHeight ? `${panelHeight}px` : undefined,
        alignSelf: "stretch",
    };

    return (
        <div className="mywork-grid-report" style={panelStyle}>
            <div className="side-panel-tabs" style={{ flexShrink: 0 }}>
                <button
                    className={`side-panel-tab${tab === "book" ? " active" : ""}`}
                    onClick={() => setTab("book")}
                >
                    Book Remedial Works
                </button>

                <button
                    className={`side-panel-tab${tab === "details" ? " active" : ""}`}
                    onClick={() => setTab("details")}
                >
                    Remedial Work Details
                </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {tab === "details" ? (
                    <DoorsReviewPanel
                        activeTicket={activeTicket}
                        role="manager"
                        onOpenDoor={onOpenDoor}
                        showTitle={false}
                    />
                ) : (
                    <DoorsReviewPanel
                        activeTicket={activeTicket}
                        role="remedial"
                        onEstimateDoor={onEstimateDoor}
                        onEstimateAllCommunal={onEstimateAllCommunal}
                        onRemedialInfoDoor={onRemedialInfoDoor}
                        refreshSignal={refreshSignal}
                        showTitle={false}
                    />
                )}
            </div>
        </div>
    );
};

const TeamActiveWorkCard = ({ ticket, nameOf, onOpen }) => {
    const elapsed = useElapsedTime(ticket.is_paused ? null : ticket.activeAt);
    const workerUid = ticket.active_by_id || ticket.assignee_id;

    return (
        <div
            onClick={() => onOpen(ticket)}
            style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "10px 16px",
                borderBottom: "1px solid var(--gray-100)",
                cursor: "pointer",
            }}
        >
            <Avatar name={nameOf(workerUid)} small />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                    <span style={{ fontWeight: 700, fontSize: "0.84rem", color: "var(--gray-800)" }}>
                        {nameOf(workerUid)}
                    </span>
                    <span
                        style={{
                            flexShrink: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "3px 9px",
                            borderRadius: "100px",
                            background: ticket.is_paused ? "rgba(217,119,6,0.1)" : "rgba(16,185,129,0.1)",
                            color: ticket.is_paused ? "#b45309" : "var(--green)",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            fontFamily: "'SF Mono','Roboto Mono','Menlo','Courier New',monospace",
                        }}
                    >
                        <FontAwesomeIcon icon={ticket.is_paused ? faPause : faClock} size="xs" className="me-1" />
                        {ticket.is_paused ? "Paused" : formatTime(elapsed)}
                    </span>
                </div>
                <div
                    style={{
                        fontSize: "0.82rem",
                        color: "var(--gray-600)",
                        marginTop: "2px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {getTitle(ticket)}
                </div>
                <div style={{ display: "flex", gap: "6px", marginTop: "5px", flexWrap: "wrap" }}>
                    <PriorityBadge priority={ticket.priority} />
                    {ticket.task_type && <TaskTypeBadge type={ticket.task_type} />}
                </div>
            </div>
        </div>
    );
};


const TeamActiveWorkPanel = ({ tickets, nameOf, onOpen, panelHeight = null }) => (
    <div
        className="mywork-grid-report team-active-work-panel"
        style={{
            background: "white",
            border: "1px solid var(--gray-200)",
            borderRadius: "var(--r-lg)",
            boxShadow: "var(--sh-sm)",
            overflow: "hidden",
            marginTop: 0,
            display: "flex",
            flexDirection: "column",
            height: panelHeight ? `${panelHeight}px` : "100%",
            minHeight: 0,
            maxHeight: panelHeight ? `${panelHeight}px` : undefined,
            alignSelf: "stretch",
        }}
    >
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "13px 18px",
                borderBottom: "1px solid var(--gray-100)",
                flexShrink: 0,
            }}
        >
            <span style={{ fontWeight: 700, fontSize: "0.9rem", color: "var(--gray-800)" }}>
                Team's Current Work
            </span>
            {tickets.length > 0 && (
                <span
                    style={{
                        background: "rgba(16,185,129,0.1)",
                        color: "var(--green)",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: "100px",
                    }}
                >
                    {tickets.length} active
                </span>
            )}
        </div>
        {tickets.length === 0 ? (
            <p className="stuck-empty" style={{ minHeight: 0, padding: "20px", margin: 0, flex: 1 }}>
                No one on the team is currently working on a task.
            </p>
        ) : (
            <div className="team-active-work-scroll">
                {tickets.map((t) => (
                    <TeamActiveWorkCard key={t.id} ticket={t} nameOf={nameOf} onOpen={onOpen} />
                ))}
            </div>
        )}
    </div>
);

const parseCalendarEventDate = (value) => {
    if (!value) return null;

    try {
        const parsed = calendarDateUtils.parseFromDatabase(value);
        if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) return parsed;
    } catch {
    }

    const fallback = parseDate(value);
    return fallback instanceof Date && !Number.isNaN(fallback.getTime()) ? fallback : null;
};

const scheduledInspectorName = (calendarEvent, nameOf) => {
    const assignedInspector = calendarEvent?.assignedInspector;

    if (assignedInspector && typeof nameOf === "function") {
        const resolvedName = nameOf(assignedInspector);
        if (resolvedName && resolvedName !== "Unknown" && resolvedName !== "—") {
            return resolvedName;
        }
    }

    return calendarEvent?.inspectorName
        || (typeof assignedInspector === "string" ? assignedInspector : "")
        || "Inspector";
};

const scheduledTimeRange = (calendarEvent) => {
    const start = parseCalendarEventDate(calendarEvent?.start);
    const end = parseCalendarEventDate(calendarEvent?.end);
    const time = (date) => date?.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

    if (start && end) return `${time(start)}–${time(end)}`;
    if (start) return `From ${time(start)}`;
    if (end) return `Until ${time(end)}`;
    return "";
};

const scheduledVisitTitle = (calendarEvent, inspector) => {
    const start = parseCalendarEventDate(calendarEvent?.start);
    const end = parseCalendarEventDate(calendarEvent?.end);

    if (!start) return `${inspector} — click to view or edit`;

    const startText = start.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const endText = end?.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });

    return `${inspector} — ${startText}${endText ? ` to ${endText}` : ""}. Click to view or edit.`;
};

const ScheduleSlotBadge = ({
    ticket,
    slotIndex,
    eventId,
    showEmpty,
    onOpen,
    nameOf,
    role,
    canOpen = false,
}) => {
    const [calendarEvent, setCalendarEvent] = useState(null);
    const [eventLoading, setEventLoading] = useState(Boolean(eventId));

    useEffect(() => {
        if (!eventId) {
            setCalendarEvent(null);
            setEventLoading(false);
            return undefined;
        }

        setCalendarEvent(null);
        setEventLoading(true);

        return onSnapshot(
            doc(db, ...CALENDAR_COLLECTION_PATH, eventId),
            (snap) => {
                setCalendarEvent(
                    snap.exists()
                        ? { id: snap.id, ...snap.data() }
                        : null
                );
                setEventLoading(false);
            },
            (err) => {
                console.error("Failed to load scheduled visit badge:", err);
                setCalendarEvent(null);
                setEventLoading(false);
            }
        );
    }, [eventId]);

    const booked = Boolean(eventId && calendarEvent);
    const staleEventLink = Boolean(
        eventId && !eventLoading && !calendarEvent
    );

    if (!eventId && !showEmpty) return null;
    if (staleEventLink && !showEmpty) return null;

    const inspector = booked
        ? scheduledInspectorName(calendarEvent, nameOf)
        : "";

    const timeRange = booked
        ? scheduledTimeRange(calendarEvent)
        : "";

    const eventDate = booked
        ? parseCalendarEventDate(calendarEvent?.start)?.toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        )
        : "";

    const label = eventLoading
        ? "Loading visit…"
        : booked
            ? role === "inspection"
                ? [inspector, eventDate, timeRange].filter(Boolean).join(" · ")
                : eventDate
            : `+ Schedule ${slotIndex + 1}`;

    return (
        <span
            className="task-tag"
            style={{
                cursor: canOpen ? "pointer" : "default",
                ...(booked
                    ? {
                        color: "#059669",
                        backgroundColor: "rgba(16,185,129,0.12)",
                        borderColor: "rgba(16,185,129,0.35)",
                    }
                    : eventLoading
                        ? {
                            color: "var(--gray-500)",
                            backgroundColor: "var(--gray-50)",
                        }
                        : {}),
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (canOpen) onOpen?.(ticket, slotIndex);
            }}
        >
            <FontAwesomeIcon
                icon={faCalendar}
                size="xs"
                className="me-1"
            />
            {label}
        </span>
    );
};

const TicketScheduleBadges = ({ ticket, role, onOpen = null, nameOf }) => {
    const ids = ticket?.schedule_ids || [];
    const showEmpty = role === "inspection";
    const visibleSlots = Array.from({ length: MAX_SCHEDULES_PER_TICKET }, (_, i) => i)
        .filter((i) => showEmpty || Boolean(ids[i]));

    if (visibleSlots.length === 0) return null;

    return (
        <>
            {visibleSlots.map((slotIndex) => (
                <ScheduleSlotBadge
                    key={`${slotIndex}-${ids[slotIndex] || "empty"}`}
                    ticket={ticket}
                    slotIndex={slotIndex}
                    eventId={ids[slotIndex] || null}
                    showEmpty={showEmpty}
                    onOpen={role === "inspection" ? onOpen : undefined}
                    nameOf={nameOf}
                    role={role}
                    canOpen={role === "inspection"}
                />
            ))}
        </>
    );
};

const MentionInput = ({ value, onChange, users, multiline = false, rows = 1, placeholder = "", onSubmit = null, autoFocus = false, menuPlacement = "up" }) => {
    const inputRef = useRef(null);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [menuAnchor, setMenuAnchor] = useState(null);

    const suggestions = useMemo(() => {
        if (mentionQuery === null) return [];
        const q = mentionQuery.toLowerCase();
        return (users || [])
            .map((u) => ({ user: u, handle: mentionHandleForUser(u, users) }))
            .filter(({ user, handle }) =>
                !q || handle.toLowerCase().includes(q) || String(user.name || user.email || "").toLowerCase().includes(q)
            )
            .slice(0, 6);
    }, [users, mentionQuery]);

    const refreshMenuAnchor = () => {
        const rect = inputRef.current?.getBoundingClientRect?.();
        if (!rect) return;
        setMenuAnchor({ left: rect.left, top: rect.top, bottom: rect.bottom, width: rect.width });
    };

    useEffect(() => {
        if (mentionQuery === null) {
            setMenuAnchor(null);
            return undefined;
        }
        refreshMenuAnchor();
        const refresh = () => refreshMenuAnchor();
        window.addEventListener("resize", refresh);
        window.addEventListener("scroll", refresh, true);
        return () => {
            window.removeEventListener("resize", refresh);
            window.removeEventListener("scroll", refresh, true);
        };
    }, [mentionQuery]);

    const updateMentionQuery = (text, cursorPosition) => {
        const before = String(text || "").slice(0, cursorPosition ?? String(text || "").length);
        const match = before.match(/(?:^|\s)@([A-Za-z0-9_.-]*)$/);
        setMentionQuery(match ? match[1] : null);
        setSelectedIndex(0);
        if (match) requestAnimationFrame(refreshMenuAnchor);
    };

    const insertSuggestion = (suggestion) => {
        const el = inputRef.current;
        const cursor = el?.selectionStart ?? value.length;
        const before = value.slice(0, cursor);
        const after = value.slice(cursor);
        const match = before.match(/(?:^|\s)@([A-Za-z0-9_.-]*)$/);
        if (!match) return;
        const atStart = before.length - match[0].length + (/^\s/.test(match[0]) ? 1 : 0);
        const next = `${before.slice(0, atStart)}@${suggestion.handle} ${after}`;
        onChange(next);
        setMentionQuery(null);
        requestAnimationFrame(() => {
            const nextCursor = atStart + suggestion.handle.length + 2;
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange?.(nextCursor, nextCursor);
        });
    };

    const commonProps = {
        ref: inputRef,
        className: "form-ctrl",
        placeholder,
        value,
        autoFocus,
        onFocus: refreshMenuAnchor,
        onChange: (e) => {
            onChange(e.target.value);
            updateMentionQuery(e.target.value, e.target.selectionStart);
        },
        onClick: (e) => updateMentionQuery(e.target.value, e.target.selectionStart),
        onKeyUp: (e) => {
            if (!["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) {
                updateMentionQuery(e.currentTarget.value, e.currentTarget.selectionStart);
            }
        },
        onKeyDown: (e) => {
            if (suggestions.length && mentionQuery !== null) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSelectedIndex((i) => (i + 1) % suggestions.length);
                    return;
                }
                if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSelectedIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
                    return;
                }
                if (e.key === "Enter") {
                    e.preventDefault();
                    insertSuggestion(suggestions[selectedIndex]);
                    return;
                }
                if (e.key === "Escape") {
                    setMentionQuery(null);
                    return;
                }
            }
            if (!multiline && e.key === "Enter" && onSubmit) onSubmit();
        },
    };

    const suggestionMenu = mentionQuery !== null && suggestions.length > 0 && menuAnchor && typeof document !== "undefined"
        ? createPortal(
            <div
                className="mention-suggestions-portal"
                style={{
                    position: "fixed",
                    left: Math.max(8, menuAnchor.left),
                    width: Math.max(220, menuAnchor.width),
                    zIndex: 2147483000,
                    ...(menuPlacement === "up"
                        ? {
                            bottom: Math.max(8, window.innerHeight - menuAnchor.top + 6),
                            maxHeight: Math.max(90, Math.min(240, menuAnchor.top - 18)),
                        }
                        : {
                            top: menuAnchor.bottom + 6,
                            maxHeight: Math.max(90, Math.min(240, window.innerHeight - menuAnchor.bottom - 18)),
                        }),
                    background: "white",
                    border: "1px solid var(--gray-200, #e7e5e4)",
                    borderRadius: 8,
                    boxShadow: "0 14px 38px rgba(15,23,42,0.18)",
                    overflowY: "auto",
                }}
            >
                {suggestions.map((suggestion, index) => (
                    <button
                        key={suggestion.user.uid}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); insertSuggestion(suggestion); }}
                        style={{
                            width: "100%", border: 0, textAlign: "left", padding: "8px 10px", cursor: "pointer",
                            background: index === selectedIndex ? "var(--gray-50, #fafaf9)" : "white",
                            display: "flex", justifyContent: "space-between", gap: 10,
                        }}
                    >
                        <span style={{ fontWeight: 700, fontSize: "0.82rem" }}>@{suggestion.handle}</span>
                        <span style={{ color: "var(--gray-500, #78716c)", fontSize: "0.76rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {suggestion.user.name || suggestion.user.email} · {normalizeUserRole(suggestion.user.user_role)}
                        </span>
                    </button>
                ))}
            </div>,
            document.body
        )
        : null;

    return (
        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
            {multiline ? <textarea {...commonProps} rows={rows} /> : <input {...commonProps} type="text" />}
            {suggestionMenu}
        </div>
    );
};

const Tickets = ({ userData }) => {

    const [user] = useAuthState(auth);
    const [allUsers, setAllUsers] = useState([]);
    const [remedialInfoRefreshSignal, setRemedialInfoRefreshSignal] = useState(0);

    useEffect(() => {
        return onSnapshot(
            collection(db, "users"),
            (snap) => setAllUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))),
            (error) => console.error("Live users listener failed:", error)
        );
    }, []);

    const usersById = useMemo(
        () => Object.fromEntries(allUsers.map((u) => [u.uid, u])),
        [allUsers]
    );


    const actualRole = normalizeUserRole(usersById[user?.uid]?.user_role);
    const isRealAdmin = actualRole === "admin";


    const [devViewAsUid, setDevViewAsUid] = useState(null);
    const effectiveUid = isRealAdmin && devViewAsUid ? devViewAsUid : user?.uid;


    const isAdmin = normalizeUserRole(usersById[effectiveUid]?.user_role) === "admin";
    const effectiveRole = normalizeUserRole(usersById[effectiveUid]?.user_role);
    const { summary: liveRewardSummary, loading: rewardSummaryLoading } = useLiveRewardSummary(effectiveUid);
    const liveRewardPoints = Number(liveRewardSummary?.available_points ?? liveRewardSummary?.confirmed_points ?? 0);

    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const [modalLoading, setModalLoading] = useState(false);

    const [showEstimateModal, setShowEstimateModal] = useState(false);
    const [selectedDoors, setSelectedDoors] = useState(null);
    const [singleDoorForEstimate, setSingleDoorForEstimate] = useState(null);
    const [isResidentialEstimate, setIsResidentialEstimate] = useState(null);
    const [resi_data, setResiData] = useState([]);

    const [estimateBuildingId, setEstimateBuildingId] = useState(null);
    const [pendingEstimate, setPendingEstimate] = useState(false);
    const buildingData = useBuildingData(estimateBuildingId);

    const openCommunalEstimate = (communalDoors, buildingId) => {
        setSingleDoorForEstimate(null);
        setSelectedDoors(communalDoors);
        setIsResidentialEstimate(false);
        setEstimateBuildingId(buildingId);
        setPendingEstimate(true);
    };

    useEffect(() => {
        if (pendingEstimate && estimateBuildingId && buildingData) {
            setShowEstimateModal(true);
            setPendingEstimate(false);
        }
    }, [pendingEstimate, estimateBuildingId, buildingData]);

    const closeEstimateModal = () => {
        setShowEstimateModal(false);
        setSelectedDoors([]);
        setSingleDoorForEstimate(null);
        setIsResidentialEstimate(false);
        setEstimateBuildingId(null);
        setPendingEstimate(false);
    };

    const ROLE_CARD_CONFIG = {
        reviewer: {
            showBlockedReason: true,
            showOwner: true,
            showFollowUp: false,
            actions: ["review"],
        },
        coordinator: {
            showBlockedReason: true,
            showOwner: true,
            showFollowUp: true,
            actions: ["unblock", "reassign"],
        },
        remedial: {
            showBlockedReason: true,
            showOwner: false,
            showFollowUp: true,
            actions: ["unblock"],
        },
        inspection: {
            showBlockedReason: false,
            showOwner: true,
            showFollowUp: false,
            actions: ["createBuilding"],
        },
        manager: {
            showBlockedReason: true,
            showOwner: true,
            showFollowUp: true,
            actions: ["unblock", "reassign"],
        },
    };

    const cardConfig = ROLE_CARD_CONFIG[effectiveRole];

    const [inspectores, setInspectores] = useState([]);

    useEffect(() => {
        if (effectiveRole !== "inspection") {
            setInspectores([]);
            return undefined;
        }

        const inspectorsQuery = query(
            collection(db, "users"),
            where("user_role", "==", "inspector")
        );

        return onSnapshot(
            inspectorsQuery,
            (snapshot) => {
                const list = snapshot.docs
                    .map((userDoc) => ({ value: userDoc.id, label: userDoc.data().name }))
                    .filter((item) => item.label);
                setInspectores(list);
            },
            (error) => console.error("Live inspector listener failed:", error)
        );
    }, [effectiveRole]);

    const actor = useMemo(
        () => (user ? { uid: effectiveUid || user.uid } : null),
        [user, effectiveUid]
    );

    const [allTickets, setAllTickets] = useState([]);
    const [cachedActiveTicket, setCachedActiveTicket] = useState(null);
    const [activeLookupReady, setActiveLookupReady] = useState(false);
    const [ticketUiOverrides, setTicketUiOverrides] = useState({});
    const ticketUiOverridesRef = useRef({});
    const activeTicketCacheKey = effectiveUid ? `tickets.active.v2.${effectiveUid}` : null;

    const cacheActiveTicket = (ticket) => {
        setCachedActiveTicket(ticket || null);
        if (typeof window === "undefined" || !activeTicketCacheKey) return;
        try {
            if (!ticket) {
                window.localStorage.removeItem(activeTicketCacheKey);
                return;
            }
            const cacheDate = (value) => {
                const ms = timestampToMillis(value, NaN);
                return Number.isFinite(ms) ? new Date(ms).toISOString() : null;
            };
            const safe = {
                ...ticket,
                __cached_at_ms: Date.now(),
                activeAt: cacheDate(ticket.activeAt),
                paused_at: cacheDate(ticket.paused_at),
                due_at: cacheDate(ticket.due_at),
                dueDate: cacheDate(ticket.dueDate),
                created_at: cacheDate(ticket.created_at),
                updated_at: cacheDate(ticket.updated_at),
            };
            window.localStorage.setItem(activeTicketCacheKey, JSON.stringify(safe));
        } catch (err) {
            console.debug("Active task cache unavailable:", err);
        }
    };

    useEffect(() => {
        if (typeof window === "undefined" || !activeTicketCacheKey) {
            setCachedActiveTicket(null);
            return;
        }
        try {
            const raw = window.localStorage.getItem(activeTicketCacheKey);
            const parsed = raw ? JSON.parse(raw) : null;
            const cachedAtMs = Number(parsed?.__cached_at_ms || 0);
            const cacheIsFresh = cachedAtMs > 0 && (Date.now() - cachedAtMs) <= ACTIVE_TICKET_CACHE_MAX_AGE_MS;
            if (parsed?.id && parsed.status === "active" && parsed.active_by_id === effectiveUid && cacheIsFresh) {
                setCachedActiveTicket(parsed);
            } else {
                setCachedActiveTicket(null);
                if (raw) window.localStorage.removeItem(activeTicketCacheKey);
            }
        } catch {
            setCachedActiveTicket(null);
            window.localStorage.removeItem(activeTicketCacheKey);
        }
    }, [activeTicketCacheKey, effectiveUid]);

    const applyTicketUiOverride = (ticket, patch) => {
        if (!ticket?.id) return;
        const next = { ...ticket, ...patch };
        ticketUiOverridesRef.current = { ...ticketUiOverridesRef.current, [ticket.id]: patch };
        setTicketUiOverrides(ticketUiOverridesRef.current);
        setAllTickets((current) => current.map((item) => item.id === ticket.id ? { ...item, ...patch } : item));
        if (next.status === "active" && next.active_by_id === effectiveUid) cacheActiveTicket(next);
    };

    const clearTicketUiOverride = (ticketId) => {
        if (!ticketId) return;
        if (!ticketUiOverridesRef.current[ticketId]) return;
        const next = { ...ticketUiOverridesRef.current };
        delete next[ticketId];
        ticketUiOverridesRef.current = next;
        setTicketUiOverrides(next);
    };

    const ticketUiOverrideConfirmed = (ticket, patch) => {
        if (!ticket || !patch) return false;
        if (Object.prototype.hasOwnProperty.call(patch, "is_paused") && Boolean(ticket.is_paused) !== Boolean(patch.is_paused)) return false;
        if (Object.prototype.hasOwnProperty.call(patch, "activeAt")) {
            if (patch.activeAt == null && ticket.activeAt != null) return false;
            if (patch.activeAt != null && ticket.activeAt == null) return false;
        }
        if (Object.prototype.hasOwnProperty.call(patch, "currentSessionId")) {
            if ((patch.currentSessionId || null) !== (ticket.currentSessionId || null)) return false;
        }
        return true;
    };

    const [loading, setLoading] = useState(true);
    const [loadedTaskLimit, setLoadedTaskLimit] = useState(TASK_INITIAL_LOAD);
    const [recentTasksHaveMore, setRecentTasksHaveMore] = useState(true);
    const [sentTasksHaveMore, setSentTasksHaveMore] = useState(true);

    useEffect(() => {
        setLoadedTaskLimit(TASK_INITIAL_LOAD);
        setRecentTasksHaveMore(true);
        setSentTasksHaveMore(true);
        setActiveLookupReady(false);
    }, [effectiveUid, isAdmin]);

    const canLoadMoreTasks = recentTasksHaveMore || sentTasksHaveMore;
    const loadMoreTasks = () => {
        if (!canLoadMoreTasks) return;
        setLoadedTaskLimit((current) => current + TASK_LOAD_MORE_BATCH);
    };

    useEffect(() => {
        if (!user || !effectiveUid) {
            setAllTickets([]);
            setLoading(false);
            return undefined;
        }

        setLoading(true);

        if (isAdmin) {
            let recentTickets = [];
            let activeTickets = [];
            let recentReady = false;
            let activeReady = false;

            const publishAdmin = () => {
                if (!recentReady || !activeReady) return;
                const merged = new Map();
                [...recentTickets, ...activeTickets]
                    .filter((ticket) => !ticket.archived)
                    .forEach((ticket) => merged.set(ticket.id, ticket));
                setAllTickets([...merged.values()].sort((a, b) => {
                    const aMs = getCreatedAt(a)?.toMillis?.() ?? (getCreatedAt(a) ? new Date(getCreatedAt(a)).getTime() : 0);
                    const bMs = getCreatedAt(b)?.toMillis?.() ?? (getCreatedAt(b) ? new Date(getCreatedAt(b)).getTime() : 0);
                    return bMs - aMs;
                }));
                setLoading(false);
            };

            const adminQuery = query(
                ticketsCol(),
                where("archived", "==", false),
                orderBy("created_at", "desc"),
                limit(loadedTaskLimit + 1)
            );
            const activeQuery = query(ticketsCol(), where("active_by_id", "==", effectiveUid));

            const unsubscribeRecent = onSnapshot(adminQuery, (snapshot) => {
                setRecentTasksHaveMore(snapshot.docs.length > loadedTaskLimit);
                recentTickets = snapshot.docs.slice(0, loadedTaskLimit).map((d) => ({ id: d.id, ...d.data() }));
                recentReady = true;
                publishAdmin();
            }, (error) => {
                console.error("Live admin ticket listener failed:", error);
                recentTickets = [];
                setRecentTasksHaveMore(false);
                recentReady = true;
                publishAdmin();
            });

            const unsubscribeActive = onSnapshot(activeQuery, (snapshot) => {
                activeTickets = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
                const activeForCache = activeTickets.find((ticket) => ticket.status === "active" && ticket.active_by_id === effectiveUid) || null;
                let activeOverride = activeForCache ? ticketUiOverridesRef.current[activeForCache.id] : null;
                if (activeForCache && activeOverride && ticketUiOverrideConfirmed(activeForCache, activeOverride)) {
                    clearTicketUiOverride(activeForCache.id);
                    activeOverride = null;
                }
                cacheActiveTicket(activeForCache && activeOverride ? { ...activeForCache, ...activeOverride } : activeForCache);
                setActiveLookupReady(true);
                activeReady = true;
                publishAdmin();
            }, (error) => {
                console.error("Live active-ticket listener failed:", error);
                activeTickets = [];
                setActiveLookupReady(true);
                activeReady = true;
                publishAdmin();
            });

            return () => { unsubscribeRecent(); unsubscribeActive(); };
        }

        let assignedTickets = [];
        let responsibleTickets = [];
        let activeTickets = [];
        let assignedReady = false;
        let responsibleReady = false;
        let activeReady = false;

        const publish = () => {
            if (!assignedReady || !responsibleReady || !activeReady) return;

            const visibleAssigned = assignedTickets.filter((ticket) => {
                if (ticket.archived) return false;
                if (ticket.status !== "waiting") return true;
                if (!ticket.responsible_person_id) return true;
                return ticket.responsible_person_id === effectiveUid;
            });

            const visibleResponsible = responsibleTickets.filter(
                (ticket) => !ticket.archived && ticket.status === "waiting" && ticket.responsible_person_id === effectiveUid
            );

            const visibleActive = activeTickets.filter((ticket) => !ticket.archived && ticket.status === "active");
            const merged = new Map();
            [...visibleAssigned, ...visibleResponsible, ...visibleActive].forEach((ticket) => merged.set(ticket.id, ticket));

            const list = [...merged.values()].sort((a, b) => {
                const aMs = getCreatedAt(a)?.toMillis?.() ?? (getCreatedAt(a) ? new Date(getCreatedAt(a)).getTime() : 0);
                const bMs = getCreatedAt(b)?.toMillis?.() ?? (getCreatedAt(b) ? new Date(getCreatedAt(b)).getTime() : 0);
                return bMs - aMs;
            });

            setAllTickets(list);
            setLoading(false);
        };

        const assignedQuery = query(
            ticketsCol(),
            where("assignee_id", "==", effectiveUid),
            where("archived", "==", false),
            orderBy("created_at", "desc"),
            limit(loadedTaskLimit + 1)
        );
        const responsibleQuery = query(ticketsCol(), where("responsible_person_id", "==", effectiveUid));
        const activeQuery = query(ticketsCol(), where("active_by_id", "==", effectiveUid));

        const unsubscribeAssigned = onSnapshot(assignedQuery, (snapshot) => {
            setRecentTasksHaveMore(snapshot.docs.length > loadedTaskLimit);
            assignedTickets = snapshot.docs.slice(0, loadedTaskLimit).map((d) => ({ id: d.id, ...d.data() }));
            assignedReady = true;
            publish();
        }, (error) => {
            console.error("Live assigned ticket listener failed:", error);
            assignedTickets = [];
            setRecentTasksHaveMore(false);
            assignedReady = true;
            publish();
        });

        const unsubscribeResponsible = onSnapshot(responsibleQuery, (snapshot) => {
            responsibleTickets = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            responsibleReady = true;
            publish();
        }, (error) => {
            console.error("Live responsible-person ticket listener failed:", error);
            responsibleTickets = [];
            responsibleReady = true;
            publish();
        });

        const unsubscribeActive = onSnapshot(activeQuery, (snapshot) => {
            activeTickets = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            const activeForCache = activeTickets.find((ticket) => ticket.status === "active" && ticket.active_by_id === effectiveUid) || null;
            let activeOverride = activeForCache ? ticketUiOverridesRef.current[activeForCache.id] : null;
            if (activeForCache && activeOverride && ticketUiOverrideConfirmed(activeForCache, activeOverride)) {
                clearTicketUiOverride(activeForCache.id);
                activeOverride = null;
            }
            cacheActiveTicket(activeForCache && activeOverride ? { ...activeForCache, ...activeOverride } : activeForCache);
            setActiveLookupReady(true);
            activeReady = true;
            publish();
        }, (error) => {
            console.error("Live active-ticket listener failed:", error);
            activeTickets = [];
            setActiveLookupReady(true);
            activeReady = true;
            publish();
        });

        return () => { unsubscribeAssigned(); unsubscribeResponsible(); unsubscribeActive(); };
    }, [user?.uid, isAdmin, effectiveUid, loadedTaskLimit]);

    const [involvedTickets, setInvolvedTickets] = useState([]);

    useEffect(() => {
        if (!user || !effectiveUid || isAdmin) {
            setInvolvedTickets([]);
            return undefined;
        }

        const participantQuery = query(
            ticketsCol(),
            where("participant_ids", "array-contains", effectiveUid),
            where("archived", "==", false)
        );

        return onSnapshot(
            participantQuery,
            (snapshot) => setInvolvedTickets(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (error) => {
                console.error("Live participant ticket listener failed:", error);
                setInvolvedTickets([]);
            }
        );
    }, [user?.uid, isAdmin, effectiveUid]);

    const [sentTicketIds, setSentTicketIds] = useState([]);

    useEffect(() => {
        if (!user || !effectiveUid) {
            setSentTicketIds([]);
            setSentTasksHaveMore(false);
            return undefined;
        }
        const sentPointersQuery = query(
            collection(db, "users", effectiveUid, "sent_tickets"),
            orderBy("sent_at", "desc"),
            limit(loadedTaskLimit + 1)
        );
        return onSnapshot(
            sentPointersQuery,
            (snapshot) => {
                setSentTasksHaveMore(snapshot.docs.length > loadedTaskLimit);
                setSentTicketIds(snapshot.docs.slice(0, loadedTaskLimit).map((d) => d.id));
            },
            (error) => {
                console.error("Live sent-tickets listener failed:", error);
                setSentTasksHaveMore(false);
                setSentTicketIds([]);
            }
        );
    }, [user?.uid, effectiveUid, loadedTaskLimit]);

    const [sentTicketsData, setSentTicketsData] = useState({});

    useEffect(() => {
        if (!sentTicketIds.length) {
            setSentTicketsData({});
            return undefined;
        }

        const unsubs = sentTicketIds.map((id) =>
            onSnapshot(
                doc(db, "tickets", id),
                (snap) => {
                    setSentTicketsData((prev) => ({
                        ...prev,
                        [id]: snap.exists() ? { id: snap.id, ...snap.data() } : null,
                    }));
                },
                (error) => console.error("Live sent-ticket doc listener failed:", id, error)
            )
        );

        return () => unsubs.forEach((u) => u());
    }, [sentTicketIds.join("|")]);

    const sentTicketsList = useMemo(
        () => sentTicketIds
            .map((id) => sentTicketsData[id])
            .filter((t) => t && !t.archived),
        [sentTicketIds, sentTicketsData]
    );

    const allKnownTickets = useMemo(() => {
        const map = new Map();
        allTickets.forEach((t) => map.set(t.id, t));
        involvedTickets.forEach((t) => { if (!map.has(t.id)) map.set(t.id, t); });
        sentTicketsList.forEach((t) => { if (!map.has(t.id)) map.set(t.id, t); });
        return [...map.values()];
    }, [allTickets, involvedTickets, sentTicketsList]);

    const activeTicket = useMemo(() => {
        const live = allTickets.find((t) => t.status === "active" && t.active_by_id === effectiveUid) || null;
        const cachedFallback = !activeLookupReady
            && cachedActiveTicket?.status === "active"
            && cachedActiveTicket?.active_by_id === effectiveUid
            ? cachedActiveTicket
            : null;
        const base = live || cachedFallback;
        if (!base) return null;
        const override = ticketUiOverrides[base.id];
        return override ? { ...base, ...override } : base;
    }, [allTickets, cachedActiveTicket, ticketUiOverrides, effectiveUid, activeLookupReady]);
    const readyTickets = useMemo(() => allTickets.filter((t) => t.status === "ready" && t.assignee_id === effectiveUid), [allTickets, effectiveUid]);


    const waitingTickets = useMemo(
        () => allTickets.filter((ticket) =>
            ticket.status === "waiting"
            && (
                ticket.responsible_person_id
                    ? ticket.responsible_person_id === effectiveUid
                    : ticket.assignee_id === effectiveUid
            )
        ),
        [allTickets, effectiveUid]
    );
    const sortedQueue = useMemo(() => sortQueue(readyTickets), [readyTickets]);

    const sentTickets = useMemo(() => {
        if (isAdmin) {
            return allTickets.filter((t) =>
                !t.archived
                && t.assignee_id !== effectiveUid
                && (t.assignment_history || []).some((h) => h.assignee_id === effectiveUid)
            );
        }

        const map = new Map();
        sentTicketsList
            .filter((t) => t.assignee_id !== effectiveUid)
            .forEach((t) => map.set(t.id, t));
        involvedTickets
            .filter((t) => !t.archived && t.assignee_id !== effectiveUid)
            .forEach((t) => { if (!map.has(t.id)) map.set(t.id, t); });
        return [...map.values()];
    }, [isAdmin, allTickets, sentTicketsList, involvedTickets, effectiveUid]);

    const sortedSentTickets = useMemo(
        () => [...sentTickets].sort((a, b) => {
            const aSent = sentAtFor(a, effectiveUid);
            const bSent = sentAtFor(b, effectiveUid);
            const aMs = aSent ? new Date(aSent).getTime() : 0;
            const bMs = bSent ? new Date(bSent).getTime() : 0;
            return bMs - aMs;
        }),
        [sentTickets, effectiveUid]
    );

    const ticketsById = useMemo(
        () => Object.fromEntries(allKnownTickets.map((t) => [t.id, t])),
        [allKnownTickets]
    );

    const activityFeed = useMemo(() => {
        const newestFirst = (events) => [...events]
            .sort((a, b) => {
                const aTime = new Date(a.created_at_client || 0).getTime();
                const bTime = new Date(b.created_at_client || 0).getTime();
                return bTime - aTime;
            })
            .slice(0, ACTIVITY_MAX_LIMIT);

        if (isAdmin) {
            return newestFirst(
                allTickets.flatMap((t) =>
                    (t.activity_log || []).map((ev) => ({ ...ev, task_id: t.id }))
                )
            );
        }

        return newestFirst(
            allKnownTickets.flatMap((t) => {
                const windows = (t.assignment_history || [])
                    .filter((h) => h.assignee_id === effectiveUid)
                    .map((h) => ({
                        start: h.assigned_at ? new Date(h.assigned_at).getTime() : 0,
                        end: h.unassigned_at ? new Date(h.unassigned_at).getTime() : Infinity,
                    }));

                return (t.activity_log || [])
                    .filter((ev) => {
                        if (ev.user_id === effectiveUid) return true;
                        if (!windows.length) return false;
                        const evTime = new Date(ev.created_at_client || 0).getTime();
                        return windows.some((w) => evTime >= w.start && evTime < w.end);
                    })
                    .map((ev) => ({ ...ev, task_id: t.id }));
            })
        );
    }, [allTickets, allKnownTickets, isAdmin, effectiveUid]);


    const teamActiveTickets = useMemo(() => {
        if (!isAdmin) return [];
        return allTickets.filter((t) => t.status === "active" && t.id !== activeTicket?.id);
    }, [allTickets, isAdmin, activeTicket?.id]);


    const [prevSecs, setPrevSecs] = useState(0);
    const [timerHold, setTimerHold] = useState(null);
    useEffect(() => {
        if (!activeTicket?.id || !effectiveUid) { setPrevSecs(0); return; }
        return onSnapshot(
            collection(db, "tickets", activeTicket.id, "workSessions"),
            (s) => setPrevSecs(s.docs.reduce((sum, d) => {
                const data = d.data();
                if (data.user_id !== effectiveUid) return sum;
                if (d.id === activeTicket.currentSessionId) return sum;
                return sum + (typeof data.duration_seconds === "number" ? data.duration_seconds : 0);
            }, 0)),
            console.error
        );
    }, [activeTicket?.id, activeTicket?.currentSessionId, effectiveUid]);

    const currentElapsed = useElapsedTime(activeTicket?.is_paused ? null : activeTicket?.activeAt);
    const computedTotalElapsed = prevSecs + currentElapsed;
    const hasTimerHoldForActiveTicket = Boolean(
        timerHold && activeTicket?.id && timerHold.ticketId === activeTicket.id
    );
    const totalElapsed = hasTimerHoldForActiveTicket
        ? Math.max(timerHold.totalSeconds, computedTotalElapsed)
        : computedTotalElapsed;

    useEffect(() => {
        if (!timerHold) return;
        if (!activeTicket?.id || activeTicket.id !== timerHold.ticketId) {
            setTimerHold(null);
            return;
        }
        if (!activeTicket.is_paused && prevSecs >= timerHold.totalSeconds) {
            setTimerHold(null);
        }
    }, [activeTicket?.id, activeTicket?.is_paused, prevSecs, timerHold]);

    const activeStartTime = useMemo(() => {
        if (!activeTicket?.activeAt) return "";
        return fmtTimestamp(activeTicket.activeAt);
    }, [activeTicket?.activeAt]);

    const nameOf = (uid) => (uid ? (usersById[uid]?.name || usersById[uid]?.email || "Unknown") : "—");
    const emailOf = (uid) => (uid ? usersById[uid]?.email : "");
    const mentionableUsers = useMemo(
        () => allUsers.filter((u) => ASSIGNABLE_ROLES.includes(normalizeUserRole(u.user_role))),
        [allUsers]
    );
    const currentAssignmentFor = (ticket) => {
        const history = ticket?.assignment_history || [];
        return [...history].reverse().find((entry) => !entry.unassigned_at)
            || [...history].sort((a, b) => new Date(b.assigned_at || 0).getTime() - new Date(a.assigned_at || 0).getTime())[0]
            || null;
    };
    const sentByUidFor = (ticket) => currentAssignmentFor(ticket)?.assigned_by || ticket?.created_by_id || null;
    const requiresHandoverChecklist = (ticket) => {
        if (!ticket) return false;
        const storedCreatorRole = normalizeUserRole(ticket.created_by_role);
        if (storedCreatorRole) return storedCreatorRole === "inspection";

        return normalizeUserRole(usersById[ticket.created_by_id]?.user_role) === "inspection";
    };
    const defaultMentionForTicket = (ticket) => {
        const creator = mentionableUsers.find((u) => u.uid === ticket?.created_by_id);
        return creator ? `@${mentionHandleForUser(creator, mentionableUsers)} ` : "";
    };

    const notifyMentionedUsers = async (ticket, text, eventId = null) => {
        const mentionedIds = mentionedUserIdsFromText(text, mentionableUsers).filter((uid) => uid !== actor?.uid);
        if (!mentionedIds.length || !ticket?.id) return;

        try {
            await updateDoc(doc(db, "tickets", ticket.id), {
                participant_ids: arrayUnion(...mentionedIds),
                updated_at: serverTimestamp(),
                updated_by_id: actor.uid,
            });
        } catch (err) {
            console.error("Failed to add mentioned users as task participants:", err);
        }

        await Promise.all(mentionedIds.map((uid) => notifyUserSafely(uid, {
            type: "mention",
            ticket_id: ticket.id,
            ticket_title: getTitle(ticket),
            event_id: eventId,
            actor_id: actor.uid,
            workflow_stage: getWorkflowStageKey(ticket),
            message: `You were mentioned in ${getTitle(ticket)}`,
            dedupe_key: eventId ? `mention:${eventId}:${uid}` : null,
        })));
    };

    const assignableUsers = useMemo(
        () => allUsers
            .map((u) => ({ ...u, original_user_role: u.user_role, user_role: normalizeUserRole(u.user_role) }))
            .filter((u) => ASSIGNABLE_ROLES.includes(u.user_role)),
        [allUsers]
    );

    const assignmentOptions = useMemo(() => assignableUsers.flatMap((u) => {
        const baseName = u.name || u.email || "Unknown";
        if (u.user_role === "coordinator") {
            return [
                { uid: u.uid, stageKey: "coordinator_qc", label: `${baseName} — Coordinator (Quality Control)` },
                { uid: u.uid, stageKey: "coordinator_review", label: `${baseName} — Coordinator (Review)` },
            ];
        }
        const stage = STAGE_CHAIN.find((item) => item.role === u.user_role);
        return [{ uid: u.uid, stageKey: null, label: `${baseName}${stage?.label ? ` — ${stage.label}` : ""}` }];
    }), [assignableUsers]);

    const [buildingReports, setBuildingReports] = useState([]);
    const [buildingsLoading, setBuildingsLoading] = useState(true);


    const reportSortMillis = (data) => {
        const candidates = [data?.dateCreated, data?.date];
        for (const c of candidates) {
            if (!c) continue;
            if (typeof c.toMillis === "function") return c.toMillis();
            if (typeof c === "string") {
                const iso = c.includes("T") ? c : c.replace(" ", "T");
                const ms = new Date(iso).getTime();
                if (Number.isFinite(ms)) return ms;
            }
        }
        return 0;
    };

    useEffect(() => {
        setBuildingsLoading(true);

        return onSnapshot(
            collection(db, "Reports"),
            (snap) => {
                const combinedDocs = [...snap.docs];
                combinedDocs.sort((a, b) => reportSortMillis(b.data()) - reportSortMillis(a.data()));

                const newBuildingGroups = new Map();
                combinedDocs.forEach((d) => {
                    const data = d.data();
                    if (!data.buildingName) return;
                    if (!newBuildingGroups.has(data.buildingName)) newBuildingGroups.set(data.buildingName, []);
                    newBuildingGroups.get(data.buildingName).push({ docId: d.id, data });
                });

                newBuildingGroups.forEach((group) =>
                    group.sort((a, b) => reportSortMillis(b.data) - reportSortMillis(a.data))
                );

                const latest = [...newBuildingGroups.entries()].map(([buildingName, group]) => {
                    const top = group[0];
                    return {
                        docId: top.docId,
                        buildingName,
                        reportId: top.data.reportId || "",
                        city: top.data.city || "",
                    };
                });
                latest.sort((a, b) => a.buildingName.localeCompare(b.buildingName));

                setBuildingReports(latest);
                setBuildingsLoading(false);
            },
            (error) => {
                console.error("Live building listener failed:", error);
                setBuildingReports([]);
                setBuildingsLoading(false);
            }
        );
    }, []);

    const buildingOptions = useMemo(
        () => buildingReports.map((b) => ({
            value: b.docId,
            label: `${b.buildingName}${b.reportId ? ` — ${b.reportId}` : ""}${b.city ? ` (${b.city})` : ""}`,
            buildingName: b.buildingName,
            reportId: b.reportId || "",
        })),
        [buildingReports]
    );


    const getWorkflowStageKey = (ticket) => workflowStageKeyForTicket(
        ticket,
        normalizeUserRole(
            usersById[ticket?.assignee_id]?.user_role
            || (ticket?.assignee_id === effectiveUid ? effectiveRole : null)
        )
    );

    const getStageIndex = (ticket) => {
        const key = getWorkflowStageKey(ticket);
        const idx = STAGE_CHAIN.findIndex((stage) => stage.key === key);
        return idx === -1 ? 0 : idx;
    };

    const getCoordinatorContext = (ticket) => {
        if (!ticket) return null;
        const key = getWorkflowStageKey(ticket);
        if (key === "coordinator_review") return "review";
        if (key === "coordinator_qc") return "qc";
        return null;
    };

    const isCoordinatorQcTicket = (ticket) =>
        effectiveRole === "coordinator" && getCoordinatorContext(ticket) === "qc";

    const isCoordinatorReviewTicket = (ticket) =>
        effectiveRole === "coordinator" && getCoordinatorContext(ticket) === "review";

    const doorRoleForTicket = (ticket) => {
        const key = getWorkflowStageKey(ticket);
        if (key === "reviewer") return "reviewer";
        if (key === "coordinator_review") return "coordinator_review";
        if (key === "remedial") return "remedial";
        if (key === "manager") return "manager";
        return null;
    };

    const resolveStageIndex = (ticket, newAssigneeUid, targetStageKey = null) => {
        const curIdx = getStageIndex(ticket);
        if (targetStageKey) {
            const explicitIdx = STAGE_CHAIN.findIndex((stage) => stage.key === targetStageKey);
            if (explicitIdx !== -1) return explicitIdx;
        }
        const newRole = normalizeUserRole(usersById[newAssigneeUid]?.user_role);
        if (STAGE_CHAIN[curIdx + 1]?.role === newRole) return curIdx + 1;
        if (STAGE_CHAIN[curIdx - 1]?.role === newRole) return curIdx - 1;
        if (newRole === "coordinator") return STAGE_CHAIN.findIndex((stage) => stage.key === "coordinator_qc");
        const idx = STAGE_CHAIN.findIndex((stage) => stage.role === newRole);
        return idx === -1 ? curIdx : idx;
    };


    const getNextStage = (ticket) => STAGE_CHAIN[getStageIndex(ticket) + 1] || null;
    const getPrevStage = (ticket) => STAGE_CHAIN[getStageIndex(ticket) - 1] || null;

    const getForwardUsers = (ticket) => {
        const idx = getStageIndex(ticket);
        const nextStage = STAGE_CHAIN[idx + 1];
        if (!nextStage) return [];
        return assignableUsers.filter((u) => normalizeUserRole(u.user_role) === nextStage.role);
    };

    const getBackwardUsers = (ticket) => {
        if (isAdmin) return assignableUsers.filter((u) => u.uid !== ticket.assignee_id);
        const idx = getStageIndex(ticket);
        const prevStage = STAGE_CHAIN[idx - 1];
        if (!prevStage) return [];
        return assignableUsers.filter((u) => normalizeUserRole(u.user_role) === prevStage.role);
    };

    const getOtherUsers = (ticket) => {
        if (isAdmin) return [];
        const suggestedIds = new Set([
            ticket.assignee_id,
            ...getForwardUsers(ticket).map((u) => u.uid),
            ...getBackwardUsers(ticket).map((u) => u.uid),
        ]);
        return assignableUsers.filter((u) => !suggestedIds.has(u.uid));
    };

    const stats = useMemo(() => {
        const mine = isAdmin
            ? allTickets.filter((t) => t.assignee_id === effectiveUid)
            : allTickets;
        return {
            active: activeTicket ? 1 : 0,
            ready: readyTickets.length,
            waiting: waitingTickets.length,
            dueToday: mine.filter(isDueToday).length,
        };
    }, [activeTicket, readyTickets, waitingTickets, allTickets, effectiveUid, isAdmin]);

    const [taskFilter, setTaskFilter] = useState("all");
    const [filterAssignee, setFilterAssignee] = useState("all");
    const [filterCreator, setFilterCreator] = useState("all");
    const [filterType, setFilterType] = useState("all");
    const [filterPriority, setFilterPriority] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");

    const taskCounts = useMemo(() => ({
        all: allTickets.length,
        ready: allTickets.filter((t) => t.status === "ready").length,
        active: allTickets.filter((t) => t.status === "active").length,
        waiting: allTickets.filter((t) => t.status === "waiting").length,
        completed: allTickets.filter((t) => t.status === "completed").length,
    }), [allTickets]);


    const filterAssigneeOptions = useMemo(() => {
        const ids = [...new Set(allTickets.map((t) => t.assignee_id).filter(Boolean))];
        return ids
            .map((uid) => ({ uid, name: nameOf(uid) }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allTickets, usersById]);

    const filterCreatorOptions = useMemo(() => {
        const ids = [...new Set(allTickets.map((t) => t.created_by_id).filter(Boolean))];
        return ids
            .map((uid) => ({ uid, name: nameOf(uid) }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allTickets, usersById]);

    const filterTypeOptions = useMemo(() => {
        const types = [...new Set(allTickets.map((t) => t.task_type).filter(Boolean))];
        return TASK_TYPES.filter((t) => types.includes(t.value));
    }, [allTickets]);

    const [sortConfig, setSortConfig] = useState({ key: "created", dir: "desc" });

    const toggleSort = (key) => {
        setSortConfig((prev) => prev.key === key
            ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
            : { key, dir: "asc" });
    };


    const totalTimeForSort = (t) => {
        const base = typeof t.total_time_seconds === "number" ? t.total_time_seconds : 0;
        if (t.status === "active" && t.activeAt) return base + (secsSince(t.activeAt) || 0);
        return base;
    };

    const SORTERS = {
        id: (t) => t.id,
        task: (t) => getTitle(t).toLowerCase(),
        type: (t) => TASK_TYPE_CFG[t.task_type]?.label || "",
        creator: (t) => nameOf(t.created_by_id).toLowerCase(),
        assignee: (t) => nameOf(t.assignee_id).toLowerCase(),
        priority: (t) => PRIORITY_ORDER[t.priority] ?? -1,
        status: (t) => t.status || "",
        due: (t) => getDueAt(t)?.toMillis?.() ?? (getDueAt(t) ? new Date(getDueAt(t)).getTime() : Infinity),
        created: (t) => getCreatedAt(t)?.toMillis?.() ?? (getCreatedAt(t) ? new Date(getCreatedAt(t)).getTime() : 0),
        totalTime: totalTimeForSort,
    };

    const filteredAllTasks = useMemo(() => {
        let list = taskFilter === "all" ? allTickets : allTickets.filter((t) => t.status === taskFilter);
        if (filterAssignee !== "all") list = list.filter((t) => t.assignee_id === filterAssignee);
        if (filterCreator !== "all") list = list.filter((t) => t.created_by_id === filterCreator);
        if (filterType !== "all") list = list.filter((t) => t.task_type === filterType);
        if (filterPriority !== "all") list = list.filter((t) => t.priority === filterPriority);
        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            list = list.filter((t) =>
                getTitle(t).toLowerCase().includes(q) ||
                getTaskId(t).toLowerCase().includes(q) ||
                (t.description || "").toLowerCase().includes(q)
            );
        }

        const sorter = SORTERS[sortConfig.key] || SORTERS.created;
        const dir = sortConfig.dir === "asc" ? 1 : -1;
        return [...list].sort((a, b) => {
            const av = sorter(a), bv = sorter(b);
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
    }, [allTickets, taskFilter, filterAssignee, filterCreator, filterType, filterPriority, searchQuery, sortConfig, usersById]);

    const clearAllTaskFilters = () => {
        setTaskFilter("all"); setFilterAssignee("all"); setFilterCreator("all"); setFilterType("all");
        setFilterPriority("all"); setSearchQuery("");
    };
    const hasActiveFilters = taskFilter !== "all" || filterAssignee !== "all" || filterCreator !== "all" ||
        filterType !== "all" || filterPriority !== "all" || !!searchQuery.trim();

    const renderSortTh = (label, key, style) => (
        <th
            style={style}
            className={`sortable-th${sortConfig.key === key ? " active" : ""}`}
            onClick={() => toggleSort(key)}
        >
            <span className="th-inner">
                {label}
                <FontAwesomeIcon
                    icon={faChevronDown}
                    size="xs"
                    className={`sort-icon${sortConfig.key === key ? " active" : ""}${sortConfig.key === key && sortConfig.dir === "asc" ? " asc" : ""}`}
                />
            </span>
        </th>
    );

    const [activeTab, setActiveTab] = useState("mywork");
    const [sidePanelTab, setSidePanelTab] = useState("stuck");
    const [queueTab, setQueueTab] = useState("todo");
    const [coordinatorView, setCoordinatorView] = useState("qc");
    const [queueSearchQuery, setQueueSearchQuery] = useState("");
    const [queueTypeFilter, setQueueTypeFilter] = useState("all");
    const [queuePriorityFilter, setQueuePriorityFilter] = useState("all");
    const [queueStatusFilter, setQueueStatusFilter] = useState("all");
    const [queueSortConfig, setQueueSortConfig] = useState({ key: "default", dir: "asc" });
    const [stuckSearchQuery, setStuckSearchQuery] = useState("");
    const [stuckDateFilter, setStuckDateFilter] = useState(() => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, "0");
        const d = String(today.getDate()).padStart(2, "0");
        return `date:${y}-${m}-${d}`;
    });
    const [stuckSortDir, setStuckSortDir] = useState("asc");
    const stuckSearchInputRef = useRef(null);
    const [activitySearchQuery, setActivitySearchQuery] = useState("");
    const [activityTypeFilter, setActivityTypeFilter] = useState("all");
    const [activityUserFilter, setActivityUserFilter] = useState("all");
    const [activityDisplayLimit, setActivityDisplayLimit] = useState(ACTIVITY_DISPLAY_LIMIT);
    const [notifications, setNotifications] = useState([]);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [expandedNotificationIds, setExpandedNotificationIds] = useState(() => new Set());
    const notificationsRef = useRef(null);
    const [highlightTicketId, setHighlightTicketId] = useState(null);
    const [highlightStuckTicketId, setHighlightStuckTicketId] = useState(null);
    const [highlightEventId, setHighlightEventId] = useState(null);
    const [rewardNavigationTarget, setRewardNavigationTarget] = useState(null);
    const queueRowRefs = useRef({});
    const stuckCardRefs = useRef({});
    const eventRowRefs = useRef({});
    const notificationInitializedRef = useRef(false);
    const notificationAudioContextRef = useRef(null);
    const notificationSoundTimerRef = useRef(null);
    const notificationSoundPlayingRef = useRef(false);
    const notificationSoundPendingRef = useRef(false);
    const [rewardCelebration, setRewardCelebration] = useState(null);
    const rewardCelebrationTimerRef = useRef(null);
    const rewardCelebrationQueueRef = useRef([]);
    const rewardCelebratedIdsRef = useRef(new Set());

    const coordinatorQcQueue = useMemo(
        () => sortedQueue.filter((ticket) => getCoordinatorContext(ticket) !== "review"),
        [sortedQueue, usersById]
    );
    const coordinatorReviewQueue = useMemo(
        () => sortedQueue.filter((ticket) => getCoordinatorContext(ticket) === "review"),
        [sortedQueue, usersById]
    );
    const coordinatorBaseQueue = effectiveRole === "coordinator"
        ? (coordinatorView === "review" ? coordinatorReviewQueue : coordinatorQcQueue)
        : sortedQueue;

    const clearQueueFilters = () => {
        setQueueSearchQuery("");
        setQueueTypeFilter("all");
        setQueuePriorityFilter("all");
        setQueueStatusFilter("all");
        setQueueSortConfig({ key: "default", dir: "asc" });
    };

    const hasQueueFilters = Boolean(
        queueSearchQuery.trim()
        || queueTypeFilter !== "all"
        || queuePriorityFilter !== "all"
        || (queueTab === "sent" && queueStatusFilter !== "all")
        || queueSortConfig.key !== "default"
    );

    const queueSortValue = (ticket, key) => {
        switch (key) {
            case "task": return getTitle(ticket).toLowerCase();
            case "type": return TASK_TYPE_CFG[ticket.task_type]?.label || "";
            case "priority": return PRIORITY_ORDER[ticket.priority] ?? -1;
            case "due": return getDueAt(ticket)?.toMillis?.() ?? (getDueAt(ticket) ? new Date(getDueAt(ticket)).getTime() : Infinity);
            case "assignee": return nameOf(ticket.assignee_id).toLowerCase();
            case "status": return ticket.status || "";
            case "sent": {
                const sentAt = sentAtFor(ticket, effectiveUid);
                return sentAt ? new Date(sentAt).getTime() : 0;
            }
            default: return 0;
        }
    };

    const filterAndSortQueue = (source, mode) => {
        let list = [...source];
        const q = queueSearchQuery.trim().toLowerCase();
        if (q) {
            list = list.filter((ticket) =>
                getTitle(ticket).toLowerCase().includes(q)
                || getTaskId(ticket).toLowerCase().includes(q)
                || String(ticket.description || "").toLowerCase().includes(q)
                || String(ticket.building_name || "").toLowerCase().includes(q)
                || nameOf(ticket.assignee_id).toLowerCase().includes(q)
            );
        }
        if (queueTypeFilter !== "all") list = list.filter((ticket) => ticket.task_type === queueTypeFilter);
        if (queuePriorityFilter !== "all") list = list.filter((ticket) => ticket.priority === queuePriorityFilter);
        if (mode === "sent" && queueStatusFilter !== "all") list = list.filter((ticket) => ticket.status === queueStatusFilter);

        if (queueSortConfig.key !== "default") {
            const dir = queueSortConfig.dir === "asc" ? 1 : -1;
            list.sort((a, b) => {
                const av = queueSortValue(a, queueSortConfig.key);
                const bv = queueSortValue(b, queueSortConfig.key);
                if (av < bv) return -1 * dir;
                if (av > bv) return 1 * dir;
                return 0;
            });
        }
        return list;
    };

    const visibleQueue = useMemo(
        () => filterAndSortQueue(coordinatorBaseQueue, "todo"),
        [coordinatorBaseQueue, queueSearchQuery, queueTypeFilter, queuePriorityFilter, queueSortConfig, usersById]
    );
    const visibleSentTickets = useMemo(
        () => filterAndSortQueue(sortedSentTickets, "sent"),
        [sortedSentTickets, queueSearchQuery, queueTypeFilter, queuePriorityFilter, queueStatusFilter, queueSortConfig, usersById, effectiveUid]
    );

    const stuckView = useMemo(() => {
        const q = stuckSearchQuery.trim().toLowerCase();
        const searched = waitingTickets.filter((ticket) => {
            if (!q) return true;
            const searchable = [
                getTitle(ticket),
                getTaskId(ticket),
                ticket.description,
                ticket.blocked_reason,
                ticket.waiting_on_type,
                ticket.building_name,
                nameOf(ticket.assignee_id),
                nameOf(ticket.responsible_person_id),
            ].filter(Boolean).join(" ").toLowerCase();
            return searchable.includes(q);
        });

        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const localDateKey = (value) => {
            const d = value instanceof Date ? value : new Date(value);
            if (Number.isNaN(d.getTime())) return "";
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        };
        const chaseDateKey = (ticket) => {
            const ms = timestampToMillis(ticket.next_follow_up_at, NaN);
            return Number.isFinite(ms) ? localDateKey(ms) : "";
        };
        const dayOptions = Array.from({ length: 7 }, (_, index) => {
            const date = new Date(startOfToday);
            date.setDate(date.getDate() + index);
            const key = localDateKey(date);
            const count = searched.filter((ticket) => chaseDateKey(ticket) === key).length;
            const label = date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
            return { value: `date:${key}`, key, label, count };
        });
        const overdueCount = searched.filter((ticket) => {
            const chaseMs = timestampToMillis(ticket.next_follow_up_at, NaN);
            return Number.isFinite(chaseMs) && chaseMs < startOfToday.getTime();
        }).length;
        const visibleDayOptions = dayOptions.filter((option, index) =>
            index < 2 || option.count > 0 || option.value === stuckDateFilter
        );
        const filterOptions = [
            ...(overdueCount > 0 ? [{ value: "overdue", label: "Overdue", count: overdueCount }] : []),
            ...visibleDayOptions,
            { value: "all", label: "All", count: searched.length },
        ];

        let selected = searched;
        if (stuckDateFilter === "overdue") {
            selected = searched.filter((ticket) => {
                const chaseMs = timestampToMillis(ticket.next_follow_up_at, NaN);
                return Number.isFinite(chaseMs) && chaseMs < startOfToday.getTime();
            });
        } else if (stuckDateFilter !== "all") {
            const selectedOption = filterOptions.find((option) => option.value === stuckDateFilter) || dayOptions[0];
            selected = searched.filter((ticket) => chaseDateKey(ticket) === selectedOption.key);
        }

        const sortByNextChase = (a, b) => {
            const aChase = timestampToMillis(a.next_follow_up_at, NaN);
            const bChase = timestampToMillis(b.next_follow_up_at, NaN);
            const aValid = Number.isFinite(aChase);
            const bValid = Number.isFinite(bChase);
            if (aValid !== bValid) return aValid ? -1 : 1;
            if (aValid && bValid && aChase !== bChase) {
                return stuckSortDir === "asc" ? aChase - bChase : bChase - aChase;
            }
            return getTitle(a).localeCompare(getTitle(b));
        };

        return {
            visible: [...selected].sort(sortByNextChase),
            filterOptions,
            searchedTotal: searched.length,
        };
    }, [waitingTickets, stuckSearchQuery, stuckDateFilter, stuckSortDir, usersById]);

    const visibleWaitingTickets = stuckView.visible;
    const stuckDateOptions = stuckView.filterOptions;

    const toggleQueueSort = (key) => {
        setQueueSortConfig((prev) => prev.key === key
            ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
            : { key, dir: "asc" });
    };

    const renderQueueSortTh = (label, key, style) => (
        <th
            style={style}
            className={`sortable-th${queueSortConfig.key === key ? " active" : ""}`}
            onClick={() => toggleQueueSort(key)}
        >
            <span className="th-inner">
                {label}
                <FontAwesomeIcon
                    icon={faChevronDown}
                    size="xs"
                    className={`sort-icon${queueSortConfig.key === key ? " active" : ""}${queueSortConfig.key === key && queueSortConfig.dir === "asc" ? " asc" : ""}`}
                />
            </span>
        </th>
    );

    const activityTypeOptions = useMemo(
        () => [...new Set(activityFeed.map((event) => event.event_type).filter(Boolean))].sort(),
        [activityFeed]
    );
    const activityUserOptions = useMemo(
        () => [...new Set(activityFeed.map((event) => event.user_id).filter(Boolean))]
            .map((uid) => ({ uid, name: nameOf(uid) }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        [activityFeed, usersById]
    );
    const filteredActivityFeed = useMemo(() => {
        const q = activitySearchQuery.trim().toLowerCase();
        return activityFeed.filter((event) => {
            if (activityTypeFilter !== "all" && event.event_type !== activityTypeFilter) return false;
            if (activityUserFilter !== "all" && event.user_id !== activityUserFilter) return false;
            if (!q) return true;
            const ticket = ticketsById[event.task_id];
            return String(event.note || "").toLowerCase().includes(q)
                || String(getTitle(ticket || {})).toLowerCase().includes(q)
                || nameOf(event.user_id).toLowerCase().includes(q);
        });
    }, [activityFeed, activitySearchQuery, activityTypeFilter, activityUserFilter, ticketsById, usersById]);
    const visibleActivityFeed = filteredActivityFeed.slice(0, activityDisplayLimit);

    useEffect(() => {
        setActivityDisplayLimit(ACTIVITY_DISPLAY_LIMIT);
    }, [activitySearchQuery, activityTypeFilter, activityUserFilter]);

    const activeCardRef = useRef(null);
    const [activeCardHeight, setActiveCardHeight] = useState(null);

    useEffect(() => {
        if (!notificationsOpen) return;

        const handleClickOutside = (event) => {
            if (
                notificationsRef.current &&
                !notificationsRef.current.contains(event.target)
            ) {
                setNotificationsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [notificationsOpen]);

    useEffect(() => {
        if (effectiveRole !== "coordinator" || !activeTicket) return;
        const context = getCoordinatorContext(activeTicket);
        if (context && context !== coordinatorView) setCoordinatorView(context);
    }, [effectiveRole, activeTicket?.id, activeTicket?.task_type, activeTicket?.workflow_stage]);

    useEffect(() => {
        if (activeTab !== "mywork") return undefined;

        const element = activeCardRef.current;
        if (!element) return undefined;

        const updateHeight = () => {
            const nextHeight = Math.ceil(element.getBoundingClientRect().height || 0);
            if (nextHeight > 0) setActiveCardHeight(nextHeight);
        };

        updateHeight();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(updateHeight);
            observer.observe(element);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", updateHeight);
        return () => window.removeEventListener("resize", updateHeight);
    }, [activeTab, activeTicket?.id, effectiveRole]);

    const notificationPresentation = (notification) => {
        const actorName = notification?.actor_id ? nameOf(notification.actor_id) : "System";
        const ticketTitle = notification?.ticket_title || "Task";
        const type = String(notification?.type || "");
        const metadata = notification?.metadata || {};
        const confirmedDelta = Number(notification?.confirmed_points_delta || 0);
        const pendingDelta = Number(notification?.pending_points_delta || 0);
        const pointText = (value) => `${value > 0 ? "+" : ""}${value.toLocaleString("en-GB")} ${Math.abs(value) === 1 ? "pt" : "pts"}`;
        const rewardTitle = notification?.title || "Rewards updated";
        const withTask = (lead) => ticketTitle && ticketTitle !== "Task" ? `${lead} · ${ticketTitle}` : lead;

        if (type.startsWith("reward_")) {
            const state = metadata.notification_state || metadata.decision || "";
            const rewardRows = Array.isArray(metadata.reward_rows)
                ? metadata.reward_rows.map((row) => ({
                    type: String(row?.type || ""),
                    title: row?.title || ({
                        task_completed: "Task completed",
                        speed_bonus: "Speed & quality",
                        difficult_task: "Difficult task",
                        urgent_management_request: "Urgent management request",
                    }[row?.type] || "Reward"),
                    points: Number(row?.points || 0),
                    status: String(row?.status || "earned"),
                })).filter((row) => row.type || row.title)
                : [];
            if (type === "reward_summary" || type === "reward_review_summary") {
                const earnedPoints = Math.max(0, confirmedDelta);
                const pendingPoints = type === "reward_review_summary"
                    ? Math.max(0, Number(metadata.pending_points_remaining || 0))
                    : Math.max(0, pendingDelta);
                const hasRejected = rewardRows.some((row) => row.status === "rejected");
                const hasApproved = rewardRows.some((row) => ["earned", "approved", "confirmed"].includes(row.status));
                return {
                    title: rewardTitle,
                    subtitle: ticketTitle && ticketTitle !== "Task" ? ticketTitle : "Reward activity",
                    icon: hasRejected && !hasApproved ? faCircleXmark : faCoins,
                    tone: hasRejected && !hasApproved ? "danger" : pendingPoints > 0 ? "pending" : "success",
                    reward: true,
                    rewardSummary: { earnedPoints, pendingPoints },
                    rewardRows,
                };
            }
            if (type === "reward_redemption_requested") {
                const itemTitle = metadata.item_title || "Reward";
                const reserved = Number(metadata.reserved_points || Math.abs(metadata.available_points_delta || 0) || 0);
                const availableText = notification?.available_balance_before != null && notification?.available_balance_after != null
                    ? ` · Available ${Number(notification.available_balance_before).toLocaleString("en-GB")} → ${Number(notification.available_balance_after).toLocaleString("en-GB")} pts`
                    : "";
                return { title: "Reward redemption requested", subtitle: `${itemTitle}${reserved ? ` · ${reserved} pts reserved` : ""}${availableText}`, icon: faGift, tone: "pending", reward: true };
            }
            if (type === "reward_redemption_cancelled") {
                const itemTitle = metadata.item_title || "Reward";
                const released = Number(metadata.released_points || metadata.available_points_delta || 0);
                const availableText = notification?.available_balance_before != null && notification?.available_balance_after != null
                    ? ` · Available ${Number(notification.available_balance_before).toLocaleString("en-GB")} → ${Number(notification.available_balance_after).toLocaleString("en-GB")} pts`
                    : "";
                return { title: "Reward redemption cancelled", subtitle: `${itemTitle}${released ? ` · ${released} pts released` : ""}${availableText}`, icon: faGift, tone: "neutral", reward: true };
            }
            if (type === "reward_redemption_reviewed") {
                const decision = metadata.decision;
                const itemTitle = metadata.item_title || "Reward request";
                if (decision === "rejected") {
                    const released = Number(metadata.released_points || metadata.available_points_delta || 0);
                    const availableText = notification?.available_balance_before != null && notification?.available_balance_after != null
                        ? ` · Available ${Number(notification.available_balance_before).toLocaleString("en-GB")} → ${Number(notification.available_balance_after).toLocaleString("en-GB")} pts`
                        : "";
                    return { title: "Reward request declined", subtitle: `${itemTitle}${released ? ` · ${released} pts released` : ""}${availableText}`, icon: faCircleXmark, tone: "danger", reward: true };
                }
                const spent = Number(metadata.spent_points || Math.abs(confirmedDelta) || 0);
                const balanceText = notification?.balance_before != null && notification?.balance_after != null
                    ? ` · Balance ${Number(notification.balance_before).toLocaleString("en-GB")} → ${Number(notification.balance_after).toLocaleString("en-GB")} pts`
                    : "";
                return { title: "Reward request approved", subtitle: `${itemTitle}${spent ? ` · ${spent} pts redeemed` : ""}${balanceText}`, icon: faGift, tone: "success", reward: true };
            }
            if (metadata.source === "manual_adjustment" || type === "reward_adjustment") {
                const balanceText = notification?.balance_before != null && notification?.balance_after != null
                    ? `${Number(notification.balance_before).toLocaleString("en-GB")} → ${Number(notification.balance_after).toLocaleString("en-GB")} pts`
                    : "";
                const adjustmentReason = String(notification?.reason || "").trim();
                const lead = [
                    confirmedDelta ? pointText(confirmedDelta) : "Points adjusted",
                    balanceText,
                    adjustmentReason ? `Reason: ${adjustmentReason}` : "",
                ].filter(Boolean).join(" · ");
                return {
                    title: rewardTitle,
                    subtitle: withTask(lead),
                    icon: faCoins,
                    tone: confirmedDelta < 0 ? "danger" : "success",
                    reward: true,
                };
            }
            const legacyDecision = Array.isArray(notification?.decisions) && notification.decisions.length === 1 ? notification.decisions[0]?.decision : "";
            if (type === "reward_review_approved" || state === "approved" || legacyDecision === "confirmed") {
                return { title: rewardTitle, subtitle: withTask(pointText(Math.max(0, confirmedDelta))), icon: faCircleCheck, tone: "success", reward: true };
            }
            if (type === "reward_review_rejected" || state === "rejected" || legacyDecision === "rejected") {
                return { title: rewardTitle, subtitle: withTask("No points awarded"), icon: faCircleXmark, tone: "danger", reward: true };
            }
            if (type === "reward_pending" || state === "pending_review" || pendingDelta > 0) {
                const pending = Math.max(0, pendingDelta || Number(metadata.points || 0));
                return { title: rewardTitle, subtitle: withTask(pending ? `${pointText(pending)} awaiting review` : "Awaiting review"), icon: faHourglassHalf, tone: "pending", reward: true };
            }
            if (type === "reward_penalty" || confirmedDelta < 0) {
                if (metadata.source === "overdue_daily_penalty") {
                    const overdueDate = metadata.overdue_date ? ` · ${metadata.overdue_date}` : "";
                    return { title: "Overdue task — 1 point deducted", subtitle: `${ticketTitle}${overdueDate}`, icon: faCircleExclamation, tone: "danger", reward: true };
                }
                return { title: rewardTitle, subtitle: withTask(pointText(confirmedDelta)), icon: faCircleExclamation, tone: "danger", reward: true };
            }
            if (type === "reward_achievement") {
                const cashValue = Number(metadata.cash_value_gbp || 0);
                return { title: rewardTitle, subtitle: cashValue ? `£${cashValue.toLocaleString("en-GB")} recognition award` : (confirmedDelta ? pointText(confirmedDelta) : "Recognition award"), icon: faTrophy, tone: "success", reward: true };
            }
            if (type === "reward_points_added" || confirmedDelta > 0) {
                return { title: rewardTitle, subtitle: withTask(pointText(confirmedDelta)), icon: faCircleCheck, tone: "success", reward: true };
            }
            return { title: rewardTitle, subtitle: notification?.message || "Rewards updated", icon: faCoins, tone: "neutral", reward: true };
        }

        switch (type) {
            case "mention":
                return { title: `${actorName} mentioned you`, subtitle: ticketTitle, icon: faComment, tone: "neutral" };
            case "stuck_responsibility":
                return { title: `${actorName} needs your follow-up`, subtitle: ticketTitle, icon: faCircleExclamation, tone: "pending" };
            case "follow_up_due":
                return { title: "Follow-up is due", subtitle: ticketTitle, icon: faClock, tone: "pending" };
            case "amendment_required":
                return { title: `${actorName} requested an amendment`, subtitle: ticketTitle, icon: faRotateLeft, tone: "pending" };
            case "task_updated":
                return { title: `${actorName} updated your task`, subtitle: ticketTitle, icon: faBell, tone: "neutral" };
            case "task_completed":
                return { title: `${actorName} completed a task`, subtitle: ticketTitle, icon: faCircleCheck, tone: "success" };
            case "task_archived":
                return { title: `${actorName} archived a task`, subtitle: ticketTitle, icon: faBoxArchive, tone: "neutral" };
            case "report_ready": {
                const reportType = notification?.report_type === "residential" ? "Residential" : "Communal";
                const reportLabel = [notification?.report_id || notification?.building_name].filter(Boolean).join(" ");
                return { title: `${reportType} report ready`, subtitle: reportLabel || "Report ready", icon: faClipboardCheck, tone: "success" };
            }
            case "task_assigned":
            default:
                return { title: `${actorName} sent you a task`, subtitle: ticketTitle, icon: faBell, tone: "neutral" };
        }
    };

    const queueNotificationSound = () => {
        if (notificationSoundTimerRef.current) {
            clearTimeout(notificationSoundTimerRef.current);
        }

        notificationSoundTimerRef.current = setTimeout(() => {
            notificationSoundTimerRef.current = null;
            playNotificationSound();
        }, 250);
    };

    const playNotificationSound = () => {
        if (typeof window === "undefined") return;

        if (notificationSoundPlayingRef.current) {
            notificationSoundPendingRef.current = true;
            return;
        }

        try {
            const AudioContextCtor =
                window.AudioContext || window.webkitAudioContext;

            if (!AudioContextCtor) return;

            const ctx =
                notificationAudioContextRef.current ||
                new AudioContextCtor();

            notificationAudioContextRef.current = ctx;

            const play = () => {
                if (notificationSoundPlayingRef.current) {
                    notificationSoundPendingRef.current = true;
                    return;
                }

                notificationSoundPlayingRef.current = true;

                const start = ctx.currentTime;

                const masterGain = ctx.createGain();
                masterGain.gain.setValueAtTime(0.0001, start);
                masterGain.gain.exponentialRampToValueAtTime(
                    0.1,
                    start + 0.03
                );
                masterGain.gain.setValueAtTime(
                    0.055,
                    start + 0.7
                );
                masterGain.gain.exponentialRampToValueAtTime(
                    0.0001,
                    start + 2.2
                );
                masterGain.connect(ctx.destination);

                const notes = [
                    {
                        frequency: 659.25,
                        offset: 0,
                        duration: 1.8,
                        volume: 0.75,
                    },
                    {
                        frequency: 783.99,
                        offset: 0.16,
                        duration: 1.7,
                        volume: 0.6,
                    },
                    {
                        frequency: 987.77,
                        offset: 0.34,
                        duration: 1.5,
                        volume: 0.45,
                    },
                ];

                notes.forEach(
                    ({ frequency, offset, duration, volume }) => {
                        const osc = ctx.createOscillator();
                        const noteGain = ctx.createGain();

                        const noteStart = start + offset;
                        const noteEnd = noteStart + duration;

                        osc.type = "sine";
                        osc.frequency.setValueAtTime(
                            frequency,
                            noteStart
                        );

                        noteGain.gain.setValueAtTime(
                            0.0001,
                            noteStart
                        );

                        noteGain.gain.exponentialRampToValueAtTime(
                            volume,
                            noteStart + 0.04
                        );

                        noteGain.gain.exponentialRampToValueAtTime(
                            0.0001,
                            noteEnd
                        );

                        osc.connect(noteGain);
                        noteGain.connect(masterGain);

                        osc.start(noteStart);
                        osc.stop(noteEnd);
                    }
                );

                const bodyOsc = ctx.createOscillator();
                const bodyGain = ctx.createGain();

                bodyOsc.type = "sine";
                bodyOsc.frequency.setValueAtTime(
                    329.63,
                    start
                );

                bodyGain.gain.setValueAtTime(
                    0.0001,
                    start
                );

                bodyGain.gain.exponentialRampToValueAtTime(
                    0.18,
                    start + 0.05
                );

                bodyGain.gain.exponentialRampToValueAtTime(
                    0.0001,
                    start + 1.6
                );

                bodyOsc.connect(bodyGain);
                bodyGain.connect(masterGain);

                bodyOsc.start(start);
                bodyOsc.stop(start + 1.6);

                window.setTimeout(() => {
                    notificationSoundPlayingRef.current = false;

                    if (notificationSoundPendingRef.current) {
                        notificationSoundPendingRef.current = false;

                        window.setTimeout(() => {
                            playNotificationSound();
                        }, 100);
                    }
                }, 2250);
            };

            if (ctx.state === "suspended") {
                ctx.resume()
                    .then(play)
                    .catch(() => {
                        notificationSoundPlayingRef.current = false;
                    });
            } else {
                play();
            }
        } catch (err) {
            notificationSoundPlayingRef.current = false;

            console.debug(
                "Notification sound unavailable:",
                err
            );
        }
    };

    const showNotificationToast = (notification) => {
        const presentation = notificationPresentation(notification);
        toast(
            <div className={`ticket-notification-toast is-${presentation.tone || "neutral"}${presentation.reward ? " is-reward" : ""}`}>
                <div className="ticket-notification-toast-icon"><FontAwesomeIcon icon={presentation.icon || faBell} /></div>
                <div style={{ minWidth: 0 }}>
                    <div className="ticket-notification-toast-title">{presentation.title}</div>
                    <div className="ticket-notification-toast-subtitle">{presentation.subtitle}</div>
                </div>
            </div>,
            {
                icon: false,
                autoClose: presentation.reward ? 3300 : 4000,
                closeOnClick: true,
                pauseOnHover: true,
                className: `ticket-notification-toast-shell is-${presentation.tone || "neutral"}`,
                progressClassName: "ticket-notification-toast-progress",
                toastId: notification?.id || undefined,
            }
        );
    };

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const primeAudio = () => {
            try {
                const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
                if (!AudioContextCtor) return;
                const ctx = notificationAudioContextRef.current || new AudioContextCtor();
                notificationAudioContextRef.current = ctx;
                if (ctx.state === "suspended") ctx.resume().catch(() => { });
            } catch { }
        };
        window.addEventListener("pointerdown", primeAudio, { once: true });
        window.addEventListener("keydown", primeAudio, { once: true });
        return () => {
            window.removeEventListener("pointerdown", primeAudio);
            window.removeEventListener("keydown", primeAudio);
        };
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        if (rewardCelebrationTimerRef.current) {
            clearTimeout(rewardCelebrationTimerRef.current);
            rewardCelebrationTimerRef.current = null;
        }
        setRewardCelebration(null);
        rewardCelebrationQueueRef.current = [];
        if (!effectiveUid) {
            rewardCelebratedIdsRef.current = new Set();
            return undefined;
        }
        try {
            const stored = JSON.parse(window.localStorage.getItem(`tickets.rewardCelebrated.${effectiveUid}`) || "[]");
            rewardCelebratedIdsRef.current = new Set(Array.isArray(stored) ? stored.slice(-80) : []);
        } catch (error) {
            rewardCelebratedIdsRef.current = new Set();
        }
        return () => {
            if (rewardCelebrationTimerRef.current) clearTimeout(rewardCelebrationTimerRef.current);
        };
    }, [effectiveUid]);

    const playNextRewardCelebration = () => {
        if (typeof window === "undefined" || rewardCelebrationTimerRef.current) return;
        const next = rewardCelebrationQueueRef.current.shift();
        if (!next) {
            setRewardCelebration(null);
            return;
        }
        setRewardCelebration(next);
        rewardCelebrationTimerRef.current = window.setTimeout(() => {
            rewardCelebrationTimerRef.current = null;
            if (rewardCelebrationQueueRef.current.length > 0) playNextRewardCelebration();
            else setRewardCelebration(null);
        }, 2600);
    };

    const celebrateRewardNotifications = (items = []) => {
        if (typeof window === "undefined" || !effectiveUid) return;
        const rewardOrder = { task_completed: 0, speed_bonus: 1, urgent_management_request: 2, difficult_task: 3 };
        const eligible = items.filter((item) =>
            item?.id
            && !item.read_at
            && Number(item.confirmed_points_delta || 0) > 0
            && String(item.type || "").startsWith("reward_")
            && !rewardCelebratedIdsRef.current.has(item.id)
        ).sort((a, b) => {
            const aType = a?.metadata?.reward_type || "";
            const bType = b?.metadata?.reward_type || "";
            return (rewardOrder[aType] ?? 99) - (rewardOrder[bType] ?? 99);
        });
        if (!eligible.length) return;

        eligible.forEach((item) => rewardCelebratedIdsRef.current.add(item.id));
        try {
            window.localStorage.setItem(`tickets.rewardCelebrated.${effectiveUid}`, JSON.stringify([...rewardCelebratedIdsRef.current].slice(-80)));
        } catch (error) {
        }

        rewardCelebrationQueueRef.current.push(...eligible.map((item) => ({
            id: item.id,
            points: Number(item.confirmed_points_delta || 0),
            title: String(item.title || "Reward earned").replace(/\s*·\s*\+?\d+\s*pts.*$/i, ""),
        })));
        playNextRewardCelebration();
    };

    useEffect(() => {
        notificationInitializedRef.current = false;

        if (notificationSoundTimerRef.current) {
            clearTimeout(notificationSoundTimerRef.current);
            notificationSoundTimerRef.current = null;
        }

        if (!effectiveUid) {
            setNotifications([]);
            return undefined;
        }

        const notificationsQuery = query(
            collection(
                db,
                "users",
                effectiveUid,
                "notifications"
            ),
            orderBy("created_at", "desc"),
            limit(NOTIFICATION_FETCH_LIMIT)
        );

        const unsubscribe = onSnapshot(
            notificationsQuery,
            (snapshot) => {
                const isSelfAuthoredNotification = (item) => {
                    const source = String(item?.metadata?.source || "");
                    const type = String(item?.type || "");
                    const alwaysNotifyRecipient = source === "manual_adjustment"
                        || source === "quality_review"
                        || source === "avoidable_rework"
                        || source === "employee_of_month"
                        || source === "redemption_request"
                        || source === "redemption_cancel"
                        || source === "redemption_review"
                        || type === "reward_adjustment"
                        || type === "reward_review_summary"
                        || type === "reward_penalty"
                        || type === "reward_achievement";
                    return Boolean(
                        effectiveUid
                        && item?.actor_id
                        && item.actor_id === effectiveUid
                        && !alwaysNotifyRecipient
                    );
                };

                const next = snapshot.docs
                    .map((d) => ({
                        id: d.id,
                        ...d.data(),
                    }))
                    .filter((item) => !item.cleared_at && !isSelfAuthoredNotification(item))
                    .slice(0, NOTIFICATION_DISPLAY_LIMIT);

                if (notificationInitializedRef.current) {
                    const newUnreadNotifications = [];

                    snapshot.docChanges().forEach((change) => {
                        if (change.type !== "added" && change.type !== "modified") return;

                        const data = {
                            id: change.doc.id,
                            ...change.doc.data(),
                        };

                        if (change.doc.metadata?.hasPendingWrites) return;
                        if (data.read_at || data.cleared_at || isSelfAuthoredNotification(data)) return;

                        const isRecipientResult = data?.metadata?.source === "quality_review"
                            || data?.metadata?.source === "manual_adjustment"
                            || data?.metadata?.source === "avoidable_rework"
                            || data?.metadata?.source === "employee_of_month"
                            || data?.metadata?.source === "redemption_request"
                            || data?.metadata?.source === "redemption_cancel"
                            || data?.metadata?.source === "redemption_review"
                            || data?.type === "reward_review_summary"
                            || data?.type === "reward_adjustment"
                            || data?.type === "reward_penalty"
                            || data?.type === "reward_achievement";
                        if (change.type === "modified" && !isRecipientResult) return;

                        newUnreadNotifications.push(data);
                        showNotificationToast(data);
                    });

                    if (newUnreadNotifications.length > 0) {
                        queueNotificationSound();
                        celebrateRewardNotifications(newUnreadNotifications);
                    }
                } else {
                    celebrateRewardNotifications(next);
                }

                notificationInitializedRef.current = true;
                setNotifications(next);
            },
            (error) => {
                console.error(
                    "Live notifications listener failed:",
                    error
                );

                setNotifications([]);
            }
        );

        return () => {
            unsubscribe();

            if (notificationSoundTimerRef.current) {
                clearTimeout(notificationSoundTimerRef.current);
                notificationSoundTimerRef.current = null;
            }
        };
    }, [effectiveUid, usersById]);

    useEffect(() => {
        if (!effectiveUid) return undefined;

        const requestCollection = collection(
            db,
            "users",
            effectiveUid,
            "report_generation_requests"
        );

        let disposed = false;
        const requestDataById = new Map();
        const reportUnsubscribers = new Map();

        const stopReportListener = (requestDocId) => {
            const unsubscribe = reportUnsubscribers.get(requestDocId);
            if (unsubscribe) unsubscribe();
            reportUnsubscribers.delete(requestDocId);
        };

        const startReportListener = (requestDocId, requestRef) => {
            if (reportUnsubscribers.has(requestDocId)) return;

            const request = requestDataById.get(requestDocId);
            if (!request?.building_id || request.status !== "waiting") return;

            const reportRef = doc(db, "Reports", request.building_id);

            const unsubscribe = onSnapshot(
                reportRef,
                (reportSnapshot) => {
                    if (disposed || !reportSnapshot.exists()) return;

                    const latestRequest = requestDataById.get(requestDocId);
                    if (!latestRequest || latestRequest.status !== "waiting") return;

                    const reportData = reportSnapshot.data() || {};
                    const requestedAtMs = Number(latestRequest.requested_at_client_ms || 0);
                    const requestAgeMs = requestedAtMs ? Date.now() - requestedAtMs : 0;

                    if (requestedAtMs && requestAgeMs > 24 * 60 * 60 * 1000) {
                        setDoc(
                            requestRef,
                            {
                                status: "expired",
                                expired_at: serverTimestamp(),
                            },
                            { merge: true }
                        ).catch((err) =>
                            console.error("Failed to expire report generation request:", err)
                        );
                        return;
                    }

                    const currentCommunalMs = reportTimestampMillis(reportData.pdfupdatedAt);
                    const currentResidentialMs = reportTimestampMillis(reportData.pdfResiupdatedAt);
                    const baselineCommunalMs = Number(latestRequest.previous_pdf_updated_ms || 0);
                    const baselineResidentialMs = Number(latestRequest.previous_pdf_resi_updated_ms || 0);

                    const communalReady = Boolean(
                        reportData.pdfLink
                        && !latestRequest.communal_notified
                        && currentCommunalMs > baselineCommunalMs
                    );

                    const residentialReady = Boolean(
                        reportData.pdfResiLink
                        && !latestRequest.residential_notified
                        && currentResidentialMs > baselineResidentialMs
                    );

                    if (!communalReady && !residentialReady) return;

                    (async () => {
                        const updates = {};
                        let communalNotified = Boolean(latestRequest.communal_notified);
                        let residentialNotified = Boolean(latestRequest.residential_notified);

                        if (communalReady) {
                            const notificationRef = await notifyUserSafely(effectiveUid, {
                                type: "report_ready",
                                ticket_id: latestRequest.ticket_id || null,
                                ticket_title: latestRequest.ticket_title || null,
                                actor_id: null,
                                building_id: latestRequest.building_id,
                                building_name: latestRequest.building_name || reportData.buildingName || "Building",
                                report_id: latestRequest.report_id || reportData.reportId || "",
                                report_type: "communal",
                                report_url: reportData.pdfLink,
                                report_updated_at: reportData.pdfupdatedAt || null,
                                message: `Communal report ready${latestRequest.report_id || reportData.reportId ? ` — ${latestRequest.report_id || reportData.reportId}` : ""} for ${latestRequest.building_name || reportData.buildingName || "building"}`,
                                dedupe_key: `report-ready:${latestRequest.request_id || requestDocId}:communal`,
                            });

                            if (notificationRef) {
                                communalNotified = true;
                                updates.communal_notified = true;
                                updates.communal_ready_at = reportData.pdfupdatedAt || serverTimestamp();
                                updates.communal_url = reportData.pdfLink;
                            }
                        }

                        if (residentialReady) {
                            const notificationRef = await notifyUserSafely(effectiveUid, {
                                type: "report_ready",
                                ticket_id: latestRequest.ticket_id || null,
                                ticket_title: latestRequest.ticket_title || null,
                                actor_id: null,
                                building_id: latestRequest.building_id,
                                building_name: latestRequest.building_name || reportData.buildingName || "Building",
                                report_id: latestRequest.report_id || reportData.reportId || "",
                                report_type: "residential",
                                report_url: reportData.pdfResiLink,
                                report_updated_at: reportData.pdfResiupdatedAt || null,
                                message: `Residential report ready${latestRequest.report_id || reportData.reportId ? ` — ${latestRequest.report_id || reportData.reportId}` : ""} for ${latestRequest.building_name || reportData.buildingName || "building"}`,
                                dedupe_key: `report-ready:${latestRequest.request_id || requestDocId}:residential`,
                            });

                            if (notificationRef) {
                                residentialNotified = true;
                                updates.residential_notified = true;
                                updates.residential_ready_at = reportData.pdfResiupdatedAt || serverTimestamp();
                                updates.residential_url = reportData.pdfResiLink;
                            }
                        }

                        if (communalNotified && residentialNotified) {
                            updates.status = "complete";
                            updates.completed_at = serverTimestamp();
                        }

                        if (Object.keys(updates).length > 0) {
                            await setDoc(requestRef, updates, { merge: true });
                        }
                    })().catch((err) => {
                        console.error("Failed to publish report-ready notification:", err);
                    });
                },
                (error) => {
                    console.error(
                        `Report listener failed for generation request ${requestDocId}:`,
                        error
                    );
                }
            );

            reportUnsubscribers.set(requestDocId, unsubscribe);
        };

        const unsubscribeRequests = onSnapshot(
            requestCollection,
            (snapshot) => {
                if (disposed) return;

                const liveRequestIds = new Set();

                snapshot.docs.forEach((requestDoc) => {
                    const requestDocId = requestDoc.id;
                    const request = requestDoc.data() || {};
                    liveRequestIds.add(requestDocId);
                    requestDataById.set(requestDocId, request);

                    if (request.status === "waiting" && request.building_id) {
                        startReportListener(requestDocId, requestDoc.ref);
                    } else {
                        stopReportListener(requestDocId);
                    }
                });

                [...requestDataById.keys()].forEach((requestDocId) => {
                    if (liveRequestIds.has(requestDocId)) return;
                    requestDataById.delete(requestDocId);
                    stopReportListener(requestDocId);
                });
            },
            (error) => {
                console.error("Report generation request listener failed:", error);
            }
        );

        return () => {
            disposed = true;
            unsubscribeRequests();
            reportUnsubscribers.forEach((unsubscribe) => unsubscribe());
            reportUnsubscribers.clear();
            requestDataById.clear();
        };
    }, [effectiveUid]);

    const unreadNotificationCount = notifications.filter((notification) => !notification.read_at).length;

    const toggleNotificationExpanded = (notificationId) => {
        if (!notificationId) return;
        setExpandedNotificationIds((current) => {
            const next = new Set(current);
            if (next.has(notificationId)) next.delete(notificationId);
            else next.add(notificationId);
            return next;
        });
    };

    const markNotificationRead = async (notification) => {
        if (!notification?.id || notification.read_at || !effectiveUid) return;
        try {
            await updateDoc(doc(db, "users", effectiveUid, "notifications", notification.id), {
                read_at: serverTimestamp(),
            });
        } catch (err) {
            console.error("Failed to mark notification read:", err);
        }
    };

    const markAllNotificationsRead = async () => {
        const unread = notifications.filter((notification) => !notification.read_at);
        if (!effectiveUid || !unread.length) return;
        try {
            const batch = writeBatch(db);
            unread.forEach((notification) => {
                batch.update(doc(db, "users", effectiveUid, "notifications", notification.id), {
                    read_at: serverTimestamp(),
                });
            });
            await batch.commit();
        } catch (err) {
            console.error("Failed to mark all notifications read:", err);
        }
    };

    const clearNotification = async (notification) => {
        if (!effectiveUid || !notification?.id || notification.cleared_at) return;
        try {
            await updateDoc(doc(db, "users", effectiveUid, "notifications", notification.id), {
                cleared_at: serverTimestamp(),
                cleared_by_id: effectiveUid,
                read_at: notification.read_at || serverTimestamp(),
            });
        } catch (err) {
            console.error("Failed to clear notification:", err);
            showTicketToast("Could not clear that notification.", "Notification not cleared");
        }
    };

    const clearAllNotifications = async () => {
        if (!effectiveUid || notifications.length === 0) return;
        try {
            const notificationsRef = collection(db, "users", effectiveUid, "notifications");
            let lastDoc = null;
            let clearedCount = 0;

            while (true) {
                const pageQuery = lastDoc
                    ? query(
                        notificationsRef,
                        orderBy("created_at", "desc"),
                        startAfter(lastDoc),
                        limit(NOTIFICATION_CLEAR_BATCH_SIZE)
                    )
                    : query(
                        notificationsRef,
                        orderBy("created_at", "desc"),
                        limit(NOTIFICATION_CLEAR_BATCH_SIZE)
                    );
                const snapshot = await getDocs(pageQuery);
                if (snapshot.empty) break;

                const docsToClear = snapshot.docs.filter((notificationDoc) => !notificationDoc.data()?.cleared_at);
                if (docsToClear.length) {
                    const batch = writeBatch(db);
                    docsToClear.forEach((notificationDoc) => {
                        const data = notificationDoc.data() || {};
                        batch.update(notificationDoc.ref, {
                            cleared_at: serverTimestamp(),
                            cleared_by_id: effectiveUid,
                            read_at: data.read_at || serverTimestamp(),
                        });
                    });
                    await batch.commit();
                    clearedCount += docsToClear.length;
                }

                lastDoc = snapshot.docs[snapshot.docs.length - 1];
                if (snapshot.size < NOTIFICATION_CLEAR_BATCH_SIZE) break;
            }

            if (clearedCount > 0) {
                showTicketToast(
                    `${clearedCount.toLocaleString("en-GB")} notification${clearedCount === 1 ? "" : "s"} cleared.`,
                    "Notifications cleared"
                );
            }
        } catch (err) {
            console.error("Failed to clear notifications:", err);
            showTicketToast("Could not clear notifications.", "Notifications not cleared");
        }
    };

    const handleNotificationClick = async (notification) => {
        if (!notification) return;

        if (String(notification.type || "").startsWith("reward_")) {
            await markNotificationRead(notification);
            setNotificationsOpen(false);
            setRewardNavigationTarget(notification);
            setActiveTab("rewards");
            return;
        }

        if (notification.type === "report_ready") {
            setNotificationsOpen(false);

            if (notification.report_url) {
                window.open(notification.report_url, "_blank", "noopener,noreferrer");
            } else {
                showTicketToast("The generated report link is not available.", "Report unavailable");
            }

            await markNotificationRead(notification);
            return;
        }

        await markNotificationRead(notification);
        setNotificationsOpen(false);

        let ticket = null;
        if (notification.ticket_id) {
            try {
                const snapshot = await getDoc(doc(db, "tickets", notification.ticket_id));
                if (snapshot.exists()) ticket = { id: snapshot.id, ...snapshot.data() };
            } catch (err) {
                console.error("Failed to load notification ticket:", err);
            }
        }
        if (!ticket) {
            showTicketToast("This task is no longer available.", "Task moved");
            return;
        }

        setHighlightTicketId(null);
        setHighlightStuckTicketId(null);
        setHighlightEventId(null);

        if (notification.type === "mention") {
            setActivityLogOpen(true);
            setHighlightEventId(notification.event_id || null);
            openDetail(ticket);
            return;
        }

        if (notification.type === "stuck_responsibility" || notification.type === "follow_up_due") {
            const stillMineInStuck = ticket.status === "waiting" && (
                ticket.responsible_person_id
                    ? ticket.responsible_person_id === effectiveUid
                    : ticket.assignee_id === effectiveUid
            );
            if (!stillMineInStuck) {
                showTicketToast("This task is no longer in your Stuck / Waiting list. It may have been unblocked or moved on.", "Task moved");
                return;
            }
            setActiveTab("mywork");
            setSidePanelTab("stuck");
            setStuckSearchQuery("");
            setStuckDateFilter("all");
            setHighlightStuckTicketId(ticket.id);
            return;
        }

        const isQueueNotification = notification.type === "task_assigned" || notification.type === "amendment_required";
        if (isQueueNotification) {
            const stillMineInTodo = ticket.status === "ready" && ticket.assignee_id === effectiveUid;
            if (!stillMineInTodo) {
                showTicketToast("This task is no longer in your To Do queue. It may be active, completed, or with another person.", "Task moved");
                return;
            }
            setActiveTab("mywork");
            setQueueTab("todo");
            if (effectiveRole === "coordinator") {
                const targetContext = notification.workflow_stage === "coordinator_review"
                    || getCoordinatorContext(ticket) === "review"
                    ? "review"
                    : "qc";
                setCoordinatorView(targetContext);
            }
            setHighlightTicketId(ticket.id);
            return;
        }

        const currentlyRelevant = ticket.assignee_id === effectiveUid
            || (ticket.status === "waiting" && ticket.responsible_person_id === effectiveUid);
        if (!currentlyRelevant) {
            showTicketToast("This task has moved on and is no longer in your current work list.", "Task moved");
            return;
        }

        openDetail(ticket);
    };

    useEffect(() => {
        if (!highlightTicketId || activeTab !== "mywork" || queueTab !== "todo") return undefined;
        const scrollTimer = setTimeout(() => {
            queueRowRefs.current[highlightTicketId]?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        }, 80);
        const clearTimer = setTimeout(() => setHighlightTicketId(null), 3000);
        return () => {
            clearTimeout(scrollTimer);
            clearTimeout(clearTimer);
        };
    }, [highlightTicketId, activeTab, queueTab, coordinatorView, visibleQueue.length]);

    useEffect(() => {
        if (!highlightStuckTicketId || activeTab !== "mywork" || sidePanelTab !== "stuck") return undefined;
        const scrollTimer = setTimeout(() => {
            stuckCardRefs.current[highlightStuckTicketId]?.scrollIntoView?.({ behavior: "smooth", block: "center" });
        }, 100);
        const clearTimer = setTimeout(() => setHighlightStuckTicketId(null), 3000);
        return () => {
            clearTimeout(scrollTimer);
            clearTimeout(clearTimer);
        };
    }, [highlightStuckTicketId, activeTab, sidePanelTab, visibleWaitingTickets.length]);

    const [switchTarget, setSwitchTarget] = useState(null);

    const [switchFrom, setSwitchFrom] = useState(null);
    const [stuckTicket, setStuckTicket] = useState(null);
    const [stuckWaitingOn, setStuckWaitingOn] = useState("Client");
    const [stuckFollowUp, setStuckFollowUp] = useState("");
    const [stuckResponsibleId, setStuckResponsibleId] = useState("");
    const [stuckNotes, setStuckNotes] = useState("");
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [emailLogTicket, setEmailLogTicket] = useState(null);
    const [emailTo, setEmailTo] = useState("");
    const [emailSubject, setEmailSubject] = useState("");
    const [emailNotes, setEmailNotes] = useState("");


    const [reassignTicket, setReassignTicket] = useState(null);
    const [reassignUserId, setReassignUserId] = useState("");
    const [reassignTargetStage, setReassignTargetStage] = useState(null);
    const [reassignDueDate, setReassignDueDate] = useState("");
    const [reassignNotes, setReassignNotes] = useState("");
    const [reassignForcedRole, setReassignForcedRole] = useState(null);
    const [reassignForcedLabel, setReassignForcedLabel] = useState("");

    const [confirmAction, setConfirmAction] = useState(null);

    const [completeTicket, setCompleteTicket] = useState(null);
    const [completeNextUserId, setCompleteNextUserId] = useState("");
    const [completeNextStageKey, setCompleteNextStageKey] = useState(null);
    const [completeDueDate, setCompleteDueDate] = useState("");
    const [completeNotes, setCompleteNotes] = useState("");
    const [completeFiles, setCompleteFiles] = useState([]);
    const [completeChecklist, setCompleteChecklist] = useState(emptyHandoverChecklist());

    const [drawerMode, setDrawerMode] = useState(null);
    const [detailTicket, setDetailTicket] = useState(null);
    const [ticketEvents, setTicketEvents] = useState([]);
    const [taskLinks, setTaskLinks] = useState([]);
    const [commentText, setCommentText] = useState("");
    const [commentFiles, setCommentFiles] = useState([]);
    const commentFileInputRef = useRef(null);
    const [detailLogTab, setDetailLogTab] = useState("info");
    const [detailDescriptionExpanded, setDetailDescriptionExpanded] = useState(false);
    const [confirmArchive, setConfirmArchive] = useState(false);
    const [mediaPreview, setMediaPreview] = useState(null);
    const [attachmentPreview, setAttachmentPreview] = useState(null);
    const [doorReviewModal, setDoorReviewModal] = useState(null);
    const [managerWorkModal, setManagerWorkModal] = useState(null);

    const [remedialInfoModal, setRemedialInfoModal] = useState(null);
    const [remedialInfoEntries, setRemedialInfoEntries] = useState([]);
    const [remedialJobTime, setRemedialJobTime] = useState("Half day");
    const [remedialNotes, setRemedialNotes] = useState("");
    const [remedialSaving, setRemedialSaving] = useState(false);

    const [isEditingTicket, setIsEditingTicket] = useState(false);
    const [editTitle, setEditTitle] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editAssignee, setEditAssignee] = useState("");
    const [editAssigneeStage, setEditAssigneeStage] = useState(null);
    const [editPriority, setEditPriority] = useState("Medium");
    const [editTaskType, setEditTaskType] = useState("");
    const [editDueDate, setEditDueDate] = useState("");
    const [editBuilding, setEditBuilding] = useState(null);

    const [editSelectedFile, setEditSelectedFile] = useState(null);
    const [editRemoveImage, setEditRemoveImage] = useState(false);

    const openRemedialInfoModal = (door) => {
        setRemedialInfoModal(door);
        setRemedialJobTime("Half day");
        setRemedialNotes("");
    };

    const closeRemedialInfoModal = () => {
        setRemedialInfoModal(null);
        setRemedialInfoEntries([]);
        setRemedialJobTime("Half day");
        setRemedialNotes("");
    };

    useEffect(() => {
        if (!remedialInfoModal?.buildingId || !remedialInfoModal?.id) {
            setRemedialInfoEntries([]);
            return undefined;
        }
        const collectionName = remedialInfoCollectionName(remedialInfoModal);
        return onSnapshot(
            query(
                collection(db, "Reports", remedialInfoModal.buildingId, collectionName, remedialInfoModal.id, "remedial-info"),
                orderBy("created_at", "desc")
            ),
            (snap) => setRemedialInfoEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
            (err) => {
                console.error("Live remedial-info listener failed:", err);
                setRemedialInfoEntries([]);
            }
        );
    }, [remedialInfoModal?.buildingId, remedialInfoModal?.id, remedialInfoModal?.type]);

    const saveRemedialInfo = async () => {
        if (!remedialInfoModal || !remedialJobTime) return;

        setRemedialSaving(true);
        let infoSaved = false;

        try {
            const collectionName = remedialInfoCollectionName(remedialInfoModal);
            const slotsRequired = remedialJobTime === "Full day" ? 2 : 1;

            await addDoc(
                collection(
                    db,
                    "Reports",
                    remedialInfoModal.buildingId,
                    collectionName,
                    remedialInfoModal.id,
                    "remedial-info"
                ),
                {
                    job_time: remedialJobTime,
                    slots_required: slotsRequired,

                    booking_slot: null,

                    notes: remedialNotes.trim() || null,
                    door_number: doorLabel(remedialInfoModal),
                    door_type: remedialInfoModal.type,
                    created_by_id: actor.uid,
                    created_by_name: nameOf(actor.uid),
                    created_at: serverTimestamp(),
                }
            );

            infoSaved = true;
            showTicketToast("Remedial info saved.", "Done");
            setRemedialNotes("");
            setRemedialInfoRefreshSignal((n) => n + 1);
        } catch (err) {
            console.error("Failed to save remedial info:", err);
            showTicketToast("Could not save remedial info.", "Couldn’t complete");
        } finally {
            setRemedialSaving(false);
        }

        if (
            infoSaved
            && !remedialInfoModal.remedial_booking
            && !remedialInfoModal.workDone
            && !remedialInfoModal.workDoneUpdatedAt
        ) {
            try {
                const slotsRequired = remedialJobTime === "Full day" ? 2 : 1;
                const availableJobsDocRef = doc(db, "book-remedials", "available-jobs");
                const pointerRef = doc(
                    db,
                    "book-remedials",
                    "available-jobs",
                    remedialInfoModal.buildingId,
                    remedialInfoModal.id
                );

                await setDoc(
                    pointerRef,
                    {
                        door_type: remedialInfoModal.type,
                        job_time: remedialJobTime,
                        slots_required: slotsRequired,
                        updated_at: serverTimestamp(),
                    },
                    { merge: true }
                );

                await setDoc(
                    availableJobsDocRef,
                    {
                        building_ids: arrayUnion(remedialInfoModal.buildingId),
                    },
                    { merge: true }
                );
            } catch (queueErr) {
                console.error("Failed to queue door for remedial booking:", queueErr);
                showTicketToast("Saved, but couldn't add this door to the booking queue.", "Attention");
            }
        }

        if (infoSaved) {
            setRemedialInfoModal(null);
        }
    };

    useEffect(() => {
        if (!managerWorkModal?.buildingId || !managerWorkModal?.id) return undefined;

        const collectionName = managerWorkModal.type === "residential"
            ? "ResidentialReports"
            : "DoorReports";

        return onSnapshot(
            doc(db, "Reports", managerWorkModal.buildingId, collectionName, managerWorkModal.id),
            (snapshot) => {
                if (!snapshot.exists()) {
                    setManagerWorkModal(null);
                    return;
                }

                const liveDoor = {
                    id: snapshot.id,
                    buildingId: managerWorkModal.buildingId,
                    type: managerWorkModal.type || (collectionName === "ResidentialReports" ? "residential" : "communal"),
                    ...snapshot.data(),
                };

                if (liveDoor.archived || liveDoor.hasAccess === false || liveDoor.requiresAttention || !isDoorFailed(liveDoor)) {
                    setManagerWorkModal(null);
                    return;
                }

                setManagerWorkModal(liveDoor);
            },
            (error) => console.error("Live manager door listener failed:", error)
        );
    }, [managerWorkModal?.buildingId, managerWorkModal?.type, managerWorkModal?.id]);


    const [activityLogOpen, setActivityLogOpen] = useState(true);
    const [assignHistoryOpen, setAssignHistoryOpen] = useState(true);
    const [calendarModal, setCalendarModal] = useState(null);


    const [bookingInfoByTicket, setBookingInfoByTicket] = useState({});
    const [bookingsLoading, setBookingsLoading] = useState(false);
    const [bookingModal, setBookingModal] = useState(null);

    const normalizeBuildingName = (value = "") =>
        String(value)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, " ")
            .trim();

    const buildingNamesMatch = (left, right) => {
        const a = normalizeBuildingName(left);
        const b = normalizeBuildingName(right);
        if (!a || !b) return false;
        if (a === b) return true;

        return a.length >= 4 && b.length >= 4 && (a.includes(b) || b.includes(a));
    };

    const calendarDateId = (value) => {
        if (!value) return "";

        if (typeof value === "string") {
            const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
            if (match) {
                const [, day, month, year] = match;
                return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year}`;
            }
        }

        const parsed = parseCalendarEventDate(value);
        if (!parsed) return "";
        const day = String(parsed.getDate()).padStart(2, "0");
        const month = String(parsed.getMonth() + 1).padStart(2, "0");
        return `${day}-${month}-${parsed.getFullYear()}`;
    };

    const calendarDisplayDate = (value) => {
        if (!value) return "";
        if (typeof value === "string") {
            const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
            if (match) {
                const [, day, month, year] = match;
                return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
            }
        }
        return parseCalendarEventDate(value)?.toLocaleDateString("en-GB") || "";
    };

    const dateIdSortValue = (dateId) => {
        const match = String(dateId || "").match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (!match) return 0;
        const [, day, month, year] = match;
        return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
    };

    const shortBookingDate = (displayDate) => {
        const match = String(displayDate || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!match) return displayDate || "";
        const [, day, month, year] = match;
        const d = new Date(Number(year), Number(month) - 1, Number(day));
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    };

    const calendarEventBuildingCandidates = (event, ticket) => {

        const raw = [
            event?.buildingName,
            event?.building_name,
            event?.buildingAddress,
            event?.buildingAddress?.split(",")?.[0],
            event?.address,
            ticket?.building_name,
        ];

        return [...new Set(raw.map(normalizeBuildingName).filter(Boolean))];
    };

    const emptyCoordinatorBookingInfo = (ticket) => ({
        ticketId: ticket.id,
        buildingName: ticket.building_name || "",
        totalBookings: 0,
        bookings: [],
        days: [],
        dates: [],
    });

    const coordinatorBookingTickets = useMemo(() => {
        if (effectiveRole !== "coordinator") return [];
        return allTickets.filter(
            (ticket) => ticket.assignee_id === effectiveUid && !ticket.archived && getCoordinatorContext(ticket) === "qc"
        );
    }, [effectiveRole, effectiveUid, allTickets, usersById]);


    const coordinatorBookingSignature = useMemo(
        () => coordinatorBookingTickets
            .map((ticket) => [
                ticket.id,
                ticket.building_id || "",
                ticket.building_name || "",
                ...(ticket.schedule_ids || []).filter(Boolean),
            ].join(":"))
            .sort()
            .join("|"),
        [coordinatorBookingTickets]
    );

    useEffect(() => {
        if (effectiveRole !== "coordinator") {
            setBookingInfoByTicket({});
            setBookingModal(null);
            setBookingsLoading(false);
            return undefined;
        }

        const tickets = coordinatorBookingTickets;
        const baseInfo = Object.fromEntries(
            tickets.map((ticket) => [ticket.id, emptyCoordinatorBookingInfo(ticket)])
        );

        if (!tickets.length) {
            setBookingInfoByTicket({});
            setBookingsLoading(false);
            return undefined;
        }

        const eventIds = [...new Set(
            tickets.flatMap((ticket) => (ticket.schedule_ids || []).filter(Boolean))
        )];

        if (!eventIds.length) {
            setBookingInfoByTicket(baseInfo);
            setBookingsLoading(false);
            return undefined;
        }

        let disposed = false;
        let inspectionRoots = [];
        let inspectionsReady = false;

        const eventById = new Map();
        const eventReady = new Set();
        const dateStateByKey = new Map();
        const dateReady = new Set();
        const bookingDocsByKey = new Map();
        const bookingReady = new Set();

        const staticUnsubscribers = [];
        const dateUnsubscribers = new Map();
        const bookingUnsubscribers = new Map();

        setBookingsLoading(true);

        const currentRequirements = () => {
            const requirementsByTicket = {};
            const allRequirements = [];

            tickets.forEach((ticket) => {
                const seenDateIds = new Set();
                const requirements = [];

                (ticket.schedule_ids || []).filter(Boolean).forEach((eventId) => {
                    const event = eventById.get(eventId);
                    if (!event) return;

                    const dateId = calendarDateId(event.start);
                    const displayDate = calendarDisplayDate(event.start);
                    if (!dateId || seenDateIds.has(dateId)) return;
                    seenDateIds.add(dateId);

                    const requirement = {
                        ticket,
                        ticketId: ticket.id,
                        event,
                        eventId,
                        dateId,
                        displayDate,
                        inspectionUid:
                            event?.inspectionUid
                            || event?.inspection_uid
                            || event?.inspectionDocumentId
                            || "",
                        buildingCandidates: calendarEventBuildingCandidates(event, ticket),
                    };

                    requirements.push(requirement);
                    allRequirements.push(requirement);
                });

                requirements.sort((a, b) => dateIdSortValue(a.dateId) - dateIdSortValue(b.dateId));
                requirementsByTicket[ticket.id] = requirements;
            });

            return { requirementsByTicket, allRequirements };
        };

        const rootDataForId = (inspectionUid) =>
            inspectionRoots.find((root) => root.id === inspectionUid)?.data || {};

        const rootsForRequirement = (requirement) => {
            if (requirement.inspectionUid) {
                const directRoot = inspectionRoots.find(
                    (root) => root.id === requirement.inspectionUid
                );
                if (directRoot) return [directRoot];


                return [{ id: requirement.inspectionUid, data: {} }];
            }

            const namedMatches = inspectionRoots.filter((root) => {
                const rootNames = [
                    root.data?.buildingName,
                    root.data?.building_name,
                    root.data?.name,
                    root.data?.buildingAddress,
                ].filter(Boolean);

                return requirement.buildingCandidates.some((candidate) =>
                    rootNames.some((rootName) => buildingNamesMatch(candidate, rootName))
                );
            });


            const autoIdRoots = inspectionRoots.filter((root) =>
                /^[A-Za-z0-9]{20}$/.test(root.id)
            );

            if (!namedMatches.length && !autoIdRoots.length) return inspectionRoots;

            const combined = new Map();
            [...namedMatches, ...autoIdRoots].forEach((root) => combined.set(root.id, root));
            return [...combined.values()];
        };

        const matchRequirement = (requirement) => {
            const candidateKeys = rootsForRequirement(requirement).map(
                (root) => `${root.id}/${requirement.dateId}`
            );


            if (!candidateKeys.every((key) => dateReady.has(key))) return null;

            const sameDate = [...dateStateByKey.values()].filter(
                (candidate) => candidate.exists && candidate.dateId === requirement.dateId
            );

            if (!sameDate.length) return null;

            if (requirement.inspectionUid) {
                const directMatch = sameDate.find(
                    (candidate) => candidate.inspectionUid === requirement.inspectionUid
                );
                if (directMatch) return directMatch;
            }

            const buildingMatches = sameDate.filter((candidate) => {
                const dateData = candidate.data || {};
                const rootData = rootDataForId(candidate.inspectionUid);
                const names = [
                    dateData.buildingName,
                    dateData.building_name,
                    rootData?.buildingName,
                    rootData?.building_name,
                    rootData?.name,
                ].filter(Boolean);

                return requirement.buildingCandidates.some((wanted) =>
                    names.some((actual) => buildingNamesMatch(wanted, actual))
                );
            });

            if (buildingMatches.length) {
                const scored = buildingMatches.map((candidate) => {
                    const dateData = candidate.data || {};
                    const rootData = rootDataForId(candidate.inspectionUid);
                    const embeddedUid =
                        dateData.inspectionUid
                        || dateData.inspection_uid
                        || dateData.uid
                        || rootData?.inspectionUid
                        || rootData?.inspection_uid
                        || "";

                    let score = 0;
                    if (embeddedUid && embeddedUid === candidate.inspectionUid) score += 100;
                    if (/^[A-Za-z0-9]{20}$/.test(candidate.inspectionUid)) score += 20;
                    if (candidate.inspectionUid.endsWith(`_${requirement.dateId}`)) score -= 20;
                    return { candidate, score };
                });

                scored.sort((a, b) => b.score - a.score);
                return scored[0].candidate;
            }

            return sameDate.length === 1 ? sameDate[0] : null;
        };

        const resolvedRequirements = () => {
            const { requirementsByTicket, allRequirements } = currentRequirements();
            const resolved = allRequirements.map((requirement) => ({
                requirement,
                match: matchRequirement(requirement),
            }));
            return { requirementsByTicket, allRequirements, resolved };
        };

        const publish = () => {
            if (disposed) return;

            const { requirementsByTicket, allRequirements, resolved } = resolvedRequirements();
            const nextInfo = { ...baseInfo };

            tickets.forEach((ticket) => {
                const requirements = requirementsByTicket[ticket.id] || [];

                const days = requirements.map((requirement) => {
                    const resolvedItem = resolved.find(
                        (item) =>
                            item.requirement.ticketId === ticket.id
                            && item.requirement.dateId === requirement.dateId
                    );
                    const match = resolvedItem?.match || null;

                    if (!match) {
                        return {
                            dateId: requirement.dateId,
                            date: requirement.displayDate,
                            eventId: requirement.eventId,
                            sourceInspectionUid: "",
                            inspectionUid: "",
                            buildingName:
                                requirement.event?.buildingName
                                || requirement.event?.building_name
                                || requirement.event?.buildingAddress?.split(",")?.[0]
                                || ticket.building_name
                                || "",
                            totalBookings: 0,
                            bookings: [],
                        };
                    }

                    const dateData = match.data || {};
                    const rawBookings = bookingDocsByKey.get(match.key) || [];
                    const bookings = rawBookings.map((rawBooking) => ({
                        ...rawBooking.data,

                        id: rawBooking.id,
                        sourceInspectionUid: match.inspectionUid,
                        inspectionUid: match.inspectionUid,
                        dateId: requirement.dateId,

                        date: dateData.date || requirement.displayDate,
                        buildingName:
                            dateData.buildingName
                            || requirement.event?.buildingName
                            || requirement.event?.buildingAddress?.split(",")?.[0]
                            || ticket.building_name
                            || "",
                    }));

                    bookings.sort((a, b) => {
                        const aTime = a.submittedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
                        const bTime = b.submittedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
                        return bTime - aTime;
                    });

                    const storedTotal = Number(dateData.totalBookings ?? 0);
                    const safeStoredTotal = Number.isFinite(storedTotal) ? storedTotal : 0;
                    const totalBookings = bookingReady.has(match.key)
                        ? bookings.length
                        : Math.max(safeStoredTotal, bookings.length);

                    return {
                        dateId: requirement.dateId,
                        date: dateData.date || requirement.displayDate,
                        eventId: requirement.eventId,
                        sourceInspectionUid: match.inspectionUid,
                        inspectionUid: match.inspectionUid,
                        buildingName:
                            dateData.buildingName
                            || requirement.event?.buildingName
                            || requirement.event?.buildingAddress?.split(",")?.[0]
                            || ticket.building_name
                            || "",
                        dateData,
                        totalBookings,
                        bookings,
                    };
                });

                days.sort((a, b) => dateIdSortValue(a.dateId) - dateIdSortValue(b.dateId));

                const bookingMap = new Map();
                days.flatMap((day) => day.bookings).forEach((booking) => {
                    bookingMap.set(
                        `${booking.sourceInspectionUid || booking.inspectionUid}/${booking.dateId}/${booking.id}`,
                        booking
                    );
                });

                const bookings = [...bookingMap.values()].sort((a, b) => {
                    const dateDiff = dateIdSortValue(a.dateId) - dateIdSortValue(b.dateId);
                    if (dateDiff) return dateDiff;
                    return String(a.firstDate_from || "").localeCompare(String(b.firstDate_from || ""));
                });

                nextInfo[ticket.id] = {
                    ticketId: ticket.id,
                    buildingName:
                        days.find((day) => day.buildingName)?.buildingName
                        || ticket.building_name
                        || "",
                    days,
                    dates: days.map((day) => day.date).filter(Boolean),
                    totalBookings: days.reduce((sum, day) => sum + (day.totalBookings || 0), 0),
                    bookings,
                };
            });

            setBookingInfoByTicket(nextInfo);

            const eventsLoaded = eventReady.size === eventIds.length;
            const desiredDateKeys = new Set();
            allRequirements.forEach((requirement) => {
                rootsForRequirement(requirement).forEach((root) => {
                    desiredDateKeys.add(`${root.id}/${requirement.dateId}`);
                });
            });

            const datesLoaded = [...desiredDateKeys].every((key) => dateReady.has(key));
            const matchedBookingKeys = new Set(
                resolved.filter((item) => item.match).map((item) => item.match.key)
            );
            const bookingCollectionsLoaded = [...matchedBookingKeys].every((key) => bookingReady.has(key));

            setBookingsLoading(
                !(eventsLoaded && inspectionsReady && datesLoaded && bookingCollectionsLoaded)
            );
        };

        const syncBookingListeners = () => {
            if (disposed) return;

            const { resolved } = resolvedRequirements();
            const desiredMatches = new Map();
            resolved.forEach(({ match }) => {
                if (match) desiredMatches.set(match.key, match);
            });

            [...bookingUnsubscribers.keys()].forEach((key) => {
                if (desiredMatches.has(key)) return;
                bookingUnsubscribers.get(key)?.();
                bookingUnsubscribers.delete(key);
                bookingDocsByKey.delete(key);
                bookingReady.delete(key);
            });

            desiredMatches.forEach((match, key) => {
                if (bookingUnsubscribers.has(key)) return;

                const unsubscribe = onSnapshot(
                    collection(match.dateRef, "bookings"),
                    (snapshot) => {
                        bookingDocsByKey.set(
                            key,
                            snapshot.docs.map((bookingDoc) => ({
                                id: bookingDoc.id,
                                data: bookingDoc.data(),
                            }))
                        );
                        bookingReady.add(key);
                        publish();
                    },
                    (error) => {
                        console.error(`Live booking listener failed for ${key}:`, error);
                        bookingDocsByKey.set(key, []);
                        bookingReady.add(key);
                        publish();
                    }
                );

                bookingUnsubscribers.set(key, unsubscribe);
            });

            publish();
        };

        const syncDateListeners = () => {
            if (disposed) return;

            const { allRequirements } = currentRequirements();
            const desiredDates = new Map();

            allRequirements.forEach((requirement) => {
                rootsForRequirement(requirement).forEach((root) => {
                    const key = `${root.id}/${requirement.dateId}`;
                    if (desiredDates.has(key)) return;
                    desiredDates.set(key, {
                        key,
                        inspectionUid: root.id,
                        dateId: requirement.dateId,
                        dateRef: doc(db, "inspections", root.id, "dates", requirement.dateId),
                    });
                });
            });

            [...dateUnsubscribers.keys()].forEach((key) => {
                if (desiredDates.has(key)) return;
                dateUnsubscribers.get(key)?.();
                dateUnsubscribers.delete(key);
                dateStateByKey.delete(key);
                dateReady.delete(key);
            });

            desiredDates.forEach((definition, key) => {
                if (dateUnsubscribers.has(key)) return;

                const unsubscribe = onSnapshot(
                    definition.dateRef,
                    (snapshot) => {
                        dateStateByKey.set(key, {
                            ...definition,
                            exists: snapshot.exists(),
                            data: snapshot.exists() ? snapshot.data() : {},
                        });
                        dateReady.add(key);
                        syncBookingListeners();
                    },
                    (error) => {
                        console.error(`Live inspection-date listener failed for ${key}:`, error);
                        dateStateByKey.set(key, {
                            ...definition,
                            exists: false,
                            data: {},
                        });
                        dateReady.add(key);
                        syncBookingListeners();
                    }
                );

                dateUnsubscribers.set(key, unsubscribe);
            });

            syncBookingListeners();
        };

        eventIds.forEach((eventId) => {
            const unsubscribe = onSnapshot(
                doc(db, ...CALENDAR_COLLECTION_PATH, eventId),
                (snapshot) => {
                    eventById.set(
                        eventId,
                        snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null
                    );
                    eventReady.add(eventId);
                    syncDateListeners();
                },
                (error) => {
                    console.error(`Live calendar event listener failed for ${eventId}:`, error);
                    eventById.set(eventId, null);
                    eventReady.add(eventId);
                    syncDateListeners();
                }
            );
            staticUnsubscribers.push(unsubscribe);
        });

        const unsubscribeInspections = onSnapshot(
            collection(db, "inspections"),
            (snapshot) => {
                inspectionRoots = snapshot.docs.map((inspectionDoc) => ({
                    id: inspectionDoc.id,
                    data: inspectionDoc.data() || {},
                }));
                inspectionsReady = true;
                syncDateListeners();
            },
            (error) => {
                console.error("Live inspection-root listener failed:", error);
                inspectionRoots = [];
                inspectionsReady = true;
                syncDateListeners();
            }
        );
        staticUnsubscribers.push(unsubscribeInspections);

        return () => {
            disposed = true;
            staticUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
            dateUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
            bookingUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
        };
    }, [effectiveRole, effectiveUid, coordinatorBookingSignature]);

    const bookingInfoForTicket = (ticket) =>
        ticket?.id ? bookingInfoByTicket[ticket.id] || null : null;

    const openCoordinatorBookingModal = (ticket) => {
        if (!ticket) return;
        const bookingInfo = bookingInfoForTicket(ticket) || emptyCoordinatorBookingInfo(ticket);
        setBookingModal({
            ticket,
            bookingInfo,
            bookings: bookingInfo.bookings || [],
        });
    };

    useEffect(() => {
        const ticketId = bookingModal?.ticket?.id;
        if (!ticketId) return;

        const liveInfo = bookingInfoByTicket[ticketId];
        if (!liveInfo) return;

        setBookingModal((prev) => {
            if (!prev || prev.ticket?.id !== ticketId) return prev;
            return {
                ...prev,
                bookingInfo: liveInfo,
                bookings: liveInfo.bookings || [],
            };
        });
    }, [bookingInfoByTicket, bookingModal?.ticket?.id]);

    const [resendingBookingIds, setResendingBookingIds] = useState(new Set());
    const [deletingBookingIds, setDeletingBookingIds] = useState(new Set());

    const bookingInspectionUid = (booking) =>
        booking?.sourceInspectionUid || booking?.inspectionUid || "";

    const coordinatorBookingKey = (booking) =>
        `${bookingInspectionUid(booking)}/${booking?.dateId || ""}/${booking?.id || ""}`;

    const sameCoordinatorBooking = (left, right) =>
        left?.id === right?.id
        && left?.dateId === right?.dateId
        && bookingInspectionUid(left) === bookingInspectionUid(right);

    const removeBookingFromCoordinatorInfo = (info, booking) => {
        if (!info) return info;

        const sourceUid = bookingInspectionUid(booking);
        const days = (info.days || []).map((day) => {
            const dayUid = day?.sourceInspectionUid || day?.inspectionUid || "";
            if (dayUid !== sourceUid || day.dateId !== booking.dateId) {
                return day;
            }

            const bookings = (day.bookings || []).filter(
                (item) => !sameCoordinatorBooking(item, booking)
            );

            return {
                ...day,
                sourceInspectionUid: sourceUid,
                inspectionUid: sourceUid,
                bookings,
                totalBookings: bookings.length,
                dateData: day.dateData
                    ? { ...day.dateData, totalBookings: bookings.length }
                    : day.dateData,
            };
        });

        const bookings = days.flatMap((day) => day.bookings || []).sort((a, b) => {
            const dateDiff = dateIdSortValue(a.dateId) - dateIdSortValue(b.dateId);
            if (dateDiff) return dateDiff;
            return String(a.firstDate_from || "").localeCompare(String(b.firstDate_from || ""));
        });

        return {
            ...info,
            days,
            dates: days.map((day) => day.date).filter(Boolean),
            totalBookings: days.reduce((sum, day) => sum + (day.totalBookings || 0), 0),
            bookings,
        };
    };

    const resendCoordinatorBookingEmail = async (booking) => {
        const inspectionUid = bookingInspectionUid(booking);
        if (!inspectionUid || !booking?.dateId || !booking?.id) {
            console.error("Missing booking Firestore path data:", booking);
            showTicketToast("Booking path information is missing.", "Couldn’t complete");
            return;
        }

        const key = coordinatorBookingKey(booking);
        setResendingBookingIds((prevSet) => {
            const next = new Set(prevSet);
            next.add(key);
            return next;
        });

        try {
            const bookingRef = doc(
                db,
                "inspections",
                inspectionUid,
                "dates",
                booking.dateId,
                "bookings",
                booking.id
            );

            await updateDoc(bookingRef, {
                resendRequested: true,
                resendRequestedAt: serverTimestamp(),
            });


            const markResendRequested = (item) =>
                sameCoordinatorBooking(item, booking)
                    ? {
                        ...item,
                        sourceInspectionUid: inspectionUid,
                        inspectionUid,
                        resendRequested: true,
                    }
                    : item;

            setBookingInfoByTicket((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((ticketId) => {
                    const info = next[ticketId];
                    next[ticketId] = {
                        ...info,
                        bookings: (info.bookings || []).map(markResendRequested),
                        days: (info.days || []).map((day) => ({
                            ...day,
                            bookings: (day.bookings || []).map(markResendRequested),
                        })),
                    };
                });
                return next;
            });

            setBookingModal((prev) => {
                if (!prev) return prev;
                const bookingInfo = prev.bookingInfo
                    ? {
                        ...prev.bookingInfo,
                        bookings: (prev.bookingInfo.bookings || []).map(markResendRequested),
                        days: (prev.bookingInfo.days || []).map((day) => ({
                            ...day,
                            bookings: (day.bookings || []).map(markResendRequested),
                        })),
                    }
                    : prev.bookingInfo;

                return {
                    ...prev,
                    bookingInfo,
                    bookings: (prev.bookings || []).map(markResendRequested),
                };
            });

            showTicketToast("Booking email resend requested.", "Done");
        } catch (error) {
            console.error("Failed to request booking email resend:", error);
            showTicketToast("Could not request the booking email resend.", "Couldn’t complete");
        } finally {
            setResendingBookingIds((prevSet) => {
                const next = new Set(prevSet);
                next.delete(key);
                return next;
            });
        }
    };

    const deleteCoordinatorBooking = async (booking) => {
        const inspectionUid = bookingInspectionUid(booking);
        if (!inspectionUid || !booking?.dateId || !booking?.id) {
            console.error("Missing booking Firestore path data:", booking);
            showTicketToast("Booking path information is missing.", "Couldn’t complete");
            return;
        }

        const confirmed = window.confirm(
            `Delete booking for ${booking.flatNo || "this resident"} on ${booking.firstDate || booking.date || booking.dateId}?`
        );
        if (!confirmed) return;

        const key = coordinatorBookingKey(booking);
        setDeletingBookingIds((prevSet) => {
            const next = new Set(prevSet);
            next.add(key);
            return next;
        });

        try {
            const dateRef = doc(db, "inspections", inspectionUid, "dates", booking.dateId);
            const bookingRef = doc(
                db,
                "inspections",
                inspectionUid,
                "dates",
                booking.dateId,
                "bookings",
                booking.id
            );

            const dateSnap = await getDoc(dateRef);
            const dateData = dateSnap.exists() ? dateSnap.data() : {};
            const batch = writeBatch(db);
            batch.delete(bookingRef);

            if (dateSnap.exists()) {
                const updatePayload = {
                    totalBookings: Math.max(0, Number(dateData.totalBookings || 0) - 1),
                    lastUpdated: serverTimestamp(),
                };

                if (booking.flatNo) {
                    updatePayload.flatNumbers = arrayRemove(booking.flatNo);
                }

                const slotKey = booking.firstDate_from;
                const slot = slotKey ? dateData.timeSlots?.[slotKey] : null;
                if (slotKey && slot) {
                    updatePayload[`timeSlots.${slotKey}.bookedCount`] = Math.max(
                        0,
                        Number(slot.bookedCount || 0) - 1
                    );
                    updatePayload[`timeSlots.${slotKey}.totalBookings`] = Math.max(
                        0,
                        Number(slot.totalBookings || 0) - 1
                    );
                }

                batch.update(dateRef, updatePayload);
            }

            await batch.commit();

            setBookingInfoByTicket((prevInfo) => {
                const next = { ...prevInfo };
                Object.keys(next).forEach((ticketId) => {
                    next[ticketId] = removeBookingFromCoordinatorInfo(next[ticketId], booking);
                });
                return next;
            });

            setBookingModal((prevModal) => {
                if (!prevModal) return null;
                const nextInfo = removeBookingFromCoordinatorInfo(prevModal.bookingInfo, booking);
                return {
                    ...prevModal,
                    bookingInfo: nextInfo,
                    bookings: nextInfo?.bookings || [],
                };
            });

            showTicketToast("Booking deleted.", "Done");
        } catch (error) {
            console.error("Failed to delete booking:", error);
            showTicketToast("Could not delete the booking.", "Couldn’t complete");
        } finally {
            setDeletingBookingIds((prevSet) => {
                const next = new Set(prevSet);
                next.delete(key);
                return next;
            });
        }
    };

    const [newTitle, setNewTitle] = useState("");
    const [newDescription, setNewDescription] = useState("");
    const [newAssignee, setNewAssignee] = useState("");
    const [newAssigneeStage, setNewAssigneeStage] = useState(null);
    const [newPriority, setNewPriority] = useState("Medium");
    const [newTaskType, setNewTaskType] = useState("inspection");
    const [newDueDate, setNewDueDate] = useState("");
    const [newBuilding, setNewBuilding] = useState(null);
    const [createFiles, setCreateFiles] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (drawerMode === "create" && assignableUsers.length > 0 && !newAssignee) {
            const self = assignableUsers.find((u) => u.uid === effectiveUid);
            if (self) {
                setNewAssignee(self.uid);
                setNewAssigneeStage(self.user_role === "coordinator" ? "coordinator_qc" : null);
            }
        }
    }, [drawerMode, assignableUsers.length, effectiveUid]);

    useEffect(() => {
        if (!detailTicket || drawerMode !== "detail") return;
        const fresh = allKnownTickets.find((t) => t.id === detailTicket.id);
        if (fresh) {
            setDetailTicket(fresh);
        } else if (!isAdmin && !detailTicket?.archived) {
            const remainsAccessible = detailTicket.created_by_id === effectiveUid
                || detailTicket.assignee_id === effectiveUid
                || (detailTicket.assignment_history || []).some((entry) => entry?.assignee_id === effectiveUid);
            if (!remainsAccessible) closeDrawer();
        }
    }, [allKnownTickets, detailTicket?.id, detailTicket?.archived, drawerMode, isAdmin, effectiveUid]);

    useEffect(() => {
        if (!detailTicket?.id) { setTicketEvents([]); setTaskLinks([]); return; }
        const uE = onSnapshot(
            query(collection(db, "tickets", detailTicket.id, "events"), orderBy("created_at", "desc")),
            (s) => setTicketEvents(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
            console.error
        );
        const uL = onSnapshot(
            collection(db, "tickets", detailTicket.id, "task_links"),
            (s) => setTaskLinks(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
            console.error
        );
        return () => { uE(); uL(); };
    }, [detailTicket?.id]);

    useEffect(() => {
        if (!highlightEventId || drawerMode !== "detail" || !detailTicket?.id) return undefined;
        if (detailLogTab !== "activity") {
            setDetailLogTab("activity");
            return undefined;
        }
        if (!activityLogOpen) {
            setActivityLogOpen(true);
            return undefined;
        }
        const target = eventRowRefs.current[highlightEventId];
        if (!target) return undefined;
        const scrollTimer = setTimeout(() => {
            target.scrollIntoView?.({ behavior: "smooth", block: "center" });
        }, 100);
        const clearTimer = setTimeout(() => setHighlightEventId(null), 3000);
        return () => {
            clearTimeout(scrollTimer);
            clearTimeout(clearTimer);
        };
    }, [highlightEventId, drawerMode, detailTicket?.id, detailLogTab, activityLogOpen, ticketEvents.length]);

    const openCreate = () => {
        setNewTitle(""); setNewDescription(""); setNewPriority("Medium");
        setNewTaskType("inspection"); setNewDueDate(""); setCreateFiles([]); setNewAssignee(""); setNewAssigneeStage(null);
        setNewBuilding(null);
        setDrawerMode("create");
    };

    const hasTicketDrawerAccess = (ticket) => {
        if (!ticket || !effectiveUid) return false;
        if (isAdmin) return true;
        if (ticket.created_by_id === effectiveUid || ticket.assignee_id === effectiveUid) return true;
        return (ticket.assignment_history || []).some((entry) => entry?.assignee_id === effectiveUid);
    };

    const openDetail = (ticket, options = {}) => {
        if (!hasTicketDrawerAccess(ticket)) {
            showTicketToast("You can only open tasks you created or were assigned to.", "Task access");
            return false;
        }
        setDetailTicket(ticket); setConfirmArchive(false); setCommentText(defaultMentionForTicket(ticket)); setDrawerMode("detail");
        setCommentFiles([]);
        setDetailLogTab("info");
        setDetailDescriptionExpanded(Boolean(options.expandDescription));
        setIsEditingTicket(false);
        return true;
    };

    const openDetailById = async (ticketId) => {
        if (!ticketId) return false;
        const known = allKnownTickets.find((ticket) => ticket.id === ticketId)
            || allTickets.find((ticket) => ticket.id === ticketId);
        if (known) return openDetail(known);

        try {
            const snapshot = await getDoc(doc(db, "tickets", ticketId));
            if (!snapshot.exists()) {
                showTicketToast("This task could not be found.", "Task unavailable");
                return false;
            }
            return openDetail({ id: snapshot.id, ...snapshot.data() });
        } catch (error) {
            console.error("Failed to open ticket from activity/history:", ticketId, error);
            showTicketToast("This task could not be opened right now.", "Task unavailable");
            return false;
        }
    };

    const handleTicketSurfaceClick = (event, ticket) => {
        if (!ticket || event?.defaultPrevented) return;
        const interactive = event?.target?.closest?.("button, a, input, select, textarea, label, [role='button']");
        if (interactive) return;
        openDetail(ticket);
    };

    const openNoteModal = (ticket) => {
        setNoteText(defaultMentionForTicket(ticket));
        setShowNoteModal(true);
    };

    const closeDrawer = () => {
        setDrawerMode(null); setDetailTicket(null); setConfirmArchive(false); setCommentText("");
        setDetailDescriptionExpanded(false);
        setHighlightEventId(null);
        setIsEditingTicket(false);
        window.dispatchEvent(new CustomEvent("tickets:drawer-closed"));
    };

    const canEditTicket = (t) => Boolean(t) && (isAdmin || t.created_by_id === effectiveUid);

    const adminOwnsActiveTicket = (ticket) => Boolean(
        ticket &&
        effectiveUid &&
        isAdmin &&
        ticket.status === "active" &&
        ticket.active_by_id === effectiveUid
    );

    const canCompleteTicketStep = (ticket) => Boolean(
        ticket &&
        effectiveUid &&
        (ticket.assignee_id === effectiveUid || adminOwnsActiveTicket(ticket))
    );

    const creatorCanFinalComplete = (ticket) => Boolean(
        ticket &&
        effectiveUid &&
        !["completed", "cancelled"].includes(ticket.status) &&
        ticket.created_by_id === effectiveUid &&
        ticket.assignee_id === effectiveUid
    );

    const openEditTicket = (t) => {
        if (!canEditTicket(t)) return;
        setDetailLogTab("info");
        setEditTitle(getTitle(t));
        setEditDescription(t.description || "");
        setEditAssignee(t.assignee_id || "");
        setEditAssigneeStage(normalizeUserRole(usersById[t.assignee_id]?.user_role) === "coordinator" ? getWorkflowStageKey(t) : null);
        setEditPriority(t.priority || "Medium");
        setEditTaskType(t.task_type || "");
        setEditDueDate(toLocalDateInputValue(getDueAt(t)));

        const currentBuildingReport = buildingReports.find((building) => building.docId === t.building_id);
        const currentBuildingOption = buildingOptions.find(
            (option) => option.value === t.building_id
        ) || (t.building_id ? {
            value: t.building_id,
            label: `${t.building_name || "Current building"}${currentBuildingReport?.reportId ? ` — ${currentBuildingReport.reportId}` : ""}`,
            buildingName: t.building_name || "",
            reportId: currentBuildingReport?.reportId || "",
        } : null);
        setEditBuilding(currentBuildingOption);

        setEditSelectedFile(null);
        setEditRemoveImage(false);
        setIsEditingTicket(true);
    };

    const closeEditTicket = () => {
        setIsEditingTicket(false);
        setEditBuilding(null);
        setEditSelectedFile(null);
        setEditRemoveImage(false);
    };

    const doSaveTicketEdit = async () => {
        const t = detailTicket;
        if (!t || !canEditTicket(t)) return;
        if (!editTitle.trim() || !editAssignee || !editDueDate) return;

        await run(async () => {
            const assigneeChanged = editAssignee !== t.assignee_id;
            const newAssignee = assignableUsers.find((u) => u.uid === editAssignee);
            if (assigneeChanged && !newAssignee) throw new Error("Select a valid assignee.");
            const targetStageKey = editAssigneeStage
                || (newAssignee?.user_role === "coordinator" ? "coordinator_qc" : null)
                || (assigneeChanged ? null : getWorkflowStageKey(t));
            const assignmentContextChanged = assigneeChanged || targetStageKey !== getWorkflowStageKey(t);
            const targetStageIndex = assignmentContextChanged
                ? resolveStageIndex(t, editAssignee, targetStageKey)
                : getStageIndex(t);
            const targetStage = STAGE_CHAIN[targetStageIndex] || STAGE_CHAIN[getStageIndex(t)];
            const resolvedEditTaskType = editTaskType || targetStage?.taskType || t.task_type;

            let imageUrl = t.image || "";
            if (editSelectedFile) {
                const sr = ref(storage, `tickets/${t.id}/${editSelectedFile.name}`);
                await uploadBytes(sr, editSelectedFile);
                imageUrl = await getDownloadURL(sr);
            } else if (editRemoveImage) {
                imageUrl = "";
            }

            const oldBuildingId = t.building_id || null;
            const oldBuildingName = t.building_name || null;
            const requestedBuildingId = editBuilding?.value || null;
            const requestedBuildingName = editBuilding?.buildingName || null;

            const newBuildingId = isAdmin ? requestedBuildingId : oldBuildingId;
            const newBuildingName = isAdmin ? requestedBuildingName : oldBuildingName;
            const buildingChanged = Boolean(isAdmin && oldBuildingId !== newBuildingId);

            let oldBuildingReportId = "";
            let newBuildingReportId = isAdmin ? String(editBuilding?.reportId || "") : "";

            if (buildingChanged) {
                const [oldBuildingSnapshot, newBuildingSnapshot] = await Promise.all([
                    oldBuildingId ? getDoc(doc(db, "Reports", oldBuildingId)).catch(() => null) : Promise.resolve(null),
                    newBuildingId ? getDoc(doc(db, "Reports", newBuildingId)).catch(() => null) : Promise.resolve(null),
                ]);
                oldBuildingReportId = oldBuildingSnapshot?.exists?.()
                    ? String(oldBuildingSnapshot.data()?.reportId || "")
                    : "";
                if (!newBuildingReportId && newBuildingSnapshot?.exists?.()) {
                    newBuildingReportId = String(newBuildingSnapshot.data()?.reportId || "");
                }
            }

            const updateData = {
                title: editTitle.trim(),
                description: editDescription.trim() || null,
                priority: editPriority,
                task_type: resolvedEditTaskType,
                workflow_stage: targetStage?.key || getWorkflowStageKey(t),
                stage_index: targetStageIndex,
                due_at: dateOnlyEndOfDay(editDueDate),
                building_id: newBuildingId,
                building_name: newBuildingName,
                image: imageUrl,
                updated_at: serverTimestamp(),
                updated_by_id: actor.uid,
            };

            if (assignmentContextChanged) {
                const now = new Date().toISOString();
                const nextUid = newAssignee?.uid || editAssignee;
                updateData.assignee_id = nextUid;
                updateData.participant_ids = arrayUnion(t.assignee_id, nextUid);
                updateData.assignment_history = (t.assignment_history || []).map((entry) =>
                    !entry.unassigned_at
                        ? { ...entry, unassigned_at: now, released_by: actor.uid, direction: "transfer" }
                        : entry
                );
                updateData.assignment_history.push({
                    assignee_id: nextUid,
                    assigned_at: now,
                    unassigned_at: null,
                    assigned_by: actor.uid,
                    workflow_stage: targetStage?.key || targetStageKey,
                    due_at: dateOnlyEndOfDay(editDueDate),
                    work_note: null,
                    direction: null,
                    reason: "Assignment updated while editing task",
                });
            }

            await updateDoc(doc(db, "tickets", t.id), updateData);

            if (buildingChanged) {
                if (oldBuildingId) {
                    try {
                        const oldReportRef = doc(db, "Reports", oldBuildingId);
                        const oldReportSnapshot = await getDoc(oldReportRef);
                        if (oldReportSnapshot.exists()) {
                            const oldReportData = oldReportSnapshot.data() || {};
                            const oldReportUpdate = {
                                linked_ticket_ids: arrayRemove(t.id),
                            };
                            if (oldReportData.ticket_id === t.id) {
                                oldReportUpdate.ticket_id = null;
                            }
                            await updateDoc(oldReportRef, oldReportUpdate);
                        }
                    } catch (err) {
                        console.error("Failed to unlink old building from ticket:", err);
                    }
                }

                if (newBuildingId) {
                    try {
                        await updateDoc(doc(db, "Reports", newBuildingId), {
                            ticket_id: t.id,
                            linked_ticket_ids: arrayUnion(t.id),
                        });
                    } catch (err) {
                        console.error("Failed to link new building to ticket:", err);
                    }
                }

                const existingBuildingLink = taskLinks.find(
                    (link) => link.entity_type === "building"
                );

                if (existingBuildingLink && newBuildingId) {
                    await updateDoc(
                        doc(db, "tickets", t.id, "task_links", existingBuildingLink.id),
                        {
                            building_id: newBuildingId,
                            display_label: newBuildingName || "",
                        }
                    );
                } else if (!existingBuildingLink && newBuildingId) {
                    await addDoc(collection(db, "tickets", t.id, "task_links"), {
                        entity_type: "building",
                        building_id: newBuildingId,
                        display_label: newBuildingName || "",
                    });
                }
            }

            const imageChanged = imageUrl !== (t.image || "");
            const originalDue = getDueAt(t);
            const nextDue = dateOnlyEndOfDay(editDueDate);
            const originalDueMs = originalDue?.toMillis?.() ?? (originalDue ? new Date(originalDue).getTime() : null);
            const nextDueMs = nextDue.getTime();
            const materialDetailsChanged =
                editTitle.trim() !== getTitle(t)
                || (editDescription.trim() || null) !== (t.description || null)
                || editPriority !== (t.priority || "Medium")
                || resolvedEditTaskType !== t.task_type
                || (targetStage?.key || getWorkflowStageKey(t)) !== getWorkflowStageKey(t)
                || originalDueMs !== nextDueMs
                || buildingChanged
                || imageChanged;

            const [editEventRef] = await logEvent(
                t.id,
                actor,
                "edited",
                `Task details edited by ${nameOf(actor.uid)}` +
                (assigneeChanged ? ` — reassigned to ${nameOf(editAssignee)}` : "") +
                (buildingChanged
                    ? ` — building changed from ${oldBuildingName || "None"}${oldBuildingReportId ? ` — ${oldBuildingReportId}` : ""} to ${newBuildingName || "None"}${newBuildingReportId ? ` — ${newBuildingReportId}` : ""}`
                    : "") +
                (imageChanged ? (imageUrl ? " — image updated" : " — image removed") : ""),
                {
                    edited_title: editTitle.trim(),
                    edited_priority: editPriority,
                    edited_task_type: resolvedEditTaskType,
                    edited_due_at: dateOnlyEndOfDay(editDueDate),
                    edited_workflow_stage: targetStage?.key || getWorkflowStageKey(t),
                    ...(buildingChanged ? {
                        old_building_id: oldBuildingId,
                        old_building_name: oldBuildingName,
                        old_building_report_id: oldBuildingReportId || null,
                        new_building_id: newBuildingId,
                        new_building_name: newBuildingName,
                        new_building_report_id: newBuildingReportId || null,
                    } : {}),
                    ...(assignmentContextChanged ? { from_uid: t.assignee_id, to_uid: editAssignee } : {}),
                    ...(imageChanged ? { image_changed: true } : {}),
                }
            );

            if (assignmentContextChanged) {
                if (assigneeChanged) await markTicketSentSafely(t.assignee_id, t.id, editAssignee, actor.uid);
                await notifyUserSafely(editAssignee, {
                    type: "task_assigned",
                    ticket_id: t.id,
                    ticket_title: editTitle.trim(),
                    event_id: editEventRef?.id || null,
                    actor_id: actor.uid,
                    workflow_stage: targetStage?.key || targetStageKey,
                    message: `A task was assigned to you: ${editTitle.trim()}`,
                    dedupe_key: editEventRef?.id ? `edit-assignment:${editEventRef.id}:${editAssignee}` : null,
                });

                if (assigneeChanged && t.assignee_id && t.assignee_id !== actor.uid) {
                    await notifyUserSafely(t.assignee_id, {
                        type: "task_updated",
                        ticket_id: t.id,
                        ticket_title: editTitle.trim(),
                        event_id: editEventRef?.id || null,
                        actor_id: actor.uid,
                        workflow_stage: targetStage?.key || targetStageKey,
                        message: `This task was reassigned to ${nameOf(editAssignee)}`,
                        dedupe_key: editEventRef?.id ? `edit-reassigned-away:${editEventRef.id}:${t.assignee_id}` : null,
                    });
                }
            } else if (materialDetailsChanged && t.assignee_id && t.assignee_id !== actor.uid) {
                await notifyUserSafely(t.assignee_id, {
                    type: "task_updated",
                    ticket_id: t.id,
                    ticket_title: editTitle.trim(),
                    event_id: editEventRef?.id || null,
                    actor_id: actor.uid,
                    workflow_stage: getWorkflowStageKey(t),
                    message: `${nameOf(actor.uid)} updated this task`,
                    dedupe_key: editEventRef?.id ? `edit:${editEventRef.id}:${t.assignee_id}` : null,
                });
            }

            setIsEditingTicket(false);
            setEditBuilding(null);
            setEditSelectedFile(null);
            setEditRemoveImage(false);
        }, "Task updated.");
    };

    const actionLockRef = useRef(false);
    const [actionInProgress, setActionInProgress] = useState(false);

    const run = async (fn, _successMessage = null) => {
        if (actionLockRef.current) return false;
        actionLockRef.current = true;
        setActionInProgress(true);
        try {
            await fn();
            return true;
        } catch (err) {
            console.error(err);
            showTicketToast(err.message || "Something went wrong.", "Couldn’t complete", { tone: "danger" });
            return false;
        } finally {
            actionLockRef.current = false;
            setActionInProgress(false);
        }
    };



    const doStart = async (ticket) => {
        if (ticket.assignee_id !== effectiveUid) {
            showTicketToast("Only the person this task is assigned to can start it.", "Cannot start task");
            return;
        }
        if (activeTicket && activeTicket.id !== ticket.id) {
            setSwitchTarget(ticket);
            setSwitchFrom(activeTicket);
            return;
        }
        await run(async () => {
            const prev = ticket.status;
            const durationInPrev = secsSince(statusStartOf(ticket));
            const sessionRef = await openSession(ticket.id, actor);
            await updateDoc(doc(db, "tickets", ticket.id), {
                status: "active", active_by_id: actor.uid, activeAt: serverTimestamp(),
                currentSessionId: sessionRef.id, status_changed_at: serverTimestamp(),
                is_paused: false, paused_at: null,
                updated_at: serverTimestamp(), updated_by_id: actor.uid,
            });
            await logEvent(ticket.id, actor, "started", "Task started", { from_status: prev, to_status: "active", duration_seconds: durationInPrev });
        }, "Task started.");
    };

    const closeSwitchModal = () => { setSwitchTarget(null); setSwitchFrom(null); };

    const doSwitch = async () => {
        if (!switchTarget || !switchFrom) return;
        await run(async () => {
            if (switchFrom.currentSessionId)
                await closeSession(switchFrom.id, switchFrom.currentSessionId, "switch", switchFrom.activeAt);

            await updateDoc(doc(db, "tickets", switchFrom.id), {
                status: "ready", active_by_id: null, activeAt: null, currentSessionId: null,
                is_paused: false, paused_at: null,
                status_changed_at: serverTimestamp(),
                updated_at: serverTimestamp(), updated_by_id: actor.uid,
            });
            await logEvent(switchFrom.id, actor, "switched",
                `Switched away. Time on this task so far: ${formatTime(totalElapsed)}`,
                { from_status: "active", to_status: "ready", duration_seconds: switchFrom.activeAt ? secsSince(switchFrom.activeAt) : 0 }
            );

            const prev = switchTarget.status;
            const durationInPrev = secsSince(statusStartOf(switchTarget));
            const sessionRef = await openSession(switchTarget.id, actor);
            await updateDoc(doc(db, "tickets", switchTarget.id), {
                status: "active", active_by_id: actor.uid, activeAt: serverTimestamp(),
                currentSessionId: sessionRef.id, status_changed_at: serverTimestamp(),
                is_paused: false, paused_at: null,
                updated_at: serverTimestamp(), updated_by_id: actor.uid,
            });
            await logEvent(switchTarget.id, actor, "started",
                "Task started (switched from another task)",
                { from_status: prev, to_status: "active", duration_seconds: durationInPrev }
            );
            closeSwitchModal();
        }, "Switched task.");
    };

    const doPauseActiveTask = async (ticket) => {
        if (!ticket || ticket.status !== "active" || ticket.active_by_id !== effectiveUid) {
            showTicketToast("Only your active task can be paused.", "Cannot pause task", { tone: "danger" });
            return;
        }
        if (ticket.is_paused) return;

        const before = { ...ticket };
        const pausedAt = new Date();
        const sessionDuration = ticket.activeAt ? Math.max(0, currentElapsed) : 0;
        setTimerHold({ ticketId: ticket.id, totalSeconds: prevSecs + sessionDuration });
        applyTicketUiOverride(ticket, {
            is_paused: true,
            paused_at: pausedAt,
            activeAt: null,
            currentSessionId: null,
        });

        const ok = await run(async () => {
            if (ticket.currentSessionId) {
                await closeSession(ticket.id, ticket.currentSessionId, "pause", ticket.activeAt, sessionDuration);
            }
            await updateDoc(doc(db, "tickets", ticket.id), {
                is_paused: true,
                paused_at: serverTimestamp(),
                activeAt: null,
                currentSessionId: null,
                updated_at: serverTimestamp(),
                updated_by_id: actor.uid,
            });
            await logEvent(ticket.id, actor, "paused", "Task paused — timer stopped for a break.", {
                from_status: "active",
                to_status: "active",
                duration_seconds: sessionDuration,
            });
        }, "Task paused. Timer stopped.");

        if (!ok) {
            applyTicketUiOverride(before, {
                is_paused: before.is_paused,
                paused_at: before.paused_at || null,
                activeAt: before.activeAt || null,
                currentSessionId: before.currentSessionId || null,
            });
            clearTicketUiOverride(ticket.id);
            cacheActiveTicket(before);
            setTimerHold(null);
            return;
        }
    };

    const doResumeActiveTask = async (ticket) => {
        if (!ticket || ticket.status !== "active" || ticket.active_by_id !== effectiveUid) {
            showTicketToast("Only your active task can be resumed.", "Cannot resume task", { tone: "danger" });
            return;
        }
        if (!ticket.is_paused) return;

        const before = { ...ticket };
        const resumedAt = new Date();
        applyTicketUiOverride(ticket, {
            is_paused: false,
            paused_at: null,
            activeAt: resumedAt,
            currentSessionId: null,
        });

        let sessionId = null;
        const ok = await run(async () => {
            const sessionRef = await openSession(ticket.id, actor);
            sessionId = sessionRef.id;
            applyTicketUiOverride({ ...ticket, activeAt: resumedAt }, {
                is_paused: false,
                paused_at: null,
                activeAt: resumedAt,
                currentSessionId: sessionRef.id,
            });
            await updateDoc(doc(db, "tickets", ticket.id), {
                is_paused: false,
                paused_at: null,
                activeAt: serverTimestamp(),
                currentSessionId: sessionRef.id,
                updated_at: serverTimestamp(),
                updated_by_id: actor.uid,
            });
            await logEvent(ticket.id, actor, "resumed", "Task resumed — timer restarted.", {
                from_status: "active",
                to_status: "active",
                duration_seconds: 0,
            });
        }, "Task resumed. Timer running.");

        if (!ok) {
            if (sessionId) {
                try {
                    await updateDoc(doc(db, "tickets", ticket.id, "workSessions", sessionId), {
                        stopped_at: serverTimestamp(),
                        stop_reason: "resume_failed",
                        duration_seconds: 0,
                    });
                } catch { }
            }
            applyTicketUiOverride(before, {
                is_paused: true,
                paused_at: before.paused_at || new Date(),
                activeAt: null,
                currentSessionId: null,
            });
            clearTicketUiOverride(ticket.id);
            cacheActiveTicket(before);
            return;
        }
    };


    const openCompleteModal = (ticket) => {
        if (!canCompleteTicketStep(ticket)) {
            showTicketToast("Only the current task owner can complete this step, unless an admin has the task active on their account.", "Cannot complete task");
            return;
        }

        const fwd = getForwardUsers(ticket);
        const nextStage = getNextStage(ticket);

        setCompleteTicket(ticket);
        setCompleteNextUserId(fwd.length === 1 ? fwd[0].uid : "");
        setCompleteNextStageKey(nextStage?.key || null);
        setCompleteDueDate("");
        setCompleteNotes("");
        setCompleteFiles([]);
        setCompleteChecklist(checklistFromHandoverLog(ticket));
    };

    const closeCompleteModal = () => {
        setCompleteTicket(null); setCompleteNextUserId(""); setCompleteNextStageKey(null); setCompleteDueDate(""); setCompleteNotes("");
        setCompleteFiles([]);
        setCompleteChecklist(emptyHandoverChecklist());
    };

    const openStuckModal = (ticket) => {
        setStuckTicket(ticket);
        setStuckWaitingOn("Client"); setStuckFollowUp("");
        setStuckResponsibleId(effectiveUid || ""); setStuckNotes("");
    };

    const closeStuckModal = () => {
        setStuckTicket(null); setStuckWaitingOn("Client"); setStuckFollowUp("");
        setStuckResponsibleId(""); setStuckNotes("");
    };

    const doCompleteStage = async () => {
        const ticket = completeTicket;
        if (!ticket) return;

        if (!canCompleteTicketStep(ticket)) {
            showTicketToast("Only the current task owner can complete this step, unless an admin has the task active on their account.", "Cannot complete task");
            closeCompleteModal();
            return;
        }

        const nextAssignee = assignableUsers.find((u) => u.uid === completeNextUserId);
        const assigneeFinal = Boolean(
            ticket.assignee_id === actor.uid &&
            !nextAssignee
        );
        const creatorFinal = Boolean(
            assigneeFinal &&
            ticket.created_by_id === actor.uid
        );
        const adminFinal = Boolean(
            adminOwnsActiveTicket(ticket) &&
            !nextAssignee
        );
        const finalCompletionAllowed = assigneeFinal || adminFinal;


        if (!nextAssignee && !finalCompletionAllowed) {
            showTicketToast("Only the current assignee can finally complete this task, unless an admin is actively handling it.", "Cannot complete task");
            return;
        }

        if (nextAssignee && !completeDueDate) return;
        if (!completeNotes.trim()) return;
        const checklistRequired = requiresHandoverChecklist(ticket);
        if (nextAssignee && checklistRequired && !isHandoverChecklistComplete(completeChecklist)) return;

        await run(async () => {
            const actorName = nameOf(actor.uid);
            const workUnitAssigneeId = ticket.assignee_id || null;
            const workUnitAssignment = currentAssignmentFor(ticket);
            const rewardEligibleForActor = Boolean(workUnitAssigneeId && workUnitAssigneeId === actor.uid);
            const attachments = await uploadTicketFiles(ticket.id, completeFiles, "completion");
            const completionDuration = ticket.status === "active" && ticket.activeAt
                ? secsSince(ticket.activeAt)
                : 0;
            const completionTotalTime = Math.max(0, Number(ticket.total_time_seconds || 0)) + completionDuration;

            if (ticket.currentSessionId)
                await closeSession(ticket.id, ticket.currentSessionId, "complete", ticket.activeAt);

            if (!nextAssignee) {
                await updateDoc(doc(db, "tickets", ticket.id), {
                    status: "completed",
                    active_by_id: null,
                    activeAt: null,
                    currentSessionId: null,
                    is_paused: false,
                    paused_at: null,
                    completed_at: serverTimestamp(),
                    blocked_reason: null,
                    waiting_on_type: null,
                    next_follow_up_at: null,
                    waiting_started_at: null,
                    responsible_person_id: null,
                    stuck_notes: null,
                    follow_up_notified_for: null,
                    status_changed_at: serverTimestamp(),
                    updated_at: serverTimestamp(),
                    updated_by_id: actor.uid,
                });

                const completionLabel = creatorFinal
                    ? `Ticket finally completed by original creator ${actorName}`
                    : adminFinal
                        ? `Ticket finally completed by admin ${actorName}`
                        : `Task completed ✓ by ${actorName}`;

                const [completionEventRef] = await logEvent(ticket.id, actor, "completed",
                    `${completionLabel}. Work done: ${completeNotes.trim()}. Total time: ${formatTime(completionTotalTime)}`,
                    {
                        from_status: ticket.status,
                        to_status: "completed",
                        duration_seconds: completionDuration,
                        work_note: completeNotes.trim(),
                        total_time_seconds: completionTotalTime,
                        final_completed_by_creator: creatorFinal,
                        final_completed_by_admin: adminFinal,
                        reward_eligible: rewardEligibleForActor,
                        reward_user_id: workUnitAssigneeId,
                        work_unit_assignee_id: workUnitAssigneeId,
                        work_unit_assigned_at: workUnitAssignment?.assigned_at || null,
                        work_unit_due_at: workUnitAssignment?.due_at || ticket.due_at || null,
                        work_unit_workflow_stage: workUnitAssignment?.workflow_stage || getWorkflowStageKey(ticket),
                        completion_kind: "final",
                        next_assignee_id: null,
                        administrative_close: !rewardEligibleForActor,
                        attachments,
                    }
                );

                const history = (ticket.assignment_history || []).map((h) =>
                    !h.unassigned_at
                        ? {
                            ...h,
                            unassigned_at: new Date().toISOString(),
                            released_by: actor.uid,
                            work_note: completeNotes.trim(),
                            direction: creatorFinal
                                ? "creator_final_complete"
                                : adminFinal
                                    ? "admin_final_complete"
                                    : "completed",
                        }
                        : h
                );
                await updateDoc(doc(db, "tickets", ticket.id), { assignment_history: history });

                if (ticket.created_by_id && ticket.created_by_id !== actor.uid) {
                    await notifyUserSafely(ticket.created_by_id, {
                        type: "task_completed",
                        ticket_id: ticket.id,
                        ticket_title: getTitle(ticket),
                        event_id: completionEventRef?.id || null,
                        actor_id: actor.uid,
                        workflow_stage: getWorkflowStageKey(ticket),
                        message: `${actorName} completed ${getTitle(ticket)}`,
                        dedupe_key: completionEventRef?.id ? `completed:${completionEventRef.id}` : `completed:${ticket.id}:${Date.now()}`,
                    });
                }

                if (creatorFinal && ticket.assignee_id && ticket.assignee_id !== actor.uid) {
                    await notifyUserSafely(ticket.assignee_id, {
                        type: "task_completed",
                        ticket_id: ticket.id,
                        ticket_title: getTitle(ticket),
                        event_id: completionEventRef?.id || null,
                        actor_id: actor.uid,
                        workflow_stage: getWorkflowStageKey(ticket),
                        message: `${actorName}, the original creator, finally completed ${getTitle(ticket)}`,
                        dedupe_key: completionEventRef?.id ? `creator-final:${completionEventRef.id}:${ticket.assignee_id}` : null,
                    });
                }

                if (adminFinal && ticket.assignee_id && ticket.assignee_id !== actor.uid && ticket.assignee_id !== ticket.created_by_id) {
                    await notifyUserSafely(ticket.assignee_id, {
                        type: "task_completed",
                        ticket_id: ticket.id,
                        ticket_title: getTitle(ticket),
                        event_id: completionEventRef?.id || null,
                        actor_id: actor.uid,
                        workflow_stage: getWorkflowStageKey(ticket),
                        message: `${actorName} completed ${getTitle(ticket)} while it was active on the admin account`,
                        dedupe_key: completionEventRef?.id ? `admin-final:${completionEventRef.id}:${ticket.assignee_id}` : null,
                    });
                }

                closeCompleteModal();
                return;
            }

            await logEvent(ticket.id, actor, "completed",
                `Stage completed by ${actorName}. Work done: ${completeNotes.trim()}. Time on this stage: ${formatTime(completionTotalTime)}`,
                {
                    from_status: "active", to_status: "ready", duration_seconds: completionDuration,
                    work_note: completeNotes.trim(),
                    total_time_seconds: completionTotalTime,
                    reward_eligible: rewardEligibleForActor,
                    reward_user_id: workUnitAssigneeId,
                    work_unit_assignee_id: workUnitAssigneeId,
                    work_unit_assigned_at: workUnitAssignment?.assigned_at || null,
                    work_unit_due_at: workUnitAssignment?.due_at || ticket.due_at || null,
                    work_unit_workflow_stage: workUnitAssignment?.workflow_stage || getWorkflowStageKey(ticket),
                    completion_kind: "forward",
                    next_assignee_id: nextAssignee.uid,
                    attachments,
                }
            );
            await updateDoc(doc(db, "tickets", ticket.id), {
                status: "ready", active_by_id: null, activeAt: null, currentSessionId: null,
                is_paused: false, paused_at: null,
                status_changed_at: serverTimestamp(),
                updated_at: serverTimestamp(), updated_by_id: actor.uid,
            });

            await performReassign(
                { ...ticket, status: "ready", active_by_id: null, activeAt: null, currentSessionId: null, is_paused: false, paused_at: null, status_changed_at: new Date() },
                nextAssignee.uid,
                completeDueDate,
                completeNotes.trim(),
                "",
                checklistRequired ? completeChecklist : null,
                completeNextStageKey,
                "forward"
            );
            closeCompleteModal();
        }, nextAssignee ? "Stage completed — forwarded to next person." : "Task completed!");
    };

    const doMarkStuck = async () => {
        if (!stuckTicket || !stuckNotes.trim() || !stuckFollowUp || !stuckWaitingOn || !stuckResponsibleId) return;
        await run(async () => {
            const t = stuckTicket, prev = t.status;
            const followUpDate = dateOnlyStartOfDay(stuckFollowUp);
            const durationBeforeBlock = prev === "active"
                ? (t.activeAt ? secsSince(t.activeAt) : 0)
                : secsSince(statusStartOf(t));
            if (t.currentSessionId) await closeSession(t.id, t.currentSessionId, "block", t.activeAt);
            await updateDoc(doc(db, "tickets", t.id), {
                status: "waiting", active_by_id: null, activeAt: null, currentSessionId: null,
                is_paused: false, paused_at: null,
                blocked_reason: stuckNotes.trim(),
                waiting_on_type: stuckWaitingOn,
                next_follow_up_at: followUpDate,
                waiting_started_at: serverTimestamp(),
                follow_up_notified_for: null,
                responsible_person_id: stuckResponsibleId,
                participant_ids: arrayUnion(stuckResponsibleId),
                status_changed_at: serverTimestamp(),
                updated_at: serverTimestamp(), updated_by_id: actor.uid,
            });
            const [blockedEventRef] = await logEvent(t.id, actor, "blocked",
                `Blocked: ${stuckNotes.trim()}. Waiting on: ${stuckWaitingOn}. Referred to: ${nameOf(stuckResponsibleId)}. ` +
                `Next chase: ${fmtDate(followUpDate)}.`,
                {
                    from_status: prev, to_status: "waiting", duration_seconds: durationBeforeBlock,
                    waiting_on_type: stuckWaitingOn,
                    responsible_uid: stuckResponsibleId,
                    next_chase_at: followUpDate,
                    notes: stuckNotes.trim(),
                }
            );

            await notifyUserSafely(stuckResponsibleId, {
                type: "stuck_responsibility",
                ticket_id: t.id,
                ticket_title: getTitle(t),
                event_id: blockedEventRef?.id || null,
                actor_id: actor.uid,
                workflow_stage: getWorkflowStageKey(t),
                follow_up_at: followUpDate,
                waiting_on_type: stuckWaitingOn,
                message: `${nameOf(actor.uid)} asked you to follow up a stuck task by ${fmtDate(followUpDate)}`,
                dedupe_key: blockedEventRef?.id ? `stuck:${blockedEventRef.id}:${stuckResponsibleId}` : `stuck:${t.id}:${followUpDate.toISOString()}:${stuckResponsibleId}`,
            });

            if (t.assignee_id && t.assignee_id !== actor.uid && t.assignee_id !== stuckResponsibleId) {
                await notifyUserSafely(t.assignee_id, {
                    type: "task_updated",
                    ticket_id: t.id,
                    ticket_title: getTitle(t),
                    event_id: blockedEventRef?.id || null,
                    actor_id: actor.uid,
                    workflow_stage: getWorkflowStageKey(t),
                    message: `${nameOf(actor.uid)} marked this task as stuck`,
                    dedupe_key: blockedEventRef?.id ? `stuck-owner:${blockedEventRef.id}:${t.assignee_id}` : null,
                });
            }
            closeStuckModal();
        }, "Marked as stuck.");
    };

    const doUnblock = async (ticket) => {
        await run(async () => {

            const nextAssigneeId =
                ticket.responsible_person_id ||
                ticket.assignee_id;

            const assigneeChanged =
                nextAssigneeId &&
                nextAssigneeId !== ticket.assignee_id;

            let nextStageIndex = getStageIndex(ticket);
            let nextTaskType = ticket.task_type;

            if (assigneeChanged) {
                nextStageIndex = resolveStageIndex(ticket, nextAssigneeId);
                nextTaskType = STAGE_CHAIN[nextStageIndex]?.taskType || ticket.task_type;
            }
            const nextWorkflowStage = STAGE_CHAIN[nextStageIndex]?.key || getWorkflowStageKey(ticket);

            let assignmentHistory = ticket.assignment_history || [];

            if (assigneeChanged) {
                const now = new Date().toISOString();

                assignmentHistory = assignmentHistory.map((entry) =>
                    !entry.unassigned_at
                        ? {
                            ...entry,
                            unassigned_at: now,
                            released_by: actor.uid,
                            direction: "back",
                            reason: "Taken over after stuck task was unblocked",
                        }
                        : entry
                );

                assignmentHistory.push({
                    assignee_id: nextAssigneeId,
                    assigned_at: now,
                    unassigned_at: null,
                    assigned_by: actor.uid,
                    workflow_stage: nextWorkflowStage,
                    due_at: getDueAt(ticket) || null,
                    work_note: null,
                    direction: null,
                    reason: null,
                });
            }

            const updateData = {
                status: "ready",

                assignee_id: nextAssigneeId,

                active_by_id: null,
                activeAt: null,
                currentSessionId: null,
                is_paused: false,
                paused_at: null,

                blocked_reason: null,
                waiting_on_type: null,
                next_follow_up_at: null,
                waiting_started_at: null,
                responsible_person_id: null,
                stuck_notes: null,
                follow_up_notified_for: null,

                stage_index: nextStageIndex,
                task_type: nextTaskType,
                workflow_stage: nextWorkflowStage,

                status_changed_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                updated_by_id: actor.uid,
            };

            if (assigneeChanged) {
                updateData.assignment_history = assignmentHistory;
                updateData.participant_ids = arrayUnion(ticket.assignee_id, nextAssigneeId);
            }

            await updateDoc(
                doc(db, "tickets", ticket.id),
                updateData
            );

            const [unblockedEventRef] = await logEvent(
                ticket.id,
                actor,
                "unblocked",
                assigneeChanged
                    ? `Task unblocked by ${nameOf(actor.uid)} and moved to ${nameOf(nextAssigneeId)}'s queue`
                    : `Task unblocked by ${nameOf(actor.uid)} and returned to the queue`,
                {
                    from_status: "waiting",
                    to_status: "ready",
                    duration_seconds: secsSince(
                        statusStartOf(ticket)
                    ),
                    from_uid: ticket.assignee_id,
                    to_uid: nextAssigneeId,
                }
            );

            if (assigneeChanged) {
                await markTicketSentSafely(ticket.assignee_id, ticket.id, nextAssigneeId, actor.uid);
            }
            if (nextAssigneeId && nextAssigneeId !== actor.uid) {
                await notifyUserSafely(nextAssigneeId, {
                    type: "task_assigned",
                    ticket_id: ticket.id,
                    ticket_title: getTitle(ticket),
                    event_id: unblockedEventRef?.id || null,
                    actor_id: actor.uid,
                    workflow_stage: nextWorkflowStage,
                    message: assigneeChanged
                        ? `A task was sent to you: ${getTitle(ticket)}`
                        : `A stuck task is ready for you again: ${getTitle(ticket)}`,
                    dedupe_key: unblockedEventRef?.id ? `unblocked:${unblockedEventRef.id}:${nextAssigneeId}` : null,
                });
            }
        }, "Task returned to your To Do queue.");
    };

    const doActivateFromStuck = async (ticket, switchExisting = false) => {
        if (!ticket || ticket.status !== "waiting") return;

        const nextAssigneeId = ticket.responsible_person_id || ticket.assignee_id;
        if (nextAssigneeId !== effectiveUid) {
            showTicketToast("This stuck task is not assigned/referred to you, so it cannot be made active on your account.", "Cannot start task");
            return;
        }

        await run(async () => {
            const currentActive = activeTicket && activeTicket.id !== ticket.id ? activeTicket : null;
            if (currentActive && !switchExisting) {
                throw new Error("You already have an active task. Confirm the switch first.");
            }

            if (currentActive && switchExisting) {
                const liveDuration = currentActive.activeAt ? secsSince(currentActive.activeAt) : 0;
                if (currentActive.currentSessionId) {
                    await closeSession(currentActive.id, currentActive.currentSessionId, "switch", currentActive.activeAt);
                }
                await updateDoc(doc(db, "tickets", currentActive.id), {
                    status: "ready",
                    active_by_id: null,
                    activeAt: null,
                    currentSessionId: null,
                    is_paused: false,
                    paused_at: null,
                    status_changed_at: serverTimestamp(),
                    updated_at: serverTimestamp(),
                    updated_by_id: actor.uid,
                });
                await logEvent(currentActive.id, actor, "switched",
                    `Switched away to start a stuck task. Time in the current session: ${formatTime(liveDuration)}`,
                    { from_status: "active", to_status: "ready", duration_seconds: liveDuration }
                );
            }

            const assigneeChanged = nextAssigneeId !== ticket.assignee_id;
            let nextStageIndex = getStageIndex(ticket);
            let nextTaskType = ticket.task_type;
            if (assigneeChanged) {
                nextStageIndex = resolveStageIndex(ticket, nextAssigneeId);
                nextTaskType = STAGE_CHAIN[nextStageIndex]?.taskType || ticket.task_type;
            }
            const nextWorkflowStage = STAGE_CHAIN[nextStageIndex]?.key || getWorkflowStageKey(ticket);

            let assignmentHistory = ticket.assignment_history || [];
            if (assigneeChanged) {
                const now = new Date().toISOString();
                assignmentHistory = assignmentHistory.map((entry) =>
                    !entry.unassigned_at
                        ? {
                            ...entry,
                            unassigned_at: now,
                            released_by: actor.uid,
                            direction: "back",
                            reason: "Taken over directly from the Stuck / Waiting list",
                        }
                        : entry
                );
                assignmentHistory.push({
                    assignee_id: nextAssigneeId,
                    assigned_at: now,
                    unassigned_at: null,
                    assigned_by: actor.uid,
                    workflow_stage: nextWorkflowStage,
                    due_at: getDueAt(ticket) || null,
                    work_note: null,
                    direction: null,
                    reason: null,
                });
            }

            const sessionRef = await openSession(ticket.id, actor);
            const updateData = {
                status: "active",
                assignee_id: nextAssigneeId,
                active_by_id: actor.uid,
                activeAt: serverTimestamp(),
                currentSessionId: sessionRef.id,
                is_paused: false,
                paused_at: null,
                blocked_reason: null,
                waiting_on_type: null,
                next_follow_up_at: null,
                waiting_started_at: null,
                responsible_person_id: null,
                stuck_notes: null,
                follow_up_notified_for: null,
                stage_index: nextStageIndex,
                task_type: nextTaskType,
                workflow_stage: nextWorkflowStage,
                status_changed_at: serverTimestamp(),
                updated_at: serverTimestamp(),
                updated_by_id: actor.uid,
            };
            if (assigneeChanged) {
                updateData.assignment_history = assignmentHistory;
                updateData.participant_ids = arrayUnion(ticket.assignee_id, nextAssigneeId);
            }

            await updateDoc(doc(db, "tickets", ticket.id), updateData);
            const [unblockedEventRef] = await logEvent(ticket.id, actor, "unblocked",
                assigneeChanged
                    ? `Task taken over by ${nameOf(actor.uid)} and activated directly from Stuck / Waiting`
                    : `Task unblocked and activated directly from Stuck / Waiting by ${nameOf(actor.uid)}`,
                {
                    from_status: "waiting",
                    to_status: "active",
                    duration_seconds: secsSince(statusStartOf(ticket)),
                    from_uid: ticket.assignee_id,
                    to_uid: nextAssigneeId,
                }
            );
            await logEvent(ticket.id, actor, "started", "Task started directly from the Stuck / Waiting list", {
                from_status: "waiting",
                to_status: "active",
                duration_seconds: 0,
                unblocked_event_id: unblockedEventRef?.id || null,
            });

            if (assigneeChanged) {
                await markTicketSentSafely(ticket.assignee_id, ticket.id, nextAssigneeId, actor.uid);
            }
        }, "Task is now active.");
    };

    const activateFromStuck = (ticket) => {
        if (activeTicket && activeTicket.id !== ticket.id) {
            setConfirmAction({
                title: "Switch active task?",
                message: `Your current task is ${activeTicket.is_paused ? "paused" : "active"}. Starting "${getTitle(ticket)}" will return the current task to To Do and make this stuck task active. Continue?`,
                onConfirm: () => doActivateFromStuck(ticket, true),
            });
            return;
        }
        doActivateFromStuck(ticket, false);
    };


    const followUpNotificationInFlightRef = useRef(new Set());

    useEffect(() => {
        if (!effectiveUid) return undefined;

        const checkDueWaitingFollowUps = () => {
            waitingTickets.forEach((ticket) => {
                if (ticket.status !== "waiting" || !ticket.next_follow_up_at) return;
                const followUpDate = ticket.next_follow_up_at.toDate
                    ? ticket.next_follow_up_at.toDate()
                    : new Date(ticket.next_follow_up_at);
                if (Number.isNaN(followUpDate.getTime()) || followUpDate.getTime() > Date.now()) return;

                const recipientUid = ticket.responsible_person_id || ticket.assignee_id;
                if (!recipientUid || recipientUid !== effectiveUid) return;
                const followUpKey = followUpDate.toISOString();
                if (ticket.follow_up_notified_for === followUpKey) return;
                const inFlightKey = `${ticket.id}:${followUpKey}`;
                if (followUpNotificationInFlightRef.current.has(inFlightKey)) return;

                followUpNotificationInFlightRef.current.add(inFlightKey);
                (async () => {
                    const notificationRef = await notifyUserSafely(recipientUid, {
                        type: "follow_up_due",
                        ticket_id: ticket.id,
                        ticket_title: getTitle(ticket),
                        actor_id: null,
                        workflow_stage: getWorkflowStageKey(ticket),
                        follow_up_at: followUpDate,
                        message: `Follow-up is due for ${getTitle(ticket)}`,
                        dedupe_key: `follow-up:${followUpKey}:${recipientUid}`,
                    });
                    if (notificationRef) {
                        await updateDoc(doc(db, "tickets", ticket.id), {
                            follow_up_notified_for: followUpKey,
                            updated_at: serverTimestamp(),
                        }).catch((err) => console.error("Failed to mark follow-up notification sent:", err));
                    }
                })().finally(() => {
                    followUpNotificationInFlightRef.current.delete(inFlightKey);
                });
            });
        };

        checkDueWaitingFollowUps();
        const interval = setInterval(checkDueWaitingFollowUps, 60000);
        return () => clearInterval(interval);
    }, [effectiveUid, waitingTickets]);

    const doAddNote = async () => {
        if (!noteText.trim() || !activeTicket) return;
        const text = noteText.trim();
        await run(async () => {
            const [eventRef] = await logEvent(activeTicket.id, actor, "comment", text, {
                mentioned_user_ids: mentionedUserIdsFromText(text, mentionableUsers),
            });
            await updateDoc(doc(db, "tickets", activeTicket.id), { updated_at: serverTimestamp(), updated_by_id: actor.uid });
            await notifyMentionedUsers(activeTicket, text, eventRef?.id || null);
            setNoteText(""); setShowNoteModal(false);
        }, "Note added.");
    };

    const openEmailLogModal = (ticket) => {
        setEmailLogTicket(ticket); setEmailTo(""); setEmailSubject(""); setEmailNotes("");
    };

    const closeEmailLogModal = () => {
        setEmailLogTicket(null); setEmailTo(""); setEmailSubject(""); setEmailNotes("");
    };

    const doLogEmail = async () => {
        if (!emailLogTicket || !emailTo.trim() || !emailSubject.trim()) return;
        const ticket = emailLogTicket;
        await run(async () => {
            await logEvent(ticket.id, actor, "email_sent",
                `Email sent to ${emailTo.trim()} — Subject: ${emailSubject.trim()}`,
                { to: emailTo.trim(), subject: emailSubject.trim(), notes: emailNotes.trim() || null }
            );
            await updateDoc(doc(db, "tickets", ticket.id), { updated_at: serverTimestamp(), updated_by_id: actor.uid });
            closeEmailLogModal();
        }, "Email logged.");
    };


    const openCalendarModal = async (ticket, slotIndex = 0) => {
        if (effectiveRole !== "inspection") return;

        setModalLoading(true);
        try {
            const scheduleIds = ticket.schedule_ids || [];
            const existingId = scheduleIds[slotIndex];

            if (existingId) {
                try {
                    const snap = await getDoc(doc(db, ...CALENDAR_COLLECTION_PATH, existingId));
                    if (snap.exists()) {
                        const data = snap.data();
                        setCalendarModal({
                            ticketId: ticket.id,
                            slotIndex,
                            event: {
                                id: snap.id,
                                title: data.title || '',
                                description: data.description || '',
                                type: data.type || 'general',
                                category: data.category || data.type || 'general',
                                start: calendarDateUtils.parseFromDatabase(data.start),
                                end: calendarDateUtils.parseFromDatabase(data.end),
                                buildingEmail: data.buildingEmail || '',
                                buildingAddress: data.buildingAddress || '',
                                buildingCity: data.buildingCity || '',
                                travelDistanceMiles: data.travelDistanceMiles || 0,
                                travelTimeMinutes: data.travelTimeMinutes || 0,
                                surveyTimeMinutes: data.surveyTimeMinutes || 0,
                                totalCommunalDoors: data.totalCommunalDoors || 0,
                                totalResiDoors: data.totalResiDoors || 0,
                                preventRescheduling: data.preventRescheduling || false,
                                inspectorName: data.inspectorName || '',
                                directorName: data.directorName || '',
                                carpenterName: data.carpenterName || '',
                                lunchDuration: data.lunchDuration || '',
                                postcode: data.postcode || '',
                                assignedInspector: data.assignedInspector || '',
                                assignedCarpenter: data.assignedCarpenter || '',
                                assignedDirector: data.assignedDirector || '',
                                doorUrl: data.doorUrl || '',
                                materialCost: data.materialCost || '',
                                jobPrice: data.jobPrice || '',
                                labourCost: data.labourCost || '',
                                ticket_id: data.ticket_id || null,
                            },
                            slot: null,
                            prefill: null,
                        });
                        return;
                    }

                    const clearedIds = [...scheduleIds];
                    clearedIds[slotIndex] = null;
                    updateDoc(doc(db, "tickets", ticket.id), { schedule_ids: clearedIds }).catch(() => { });
                } catch (err) {
                    console.error("Failed to load scheduled event for ticket:", err);
                }
            }

            let buildingInfo = {};
            let totalCommunalDoors = 0;
            let totalResiDoors = 0;
            if (ticket.building_id) {
                try {
                    const buildingSnap = await getDoc(doc(db, "Reports", ticket.building_id));
                    if (buildingSnap.exists()) buildingInfo = buildingSnap.data();
                } catch (err) {
                    console.error("Failed to load building details for calendar prefill:", err);
                }
                try {
                    const [communalSnap, resiSnap] = await Promise.all([
                        getDocs(collection(db, "Reports", ticket.building_id, "DoorReports")),
                        getDocs(collection(db, "Reports", ticket.building_id, "ResidentialReports")),
                    ]);
                    totalCommunalDoors = communalSnap.docs.filter((d) => !d.data()?.archived).length;
                    totalResiDoors = resiSnap.docs.filter((d) => !d.data()?.archived).length;
                } catch (err) {
                    console.error("Failed to load door counts for calendar prefill:", err);
                }
            }

            setCalendarModal({
                event: null,
                ticketId: ticket.id,
                slotIndex,
                slot: null,
                prefill: {
                    title: `${getTitle(ticket)}${slotIndex > 0 ? ` (Visit ${slotIndex + 1})` : ""}`,
                    buildingName: buildingInfo.buildingName || "",
                    buildingAddress: [buildingInfo.buildingName, buildingInfo.clientAddress].filter(Boolean).join(", "),
                    buildingCity: buildingInfo.city || buildingInfo.buildingCity || "",
                    buildingEmail: buildingInfo.email || buildingInfo.buildingEmail || "",
                    postcode: buildingInfo.postcode || "",
                    inspectorName:
                        buildingInfo.completedBy
                        || "",
                    assignedInspector:
                        buildingInfo.completedByUid
                        || "",
                    totalCommunalDoors,
                    totalResiDoors,
                },
            });
        } finally {
            setModalLoading(false);
        }
    };

    const handleTicketScheduled = async (newEvent) => {
        const ticketId = calendarModal?.ticketId;
        const slotIndex = calendarModal?.slotIndex ?? 0;
        if (!ticketId || !newEvent?.id) return;
        try {
            const ticket = ticketsById[ticketId];
            const scheduleIds = [...(ticket?.schedule_ids || [])];
            scheduleIds[slotIndex] = newEvent.id;
            await updateDoc(doc(db, "tickets", ticketId), {
                schedule_ids: scheduleIds,
                updated_at: serverTimestamp(),
            });
        } catch (err) {
            console.error("Failed to save schedule_ids on ticket:", err);
        }
    };


    const doGenerateReport = async (buildingId, ticket = null) => {
        if (!buildingId || !effectiveUid) return;

        await run(async () => {
            const reportRef = doc(db, "Reports", buildingId);
            const reportSnapshot = await getDoc(reportRef);
            const reportData = reportSnapshot.exists() ? reportSnapshot.data() || {} : {};
            const current = reportData.surveryCompleted === true;
            const requestedAtClientMs = Date.now();
            const requestId = `${buildingId}-${requestedAtClientMs}-${Math.random().toString(36).slice(2, 8)}`;
            const buildingName = ticket?.building_name || reportData.buildingName || "Building";
            const reportId = reportData.reportId || "";

            const requestRef = doc(
                db,
                "users",
                effectiveUid,
                "report_generation_requests",
                requestId
            );

            await setDoc(requestRef, {
                request_id: requestId,
                building_id: buildingId,
                building_name: buildingName,
                report_id: reportId,
                ticket_id: ticket?.id || null,
                ticket_title: ticket ? getTitle(ticket) : null,
                requested_at: serverTimestamp(),
                requested_at_client: new Date(requestedAtClientMs).toISOString(),
                requested_at_client_ms: requestedAtClientMs,
                status: "waiting",

                previous_pdf_updated_at: reportData.pdfupdatedAt || null,
                previous_pdf_resi_updated_at: reportData.pdfResiupdatedAt || null,
                previous_pdf_updated_ms: reportTimestampMillis(reportData.pdfupdatedAt),
                previous_pdf_resi_updated_ms: reportTimestampMillis(reportData.pdfResiupdatedAt),
                previous_pdf_link: reportData.pdfLink || null,
                previous_pdf_resi_link: reportData.pdfResiLink || null,

                communal_notified: false,
                residential_notified: false,
                communal_ready_at: null,
                residential_ready_at: null,
                communal_url: null,
                residential_url: null,
            });

            try {
                if (current) {
                    await updateDoc(reportRef, { surveryCompleted: false });
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                    await updateDoc(reportRef, { surveryCompleted: true });
                } else {
                    await updateDoc(reportRef, { surveryCompleted: true });
                }
            } catch (err) {
                await setDoc(
                    requestRef,
                    {
                        status: "failed",
                        failed_at: serverTimestamp(),
                        error_message: String(err?.message || err || "Report generation failed"),
                    },
                    { merge: true }
                ).catch(() => { });
                throw err;
            }
        }, "Report generation started. You’ll be notified separately when each PDF is ready.");
    };

    const performReassign = async (ticket, newAssigneeId, dueDateValue, notes = "", reason = "", checklist = null, targetStageKey = null, directionOverride = null) => {
        const newAssignee = assignableUsers.find((u) => u.uid === newAssigneeId);
        if (!newAssignee) return;

        const now = new Date().toISOString();
        const prevAssigneeName = nameOf(ticket.assignee_id);
        const nextDueDate = dateOnlyEndOfDay(dueDateValue);

        const curStageIdx = getStageIndex(ticket);
        const newStageIdx = resolveStageIndex(ticket, newAssignee.uid, targetStageKey);
        const newStage = STAGE_CHAIN[newStageIdx];

        const direction = directionOverride
            || (newStageIdx < curStageIdx ? "back" : "transfer");
        const isGoingBack = direction === "back";
        const isCompletedForward = direction === "forward";

        const history = (ticket.assignment_history || []).map((h) =>
            !h.unassigned_at ? {
                ...h,
                unassigned_at: now,
                released_by: actor.uid,
                work_note: notes || h.work_note || null,
                direction,
                reason: isGoingBack ? (reason || null) : (h.reason || null),
            } : h
        );
        history.push({
            assignee_id: newAssignee.uid,
            assigned_at: now,
            unassigned_at: null,
            assigned_by: actor.uid,
            workflow_stage: newStage?.key || targetStageKey,
            due_at: nextDueDate,
            work_note: null,
            direction: null,
            reason: null,
        });

        const updateData = {
            assignee_id: newAssignee.uid,
            assignment_history: history,
            due_at: nextDueDate,
            stage_index: newStageIdx,
            task_type: newStage?.taskType || ticket.task_type,
            workflow_stage: newStage?.key || getWorkflowStageKey(ticket),
            participant_ids: arrayUnion(ticket.assignee_id, newAssignee.uid),
            status_changed_at: serverTimestamp(),
            updated_at: serverTimestamp(),
            updated_by_id: actor.uid,
        };

        let revertedStatus = null;

        if (ticket.status === "active") {
            if (ticket.currentSessionId) {
                await closeSession(ticket.id, ticket.currentSessionId, "switch", ticket.activeAt);
            }
            updateData.status = "ready";
            updateData.active_by_id = null;
            updateData.activeAt = null;
            updateData.currentSessionId = null;
            updateData.is_paused = false;
            updateData.paused_at = null;
            revertedStatus = "ready";
        }

        if (ticket.status === "completed") {
            updateData.status = "ready";
            updateData.completed_at = null;
            updateData.active_by_id = null;
            updateData.activeAt = null;
            updateData.currentSessionId = null;
            updateData.is_paused = false;
            updateData.paused_at = null;
            revertedStatus = "ready";
        }

        if (checklist) {
            updateData.handover_log = arrayUnion({
                assignee_id: ticket.assignee_id,
                assignee_name: prevAssigneeName,
                submitted_by: actor.uid,
                submitted_at: now,
                direction,
                checklist,
            });
        }

        await updateDoc(doc(db, "tickets", ticket.id), updateData);
        await markTicketSentSafely(ticket.assignee_id, ticket.id, newAssignee.uid, actor.uid);
        await notifyUserSafely(newAssignee.uid, {
            type: isGoingBack ? "amendment_required" : "task_assigned",
            ticket_id: ticket.id,
            ticket_title: getTitle(ticket),
            actor_id: actor.uid,
            workflow_stage: newStage?.key || getWorkflowStageKey(ticket),
            message: isGoingBack
                ? `Amendment required: ${getTitle(ticket)}`
                : isCompletedForward
                    ? `A completed stage was forwarded to you: ${getTitle(ticket)}`
                    : `A task was assigned to you: ${getTitle(ticket)}`,
            dedupe_key: `handoff:${now}:${newAssignee.uid}`,
        });
        await logEvent(ticket.id, actor, "reassigned",
            `${isGoingBack ? "Amendment Required" : isCompletedForward ? "Forwarded after stage completion" : "Transferred"} from ${prevAssigneeName} to ${newAssignee.name || newAssignee.email}` +
            (notes ? ` — Work done: ${notes}` : "") +
            (isGoingBack && reason ? ` — Reason: ${reason}` : "") +
            (revertedStatus ? ` — status reverted to ${revertedStatus}` : "") +
            (nextDueDate ? ` — due ${fmtTimestamp(nextDueDate)}` : ""),
            {
                from_status: ticket.status,
                to_status: revertedStatus || ticket.status,
                duration_seconds: ticket.status === "active"
                    ? (ticket.activeAt ? secsSince(ticket.activeAt) : 0)
                    : secsSince(statusStartOf(ticket)),
                direction,
                from_uid: ticket.assignee_id,
                to_uid: newAssignee.uid,
                work_note: notes || null,
                reason: isGoingBack ? (reason || null) : null,
                due_at: nextDueDate,
                handover_checklist: checklist || null,
            }
        );

        if (!isAdmin && ticket.assignee_id === effectiveUid) {
            closeDrawer();
        }
    };

    const doReassign = async (ticket, newAssigneeId, dueDateValue, notes = "", reason = "", checklist = null, targetStageKey = null) => {
        if (!assignableUsers.find((u) => u.uid === newAssigneeId) || !dueDateValue) return;
        await run(() => performReassign(ticket, newAssigneeId, dueDateValue, notes, reason, checklist, targetStageKey), "Task reassigned.");
    };

    const openReassignModal = (ticket, forcedRole = null, forcedLabel = "", forcedStageKey = null) => {
        setReassignTicket(ticket);
        setReassignUserId(""); setReassignTargetStage(forcedStageKey); setReassignDueDate("");
        setReassignNotes("");
        setReassignForcedRole(forcedRole);
        setReassignForcedLabel(forcedLabel);
    };

    const closeReassignModal = () => {
        setReassignTicket(null); setReassignUserId(""); setReassignTargetStage(null); setReassignDueDate("");
        setReassignNotes("");
        setReassignForcedRole(null); setReassignForcedLabel("");
    };


    const doQuickReassign = async () => {
        if (!reassignTicket || !reassignUserId || !reassignDueDate || !reassignNotes.trim()) return;
        await doReassign(reassignTicket, reassignUserId, reassignDueDate, reassignNotes.trim(), "", null, reassignTargetStage);
        closeReassignModal();
    };

    const doCreate = async () => {
        if (!newTitle.trim() || !newAssignee || !newDueDate) return;
        setSaving(true);
        await run(async () => {
            const assignee = assignableUsers.find((u) => u.uid === newAssignee);
            if (!assignee) throw new Error("Select a valid assignee.");

            const docRef = await createTicket({
                title: newTitle,
                description: newDescription,
                assigneeId: assignee.uid,
                assigneeRole: assignee.user_role,
                assigneeLabel: assignee.name || assignee.email,
                assigneeStage: assignee.user_role === "coordinator" ? newAssigneeStage : null,
                actorUid: actor.uid,
                creatorRole: effectiveRole,
                priority: newPriority,
                taskType: newTaskType,
                dueDate: dateOnlyEndOfDay(newDueDate),
                files: createFiles,
                buildingId: newBuilding?.value || null,
                buildingName: newBuilding?.buildingName || null,
            });


            if (newBuilding?.value) {

                await updateDoc(doc(db, "Reports", newBuilding.value), {
                    ticket_id: docRef.id,
                    linked_ticket_ids: arrayUnion(docRef.id),
                });
            }

            closeDrawer();
        }, "Task created.");
        setSaving(false);
    };


    const doComment = () => {
        if ((!commentText.trim() && commentFiles.length === 0) || !detailTicket) return;
        const text = commentText.trim();
        run(async () => {
            const attachments = await uploadTicketFiles(detailTicket.id, commentFiles, "comment");
            const eventNote = text || (attachments.length === 1 ? "Added an attachment" : `Added ${attachments.length} attachments`);
            const [eventRef] = await logEvent(detailTicket.id, actor, "comment", eventNote, {
                mentioned_user_ids: mentionedUserIdsFromText(text, mentionableUsers),
                attachments,
            });
            await updateDoc(doc(db, "tickets", detailTicket.id), { updated_at: serverTimestamp(), updated_by_id: actor.uid });
            if (text) await notifyMentionedUsers(detailTicket, text, eventRef?.id || null);
            setCommentText(defaultMentionForTicket(detailTicket));
            setCommentFiles([]);
            if (commentFileInputRef.current) commentFileInputRef.current.value = "";
        });
    };

    const doArchive = async () => {
        if (!detailTicket) return;
        await run(async () => {
            const ticket = detailTicket;
            await updateDoc(doc(db, "tickets", ticket.id), {
                archived: true,
                updated_at: serverTimestamp(), updated_by_id: actor.uid,
            });
            const [archiveEventRef] = await logEvent(ticket.id, actor, "archived", "Ticket archived");
            const recipients = [...new Set([ticket.assignee_id, ticket.created_by_id].filter(Boolean))]
                .filter((uid) => uid !== actor.uid);
            await Promise.all(recipients.map((uid) => notifyUserSafely(uid, {
                type: "task_archived",
                ticket_id: ticket.id,
                ticket_title: getTitle(ticket),
                event_id: archiveEventRef?.id || null,
                actor_id: actor.uid,
                workflow_stage: getWorkflowStageKey(ticket),
                message: `${nameOf(actor.uid)} archived ${getTitle(ticket)}`,
                dedupe_key: archiveEventRef?.id ? `archived:${archiveEventRef.id}:${uid}` : null,
            })));
            closeDrawer();
        }, "Ticket archived.");
    };


    const renderDetail = (t) => {
        const isCompleted = t.status === "completed";
        const isCurrAssignee = t.assignee_id === effectiveUid;
        const createdEventAttachments = ticketEvents
            .filter((event) => event.event_type === "created")
            .flatMap((event) => Array.isArray(event.meta?.attachments) ? event.meta.attachments : []);
        const taskImageAttachment = t.image
            ? [{
                name: attachmentFilenameFromUrl(t.image, "Task attachment"),
                url: t.image,
                type: "",
                size: 0,
            }]
            : [];
        const drawerAttachments = [
            ...(Array.isArray(t.attachments) ? t.attachments : []),
            ...createdEventAttachments,
            ...taskImageAttachment,
        ]
            .map(normalizeAttachment)
            .filter((attachment) => attachment?.url)
            .filter((attachment, index, list) =>
                list.findIndex((candidate) => candidate?.url === attachment.url) === index
            );

        return (
            <div className="detail-stack">
                <div className="side-panel-tabs detail-log-tabs">
                    <button
                        type="button"
                        className={`side-panel-tab${detailLogTab === "info" ? " active" : ""}`}
                        onClick={() => setDetailLogTab("info")}
                    >
                        Info
                    </button>
                    <button
                        type="button"
                        className={`side-panel-tab${detailLogTab === "rewards" ? " active" : ""}`}
                        onClick={() => setDetailLogTab("rewards")}
                    >
                        Rewards
                    </button>
                    <button
                        type="button"
                        className={`side-panel-tab${detailLogTab === "assignment" ? " active" : ""}`}
                        onClick={() => setDetailLogTab("assignment")}
                    >
                        Assignment ({t.assignment_history?.length || 0})
                    </button>
                    <button
                        type="button"
                        className={`side-panel-tab${detailLogTab === "activity" ? " active" : ""}`}
                        onClick={() => setDetailLogTab("activity")}
                    >
                        Activity ({ticketEvents.length})
                    </button>
                </div>

                {detailLogTab === "info" && (
                    <div className="detail-tab-scroll detail-info-tab">
                        <div className="detail-top">
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                                {isEditingTicket ? (
                                    <input
                                        type="text"
                                        className="form-ctrl"
                                        style={{ fontSize: "1.05rem", fontWeight: 700 }}
                                        value={editTitle}
                                        onChange={(e) => setEditTitle(e.target.value)}
                                        placeholder="Task title"
                                        autoFocus
                                    />
                                ) : (
                                    <h4 className="detail-title" style={{ margin: 0 }}>{getTitle(t)}</h4>
                                )}

                                {canEditTicket(t) && !isEditingTicket && (
                                    <button
                                        className="btn-danger"
                                        style={{ flexShrink: 0, padding: "5px 11px", fontSize: "0.78rem" }}
                                        onClick={() => openEditTicket(t)}
                                    >
                                        Edit
                                    </button>
                                )}
                            </div>

                            {!isEditingTicket && t.building_id && (
                                <TicketReportIdLine buildingId={t.building_id} />
                            )}

                            {isEditingTicket ? (
                                <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
                                    <div className="form-field">
                                        <label className="form-lbl">Description</label>
                                        <textarea
                                            className="form-ctrl" rows={2}
                                            value={editDescription}
                                            onChange={(e) => setEditDescription(e.target.value)}
                                            placeholder="Additional details..."
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-field">
                                            <label className="form-lbl">Assign To</label>
                                            <select
                                                className="form-ctrl"
                                                value={editAssignee ? encodeAssignmentTarget(editAssignee, editAssigneeStage) : ""}
                                                onChange={(e) => {
                                                    const target = decodeAssignmentTarget(e.target.value);
                                                    setEditAssignee(target.uid);
                                                    setEditAssigneeStage(target.stageKey);
                                                    const stage = stageByKey(target.stageKey);
                                                    if (stage?.taskType) setEditTaskType(stage.taskType);
                                                }}
                                            >
                                                <option value="">Select person...</option>
                                                {assignmentOptions.map((option) => (
                                                    <option key={encodeAssignmentTarget(option.uid, option.stageKey)} value={encodeAssignmentTarget(option.uid, option.stageKey)}>
                                                        {option.label}{option.uid === effectiveUid ? " (me)" : ""}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-field">
                                            <label className="form-lbl">Task Type</label>
                                            <select className="form-ctrl" value={editTaskType} onChange={(e) => setEditTaskType(e.target.value)}>
                                                <option value="">—</option>
                                                {TASK_TYPES.map((tt) => (
                                                    <option key={tt.value} value={tt.value}>{tt.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-field">
                                            <label className="form-lbl">Priority</label>
                                            <select className="form-ctrl" value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
                                                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-field">
                                            <label className="form-lbl">Due Date</label>
                                            <input
                                                type="date" className="form-ctrl"
                                                value={editDueDate}
                                                onChange={(e) => setEditDueDate(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-field">
                                        {/* <label className="form-lbl">
                                            Building <span className="form-opt">({isAdmin ? "admin · searchable" : "admin only"})</span>
                                        </label> */}
                                        <label className="form-lbl">Building <span className="form-opt">(can't be changed here)</span></label>
                                        {/* {isAdmin ? (
                                            <Select
                                                options={buildingOptions}
                                                value={editBuilding}
                                                onChange={(value) => setEditBuilding(value)}
                                                placeholder={buildingsLoading ? "Loading buildings..." : "Search for a building/report..."}
                                                isSearchable
                                                isLoading={buildingsLoading}
                                                menuPlacement="auto"
                                                menuPosition="fixed"
                                                menuPortalTarget={document.body}
                                                styles={reportSelectStyles}
                                            />
                                        ) : ( */}
                                        <input
                                            type="text"
                                            className="form-ctrl"
                                            value={editBuilding?.label || t.building_name || "—"}
                                            disabled
                                            style={{ background: "var(--gray-50)", color: "var(--gray-500)" }}
                                        />
                                        {/* )} */}
                                    </div>

                                    <div className="form-field">
                                        <label className="form-lbl">Attachment</label>

                                        {t.image && !editRemoveImage && !editSelectedFile && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                                <img
                                                    src={t.image}
                                                    alt="Current attachment"
                                                    style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid var(--gray-200)" }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn-outline"
                                                    style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                                                    onClick={() => setEditRemoveImage(true)}
                                                >
                                                    Remove image
                                                </button>
                                            </div>
                                        )}

                                        {editRemoveImage && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, fontSize: "0.82rem", color: "var(--gray-500)" }}>
                                                Image will be removed.
                                                <button
                                                    type="button"
                                                    className="btn-outline"
                                                    style={{ padding: "4px 10px", fontSize: "0.78rem" }}
                                                    onClick={() => setEditRemoveImage(false)}
                                                >
                                                    Undo
                                                </button>
                                            </div>
                                        )}

                                        <label className="file-pick">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                    setEditSelectedFile(e.target.files[0]);
                                                    setEditRemoveImage(false);
                                                }}
                                            />
                                            <FontAwesomeIcon icon={faImage} />
                                            <span>{editSelectedFile ? editSelectedFile.name : (t.image ? "Replace image..." : "Choose image...")}</span>
                                        </label>
                                    </div>

                                    <div className="form-actions" style={{ marginTop: 0 }}>
                                        <button className="btn-sec" onClick={closeEditTicket} disabled={actionInProgress}>Cancel</button>
                                        <button
                                            className="btn-prim"
                                            onClick={doSaveTicketEdit}
                                            disabled={actionInProgress || !editTitle.trim() || !editAssignee || !editDueDate}
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="detail-badges-row">
                                        <div className="badge-group">
                                            <span className="badge-group-label">Status:</span>
                                            <StatusBadge status={t.status} />
                                        </div>
                                        <div className="badge-group">
                                            <span className="badge-group-label">Priority:</span>
                                            <PriorityBadge priority={t.priority} />
                                        </div>
                                        {t.task_type && (
                                            <div className="badge-group">
                                                <span className="badge-group-label">Type:</span>
                                                <TaskTypeBadge type={t.task_type} />
                                            </div>
                                        )}
                                    </div>
                                    {t.description && <ExpandableDescription key={`${t.id}-${detailDescriptionExpanded ? "full" : "preview"}`} text={t.description} initiallyExpanded={detailDescriptionExpanded} />}
                                    {drawerAttachments.length > 0 && (
                                        <div style={{ marginTop: 12 }}>
                                            <div style={{ fontSize: "0.76rem", fontWeight: 700, color: "var(--gray-500)", marginBottom: 6 }}>
                                                Attachments ({drawerAttachments.length})
                                            </div>
                                            <AttachmentCards
                                                attachments={drawerAttachments}
                                                onOpenAttachment={(attachment) => {
                                                    if (attachmentIsPdf(attachment)) {
                                                        setAttachmentPreview(attachment);
                                                    } else if (attachmentIsImage(attachment) || attachmentIsVideo(attachment)) {
                                                        setMediaPreview({
                                                            src: attachment.url,
                                                            label: attachment.name || "Attachment",
                                                            isVideo: attachmentIsVideo(attachment),
                                                        });
                                                    } else {
                                                        setAttachmentPreview(attachment);
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {taskLinks.length > 0 && (
                            <div className="links-section">
                                <div className="links-hdr">
                                    <FontAwesomeIcon icon={faLink} size="xs" className="me-2" />Linked To
                                </div>
                                <div className="links-list">
                                    {taskLinks.map((lk) => {
                                        const href = lk.entity_type === "building" && lk.building_id
                                            ? `/doors/${lk.building_id}`
                                            : (lk.source_url || "#");
                                        return (
                                            <a key={lk.id} className="link-chip" href={href}>
                                                <span className="link-type">{lk.entity_type}</span>
                                                <span className="link-label">
                                                    {lk.entity_type === "building" && lk.building_id ? (
                                                        <BuildingNameWithReportId
                                                            buildingId={lk.building_id}
                                                            buildingName={lk.display_label}
                                                            fallback="Building"
                                                        />
                                                    ) : (
                                                        lk.display_label
                                                    )}
                                                </span>
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="detail-meta-grid">
                            <div className="meta-cell">
                                <label>Created by</label>
                                <div className="user-cell">
                                    <Avatar name={nameOf(t.created_by_id)} small />
                                    <span>{nameOf(t.created_by_id)}</span>
                                </div>
                            </div>
                            <div className="meta-cell">
                                <label>Assigned to</label>
                                <div className="user-cell">
                                    <Avatar name={nameOf(t.assignee_id)} small />
                                    <span>{nameOf(t.assignee_id)}</span>
                                </div>
                            </div>
                            {(effectiveRole === "inspection" || (t.schedule_ids || []).some(Boolean)) && (
                                <div className="meta-cell wide">
                                    <label>
                                        {effectiveRole === "inspection" ? (
                                            <>Schedule <span className="form-opt">(up to {MAX_SCHEDULES_PER_TICKET} visits)</span></>
                                        ) : `Scheduled visit${(t.schedule_ids || []).filter(Boolean).length === 1 ? "" : "s"}`}
                                    </label>
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                                        <TicketScheduleBadges
                                            ticket={t}
                                            role={effectiveRole}
                                            nameOf={nameOf}
                                            onOpen={(tk, idx) => openCalendarModal(tk, idx)}
                                        />
                                    </div>
                                </div>
                            )}
                            {getDueAt(t) && (
                                <div className="meta-cell">
                                    <label>Due date</label>
                                    <div className={`detail-due${isOverdue(t) ? " overdue" : ""}`}>
                                        <FontAwesomeIcon icon={faCalendar} />
                                        {fmtTimestamp(getDueAt(t))}
                                        {isOverdue(t) && <span className="overdue-badge">Overdue</span>}
                                    </div>
                                </div>
                            )}
                            <div className="meta-cell">
                                <label>Created</label>
                                <span style={{ fontSize: "0.82rem", color: "var(--gray-500)" }}>{fmtTimestamp(getCreatedAt(t))}</span>
                            </div>
                            <TimeSpentPanel ticket={t} nameOf={nameOf} currentUid={effectiveUid} isAdmin={isAdmin} />
                            {t.blocked_reason && (
                                <div className="meta-cell wide">
                                    <label>Waiting On</label>
                                    {t.waiting_on_type && <span className="waiting-tag">{t.waiting_on_type}</span>}
                                    <p className="mb-1 mt-2">{t.blocked_reason}</p>
                                    {t.responsible_person_id && (
                                        <div className="user-cell mt-1">
                                            <Avatar name={nameOf(t.responsible_person_id)} small />
                                            <span>Referred to: {nameOf(t.responsible_person_id)}</span>
                                        </div>
                                    )}
                                    {(t.waiting_started_at || t.status_changed_at) && (
                                        <p className="stuck-followup mt-2 mb-0">
                                            <FontAwesomeIcon icon={faClock} size="xs" className="me-1" />
                                            Waiting since: {fmtTimestamp(t.waiting_started_at || t.status_changed_at)}
                                        </p>
                                    )}
                                    {t.next_follow_up_at && (
                                        <p className="stuck-followup mt-1 mb-0">
                                            <FontAwesomeIcon icon={faClock} size="xs" className="me-1" />
                                            Next chase: {fmtTimestamp(t.next_follow_up_at)}
                                        </p>
                                    )}
                                    {t.stuck_notes && (
                                        <p className="mb-0 mt-2" style={{ fontSize: "0.82rem", color: "var(--gray-600)" }}>
                                            <strong>Notes:</strong> {t.stuck_notes}
                                        </p>
                                    )}
                                </div>
                            )}
                            {t.completed_at && (
                                <div className="meta-cell wide">
                                    <label>Completed</label>
                                    <div style={{ color: "#10b981", fontSize: "0.88rem", fontWeight: 600 }}>
                                        <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                                        {fmtTimestamp(t.completed_at)}
                                    </div>
                                </div>
                            )}
                            {t.status === "ready" && isCurrAssignee && (
                                <div className="detail-start-row">
                                    <button className="btn-start-inline" disabled={actionInProgress}
                                        onClick={() => { doStart(t); closeDrawer(); }}>
                                        <FontAwesomeIcon icon={faPlay} className="me-2" />Start This Task
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {detailLogTab === "rewards" && (
                    <div className="detail-tab-scroll detail-rewards-tab">
                        <div className="rw-drawer-rewards">
                            <TicketRewardIndicator ticket={t} userId={effectiveUid} />
                            <RewardClassificationPanel ticket={t} userId={effectiveUid} />
                        </div>
                    </div>
                )}

                {detailLogTab === "assignment" && (t.assignment_history?.length > 0) && (
                    <div className="detail-tab-scroll assign-history-section">
                        <div className="assign-history-hdr collapsible-hdr" onClick={() => setAssignHistoryOpen((o) => !o)}>
                            <span>
                                <FontAwesomeIcon icon={faHistory} size="xs" className="me-2" />
                                Assignment History
                            </span>
                            <FontAwesomeIcon icon={assignHistoryOpen ? faChevronDown : faChevronRight} size="xs" />
                        </div>
                        {assignHistoryOpen && (
                            <div className="assign-history-list">
                                {[...t.assignment_history].reverse().map((entry, i) => {
                                    const isCurrent = !entry.unassigned_at;
                                    const badge = isCurrent
                                        ? { text: "Current", bg: "rgba(16,185,129,0.12)", color: "#059669" }
                                        : entry.direction === "back"
                                            ? { text: "↩ Amendment Required", bg: "rgba(245,158,11,0.14)", color: "#b45309" }
                                            : entry.direction === "forward"
                                                ? { text: "→ Stage completed & forwarded", bg: "rgba(59,130,246,0.12)", color: "#2563eb" }
                                                : entry.direction === "transfer"
                                                    ? { text: "Transferred", bg: "rgba(100,116,139,0.12)", color: "#475569" }
                                                    : entry.direction === "completed"
                                                        ? { text: "Completed", bg: "rgba(16,185,129,0.12)", color: "#059669" }
                                                        : null;
                                    return (
                                        <div key={i}
                                            style={{
                                                display: "flex", gap: 12, padding: "12px 4px",
                                                borderBottom: i < t.assignment_history.length - 1 ? "1px solid var(--gray-100)" : "none",
                                            }}
                                        >
                                            <div style={{
                                                width: 3, borderRadius: 2, flexShrink: 0,
                                                background: isCurrent ? "#10b981" : entry.direction === "back" ? "#f59e0b" : entry.direction === "forward" ? "#3b82f6" : entry.direction === "transfer" ? "#64748b" : entry.direction === "completed" ? "#10b981" : "var(--gray-200)",
                                            }} />
                                            <Avatar name={nameOf(entry.assignee_id)} small />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                    <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "var(--gray-800)" }}>
                                                        {nameOf(entry.assignee_id)}
                                                    </span>
                                                    {badge && (
                                                        <span style={{
                                                            fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px",
                                                            borderRadius: "100px", background: badge.bg, color: badge.color,
                                                        }}>
                                                            {badge.text}
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: "0.76rem", color: "var(--gray-400)", marginTop: 2 }}>
                                                    {fmtTimestamp(parseDate(entry.assigned_at))}
                                                    {entry.unassigned_at && <> → {fmtTimestamp(parseDate(entry.unassigned_at))}</>}
                                                    {entry.assigned_by && <> · assigned by {nameOf(entry.assigned_by)}</>}
                                                </div>
                                                {entry.work_note && (
                                                    <div style={{
                                                        marginTop: 6, fontSize: "0.82rem", color: "#065f46",
                                                        background: "rgba(16,185,129,0.08)", borderLeft: "3px solid #10b981",
                                                        borderRadius: 4, padding: "6px 10px",
                                                    }}>
                                                        <FontAwesomeIcon icon={faCircleCheck} size="xs" className="me-2" />
                                                        {entry.work_note}
                                                    </div>
                                                )}
                                                {entry.reason && (
                                                    <div style={{
                                                        marginTop: 6, fontSize: "0.82rem", color: "#92400e",
                                                        background: "rgba(245,158,11,0.1)", borderLeft: "3px solid #f59e0b",
                                                        borderRadius: 4, padding: "6px 10px",
                                                    }}>
                                                        <FontAwesomeIcon icon={faExclamationTriangle} size="xs" className="me-2" />
                                                        Amendment Required: {entry.reason}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {detailLogTab === "activity" && (
                    <div className="detail-tab-scroll event-log">
                        <h6 className="collapsible-hdr" onClick={() => setActivityLogOpen((o) => !o)}>
                            <span><FontAwesomeIcon icon={faClock} className="me-2" />Activity Log</span>
                            <FontAwesomeIcon icon={activityLogOpen ? faChevronDown : faChevronRight} size="xs" />
                        </h6>
                        {activityLogOpen && (
                            <div className="timeline">
                                {ticketEvents.length === 0 && (
                                    <p className="text-muted text-center py-3" style={{ fontSize: "0.84rem" }}>
                                        No activity yet.
                                    </p>
                                )}
                                {ticketEvents.map((ev) => {
                                    const c = EVENT_CFG[ev.event_type] || EVENT_CFG.comment;
                                    const meta = ev.meta || {};
                                    const hasMeta = meta && Object.keys(meta).length > 0;
                                    return (
                                        <div
                                            key={ev.id}
                                            ref={(el) => { if (el) eventRowRefs.current[ev.id] = el; else delete eventRowRefs.current[ev.id]; }}
                                            className={`tl-item${highlightEventId === ev.id ? " timeline-event-highlight" : ""}`}
                                        >
                                            <div className="tl-icon"
                                                style={{ backgroundColor: c.color + "20", color: c.color }}>
                                                <FontAwesomeIcon icon={c.icon} size="xs" />
                                            </div>
                                            <div className="tl-body">
                                                <div className="tl-hdr">
                                                    <span className="tl-actor">{nameOf(ev.user_id)}</span>
                                                    <span className="tl-time">{fmtTimestamp(ev.created_at || ev.timestamp)}</span>
                                                </div>

                                                {ev.event_type === "reassigned" && hasMeta ? (
                                                    <>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                                                            <span style={{
                                                                fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: "100px",
                                                                background: meta.direction === "back" ? "rgba(245,158,11,0.14)" : meta.direction === "transfer" ? "rgba(100,116,139,0.12)" : "rgba(59,130,246,0.12)",
                                                                color: meta.direction === "back" ? "#b45309" : meta.direction === "transfer" ? "#475569" : "#2563eb",
                                                            }}>
                                                                {meta.direction === "back" ? "↩ Amendment Required" : meta.direction === "transfer" ? "Transferred" : "→ Stage completed & forwarded"}
                                                            </span>
                                                            <span style={{ fontSize: "0.82rem", color: "var(--gray-700)" }}>
                                                                {nameOf(meta.from_uid)}
                                                                <FontAwesomeIcon icon={faArrowRight} size="xs" style={{ color: "#94a3b8", margin: "0 5px" }} />
                                                                {nameOf(meta.to_uid)}
                                                            </span>
                                                        </div>
                                                        {meta.due_at && (
                                                            <div style={{ fontSize: "0.78rem", color: "var(--gray-500)", marginTop: 4 }}>
                                                                <FontAwesomeIcon icon={faCalendar} size="xs" className="me-1" />Due {fmtTimestamp(meta.due_at)}
                                                            </div>
                                                        )}
                                                        {meta.work_note && (
                                                            <div style={{
                                                                marginTop: 6, fontSize: "0.82rem", color: "#065f46",
                                                                background: "rgba(16,185,129,0.08)", borderLeft: "3px solid #10b981",
                                                                borderRadius: 4, padding: "6px 10px",
                                                            }}>
                                                                <FontAwesomeIcon icon={faCircleCheck} size="xs" className="me-2" />{meta.work_note}
                                                            </div>
                                                        )}
                                                        {meta.reason && (
                                                            <div style={{
                                                                marginTop: 6, fontSize: "0.82rem", color: "#92400e",
                                                                background: "rgba(245,158,11,0.1)", borderLeft: "3px solid #f59e0b",
                                                                borderRadius: 4, padding: "6px 10px",
                                                            }}>
                                                                <FontAwesomeIcon icon={faExclamationTriangle} size="xs" className="me-2" />{meta.reason}
                                                            </div>
                                                        )}
                                                        {meta.handover_checklist && (
                                                            <div style={{
                                                                marginTop: 6, fontSize: "0.78rem", color: "var(--gray-600)",
                                                                background: "var(--gray-50)", border: "1px solid var(--gray-100)",
                                                                borderRadius: 4, padding: "6px 10px", display: "grid", gap: 2,
                                                            }}>
                                                                <div style={{ fontWeight: 700, color: "var(--gray-500)", marginBottom: 2 }}>Handover checklist</div>
                                                                {HANDOVER_CHECKLIST_FIELDS.map((f) => {
                                                                    const entry = meta.handover_checklist[f.key];
                                                                    if (!entry) return null;
                                                                    return (
                                                                        <div key={f.key}>
                                                                            <strong>{f.question}</strong> {entry.value ? entry.value : (entry.na ? "N/A" : "—")}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : ev.event_type === "email_sent" && hasMeta ? (
                                                    <div style={{
                                                        marginTop: 4, fontSize: "0.82rem", color: "var(--gray-700)",
                                                        background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.25)",
                                                        borderRadius: 6, padding: "8px 10px", display: "grid", gap: 3,
                                                    }}>
                                                        {meta.to && <div><strong>To:</strong> {meta.to}</div>}
                                                        {meta.subject && <div><strong>Subject:</strong> {meta.subject}</div>}
                                                        {meta.notes && <div><strong>Notes:</strong> {meta.notes}</div>}
                                                    </div>
                                                ) : ev.event_type === "blocked" && hasMeta ? (
                                                    <div style={{
                                                        marginTop: 4, fontSize: "0.82rem", color: "var(--gray-700)",
                                                        background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)",
                                                        borderRadius: 6, padding: "8px 10px", display: "grid", gap: 3,
                                                    }}>
                                                        {meta.waiting_on_type && <div><strong>Waiting on:</strong> {meta.waiting_on_type}</div>}
                                                        {meta.responsible_uid && <div><strong>Referred to:</strong> {nameOf(meta.responsible_uid)}</div>}
                                                        {meta.next_chase_at && <div><strong>Next chase:</strong> {fmtTimestamp(meta.next_chase_at)}</div>}
                                                        {meta.notes && <div><strong>Notes:</strong> {meta.notes}</div>}
                                                    </div>
                                                ) : ev.event_type === "completed" && meta.work_note ? (
                                                    <>
                                                        {ev.from_status && ev.to_status && (
                                                            <div className="tl-transition">
                                                                <StatusBadge status={ev.from_status} />
                                                                <FontAwesomeIcon icon={faArrowRight} size="xs" style={{ color: "#94a3b8" }} />
                                                                <StatusBadge status={ev.to_status} />
                                                                {typeof ev.duration_seconds === "number" && (
                                                                    <span className="tl-duration">spent {formatDuration(ev.duration_seconds)}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div style={{
                                                            marginTop: 6, fontSize: "0.82rem", color: "#065f46",
                                                            background: "rgba(16,185,129,0.08)", borderLeft: "3px solid #10b981",
                                                            borderRadius: 4, padding: "6px 10px",
                                                        }}>
                                                            <FontAwesomeIcon icon={faCircleCheck} size="xs" className="me-2" />{meta.work_note}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        {ev.from_status && ev.to_status && (
                                                            <div className="tl-transition">
                                                                <StatusBadge status={ev.from_status} />
                                                                <FontAwesomeIcon icon={faArrowRight} size="xs" style={{ color: "#94a3b8" }} />
                                                                <StatusBadge status={ev.to_status} />
                                                                {typeof ev.duration_seconds === "number" && (
                                                                    <span className="tl-duration">
                                                                        spent {formatDuration(ev.duration_seconds)} in {STATUS_CFG[ev.from_status]?.label || ev.from_status}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="tl-note">{ev.note || ev.detail}</div>
                                                    </>
                                                )}
                                                <AttachmentCards
                                                    attachments={meta.attachments}
                                                    onOpenAttachment={(attachment) => {
                                                        if (attachmentIsPdf(attachment)) {
                                                            setAttachmentPreview(attachment);
                                                        } else if (attachmentIsImage(attachment) || attachmentIsVideo(attachment)) {
                                                            setMediaPreview({
                                                                src: attachment.url,
                                                                label: attachment.name || "Attachment",
                                                                isVideo: attachmentIsVideo(attachment),
                                                            });
                                                        } else {
                                                            setAttachmentPreview(attachment);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

            </div>
        );
    };

    const renderStuckCard = (ticket, actionsNode) => {
        const isChasingForOther = ticket.responsible_person_id === effectiveUid && ticket.assignee_id !== effectiveUid;
        return (
            <div
                key={ticket.id}
                ref={(el) => { if (el) stuckCardRefs.current[ticket.id] = el; else delete stuckCardRefs.current[ticket.id]; }}
                className={`stuck-card is-ticket-clickable${highlightStuckTicketId === ticket.id ? " stuck-card-highlight" : ""}`}
                onClick={(event) => handleTicketSurfaceClick(event, ticket)}
            >
                <div className="stuck-card-title">
                    {getTitle(ticket)}
                </div>

                {ticket.waiting_on_type && (
                    <span className="waiting-tag">{ticket.waiting_on_type}</span>
                )}

                {ticket.blocked_reason && (
                    <p className="stuck-reason">{ticket.blocked_reason}</p>
                )}

                {isChasingForOther ? (
                    <p className="stuck-reason mb-1" style={{ fontSize: "0.78rem", color: "#b45309" }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} size="xs" className="me-1" />
                        Referred to you — assigned to {nameOf(ticket.assignee_id)}
                    </p>
                ) : (
                    ticket.responsible_person_id && (
                        <p className="stuck-reason mb-1" style={{ fontSize: "0.78rem" }}>
                            Referred to: {nameOf(ticket.responsible_person_id)}
                        </p>
                    )
                )}

                {(ticket.waiting_started_at || ticket.status_changed_at) && (
                    <p className="stuck-followup" style={{ marginBottom: 2 }}>
                        <FontAwesomeIcon icon={faClock} size="xs" className="me-1" />
                        Waiting since: {fmtTimestamp(ticket.waiting_started_at || ticket.status_changed_at)}
                    </p>
                )}

                {ticket.next_follow_up_at && (
                    <p className="stuck-followup">
                        <FontAwesomeIcon icon={faClock} size="xs" className="me-1" />
                        Next chase: {fmtTimestamp(ticket.next_follow_up_at)}
                    </p>
                )}

                <div className="d-flex gap-2 flex-wrap">
                    {actionsNode}
                </div>
            </div>
        );
    };

    const renderActivityTab = () => (
        <div className="activity-tab-shell">
            <div className="activity-filter-bar">
                <div className="activity-filter-search">
                    <FontAwesomeIcon icon={faSearch} size="xs" />
                    <input
                        type="text"
                        value={activitySearchQuery}
                        onChange={(e) => setActivitySearchQuery(e.target.value)}
                        placeholder="Search activity..."
                    />
                    {activitySearchQuery && (
                        <button type="button" onClick={() => setActivitySearchQuery("")}>
                            <FontAwesomeIcon icon={faTimes} size="xs" />
                        </button>
                    )}
                </div>
                <select className="activity-filter-select" value={activityTypeFilter} onChange={(e) => setActivityTypeFilter(e.target.value)}>
                    <option value="all">All activity</option>
                    {activityTypeOptions.map((type) => (
                        <option key={type} value={type}>{String(type).replace(/_/g, " ")}</option>
                    ))}
                </select>
                <select className="activity-filter-select" value={activityUserFilter} onChange={(e) => setActivityUserFilter(e.target.value)}>
                    <option value="all">Everyone</option>
                    {activityUserOptions.map((person) => (
                        <option key={person.uid} value={person.uid}>{person.name}</option>
                    ))}
                </select>
            </div>

            {filteredActivityFeed.length === 0 ? (
                <div className="activity-empty">
                    <p>No activity matches these filters.</p>
                </div>
            ) : (
                <div className="activity-scroll side-panel-scroll">
                    {visibleActivityFeed.map((ev, i) => {
                        const c = EVENT_CFG[ev.event_type] || EVENT_CFG.comment;
                        const ticket = ticketsById[ev.task_id];
                        return (
                            <div
                                key={`${ev.task_id}-${ev.created_at_client}-${i}`}
                                className={`activity-entry${ev.task_id ? " is-ticket-link" : ""}`}
                                role={ev.task_id ? "button" : undefined}
                                tabIndex={ev.task_id ? 0 : undefined}
                                onClick={() => ev.task_id && openDetailById(ev.task_id)}
                                onKeyDown={(event) => {
                                    if (!ev.task_id || (event.key !== "Enter" && event.key !== " ")) return;
                                    event.preventDefault();
                                    openDetailById(ev.task_id);
                                }}
                                title={ev.task_id ? "Open task details" : undefined}
                            >
                                <div className="activity-icon" style={{ backgroundColor: c.color + "20", color: c.color }}>
                                    <FontAwesomeIcon icon={c.icon} size="xs" />
                                </div>
                                <div className="activity-body">
                                    <div className="activity-top">
                                        <span className="activity-actor">{nameOf(ev.user_id)}</span>
                                        <span className="activity-time">{fmtTimestamp(ev.created_at_client)}</span>
                                    </div>
                                    {ticket && <div className="activity-ticket">{getTitle(ticket)}</div>}
                                    <div className="activity-note">{ev.note}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {filteredActivityFeed.length > activityDisplayLimit && (
                <div className="activity-load-more">
                    <button type="button" className="filter-clear-btn" onClick={() => setActivityDisplayLimit((value) => Math.min(ACTIVITY_MAX_LIMIT, value + ACTIVITY_DISPLAY_LIMIT))}>
                        Load {Math.min(ACTIVITY_DISPLAY_LIMIT, filteredActivityFeed.length - activityDisplayLimit)} more
                    </button>
                </div>
            )}
        </div>
    );

    const renderCoordinatorBookingPanel = () => {
        if (effectiveRole !== "coordinator") return null;

        const hasActiveTicket = Boolean(
            activeTicket
            && activeTicket.status === "active"
            && activeTicket.active_by_id === effectiveUid
            && isCoordinatorQcTicket(activeTicket)
        );
        const panelTicket = hasActiveTicket ? activeTicket : null;
        const info = panelTicket ? bookingInfoForTicket(panelTicket) : null;
        const bookings = info?.bookings || [];
        const totalBookings = info?.totalBookings ?? bookings.length ?? 0;

        return (
            <div
                className="mywork-grid-report"
                style={{
                    background: "white",
                    border: "1px solid var(--gray-200)",
                    borderRadius: "var(--r-lg)",
                    boxShadow: "var(--sh-sm)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    height: activeCardHeight ? `${activeCardHeight}px` : "100%",
                    minHeight: activeCardHeight ? `${activeCardHeight}px` : 0,
                    maxHeight: activeCardHeight ? `${activeCardHeight}px` : undefined,
                    alignSelf: "stretch",
                }}
            >
                <div
                    style={{
                        padding: "14px 16px",
                        borderBottom: "1px solid var(--gray-100)",
                        flexShrink: 0,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                        }}
                    >
                        <div>
                            <strong
                                style={{
                                    display: "block",
                                    fontSize: "0.94rem",
                                    color: "var(--gray-800)",
                                }}
                            >
                                Resident Bookings
                            </strong>
                            <p
                                style={{
                                    margin: "2px 0 0",
                                    fontSize: "0.82rem",
                                    color: "var(--gray-500)",
                                }}
                            >
                                Bookings across all attached calendar days.
                            </p>
                        </div>

                        <span
                            style={{
                                minWidth: 34,
                                height: 34,
                                padding: "0 10px",
                                borderRadius: 999,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(37,99,235,0.1)",
                                color: "#2563eb",
                                fontSize: "0.82rem",
                                fontWeight: 800,
                                flexShrink: 0,
                            }}
                        >
                            {bookingsLoading && hasActiveTicket ? "…" : totalBookings}
                        </span>
                    </div>
                </div>

                {!panelTicket ? (
                    <div
                        className="activity-empty"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                        }}
                    >
                        <p style={{ margin: 0 }}>Start a task from your queue to show its live resident bookings.</p>
                    </div>
                ) : (
                    <div style={{ padding: "12px", flex: 1, minHeight: 0, overflowY: "auto" }}>
                        <button
                            type="button"
                            onClick={() => openCoordinatorBookingModal(panelTicket)}
                            style={{
                                width: "100%",
                                border: "1px solid var(--gray-200)",
                                borderRadius: 10,
                                background: "#fff",
                                padding: "12px 14px",
                                cursor: "pointer",
                                textAlign: "left",
                                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 12,
                                }}
                            >
                                <div style={{ minWidth: 0 }}>
                                    <div
                                        style={{
                                            fontSize: "0.9rem",
                                            fontWeight: 700,
                                            color: "var(--gray-800)",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {info?.buildingName || panelTicket.building_name || "Building"}
                                    </div>
                                    <div
                                        style={{
                                            marginTop: 2,
                                            fontSize: "0.78rem",
                                            color: "var(--gray-500)",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {getTitle(panelTicket)}
                                    </div>
                                </div>

                                <span
                                    style={{
                                        flexShrink: 0,
                                        padding: "4px 9px",
                                        borderRadius: 999,
                                        background: "#eff6ff",
                                        color: "#2563eb",
                                        fontSize: "0.74rem",
                                        fontWeight: 800,
                                    }}
                                >
                                    {bookingsLoading
                                        ? "Loading…"
                                        : `${totalBookings} booking${totalBookings === 1 ? "" : "s"}`}
                                </span>
                            </div>
                        </button>
                    </div>
                )}
            </div>
        );
    };

    const renderRoleSidePanel = (roleKey, actionsForTicket, headerExtra) => (
        <div className={`side-panel mywork-grid-stuck role-panel-${roleKey}`}>
            {headerExtra}
            <div className="side-panel-tabs">
                <button
                    className={`side-panel-tab${sidePanelTab === "stuck" ? " active" : ""}`}
                    onClick={() => setSidePanelTab("stuck")}
                >
                    Stuck / Waiting On
                    <span
                        className="side-panel-tab-count warn"
                        title={`${visibleWaitingTickets.length} shown after search/date filters`}
                    >
                        {visibleWaitingTickets.length}
                    </span>
                </button>
                <button
                    className={`side-panel-tab${sidePanelTab === "activity" ? " active" : ""}`}
                    onClick={() => setSidePanelTab("activity")}
                >
                    Recent Activity
                    {filteredActivityFeed.length > 0 && (
                        <span
                            className="side-panel-tab-count"
                            title={`${visibleActivityFeed.length} loaded of ${filteredActivityFeed.length} recent activities`}
                        >
                            {visibleActivityFeed.length}
                        </span>
                    )}
                </button>
            </div>

            {sidePanelTab === "stuck" ? (
                <>
                    <div className="stuck-filter-bar">
                        <div className="stuck-filter-search">
                            <button
                                type="button"
                                className="stuck-search-action"
                                onClick={() => stuckSearchInputRef.current?.focus()}
                                title="Search stuck tasks"
                                aria-label="Search stuck tasks"
                            >
                                <FontAwesomeIcon icon={faSearch} size="xs" />
                            </button>
                            <input
                                ref={stuckSearchInputRef}
                                type="text"
                                value={stuckSearchQuery}
                                onChange={(e) => setStuckSearchQuery(e.target.value)}
                                placeholder="Search stuck tasks..."
                            />
                            {stuckSearchQuery && (
                                <button type="button" onClick={() => setStuckSearchQuery("")} title="Clear search">
                                    <FontAwesomeIcon icon={faTimes} size="xs" />
                                </button>
                            )}
                        </div>
                        <select
                            className="stuck-filter-select"
                            value={stuckDateFilter}
                            onChange={(e) => setStuckDateFilter(e.target.value)}
                            title="Next chase date"
                            aria-label="Next chase date"
                        >
                            {stuckDateOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label} ({option.count})
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="stuck-sort-toggle"
                            onClick={() => setStuckSortDir((current) => current === "asc" ? "desc" : "asc")}
                            title={`Sort by next chase date ${stuckSortDir === "asc" ? "descending" : "ascending"}`}
                            aria-label={`Sort by next chase date ${stuckSortDir === "asc" ? "descending" : "ascending"}`}
                            aria-pressed={stuckSortDir === "desc"}
                        >
                            Next chase {stuckSortDir === "asc" ? "↑" : "↓"}
                        </button>
                    </div>
                    {visibleWaitingTickets.length === 0 ? (
                        <div className="activity-empty">
                            <p>{waitingTickets.length === 0 ? "Nothing to show here." : "No stuck tasks match this search/date filter."}</p>
                        </div>
                    ) : (
                        <div className="stuck-scroll side-panel-scroll">
                            {visibleWaitingTickets.map((ticket) =>
                                renderStuckCard(ticket, actionsForTicket(ticket))
                            )}
                        </div>
                    )}
                </>
            ) : (
                renderActivityTab()
            )}
        </div>
    );

    const unblockButton = (ticket) => (
        <button
            className="btn-unblock"
            disabled={actionInProgress}
            onClick={() => setConfirmAction({
                title: "Confirm Unblock",
                message: `Return "${getTitle(ticket)}" to the queue?`,
                onConfirm: () => doUnblock(ticket),
            })}
        >
            <FontAwesomeIcon icon={faRotateLeft} size="xs" className="me-1" />Unblock
        </button>
    );

    const activateStuckButton = (ticket) => {
        const canActivate = (ticket.responsible_person_id || ticket.assignee_id) === effectiveUid;
        return (
            <button
                className="btn-stuck-start"
                disabled={actionInProgress || !canActivate}
                onClick={() => activateFromStuck(ticket)}
                title={canActivate ? "Make this your active task now" : "This task is not assigned/referred to you"}
            >
                <FontAwesomeIcon icon={faPlay} size="xs" className="me-1" />Make active
            </button>
        );
    };

    const reassignButton = (ticket) => (
        <button className="btn-unblock" onClick={() => openReassignModal(ticket)} disabled={actionInProgress}>
            <FontAwesomeIcon icon={faUserCheck} size="xs" className="me-1" />Reassign
        </button>
    );

    const reviewButton = (ticket) => (
        <button
            className="btn-unblock"
            disabled={actionInProgress || !ticket.building_id}
            onClick={() => ticket.building_id && setDoorReviewModal({ buildingId: ticket.building_id, doorId: null })}
        >
            <FontAwesomeIcon icon={faClipboardCheck} size="xs" className="me-1" />Review
        </button>
    );

    const inspectionActions = (ticket) => (
        <>
            {activateStuckButton(ticket)}
            {unblockButton(ticket)}
            {reassignButton(ticket)}
        </>
    );

    const reviewerActions = (ticket) => (
        <>
            {activateStuckButton(ticket)}
            {unblockButton(ticket)}
        </>
    );

    const coordinatorActions = (ticket) => (
        <>
            {activateStuckButton(ticket)}
            {unblockButton(ticket)}
            {reassignButton(ticket)}
        </>
    );


    const remedialActions = (ticket) => (
        <>
            {activateStuckButton(ticket)}
            {unblockButton(ticket)}
        </>
    );

    const managerActions = (ticket) => (
        <>
            {activateStuckButton(ticket)}
            {unblockButton(ticket)}
            {reassignButton(ticket)}
        </>
    );

    const defaultActions = (ticket) => (
        <>
            {activateStuckButton(ticket)}
            {unblockButton(ticket)}
            {reassignButton(ticket)}
        </>
    );

    const renderSidePanelForRole = () => {
        switch (effectiveRole) {
            case "inspection":
                return renderRoleSidePanel("inspection", inspectionActions, null);
            case "reviewer":
                return renderRoleSidePanel("reviewer", reviewerActions, null);
            case "coordinator":
                return renderRoleSidePanel("coordinator", coordinatorActions, null);
            case "remedial":
                return renderRoleSidePanel("remedial", remedialActions, null);
            case "manager":
                return renderRoleSidePanel("manager", managerActions, null);
            default:
                return renderRoleSidePanel("default", defaultActions, null);
        }
    };


    const bookingCellStyle = {
        padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
        color: "#374151",
        verticalAlign: "middle",
    };

    return (
        <div className={`tickets-theme${activeTab === "mywork" ? " mywork-view" : activeTab === "all" ? " alltasks-view" : activeTab === "rewards" ? " rewards-view" : ""}`}>
            <style>{`
                .modal-box { max-height: 88vh; overflow-y: auto; }
                .media-modal-overlay {
                    position: fixed; inset: 0; z-index: 10100; display: flex; align-items: center;
                    justify-content: center; padding: 20px; background: rgba(2, 6, 23, 0.82);
                    backdrop-filter: blur(4px);
                }
                .media-modal-container {
                    max-width: 1000px; max-height: 85vh; border-radius: 10px; overflow: hidden;
                    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.45); background: #1a1a1a;
                }
                .image-viewer-container {
                    width: 100%; height: 100%; overflow: hidden; position: relative;
                    border-radius: 8px; background: #1a1a1a; touch-action: none;
                }
                .media-viewer-topbar {
                    position: absolute; top: 0; left: 0; right: 0; z-index: 6;
                    display: flex; align-items: center; justify-content: space-between; gap: 12px;
                    padding: 10px 12px 28px;
                    background: linear-gradient(to bottom, rgba(0,0,0,0.72), rgba(0,0,0,0));
                    pointer-events: none;
                }
                .media-viewer-title-wrap {
                    min-width: 0; display: flex; align-items: center; gap: 8px; pointer-events: auto;
                }
                .media-viewer-type {
                    flex: 0 0 auto; padding: 2px 8px; border-radius: 999px;
                    background: rgba(255,255,255,0.18); backdrop-filter: blur(8px);
                    color: #fff; font-size: 0.62rem; font-weight: 800; text-transform: uppercase;
                    letter-spacing: 0.05em;
                }
                .media-viewer-title {
                    min-width: 0; max-width: min(68vw, 720px); overflow: hidden; text-overflow: ellipsis;
                    white-space: nowrap; color: rgba(255,255,255,0.9); font-size: 0.78rem; font-weight: 600;
                }
                .media-viewer-controls {
                    position: absolute; right: 14px; bottom: 14px; z-index: 7;
                    display: flex; align-items: center; gap: 8px;
                }
                .media-viewer-icon-btn {
                    width: 36px; height: 36px; padding: 0; border-radius: 50%; display: inline-flex;
                    align-items: center; justify-content: center; border: 1px solid rgba(255,255,255,0.2);
                    background: rgba(0,0,0,0.7); backdrop-filter: blur(8px); color: #fff;
                    cursor: pointer; line-height: 1;
                }
                .media-viewer-icon-btn:hover { background: rgba(0,0,0,0.86); }
                .media-viewer-close {
                    width: 32px; height: 32px; font-size: 1.3rem; font-weight: 400; pointer-events: auto;
                }
                .media-viewer-zoom {
                    min-width: 62px; height: 36px; padding: 0 12px; border-radius: 999px;
                    display: inline-flex; align-items: center; justify-content: center;
                    border: 1px solid rgba(255,255,255,0.2); background: rgba(0,0,0,0.7);
                    backdrop-filter: blur(8px); color: #fff; font-size: 0.78rem; font-weight: 600;
                }
                @media (max-width: 640px) {
                    .media-modal-overlay { padding: 10px; }
                    .media-modal-container { width: 96vw !important; height: 82vh !important; }
                    .media-viewer-title { max-width: 56vw; }
                    .media-viewer-controls { right: 10px; bottom: 10px; gap: 6px; }
                    .media-viewer-icon-btn { width: 34px; height: 34px; }
                    .media-viewer-zoom { min-width: 54px; height: 34px; padding: 0 9px; }
                }
                .attachment-preview-modal {
                    width: min(94vw, 980px); height: min(88vh, 820px); max-width: none;
                    display: flex; flex-direction: column; overflow: hidden; padding: 0;
                    background: #fff;
                }
                .attachment-preview-hdr {
                    flex: 0 0 auto; display: flex; align-items: center; gap: 12px;
                    padding: 14px 16px; border-bottom: 1px solid #e5e7eb;
                }
                .attachment-preview-body {
                    flex: 1 1 auto; min-height: 0; display: flex; align-items: center;
                    justify-content: center; overflow: hidden; background: #f8fafc;
                }
                .attachment-preview-body img {
                    display: block; max-width: 100%; max-height: 100%; object-fit: contain;
                }
                .attachment-preview-body iframe {
                    display: block; width: 100%; height: 100%; border: 0; background: #fff;
                }
                .attachment-preview-fallback {
                    max-width: 520px; padding: 28px; text-align: center; color: #475569;
                }
                .attachment-preview-actions {
                    flex: 0 0 auto; display: flex; justify-content: flex-end; gap: 8px;
                    padding: 12px 16px; border-top: 1px solid #e5e7eb; background: #fff;
                }
                .mywork-grid > .mywork-grid-active,
                .mywork-grid > .mywork-grid-report { align-self: stretch; }
                .mywork-grid > .mywork-grid-report { height: 100%; min-height: 0; }
                .queue-row-highlight, .stuck-card-highlight, .timeline-event-highlight {
                    animation: taskHighlight 1s ease-in-out 0s 3;
                    outline: 2px solid #b11111; outline-offset: -2px;
                    background: rgba(177,17,17,0.06) !important;
                }
                .timeline-event-highlight { border-radius: 8px; }
                @keyframes taskHighlight { 0%,100% { background: rgba(177,17,17,0.04); } 50% { background: rgba(177,17,17,0.15); } }
                .tk-header-row { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
                .tk-header-copy { min-width: 0; display: flex; align-items: baseline; gap: 14px; flex-wrap: wrap; }
                .tk-header-copy .tk-title, .tk-header-copy .tk-subtitle { margin-bottom: 0; }
                .tk-header-actions { display: flex; align-items: center; justify-content: flex-end; gap: 9px; flex-wrap: wrap; }
                @media (max-width: 900px) {
                    .tk-header-row { align-items: flex-start; flex-direction: column; }
                    .tk-header-actions { width: 100%; justify-content: flex-start; }
                }
            `}</style>
            <ToastContainer position="top-right" autoClose={4000} theme="light" newestOnTop closeOnClick pauseOnHover toastClassName="ticket-notification-toast-shell" progressClassName="ticket-notification-toast-progress" icon={false} />

            {(modalLoading || (pendingEstimate && !showEstimateModal)) && (
                <div
                    style={{
                        position: "fixed",
                        top: 0, left: 0, right: 0, bottom: 0,
                        zIndex: 1055,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.35)",
                    }}
                >
                    <ClipLoader color="#b11111" size={50} />
                </div>
            )}

            <div className="tk-header">
                <div
                    className="tk-header-inner"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 20,
                        flexWrap: "wrap",
                    }}
                >
                    <div>
                        <h1 className="tk-title" style={{ marginBottom: 3 }}>
                            {activeTab === "rewards" ? "Rewards" : activeTab === "all" ? (isAdmin ? "All Tasks" : "My Tasks") : "My Work"}
                        </h1>
                        {/* <button onClick={() => playNotificationSound()}>
                            Test Sound
                        </button> */}
                        <p className="tk-subtitle" style={{ margin: 0, whiteSpace: "nowrap" }}>
                            {activeTab === "rewards"
                                ? "Earn points for accurate, reliable work. Accuracy first. Speed second."
                                : activeTab === "all"
                                    ? "Search, review, and open tickets from the task list."
                                    : "Finish one task before starting another. Everything else stays in your queue."}
                        </p>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginLeft: "auto",
                            flexWrap: "wrap",
                        }}
                    >
                        {effectiveRole === "coordinator" && (
                            <select
                                className="filter-select"
                                value={coordinatorView}
                                onChange={(e) => {
                                    setCoordinatorView(e.target.value);
                                    setActiveTab("mywork");
                                    setQueueTab("todo");
                                }}
                            >
                                <option value="qc">
                                    View as: Coordinator Quality Control
                                </option>
                                <option value="review">
                                    View as: Coordinator Review
                                </option>
                            </select>
                        )}

                        {isRealAdmin && (
                            <select
                                className="filter-select"
                                value={devViewAsUid || ""}
                                onChange={(e) => setDevViewAsUid(e.target.value || null)}
                            >
                                <option value="">Viewing as: me (admin)</option>

                                {allUsers
                                    .filter(
                                        (u) =>
                                            u.user_role !== "client" &&
                                            u.user_role !== "inspector" &&
                                            u.user_role !== "carpenter" &&
                                            u.user_role !== "director"
                                    )
                                    .map((u) => (
                                        <option key={u.uid} value={u.uid}>
                                            Viewing as: {u.name || u.email} ({normalizeUserRole(u.user_role)})
                                        </option>
                                    ))}
                            </select>
                        )}

                        <div
                            ref={notificationsRef}
                            className="ticket-notification-anchor"
                        >
                            <div
                                className={`ticket-notification-trigger-wrap${unreadNotificationCount > 0 && !notificationsOpen ? " notification-danger-zone" : ""}`}
                            >
                                <button
                                    type="button"
                                    className="btn-outline ticket-notification-trigger"
                                    onClick={() => setNotificationsOpen((open) => !open)}
                                    aria-label="Notifications"
                                >
                                    <FontAwesomeIcon icon={faBell} />

                                    Notifications

                                    {unreadNotificationCount > 0 && (
                                        <span className="ticket-notification-count">
                                            {unreadNotificationCount > 99
                                                ? "99+"
                                                : unreadNotificationCount}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {notificationsOpen && (
                                <div className="ticket-notification-menu">
                                    <div className="ticket-notification-menu-header">
                                        <span className="ticket-notification-menu-title">Notifications</span>
                                        <span className="ticket-notification-menu-tools">
                                            {unreadNotificationCount > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={markAllNotificationsRead}
                                                    className="ticket-notification-mark-all"
                                                >
                                                    Mark all read
                                                </button>
                                            )}
                                            {notifications.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={clearAllNotifications}
                                                    title="Clear notifications"
                                                    className="ticket-notification-clear-all"
                                                >
                                                    <FontAwesomeIcon icon={faTrashCan} size="xs" />
                                                    <span>Clear all</span>
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                    <div className="ticket-notification-list">
                                        {notifications.length === 0 ? (
                                            <div className="ticket-notification-empty">No notifications yet.</div>
                                        ) : notifications.map((notification) => {
                                            const unread = !notification.read_at;
                                            const presentation = notificationPresentation(notification);
                                            const expanded = expandedNotificationIds.has(notification.id);
                                            const rewardRows = Array.isArray(presentation.rewardRows) ? presentation.rewardRows : [];
                                            const visibleRewardRows = expanded ? rewardRows : rewardRows.slice(0, 2);
                                            const hasMoreRewardRows = rewardRows.length > 2;

                                            return (
                                                <div
                                                    key={notification.id}
                                                    className={`ticket-notification-list-item${unread ? " is-unread" : ""}${expanded ? " is-expanded" : ""}`}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => handleNotificationClick(notification)}
                                                        className="ticket-notification-list-main"
                                                    >
                                                        <span className={`ticket-notification-list-icon is-${presentation.tone || "neutral"}${unread ? " is-unread" : ""}`} aria-hidden="true">
                                                            <FontAwesomeIcon icon={presentation.icon || faBell} />
                                                        </span>
                                                        <span className={`ticket-notification-list-copy${expanded ? " is-expanded" : ""}`}>
                                                            <span className="ticket-notification-list-primary-line">
                                                                <span className="ticket-notification-list-title">
                                                                    {presentation.title}
                                                                </span>
                                                            </span>

                                                            {rewardRows.length > 0 ? (
                                                                <>
                                                                    {expanded && presentation.subtitle && (
                                                                        <span className="ticket-notification-list-subtitle">
                                                                            {presentation.subtitle}
                                                                        </span>
                                                                    )}

                                                                    {expanded && presentation.rewardSummary && (
                                                                        <span className="ticket-reward-notification-summary">
                                                                            <span>
                                                                                <small>Earned</small>
                                                                                <b>+{presentation.rewardSummary.earnedPoints.toLocaleString("en-GB")} pts</b>
                                                                            </span>
                                                                            <span>
                                                                                <small>Pending</small>
                                                                                <b>+{presentation.rewardSummary.pendingPoints.toLocaleString("en-GB")} pts</b>
                                                                            </span>
                                                                        </span>
                                                                    )}

                                                                    <span className="ticket-reward-notification-rows">
                                                                        {visibleRewardRows.map((row, index) => (
                                                                            <span
                                                                                className={`ticket-reward-notification-row is-${row.status}`}
                                                                                key={`${row.type || row.title}-${index}`}
                                                                            >
                                                                                <span>{row.title}</span>
                                                                                <b>
                                                                                    {row.status === "rejected" ? "" : row.points > 0 ? "+" : ""}
                                                                                    {row.points.toLocaleString("en-GB")} pts{" "}
                                                                                    <small>{row.status === "pending" ? "Pending" : row.status === "rejected" ? "Rejected" : row.status === "approved" ? "Approved" : "Earned"}</small>
                                                                                </b>
                                                                            </span>
                                                                        ))}
                                                                    </span>
                                                                </>
                                                            ) : (
                                                                presentation.subtitle && (
                                                                    <span className="ticket-notification-list-subtitle is-default-visible">
                                                                        {presentation.subtitle}
                                                                    </span>
                                                                )
                                                            )}

                                                            {notification.created_at && (
                                                                <span className="ticket-notification-list-time">
                                                                    {fmtNotificationTimestamp(notification.created_at)}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </button>

                                                    <span className="ticket-notification-list-actions">
                                                        {hasMoreRewardRows && (
                                                            <button
                                                                type="button"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    toggleNotificationExpanded(notification.id);
                                                                }}
                                                                title={expanded ? "Show less" : `Show all ${rewardRows.length} rewards`}
                                                                aria-label={expanded ? "Collapse notification" : "Expand notification"}
                                                                aria-expanded={expanded}
                                                                className="ticket-notification-expand"
                                                            >
                                                                <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} size="xs" />
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                clearNotification(notification);
                                                            }}
                                                            title="Clear notification"
                                                            aria-label="Clear notification"
                                                            className="ticket-notification-clear"
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} size="xs" />
                                                            <span>Clear</span>
                                                        </button>
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {activeTab !== "rewards" && effectiveUid && (
                <button
                    type="button"
                    className={`reward-points-sidebtn${rewardCelebration ? " is-celebrating" : ""}`}
                    onClick={() => setActiveTab("rewards")}
                    aria-label={`Open rewards. ${rewardSummaryLoading ? "Reward points loading" : `${liveRewardPoints.toLocaleString("en-GB")} reward points available`}`}
                    title="Open Rewards"
                >
                    <span className="reward-points-sidebtn__icon"><FontAwesomeIcon icon={faTrophy} /></span>
                    <span className="reward-points-sidebtn__copy">
                        <small>Rewards</small>
                        <strong>{rewardSummaryLoading ? "…" : liveRewardPoints.toLocaleString("en-GB")} <b>pts</b></strong>
                    </span>
                    {rewardCelebration && <span key={rewardCelebration.id} className="reward-points-sidebtn__celebration" aria-live="polite"><small>{rewardCelebration.title}</small><b>+{rewardCelebration.points} {rewardCelebration.points === 1 ? "pt" : "pts"}</b></span>}
                </button>
            )}

            <div className="tk-body">

                <div className="tk-tabs-row">
                    <div className="tk-tabs">
                        <button
                            className={`tab-pill${activeTab === "mywork" ? " active" : ""}`}
                            onClick={() => setActiveTab("mywork")}
                        >
                            My Work
                            {stats.ready > 0 && <span className="tab-count">{stats.ready}</span>}
                        </button>
                        <button
                            className={`tab-pill${activeTab === "all" ? " active" : ""}`}
                            onClick={() => setActiveTab("all")}
                        >
                            {isAdmin ? "All Tasks" : "My Tasks"}
                            <span className="tab-count">{allTickets.length}</span>
                        </button>
                        <button
                            className={`tab-pill${activeTab === "rewards" ? " active" : ""}`}
                            onClick={() => setActiveTab("rewards")}
                        >
                            Rewards
                        </button>
                    </div>

                    {activeTab === "mywork" && (
                        <div className="stats-strip">
                            <div className="stat-chip">
                                <span className="stat-num">{stats.active}</span>
                                <span className="stat-label">Currently active</span>
                            </div>
                            <div className="stat-chip">
                                <span className="stat-num">{stats.ready}</span>
                                <span className="stat-label">Ready in queue</span>
                            </div>
                            <div className={`stat-chip${stats.waiting > 0 ? " warn" : ""}`}>
                                <span className="stat-num">{stats.waiting}</span>
                                <span className="stat-label">Stuck / waiting</span>
                            </div>
                            <div className={`stat-chip${stats.dueToday > 0 ? " due-alert" : ""}`}>
                                <span className="stat-num">{stats.dueToday}</span>
                                <span className="stat-label">Due today</span>
                            </div>
                        </div>
                    )}
                </div>

                {activeTab === "mywork" && (
                    <>
                        <div className="mywork-grid">
                            {activeTicket ? (
                                <div
                                    ref={activeCardRef}
                                    className={`active-card mywork-grid-active is-ticket-clickable${activeTicket.is_paused ? " is-paused" : ""}`}
                                    onClick={(event) => handleTicketSurfaceClick(event, activeTicket)}
                                >
                                    <div className="active-card-hdr">
                                        <div className="active-indicator">
                                            <span className={`active-dot${activeTicket.is_paused ? " is-paused" : ""}`} />
                                            {activeTicket.is_paused ? "Paused — taking a break" : "Currently Working On"}
                                        </div>
                                        <div className="active-hdr-right">
                                            <span className="task-id-chip">
                                                {getFullTaskId(activeTicket)}
                                            </span>
                                            <span className="active-only-one">
                                                {activeTicket.is_paused ? "Task stays active · timer stopped" : "Only one task can be active"}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="active-card-body">
                                        <div className="active-info">
                                            <div className="active-badges">
                                                <PriorityBadge priority={activeTicket.priority} />
                                                {activeTicket.task_type && <TaskTypeBadge type={activeTicket.task_type} />}
                                                {activeTicket.is_paused && (
                                                    <span className="paused-state-badge">
                                                        <FontAwesomeIcon icon={faPause} size="xs" />Paused
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="active-title">{getTitle(activeTicket)}</h3>
                                            {/* {activeTicket.building_id && (
                                                <TicketReportIdLine buildingId={activeTicket.building_id} />
                                            )} */}
                                            {activeTicket.description && (
                                                <ActiveExpandableDescription
                                                    key={activeTicket.id}
                                                    text={activeTicket.description}
                                                    onShowMore={() => openDetail(activeTicket, { expandDescription: true })}
                                                />
                                            )}
                                            <div className="active-tags">
                                                {activeTicket.assignee_id && (
                                                    <span className="task-tag">
                                                        <FontAwesomeIcon icon={faUser} size="xs" className="me-1" />
                                                        {nameOf(activeTicket.assignee_id)}
                                                    </span>
                                                )}
                                                {isCoordinatorReviewTicket(activeTicket) && activeTicket.building_id && (
                                                    <button className="btn-outline" disabled={actionInProgress}
                                                        onClick={() => setConfirmAction({
                                                            title: "Generate Report",
                                                            message: `Generate the report for ${activeTicket.building_name || "this building"}?`,
                                                            onConfirm: () => doGenerateReport(activeTicket.building_id, activeTicket),
                                                        })}>
                                                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />Generate report
                                                    </button>
                                                )}
                                                {(effectiveRole === "remedial" || effectiveRole === "manager") && activeTicket.building_id && (
                                                    <a
                                                        href={`/escalate/${activeTicket.building_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="task-tag"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <FontAwesomeIcon icon={faLink} size="xs" className="me-1" />
                                                        Access Details
                                                    </a>
                                                )}
                                                {activeTicket.building_id && (
                                                    <a
                                                        href={`/doors/${activeTicket.building_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="task-tag"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <FontAwesomeIcon icon={faLink} size="xs" className="me-1" />
                                                        <BuildingNameWithReportId
                                                            buildingId={activeTicket.building_id}
                                                            buildingName={activeTicket.building_name}
                                                        />
                                                    </a>
                                                )}
                                                <TicketScheduleBadges
                                                    ticket={activeTicket}
                                                    role={effectiveRole}
                                                    nameOf={nameOf}
                                                    onOpen={(t, idx) => openCalendarModal(t, idx)}
                                                />
                                                {(isCoordinatorReviewTicket(activeTicket) || effectiveRole === "reviewer" || effectiveRole === "remedial")
                                                    && activeTicket.building_id && (
                                                        <BuildingReportLinks buildingId={activeTicket.building_id} />
                                                    )}
                                                {getDueAt(activeTicket) && (
                                                    <span className={`task-tag${isOverdue(activeTicket) ? " overdue" : ""}`}>
                                                        <FontAwesomeIcon icon={faCalendar} size="xs" className="me-1" />
                                                        {isOverdue(activeTicket) ? "Overdue · " : "Due "}
                                                        {fmtDate(getDueAt(activeTicket))}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className={`timer-block${activeTicket.is_paused ? " is-paused" : ""}`}>
                                            <button
                                                type="button"
                                                className={`timer-pause-toggle${activeTicket.is_paused ? " is-resume" : ""}`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    activeTicket.is_paused ? doResumeActiveTask(activeTicket) : doPauseActiveTask(activeTicket);
                                                }}
                                                disabled={actionInProgress}
                                                title={activeTicket.is_paused ? "Resume task" : "Pause timer"}
                                                aria-label={activeTicket.is_paused ? "Resume task timer" : "Pause task timer"}
                                            >
                                                <FontAwesomeIcon icon={activeTicket.is_paused ? faPlay : faPause} />
                                            </button>
                                            <div className="timer-lbl">{activeTicket.is_paused ? "PAUSED · TIMER STOPPED" : "TOTAL TIME"}</div>
                                            <div className="timer-val">{formatTime(totalElapsed)}</div>
                                            {activeTicket.is_paused && activeTicket.paused_at ? (
                                                <div className="timer-sub paused-copy">Paused {fmtNotificationTimestamp(activeTicket.paused_at)}</div>
                                            ) : (
                                                activeStartTime && <div className="timer-sub">Session started {activeStartTime}</div>
                                            )}
                                            {prevSecs > 0 && <div className="timer-prev">+{formatTime(prevSecs)} prev</div>}
                                        </div>
                                    </div>

                                    <div className="active-card-actions">
                                        <button className="btn-complete" onClick={() => openCompleteModal(activeTicket)} disabled={actionInProgress}>
                                            <FontAwesomeIcon icon={faCircleCheck} className="me-2" />Complete step
                                        </button>
                                        {(effectiveRole === "reviewer" || effectiveRole === "remedial") && activeTicket.building_id && (
                                            <>
                                                <button className="btn-outline" disabled={actionInProgress}
                                                    onClick={() => setConfirmAction({
                                                        title: "Generate Report",
                                                        message: `Generate the report for ${activeTicket.building_name || "this building"}?`,
                                                        onConfirm: () => doGenerateReport(activeTicket.building_id, activeTicket),
                                                    })}>
                                                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />Generate report
                                                </button>
                                            </>
                                        )}
                                        <button className="btn-stuck" onClick={() => openStuckModal(activeTicket)}>
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />Mark as stuck
                                        </button>
                                        <button className="btn-outline" onClick={() => openNoteModal(activeTicket)}>
                                            <FontAwesomeIcon icon={faComment} className="me-2" />Add note
                                        </button>
                                        <button className="btn-outline" onClick={() => openDetail(activeTicket)}>
                                            <FontAwesomeIcon icon={faSearch} className="me-2" />View details
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div ref={activeCardRef} className="no-active mywork-grid-active">
                                    <FontAwesomeIcon icon={faPlay} size="lg" />
                                    <p>No active task. Start one from your queue below.</p>
                                    <button className="btn-add-empty" onClick={openCreate}>
                                        <FontAwesomeIcon icon={faPlus} size="xs" className="me-1" />New Task
                                    </button>
                                </div>
                            )}

                            <div className="queue-card mywork-grid-queue">
                                <div className="queue-card-hdr">
                                    <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                                        <span
                                            className="queue-card-title"
                                            style={{
                                                cursor: "pointer",
                                                paddingBottom: 6,
                                                borderBottom: queueTab === "todo" ? "2px solid #dc2626" : "2px solid transparent",
                                                color: queueTab === "todo" ? "#dc2626" : "var(--gray-500)",
                                            }}
                                            onClick={() => setQueueTab("todo")}
                                        >
                                            To Do Queue
                                            {sortedQueue.length > 0 && (
                                                <span className="queue-chip">{sortedQueue.length}</span>
                                            )}
                                        </span>
                                        <span
                                            className="queue-card-title"
                                            style={{
                                                cursor: "pointer",
                                                paddingBottom: 6,
                                                borderBottom: queueTab === "sent" ? "2px solid #dc2626" : "2px solid transparent",
                                                color: queueTab === "sent" ? "#dc2626" : "var(--gray-500)",
                                            }}
                                            onClick={() => setQueueTab("sent")}
                                        >
                                            Sent Tasks
                                            {sortedSentTickets.length > 0 && (
                                                <span className="queue-chip">{sortedSentTickets.length}</span>
                                            )}
                                        </span>
                                    </div>
                                    {queueTab === "todo" && (
                                        <button className="btn-add" onClick={openCreate}>
                                            <FontAwesomeIcon icon={faPlus} size="xs" className="me-1" />Add task
                                        </button>
                                    )}
                                </div>

                                <div className="queue-filter-bar">
                                    <div className="queue-filter-search">
                                        <FontAwesomeIcon icon={faSearch} size="xs" />
                                        <input
                                            type="text"
                                            placeholder={queueTab === "todo" ? "Search To Do tasks..." : "Search Sent tasks..."}
                                            value={queueSearchQuery}
                                            onChange={(e) => setQueueSearchQuery(e.target.value)}
                                        />
                                        {queueSearchQuery && (
                                            <button type="button" onClick={() => setQueueSearchQuery("")}>
                                                <FontAwesomeIcon icon={faTimes} size="xs" />
                                            </button>
                                        )}
                                    </div>
                                    <select className="queue-filter-select" value={queueTypeFilter} onChange={(e) => setQueueTypeFilter(e.target.value)}>
                                        <option value="all">All Types</option>
                                        {TASK_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                                    </select>
                                    <select className="queue-filter-select" value={queuePriorityFilter} onChange={(e) => setQueuePriorityFilter(e.target.value)}>
                                        <option value="all">All Priorities</option>
                                        {PRIORITY_OPTIONS.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                                    </select>
                                    {queueTab === "sent" && (
                                        <select className="queue-filter-select" value={queueStatusFilter} onChange={(e) => setQueueStatusFilter(e.target.value)}>
                                            <option value="all">All Statuses</option>
                                            {Object.entries(STATUS_CFG).map(([value, config]) => <option key={value} value={value}>{config.label}</option>)}
                                        </select>
                                    )}
                                    {hasQueueFilters && (
                                        <button type="button" className="queue-filter-clear" onClick={clearQueueFilters} title="Clear queue filters">
                                            <FontAwesomeIcon icon={faTimes} size="xs" />
                                        </button>
                                    )}
                                </div>

                                {queueTab === "todo" && effectiveRole === "coordinator" && (
                                    <div className="coordinator-subtabs">
                                        <button
                                            type="button"
                                            className={`coordinator-subtab${coordinatorView === "qc" ? " active" : ""}`}
                                            onClick={() => setCoordinatorView("qc")}
                                        >
                                            Quality Control
                                            {coordinatorQcQueue.length > 0 && <span className="coordinator-subtab-count">{coordinatorQcQueue.length}</span>}
                                        </button>
                                        <button
                                            type="button"
                                            className={`coordinator-subtab${coordinatorView === "review" ? " active" : ""}`}
                                            onClick={() => setCoordinatorView("review")}
                                        >
                                            Review
                                            {coordinatorReviewQueue.length > 0 && <span className="coordinator-subtab-count">{coordinatorReviewQueue.length}</span>}
                                        </button>
                                    </div>
                                )}

                                {queueTab === "todo" ? (
                                    loading ? (
                                        <div className="tk-loading">
                                            <Spinner animation="border" style={{ color: "var(--primary)", width: "1.8rem", height: "1.8rem" }} />
                                        </div>
                                    ) : visibleQueue.length === 0 ? (
                                        <div className="queue-empty">
                                            <FontAwesomeIcon icon={faCircleCheck} size="lg" />
                                            <p>{hasQueueFilters ? "No To Do tasks match these filters." : "Queue is clear!"}</p>
                                        </div>
                                    ) : (
                                        <div className="queue-scroll">
                                            <table className="queue-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 14 }}></th>
                                                        {renderQueueSortTh("Task", "task")}
                                                        {renderQueueSortTh("Type", "type")}
                                                        {renderQueueSortTh("Priority", "priority")}
                                                        {renderQueueSortTh("Due", "due")}
                                                        <th style={{ width: 76 }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleQueue.map((ticket) => (
                                                        <tr
                                                            key={ticket.id}
                                                            ref={(el) => { if (el) queueRowRefs.current[ticket.id] = el; else delete queueRowRefs.current[ticket.id]; }}
                                                            className={`${isOverdue(ticket) ? "row-overdue" : ""}${highlightTicketId === ticket.id ? " queue-row-highlight" : ""}`.trim()}
                                                            onClick={() => openDetail(ticket)}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <td>
                                                                <span className="p-dot" style={{ background: PRIORITY_CFG[ticket.priority]?.color || "#6b7280" }} />
                                                            </td>
                                                            <td className="queue-task-cell">
                                                                <div className="queue-task-title" title={getTitle(ticket)}>
                                                                    {getTitle(ticket)}
                                                                </div>
                                                                {isOverdue(ticket) && <span className="overdue-tag">Overdue</span>}
                                                                {sentByUidFor(ticket) && sentByUidFor(ticket) !== effectiveUid && (
                                                                    <div className="queue-task-sub" style={{ marginTop: 2, color: "var(--gray-500)" }}>
                                                                        Sent by {nameOf(sentByUidFor(ticket))}
                                                                    </div>
                                                                )}
                                                                {currentAssignmentFor(ticket)?.assigned_at && (
                                                                    <div className="queue-task-sub">
                                                                        Received {fmtTimestamp(parseDate(currentAssignmentFor(ticket).assigned_at))}
                                                                    </div>
                                                                )}
                                                                {effectiveRole === "remedial" && ticket.building_id && (
                                                                    <a
                                                                        href={`/escalate/${ticket.building_id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="queue-task-sub"
                                                                        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <FontAwesomeIcon icon={faLink} size="xs" style={{ color: "var(--gray-300)" }} />
                                                                        Access Details
                                                                    </a>
                                                                )}
                                                                {ticket.building_name && ticket.building_id && (
                                                                    <a
                                                                        href={`/doors/${ticket.building_id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="queue-task-sub"
                                                                        style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <FontAwesomeIcon icon={faLink} size="xs" style={{ color: "var(--gray-300)" }} />
                                                                        {ticket.building_name}
                                                                    </a>
                                                                )}
                                                                {(effectiveRole === "inspection" || (ticket.schedule_ids || []).some(Boolean)) && (
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginTop: 2 }}>
                                                                        <TicketScheduleBadges
                                                                            ticket={ticket}
                                                                            role={effectiveRole}
                                                                            nameOf={nameOf}
                                                                            onOpen={(tk, idx) => openCalendarModal(tk, idx)}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                                                                    {doorRoleForTicket(ticket) && ticket.building_id && (
                                                                        <DoorCountBadge buildingId={ticket.building_id} role={doorRoleForTicket(ticket)} />
                                                                    )}
                                                                    {ticket.task_type && <TaskTypeBadge type={ticket.task_type} />}
                                                                    {isCoordinatorQcTicket(ticket) && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                openCoordinatorBookingModal(ticket);
                                                                            }}
                                                                            style={{
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                gap: 5,
                                                                                padding: "3px 8px",
                                                                                borderRadius: 999,
                                                                                border: "1px solid rgba(37,99,235,0.2)",
                                                                                background: "rgba(37,99,235,0.07)",
                                                                                color: "#2563eb",
                                                                                fontSize: "0.69rem",
                                                                                fontWeight: 800,
                                                                                cursor: "pointer",
                                                                                whiteSpace: "nowrap",
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faCalendar} size="xs" />
                                                                            {(() => {
                                                                                const info = bookingInfoForTicket(ticket);
                                                                                const total = info?.totalBookings ?? 0;
                                                                                if (!info && bookingsLoading) return "… bookings";
                                                                                return `${total} booking${total === 1 ? "" : "s"}`;
                                                                            })()}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td><PriorityBadge priority={ticket.priority} /></td>
                                                            <td className={isOverdue(ticket) ? "date-overdue" : "date-normal"}>
                                                                {getDueAt(ticket) ? (
                                                                    <span className="date-cell">
                                                                        <FontAwesomeIcon icon={faCalendar} size="xs" />
                                                                        {fmtTimestamp(getDueAt(ticket))}
                                                                    </span>
                                                                ) : "—"}
                                                            </td>
                                                            <td>
                                                                <button className="btn-start" onClick={(e) => { e.stopPropagation(); doStart(ticket); }} disabled={actionInProgress}>
                                                                    <FontAwesomeIcon icon={faPlay} size="xs" className="me-1" />Start
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                ) : (
                                    visibleSentTickets.length === 0 ? (
                                        <div className="queue-empty">
                                            <FontAwesomeIcon icon={faPaperPlane} size="lg" />
                                            <p>{hasQueueFilters ? "No Sent tasks match these filters." : "You haven't sent any tasks on to someone else yet."}</p>
                                        </div>
                                    ) : (
                                        <div className="queue-scroll">
                                            <table className="queue-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 14 }}></th>
                                                        {renderQueueSortTh("Task", "task")}
                                                        {renderQueueSortTh("Type", "type")}
                                                        {renderQueueSortTh("Priority", "priority")}
                                                        {renderQueueSortTh("Due", "due")}
                                                        {renderQueueSortTh("Current With", "assignee")}
                                                        {renderQueueSortTh("Status", "status")}
                                                        {renderQueueSortTh("Sent", "sent")}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleSentTickets.map((ticket) => {
                                                        const sentAt = sentAtFor(ticket, effectiveUid);
                                                        return (
                                                            <tr
                                                                key={ticket.id}
                                                                onClick={() => openDetail(ticket)}
                                                                style={{ cursor: "pointer" }}
                                                            >
                                                                <td>
                                                                    <span className="p-dot" style={{ background: PRIORITY_CFG[ticket.priority]?.color || "#6b7280" }} />
                                                                </td>
                                                                <td className="queue-task-cell">
                                                                    <div className="queue-task-title" title={getTitle(ticket)}>{getTitle(ticket)}</div>
                                                                    {ticket.building_name && ticket.building_id && (
                                                                        <a
                                                                            href={`/doors/${ticket.building_id}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="queue-task-sub"
                                                                            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <FontAwesomeIcon icon={faLink} size="xs" style={{ color: "var(--gray-300)" }} />
                                                                            {ticket.building_name}
                                                                        </a>
                                                                    )}
                                                                    {(ticket.schedule_ids || []).some(Boolean) && (
                                                                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap", marginTop: 2 }}>
                                                                            <TicketScheduleBadges ticket={ticket} role="sent" nameOf={nameOf} />
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                                                                        {doorRoleForTicket(ticket) && ticket.building_id && (
                                                                            <DoorCountBadge buildingId={ticket.building_id} role={doorRoleForTicket(ticket)} />
                                                                        )}
                                                                        {ticket.task_type && <TaskTypeBadge type={ticket.task_type} />}
                                                                    </div>
                                                                </td>
                                                                <td><PriorityBadge priority={ticket.priority} /></td>
                                                                <td className={isOverdue(ticket) ? "date-overdue" : "date-normal"}>
                                                                    {getDueAt(ticket) ? fmtTimestamp(getDueAt(ticket)) : "—"}
                                                                </td>
                                                                <td>
                                                                    <div className="user-cell">
                                                                        <Avatar name={nameOf(ticket.assignee_id)} small />
                                                                        <span>{nameOf(ticket.assignee_id)}</span>
                                                                    </div>
                                                                </td>
                                                                <td><StatusBadge status={ticket.status} /></td>
                                                                <td className="date-normal">{sentAt ? fmtTimestamp(sentAt) : "—"}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                                )}
                                <div className="task-window-footer">
                                    <span>{canLoadMoreTasks ? `Loaded up to ${loadedTaskLimit} recent tasks` : "All available tasks loaded"}</span>
                                    {canLoadMoreTasks && (
                                        <button type="button" onClick={loadMoreTasks}>Load {TASK_LOAD_MORE_BATCH} more</button>
                                    )}
                                </div>
                            </div>

                            {isAdmin && (
                                <TeamActiveWorkPanel
                                    tickets={teamActiveTickets}
                                    nameOf={nameOf}
                                    onOpen={openDetail}
                                    panelHeight={activeCardHeight}
                                />
                            )}

                            {effectiveRole === "coordinator" && (
                                coordinatorView === "review" ? (
                                    <DoorsReviewPanel
                                        activeTicket={isCoordinatorReviewTicket(activeTicket) ? activeTicket : null}
                                        role="coordinator_review"
                                        panelHeight={activeCardHeight}
                                    />
                                ) : renderCoordinatorBookingPanel()
                            )}

                            {effectiveRole === "reviewer" && (
                                <DoorsReviewPanel
                                    activeTicket={activeTicket}
                                    role={effectiveRole}
                                    panelHeight={activeCardHeight}
                                    onOpenDoor={(door) =>
                                        setDoorReviewModal({
                                            buildingId: door.buildingId,
                                            doorId: door.id,
                                        })
                                    }
                                />
                            )}

                            {effectiveRole === "manager" && (
                                <ManagerRemedialTabsPanel
                                    activeTicket={activeTicket}
                                    panelHeight={activeCardHeight}
                                    onOpenDoor={(door) => setManagerWorkModal(door)}
                                    onEstimateDoor={(door) => {
                                        setSingleDoorForEstimate(door);
                                        setSelectedDoors(null);
                                        setIsResidentialEstimate(door.type === "residential");
                                        setEstimateBuildingId(door.buildingId);
                                        setPendingEstimate(true);
                                    }}
                                    onEstimateAllCommunal={openCommunalEstimate}
                                    onRemedialInfoDoor={(door) => openRemedialInfoModal(door)}
                                    refreshSignal={remedialInfoRefreshSignal}
                                />
                            )}

                            {effectiveRole === "remedial" && (
                                <DoorsReviewPanel
                                    activeTicket={activeTicket}
                                    role={effectiveRole}
                                    panelHeight={activeCardHeight}
                                    refreshSignal={remedialInfoRefreshSignal}
                                    onEstimateDoor={(door) => {
                                        setSingleDoorForEstimate(door);
                                        setSelectedDoors(null);
                                        setIsResidentialEstimate(door.type === "residential");
                                        setEstimateBuildingId(door.buildingId);
                                        setPendingEstimate(true);
                                    }}
                                    onEstimateAllCommunal={openCommunalEstimate}
                                    onRemedialInfoDoor={(door) => openRemedialInfoModal(door)}
                                />
                            )}

                            {effectiveRole === "inspection" && (
                                <InspectionTabsPanel
                                    activeTicket={activeTicket}
                                    panelHeight={activeCardHeight}
                                    onCreateBuilding={() => setShowDetailsModal(true)}
                                />
                            )}

                            {renderSidePanelForRole()}
                        </div>


                    </>
                )}

                {activeTab === "rewards" && (
                    <div className="rewards-tab-shell">
                        <RewardsPage userId={effectiveUid} viewedUserRole={effectiveRole} embedded onOpenTicket={openDetailById} navigationTarget={rewardNavigationTarget} onNavigationHandled={() => setRewardNavigationTarget(null)} />
                    </div>
                )}

                {activeTab === "all" && (
                    <div className="all-tasks-section">
                        <div className="all-tasks-hdr">
                            <span className="all-tasks-title">
                                {isAdmin ? "All Tasks" : "My Tasks"}
                            </span>
                            <div className="filter-pills">
                                {[
                                    { key: "all", label: "All" },
                                    { key: "ready", label: "Ready" },
                                    { key: "active", label: "Active" },
                                    { key: "waiting", label: "Waiting" },
                                    { key: "completed", label: "Completed" },
                                ].map(({ key, label }) => (
                                    <button
                                        key={key}
                                        className={`filter-pill${taskFilter === key ? " active" : ""}`}
                                        onClick={() => setTaskFilter(key)}
                                    >
                                        {label}
                                        <span className="pill-count">{taskCounts[key] ?? 0}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="filter-bar">
                            <div className="filter-search">
                                <FontAwesomeIcon icon={faSearch} size="xs" />
                                <input
                                    type="text"
                                    placeholder="Search title, ID, description..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                {searchQuery && (
                                    <button className="filter-search-clear" onClick={() => setSearchQuery("")}>
                                        <FontAwesomeIcon icon={faTimes} size="xs" />
                                    </button>
                                )}
                            </div>
                            <select className="filter-select" value={filterCreator} onChange={(e) => setFilterCreator(e.target.value)}>
                                <option value="all">Created By: Everyone</option>
                                {filterCreatorOptions.map((u) => (
                                    <option key={u.uid} value={u.uid}>{u.name}</option>
                                ))}
                            </select>
                            <select className="filter-select" value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
                                <option value="all">Assigned To: Everyone</option>
                                {filterAssigneeOptions.map((u) => (
                                    <option key={u.uid} value={u.uid}>{u.name}</option>
                                ))}
                            </select>
                            <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                                <option value="all">All Types</option>
                                {filterTypeOptions.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                            <select className="filter-select" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
                                <option value="all">All Priorities</option>
                                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                            {hasActiveFilters && (
                                <button className="filter-clear-btn" onClick={clearAllTaskFilters}>
                                    <FontAwesomeIcon icon={faTimes} size="xs" className="me-1" />Clear filters
                                </button>
                            )}
                        </div>

                        {loading ? (
                            <div className="tk-loading" style={{ padding: "60px" }}>
                                <Spinner animation="border" style={{ color: "var(--primary)", width: "1.8rem", height: "1.8rem" }} />
                            </div>
                        ) : filteredAllTasks.length === 0 ? (
                            <div className="all-tasks-empty">
                                <FontAwesomeIcon icon={faSearch} size="lg" />
                                <p>{hasActiveFilters ? "No tasks match these filters." : "No tasks in this category."}</p>
                                {hasActiveFilters && (
                                    <button className="filter-clear-btn" onClick={clearAllTaskFilters}>Clear filters</button>
                                )}
                            </div>
                        ) : (
                            <div className="all-tasks-scroll">
                                <table className="all-tasks-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 14 }}></th>
                                            {renderSortTh("Task", "task")}
                                            {renderSortTh("Type", "type")}
                                            {/* {isAdmin && renderSortTh("Created By", "creator")} */}
                                            {renderSortTh("Created By", "creator")}
                                            {renderSortTh("Assigned To", "assignee")}
                                            {renderSortTh("Status", "status")}
                                            {renderSortTh("Due", "due")}
                                            {renderSortTh("Created", "created")}
                                            {isAdmin && renderSortTh("Total Time", "totalTime")}
                                            {isAdmin && <th style={{ width: 90 }}>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAllTasks.map((ticket) => (
                                            <tr
                                                key={ticket.id}
                                                onClick={() => openDetail(ticket)}
                                                className={
                                                    ticket.status === "completed" ? "row-completed"
                                                        : isOverdue(ticket) ? "row-overdue"
                                                            : ""
                                                }
                                            >
                                                <td>
                                                    <span className="p-dot" style={{ background: PRIORITY_CFG[ticket.priority]?.color || "#6b7280" }} />
                                                </td>
                                                <td className="all-task-title-cell">
                                                    <div className={`all-task-title${ticket.status === "completed" ? " done" : ""}`} title={getTitle(ticket)}>
                                                        {getTitle(ticket)}
                                                    </div>
                                                    {isOverdue(ticket) && <span className="overdue-tag">Overdue</span>}
                                                    {ticket.status === "completed" && ticket.completed_at && (
                                                        <div className="completed-at">
                                                            <FontAwesomeIcon icon={faCheckCircle} size="xs" className="me-1" />
                                                            Completed {fmtTimestamp(ticket.completed_at)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{ticket.task_type && <TaskTypeBadge type={ticket.task_type} />}</td>
                                                {/* {isAdmin && ( */}
                                                <td>
                                                    <div className="user-cell">
                                                        <Avatar name={nameOf(ticket.created_by_id)} small />
                                                        <span>{nameOf(ticket.created_by_id)}</span>
                                                    </div>
                                                </td>
                                                {/* )} */}
                                                <td>
                                                    <div className="user-cell">
                                                        <Avatar name={nameOf(ticket.assignee_id)} small />
                                                        <span>{nameOf(ticket.assignee_id)}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <StatusBadge status={ticket.status} />
                                                    {ticket.status === "active" && <ActiveSince activeAt={ticket.activeAt} paused={Boolean(ticket.is_paused)} />}
                                                </td>
                                                <td className={isOverdue(ticket) ? "date-overdue" : "date-normal"}>
                                                    {getDueAt(ticket) ? (
                                                        <span className="date-cell">
                                                            <FontAwesomeIcon icon={faCalendar} size="xs" />
                                                            {fmtTimestamp(getDueAt(ticket))}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="date-normal">{fmtTimestamp(getCreatedAt(ticket))}</td>
                                                {isAdmin && (
                                                    <td>
                                                        <TotalTimeCell ticket={ticket} />
                                                    </td>
                                                )}

                                                {isAdmin && (
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            title="Delete task"
                                                            style={{
                                                                width: 28, height: 28, borderRadius: 6,
                                                                border: "1px solid #fecaca", background: "#fff",
                                                                color: "#dc2626", cursor: "pointer",
                                                            }}
                                                            onClick={() => setConfirmAction({
                                                                title: "Delete Task",
                                                                message: `Delete "${getTitle(ticket)}"? It'll be removed from all queues but kept for records.`,
                                                                onConfirm: async () => {
                                                                    await updateDoc(doc(db, "tickets", ticket.id), {
                                                                        archived: true,
                                                                        updated_at: serverTimestamp(), updated_by_id: actor.uid,
                                                                    });
                                                                    await logEvent(ticket.id, actor, "archived", "Ticket archived");
                                                                },
                                                            })}
                                                        >
                                                            <FontAwesomeIcon icon={faTrashCan} size="sm" />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="task-window-footer all-tasks-window-footer">
                            <span>{canLoadMoreTasks ? `Loaded up to ${loadedTaskLimit} most recent tasks` : "All available tasks loaded"}</span>
                            {canLoadMoreTasks && (
                                <button type="button" onClick={loadMoreTasks}>Load {TASK_LOAD_MORE_BATCH} more</button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className={`drawer-backdrop${drawerMode ? " open" : ""}`} onClick={closeDrawer} />
            <div className={`drawer${drawerMode ? " open" : ""}`}>
                <div className="drawer-hdr">
                    <div>
                        <h5>{drawerMode === "create" ? "New Task" : "Task Details"}</h5>
                        {drawerMode === "detail" && detailTicket && (
                            <span className="drawer-task-id">{getFullTaskId(detailTicket)}</span>
                        )}
                    </div>
                    <button className="drawer-close" onClick={closeDrawer}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className={`drawer-body${drawerMode === "detail" ? " detail-mode" : ""}`}>
                    {drawerMode === "create" && (
                        <div className="form-stack">
                            <div className="form-field">
                                <label className="form-lbl">Title</label>
                                <input type="text" className="form-ctrl"
                                    placeholder="What needs to be done..."
                                    value={newTitle} onChange={(e) => setNewTitle(e.target.value)} autoFocus />
                            </div>
                            <div className="form-field">
                                <label className="form-lbl">Description <span className="form-opt">(optional)</span></label>
                                <textarea className="form-ctrl" rows={2}
                                    placeholder="Additional details..."
                                    value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
                            </div>
                            <div className="form-field">
                                <label className="form-lbl">Building <span className="form-opt">(optional, searchable)</span></label>
                                <Select
                                    options={buildingOptions}
                                    value={newBuilding}
                                    onChange={(v) => setNewBuilding(v)}
                                    placeholder={buildingsLoading ? "Loading buildings..." : "Search for a building/report..."}
                                    isClearable isSearchable isLoading={buildingsLoading}
                                    menuPlacement="auto" menuPosition="fixed" menuPortalTarget={document.body}
                                    styles={reportSelectStyles}
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-field">
                                    <label className="form-lbl">Assign To</label>
                                    <select
                                        className="form-ctrl"
                                        value={newAssignee ? encodeAssignmentTarget(newAssignee, newAssigneeStage) : ""}
                                        onChange={(e) => {
                                            const target = decodeAssignmentTarget(e.target.value);
                                            setNewAssignee(target.uid);
                                            setNewAssigneeStage(target.stageKey);
                                            const stage = stageByKey(target.stageKey);
                                            if (stage?.taskType) setNewTaskType(stage.taskType);
                                        }}
                                    >
                                        <option value="">Select person...</option>
                                        {assignmentOptions.map((option) => (
                                            <option key={encodeAssignmentTarget(option.uid, option.stageKey)} value={encodeAssignmentTarget(option.uid, option.stageKey)}>
                                                {option.label}{option.uid === effectiveUid ? " (me)" : ""}
                                            </option>
                                        ))}
                                    </select>
                                    <div style={{ marginTop: 5, fontSize: "0.72rem", lineHeight: 1.4, color: "var(--gray-500)" }}>
                                        {newAssignee === effectiveUid
                                            ? "You are the first worker. Completing or forwarding this stage can earn your stage rewards."
                                            : "The selected person owns the first rewardable stage. Creating the ticket alone does not earn points."}
                                    </div>
                                </div>
                                <div className="form-field">
                                    <label className="form-lbl">Task Type</label>
                                    <select className="form-ctrl" value={newTaskType} onChange={(e) => setNewTaskType(e.target.value)}>
                                        {TASK_TYPES.map((t) => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-field">
                                    <label className="form-lbl">Priority</label>
                                    <select className="form-ctrl" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
                                        {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="form-field">
                                    <label className="form-lbl">Due Date</label>
                                    <input type="date" className="form-ctrl" value={newDueDate} min={todayISO()}
                                        onChange={(e) => setNewDueDate(e.target.value)} />
                                </div>
                            </div>
                            <div className="form-field">
                                <label className="form-lbl">Attachments <span className="form-opt">(optional)</span></label>
                                <label className="file-pick" style={{ marginBottom: createFiles.length ? 8 : 0 }}>
                                    <input
                                        type="file"
                                        multiple
                                        accept={COMMON_ATTACHMENT_ACCEPT}
                                        onChange={(e) => {
                                            const picked = Array.from(e.target.files || []);
                                            const { accepted, errors } = validateAttachmentSelection(picked, createFiles.length);
                                            showAttachmentValidationErrors(errors);
                                            if (accepted.length) setCreateFiles((files) => [...files, ...accepted]);
                                            e.target.value = "";
                                        }}
                                    />
                                    <FontAwesomeIcon icon={faLink} />
                                    <span>{createFiles.length ? "Add more files..." : "Choose files..."}</span>
                                </label>
                                <div style={{ marginTop: 5, fontSize: "0.7rem", color: "var(--gray-400)" }}>{ATTACHMENT_LIMIT_HELP}</div>
                                {createFiles.length > 0 && (
                                    <div style={{ display: "grid", gap: 6 }}>
                                        {createFiles.map((file, index) => (
                                            <div key={`${file.name}-${index}`} style={{
                                                display: "flex", alignItems: "center", gap: 8,
                                                padding: "7px 9px", border: "1px solid var(--gray-200)",
                                                borderRadius: 7, background: "var(--gray-50)",
                                            }}>
                                                <FontAwesomeIcon icon={file.type?.startsWith("image/") ? faImage : faDownload} size="xs" />
                                                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                                                    {file.name}{file.size ? ` · ${formatFileSize(file.size)}` : ""}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setCreateFiles((files) => files.filter((_, i) => i !== index))}
                                                    style={{ border: 0, background: "transparent", padding: 2, cursor: "pointer", color: "var(--gray-500)" }}
                                                    title="Remove attachment"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} size="xs" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="form-actions">
                                <button className="btn-sec" onClick={closeDrawer}>Cancel</button>
                                <button className="btn-prim" onClick={doCreate} disabled={saving || actionInProgress || !newTitle.trim() || !newAssignee || !newDueDate}>
                                    {saving ? <><Spinner size="sm" animation="border" className="me-2" />Creating...</> : "Add to Queue"}
                                </button>
                            </div>
                        </div>
                    )}

                    {drawerMode === "detail" && detailTicket && renderDetail(detailTicket)}
                </div>

                {drawerMode === "detail" && detailTicket && (
                    <div
                        className="drawer-comment-footer"
                        onClick={() => {
                            if (detailLogTab !== "activity") setDetailLogTab("activity");
                            if (!activityLogOpen) setActivityLogOpen(true);
                        }}
                        onFocusCapture={() => {
                            if (detailLogTab !== "activity") setDetailLogTab("activity");
                            if (!activityLogOpen) setActivityLogOpen(true);
                        }}
                    >
                        {detailTicket.status !== "completed" ? (
                            <div style={{ width: "100%" }}>
                                {commentFiles.length > 0 && (
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                                        {commentFiles.map((file, index) => (
                                            <span key={`${file.name}-${index}`} className="task-tag" style={{ maxWidth: "100%" }}>
                                                <span style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setCommentFiles((files) => files.filter((_, i) => i !== index))}
                                                    style={{ border: 0, background: "transparent", padding: 0, marginLeft: 5, color: "inherit", cursor: "pointer" }}
                                                    title="Remove attachment"
                                                >
                                                    <FontAwesomeIcon icon={faTimes} size="xs" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="comment-row">
                                    <MentionInput
                                        value={commentText}
                                        onChange={setCommentText}
                                        users={mentionableUsers}
                                        placeholder="Write a comment... use @ to mention someone"
                                        onSubmit={doComment}
                                        menuPlacement="up"
                                    />
                                    <label
                                        className="btn-send"
                                        title={`Attach files — ${ATTACHMENT_LIMIT_HELP}`}
                                        style={{ margin: 0, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                                    >
                                        <input
                                            ref={commentFileInputRef}
                                            type="file"
                                            multiple
                                            accept={COMMON_ATTACHMENT_ACCEPT}
                                            style={{ display: "none" }}
                                            onChange={(e) => {
                                                const picked = Array.from(e.target.files || []);
                                                const { accepted, errors } = validateAttachmentSelection(picked, commentFiles.length);
                                                showAttachmentValidationErrors(errors);
                                                if (accepted.length) setCommentFiles((files) => [...files, ...accepted]);
                                                e.target.value = "";
                                            }}
                                        />
                                        <FontAwesomeIcon icon={faLink} />
                                    </label>
                                    <button className="btn-send" onClick={doComment} disabled={(!commentText.trim() && commentFiles.length === 0) || actionInProgress}>
                                        <FontAwesomeIcon icon={faPaperPlane} />
                                    </button>
                                </div>
                                <div style={{ marginTop: 5, paddingLeft: 2, fontSize: "0.68rem", lineHeight: 1.35, color: "var(--gray-400)" }}>
                                    {ATTACHMENT_LIMIT_HELP}
                                </div>
                            </div>
                        ) : (
                            <div className="completed-notice" style={{ margin: 0 }}>
                                <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                                Task completed — comments are locked.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {switchTarget && switchFrom && (
                <div className="tk-modal-backdrop" onClick={closeSwitchModal}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <h5>Switch Task?</h5>
                        <p className="modal-sub">Currently working on:</p>
                        <div className="switch-preview">
                            <PriorityBadge priority={switchFrom.priority} />
                            <p className="mb-1 mt-2" style={{ fontWeight: 600 }}>{getTitle(switchFrom)}</p>
                            <div className="timer-inline">
                                <FontAwesomeIcon icon={faClock} size="xs" className="me-1" />
                                {formatTime(totalElapsed)} total time
                            </div>
                        </div>
                        <p className="mt-3 mb-0" style={{ fontSize: "0.9rem" }}>
                            Starting <strong>"{getTitle(switchTarget)}"</strong> will pause this task.
                            Your {formatTime(totalElapsed)} is saved and continues when you return.
                        </p>
                        <div className="modal-actions">
                            <button className="btn-sec" onClick={closeSwitchModal}>Cancel</button>
                            <button className="btn-prim" onClick={doSwitch} disabled={actionInProgress}>Switch Task</button>
                        </div>
                    </div>
                </div>
            )}

            {stuckTicket && (
                <div className="tk-modal-backdrop" onClick={closeStuckModal}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <h5>Mark as Waiting</h5>
                        <p className="modal-sub">Capture who/what this is waiting on so it can be chased up properly.</p>
                        <div className="form-field mb-3">
                            <label className="form-lbl">Who / what is being waited on</label>
                            <select className="form-ctrl" value={stuckWaitingOn} onChange={(e) => setStuckWaitingOn(e.target.value)} autoFocus>
                                {WAITING_ON_OPTIONS.map((w) => <option key={w} value={w}>{w}</option>)}
                            </select>
                        </div>
                        <div className="form-field mb-3">
                            <label className="form-lbl">Refer to</label>
                            <select className="form-ctrl" value={stuckResponsibleId} onChange={(e) => setStuckResponsibleId(e.target.value)}>
                                <option value="">Select person to refer this to...</option>
                                {assignableUsers.map((u) => (
                                    <option key={u.uid} value={u.uid}>
                                        {u.name || u.email}{u.uid === effectiveUid ? " (me)" : ""}
                                    </option>
                                ))}
                            </select>
                            {stuckResponsibleId && stuckResponsibleId !== effectiveUid && (
                                <p className="mb-0 mt-1" style={{ fontSize: "0.76rem", color: "#b45309" }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} size="xs" className="me-1" />
                                    This will appear in {nameOf(stuckResponsibleId)}'s Stuck / Waiting list.
                                </p>
                            )}
                        </div>
                        <div className="form-field mb-3">
                            <label className="form-lbl">Next chase date</label>
                            <input type="date" className="form-ctrl" value={stuckFollowUp} min={todayISO()}
                                onChange={(e) => setStuckFollowUp(e.target.value)} />
                        </div>
                        <div className="form-field mb-3">
                            <label className="form-lbl">Notes</label>
                            <textarea className="form-ctrl" rows={3}
                                placeholder="Why is this task waiting? Add any other useful context..."
                                value={stuckNotes} onChange={(e) => setStuckNotes(e.target.value)} />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-sec" onClick={closeStuckModal}>Cancel</button>
                            <button
                                className="btn-amber"
                                onClick={doMarkStuck}
                                disabled={!stuckNotes.trim() || !stuckWaitingOn || !stuckFollowUp || !stuckResponsibleId || actionInProgress}
                            >
                                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />Mark as Waiting
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {reassignTicket && (() => {
                const bwd = getBackwardUsers(reassignTicket);
                const fwd = getForwardUsers(reassignTicket);
                const others = getOtherUsers(reassignTicket);
                const forcedUsers = reassignForcedRole
                    ? assignableUsers.filter((u) => normalizeUserRole(u.user_role) === normalizeUserRole(reassignForcedRole) && u.uid !== reassignTicket.assignee_id)
                    : null;
                const nothingAvailable = reassignForcedRole
                    ? forcedUsers.length === 0
                    : (!isAdmin && bwd.length === 0 && fwd.length === 0 && others.length === 0);
                const curIdx = getStageIndex(reassignTicket);
                const selectedIdx = reassignUserId ? resolveStageIndex(reassignTicket, reassignUserId, reassignTargetStage) : null;
                const isGoingBack = selectedIdx !== null && selectedIdx < curIdx;
                const notesReady = reassignNotes.trim().length > 0;
                return (
                    <div className="tk-modal-backdrop" onClick={closeReassignModal}>
                        <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ width: "min(95vw, 780px)", maxWidth: "none" }}>
                            <h5>{reassignForcedLabel || "Reassign Task"}</h5>
                            {nothingAvailable ? (
                                <p className="modal-sub">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" style={{ color: "var(--amber)" }} />
                                    {reassignForcedRole
                                        ? `No one has the ${reassignForcedRole} role yet. Ask an admin to assign it to a teammate.`
                                        : "There's no one else to hand this task to right now. Ask an admin to add or assign a role to a teammate, or have an admin reassign this task directly."}
                                </p>
                            ) : (
                                <>
                                    <p className="modal-sub">Hand this task off to someone else. You set their due date now.</p>
                                    <div className="form-field mb-3">
                                        <label className="form-lbl">Assign to</label>
                                        <select
                                            className="form-ctrl"
                                            value={reassignUserId ? encodeAssignmentTarget(reassignUserId, reassignTargetStage) : ""}
                                            onChange={(e) => {
                                                const target = decodeAssignmentTarget(e.target.value);
                                                setReassignUserId(target.uid);
                                                setReassignTargetStage(target.stageKey);
                                            }}
                                            autoFocus
                                        >
                                            <option value="">Select person...</option>
                                            {reassignForcedRole ? (
                                                forcedUsers.map((u) => {
                                                    const fallbackStage = reassignTargetStage
                                                        || (getNextStage(reassignTicket)?.role === normalizeUserRole(u.user_role) ? getNextStage(reassignTicket)?.key : null)
                                                        || (getPrevStage(reassignTicket)?.role === normalizeUserRole(u.user_role) ? getPrevStage(reassignTicket)?.key : null)
                                                        || (u.user_role === "coordinator" ? "coordinator_qc" : STAGE_CHAIN.find((stage) => stage.role === u.user_role)?.key);
                                                    return (
                                                        <option key={encodeAssignmentTarget(u.uid, fallbackStage)} value={encodeAssignmentTarget(u.uid, fallbackStage)}>
                                                            {u.name || u.email}
                                                        </option>
                                                    );
                                                })
                                            ) : isAdmin ? (
                                                assignmentOptions
                                                    .filter((option) => option.uid !== reassignTicket.assignee_id)
                                                    .map((option) => (
                                                        <option key={encodeAssignmentTarget(option.uid, option.stageKey)} value={encodeAssignmentTarget(option.uid, option.stageKey)}>
                                                            {option.label}
                                                        </option>
                                                    ))
                                            ) : (
                                                <>
                                                    {bwd.length > 0 && (
                                                        <optgroup label="Suggested — Amendment Required">
                                                            {bwd.map((u) => {
                                                                const stageKey = getPrevStage(reassignTicket)?.key || null;
                                                                return <option key={encodeAssignmentTarget(u.uid, stageKey)} value={encodeAssignmentTarget(u.uid, stageKey)}>{u.name || u.email}</option>;
                                                            })}
                                                        </optgroup>
                                                    )}
                                                    {fwd.length > 0 && (
                                                        <optgroup label="Suggested — Forward">
                                                            {fwd.map((u) => {
                                                                const stageKey = getNextStage(reassignTicket)?.key || null;
                                                                return <option key={encodeAssignmentTarget(u.uid, stageKey)} value={encodeAssignmentTarget(u.uid, stageKey)}>{u.name || u.email}</option>;
                                                            })}
                                                        </optgroup>
                                                    )}
                                                    {others.length > 0 && (
                                                        <optgroup label="Other Team Members">
                                                            {others.flatMap((u) => assignmentOptions.filter((option) => option.uid === u.uid)).map((option) => (
                                                                <option key={encodeAssignmentTarget(option.uid, option.stageKey)} value={encodeAssignmentTarget(option.uid, option.stageKey)}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    )}
                                                </>
                                            )}
                                        </select>
                                    </div>
                                    <div className="form-field mb-3">
                                        <label className="form-lbl">Their due date</label>
                                        <input type="date" className="form-ctrl" value={reassignDueDate} min={todayISO()}
                                            onChange={(e) => setReassignDueDate(e.target.value)} />
                                    </div>
                                    <div className="form-field mb-3">
                                        <label className="form-lbl">Notes</label>
                                        <textarea className="form-ctrl" rows={3}
                                            placeholder={isGoingBack ? "What was completed, and why is this going back a stage?" : "Describe the work done on this task so far..."}
                                            value={reassignNotes} onChange={(e) => setReassignNotes(e.target.value)} />
                                    </div>
                                </>
                            )}
                            <div className="modal-actions">
                                <button className="btn-sec" onClick={closeReassignModal}>Cancel</button>
                                {!nothingAvailable && (
                                    <button
                                        className="btn-prim"
                                        disabled={!reassignUserId || !reassignDueDate || !notesReady || actionInProgress}
                                        onClick={() => setConfirmAction({
                                            title: isGoingBack ? "Confirm Amendment Required" : "Confirm Reassignment",
                                            message: `${isGoingBack ? "Mark as Amendment Required and send back" : "Reassign"} "${getTitle(reassignTicket)}" to ${nameOf(reassignUserId)}, due ${fmtDate(dateOnlyStartOfDay(reassignDueDate))}?`,
                                            onConfirm: doQuickReassign,
                                        })}
                                    >
                                        <FontAwesomeIcon icon={faUserCheck} className="me-2" />{isGoingBack ? "Amendment Required" : "Reassign"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {completeTicket && (() => {
                const nextStage = getNextStage(completeTicket);

                const suggestedForwardUsers = nextStage
                    ? assignableUsers.filter((u) =>
                        u.uid !== completeTicket.assignee_id &&
                        normalizeUserRole(u.user_role) === nextStage.role
                    )
                    : [];

                const suggestedTargetKeys = new Set(
                    suggestedForwardUsers.map((u) => encodeAssignmentTarget(u.uid, nextStage?.key))
                );

                const otherAssignmentOptions = assignmentOptions.filter((option) =>
                    option.uid !== completeTicket.assignee_id &&
                    !suggestedTargetKeys.has(encodeAssignmentTarget(option.uid, option.stageKey))
                );

                const creatorRole =
                    completeTicket?.created_by_role ||
                    normalizeUserRole(usersById[completeTicket?.created_by_id]?.user_role);

                const checklistRequired = creatorRole === "inspection";
                const assigneeFinishOption = completeTicket.assignee_id === effectiveUid;
                const creatorFinishOption = creatorCanFinalComplete(completeTicket);
                const adminFinishOption = adminOwnsActiveTicket(completeTicket);
                const finalFinishOption = assigneeFinishOption || adminFinishOption;


                const forwardIsOptional = finalFinishOption;
                const hasForwardOptions =
                    suggestedForwardUsers.length > 0 || otherAssignmentOptions.length > 0;

                const isStaffingGap = !forwardIsOptional && !hasForwardOptions;
                const selectionRequired = hasForwardOptions && !forwardIsOptional;
                const willForward = hasForwardOptions && !!completeNextUserId;
                const finalCompletion = Boolean(
                    finalFinishOption && !willForward
                );

                const showForwardDueDate = hasForwardOptions && (!finalFinishOption || willForward);

                const canSubmit =
                    !actionInProgress &&
                    canCompleteTicketStep(completeTicket) &&
                    !isStaffingGap &&
                    completeNotes.trim().length > 0 &&
                    !(selectionRequired && !completeNextUserId) &&
                    !(willForward && !completeDueDate) &&
                    !(
                        willForward &&
                        checklistRequired &&
                        !isHandoverChecklistComplete(completeChecklist)
                    );

                return (
                    <div className="tk-modal-backdrop" onClick={closeCompleteModal}>
                        <div className="modal-box" onClick={(e) => e.stopPropagation()} style={willForward ? { width: "min(95vw, 780px)", maxWidth: "none" } : undefined}>
                            <h5>Complete Step</h5>
                            <div style={{
                                margin: "8px 0 10px",
                                padding: "10px 12px",
                                border: "1px solid var(--gray-200)",
                                borderRadius: 8,
                                background: "var(--gray-50)",
                                display: "grid",
                                gap: 4,
                                fontSize: "0.86rem",
                            }}>
                                <div><strong>Building:</strong> {completeTicket.building_name || "—"}</div>
                                <div><strong>Ticket:</strong> {getTitle(completeTicket)}</div>
                            </div>
                            <div style={{
                                margin: "0 0 14px",
                                padding: "9px 11px",
                                borderRadius: 8,
                                background: completeTicket.assignee_id === actor.uid ? "rgba(16,185,129,0.08)" : "rgba(100,116,139,0.08)",
                                color: completeTicket.assignee_id === actor.uid ? "#065f46" : "#475569",
                                fontSize: "0.78rem",
                                lineHeight: 1.45,
                            }}>
                                {completeTicket.assignee_id === actor.uid
                                    ? "This completes your current work stage. Your stage rewards are calculated now; forwarding then starts a separate stage for the next person."
                                    : "You are closing this as an admin, not as the assigned worker. This closure will not create work-stage points for you or the current assignee."}
                            </div>

                            {isStaffingGap ? (
                                <p className="modal-sub">
                                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" style={{ color: "var(--amber)" }} />
                                    There is no other assignable team member to forward this task to. Add or assign a teammate first.
                                </p>
                            ) : (
                                <>
                                    {/* <p className="modal-sub">
                                        Complete your part and forward the task to another team member.
                                        {nextStage && ` Suggested next role: ${nextStage.label}.`}
                                        {checklistRequired && " Handover questions are required because this task was created by an Inspection user, and they stay with the task through each handoff."}
                                        {creatorFinishOption
                                            ? " Because you created this ticket and it is currently assigned to you, you may also leave ‘Forward to’ blank to fully complete the ticket."
                                            : " The workflow chain does not grant final-completion permission."}
                                    </p> */}

                                    <div className="form-field mb-3">
                                        <label className="form-lbl">What was completed <span className="form-opt">(required)</span></label>
                                        <textarea className="form-ctrl" rows={3}
                                            placeholder="Describe the work done on this stage..."
                                            value={completeNotes} onChange={(e) => setCompleteNotes(e.target.value)} autoFocus />
                                    </div>

                                    <div className="form-field mb-3">
                                        <label className="form-lbl">Attachments <span className="form-opt">(optional)</span></label>
                                        <label className="file-pick" style={{ marginBottom: completeFiles.length ? 8 : 0 }}>
                                            <input
                                                type="file"
                                                multiple
                                                accept={COMMON_ATTACHMENT_ACCEPT}
                                                onChange={(e) => {
                                                    const picked = Array.from(e.target.files || []);
                                                    const { accepted, errors } = validateAttachmentSelection(picked, completeFiles.length);
                                                    showAttachmentValidationErrors(errors);
                                                    if (accepted.length) setCompleteFiles((files) => [...files, ...accepted]);
                                                    e.target.value = "";
                                                }}
                                            />
                                            <FontAwesomeIcon icon={faLink} />
                                            <span>{completeFiles.length ? "Add more files..." : "Choose files..."}</span>
                                        </label>
                                        <div style={{ marginTop: 5, fontSize: "0.7rem", color: "var(--gray-400)" }}>{ATTACHMENT_LIMIT_HELP}</div>
                                        {completeFiles.length > 0 && (
                                            <div style={{ display: "grid", gap: 6 }}>
                                                {completeFiles.map((file, index) => (
                                                    <div key={`${file.name}-${index}`} style={{
                                                        display: "flex", alignItems: "center", gap: 8,
                                                        padding: "7px 9px", border: "1px solid var(--gray-200)",
                                                        borderRadius: 7, background: "var(--gray-50)",
                                                    }}>
                                                        <FontAwesomeIcon icon={file.type?.startsWith("image/") ? faImage : faDownload} size="xs" />
                                                        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                                                            {file.name}{file.size ? ` · ${formatFileSize(file.size)}` : ""}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => setCompleteFiles((files) => files.filter((_, i) => i !== index))}
                                                            style={{ border: 0, background: "transparent", padding: 2, cursor: "pointer", color: "var(--gray-500)" }}
                                                            title="Remove attachment"
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} size="xs" />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {hasForwardOptions && (
                                        <div className="form-field mb-3">
                                            <label className="form-lbl">
                                                Forward to {forwardIsOptional && <span className="form-opt">(optional)</span>}
                                            </label>
                                            <select
                                                className="form-ctrl"
                                                value={completeNextUserId ? encodeAssignmentTarget(completeNextUserId, completeNextStageKey) : ""}
                                                onChange={(e) => {
                                                    const target = decodeAssignmentTarget(e.target.value);
                                                    setCompleteNextUserId(target.uid);
                                                    setCompleteNextStageKey(target.stageKey);
                                                }}
                                            >
                                                <option value="">
                                                    {finalFinishOption
                                                        ? adminFinishOption && !assigneeFinishOption
                                                            ? "— Final complete ticket now (admin close) —"
                                                            : "— Final complete ticket now —"
                                                        : "Select person..."}
                                                </option>
                                                {suggestedForwardUsers.length > 0 && (
                                                    <optgroup label={`Suggested — ${nextStage?.label || "Next Step"}`}>
                                                        {suggestedForwardUsers.map((u) => (
                                                            <option
                                                                key={encodeAssignmentTarget(u.uid, nextStage?.key)}
                                                                value={encodeAssignmentTarget(u.uid, nextStage?.key)}
                                                            >
                                                                {u.name || u.email}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                )}
                                                {otherAssignmentOptions.length > 0 && (
                                                    <optgroup label="Other Team Members">
                                                        {otherAssignmentOptions.map((option) => (
                                                            <option
                                                                key={encodeAssignmentTarget(option.uid, option.stageKey)}
                                                                value={encodeAssignmentTarget(option.uid, option.stageKey)}
                                                            >
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                )}
                                            </select>
                                        </div>
                                    )}

                                    {showForwardDueDate && (
                                        <div className="form-field mb-3">
                                            <label className="form-lbl">
                                                Their due date <span className="form-opt">(required)</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="form-ctrl"
                                                value={completeDueDate}
                                                min={todayISO()}
                                                onChange={(e) => setCompleteDueDate(e.target.value)}
                                            />
                                        </div>
                                    )}

                                    {willForward && checklistRequired && (
                                        <HandoverChecklist
                                            checklist={completeChecklist}
                                            onChange={(key, entry) => setCompleteChecklist((c) => ({ ...c, [key]: entry }))}
                                            previousLog={completeTicket.handover_log || []}
                                        />
                                    )}

                                    {finalCompletion && (
                                        <p className="mt-2 mb-0" style={{ fontSize: "0.9rem" }}>
                                            <FontAwesomeIcon icon={faClock} size="xs" className="me-1" />
                                            Total recorded ticket time: {formatTime(
                                                Math.max(0, Number(completeTicket.total_time_seconds || 0)) +
                                                (completeTicket.status === "active" && completeTicket.activeAt ? secsSince(completeTicket.activeAt) : 0)
                                            )}
                                        </p>
                                    )}
                                </>
                            )}

                            <div className="modal-actions">
                                <button className="btn-sec" onClick={closeCompleteModal}>Cancel</button>
                                <button
                                    className="btn-prim"
                                    disabled={!canSubmit}
                                    onClick={() => setConfirmAction({
                                        title: willForward
                                            ? "Confirm Complete & Forward"
                                            : "Confirm Final Completion",
                                        message: willForward
                                            ? `Mark your step done and forward "${getTitle(completeTicket)}" to ${nameOf(completeNextUserId)}, due ${fmtDate(dateOnlyStartOfDay(completeDueDate))}?`
                                            : adminFinishOption && !assigneeFinishOption
                                                ? `Finally complete "${getTitle(completeTicket)}" as admin because it is active on your account? This closes the ticket without creating work-stage points for the admin or current assignee.`
                                                : creatorFinishOption
                                                    ? `Finally complete "${getTitle(completeTicket)}" as its creator and current assignee? Your current stage will be rewarded and the ticket will close.`
                                                    : `Finally complete "${getTitle(completeTicket)}" as the current assignee? Your current stage will be rewarded and the ticket will close.`,
                                        onConfirm: doCompleteStage,
                                    })}
                                >
                                    <FontAwesomeIcon icon={faCircleCheck} className="me-2" />
                                    {isStaffingGap
                                        ? "Can't Complete Yet"
                                        : willForward
                                            ? "Complete & Forward"
                                            : "Final Complete"}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {showNoteModal && (
                <div className="tk-modal-backdrop" onClick={() => setShowNoteModal(false)}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <h5>Add Note</h5>
                        <div className="form-field mt-3">
                            <MentionInput
                                value={noteText}
                                onChange={setNoteText}
                                users={mentionableUsers}
                                multiline
                                rows={4}
                                placeholder="Write a note about this task... use @ to mention someone"
                                autoFocus
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-sec" onClick={() => { setShowNoteModal(false); setNoteText(""); }}>Cancel</button>
                            <button className="btn-prim" onClick={doAddNote} disabled={!noteText.trim() || actionInProgress}>Add Note</button>
                        </div>
                    </div>
                </div>
            )}

            {emailLogTicket && (
                <div className="tk-modal-backdrop" onClick={closeEmailLogModal}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <h5>Log Email Sent</h5>
                        <p className="modal-sub">Record that an email was sent about this task, so it shows in Recent Activity.</p>
                        <div className="form-field mb-3">
                            <label className="form-lbl">To</label>
                            <input type="text" className="form-ctrl" placeholder="Recipient name or email"
                                value={emailTo} onChange={(e) => setEmailTo(e.target.value)} autoFocus />
                        </div>
                        <div className="form-field mb-3">
                            <label className="form-lbl">Subject</label>
                            <input type="text" className="form-ctrl" placeholder="What the email was about"
                                value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                        </div>
                        <div className="form-field mb-3">
                            <label className="form-lbl">Notes <span className="form-opt">(optional)</span></label>
                            <textarea className="form-ctrl" rows={3}
                                placeholder="Any extra detail worth keeping..."
                                value={emailNotes} onChange={(e) => setEmailNotes(e.target.value)} />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-sec" onClick={closeEmailLogModal}>Cancel</button>
                            <button className="btn-prim" onClick={doLogEmail} disabled={!emailTo.trim() || !emailSubject.trim() || actionInProgress}>
                                <FontAwesomeIcon icon={faPaperPlane} className="me-2" />Log Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmAction && (
                <div className="tk-modal-backdrop" onClick={() => setConfirmAction(null)}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <h5>{confirmAction.title}</h5>
                        <p className="modal-sub">{confirmAction.message}</p>
                        <div className="modal-actions">
                            <button className="btn-sec" onClick={() => setConfirmAction(null)}>Cancel</button>
                            <button
                                className="btn-prim"
                                disabled={actionInProgress}
                                onClick={async () => {
                                    const action = confirmAction;
                                    setConfirmAction(null);
                                    await action.onConfirm();
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {attachmentPreview && (() => {
                const previewAttachment = normalizeAttachment(attachmentPreview);
                if (!previewAttachment?.url) return null;
                const isImage = attachmentIsImage(previewAttachment);
                const isPdf = attachmentIsPdf(previewAttachment);
                const title = previewAttachment.name || "Attachment";
                return (
                    <div className="tk-modal-backdrop" style={{ zIndex: 10060 }} onClick={() => setAttachmentPreview(null)}>
                        <div className="modal-box attachment-preview-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="attachment-preview-hdr">
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
                                    <div style={{ marginTop: 2, fontSize: "0.76rem", color: "var(--gray-500)" }}>
                                        {isImage ? "Image" : isPdf ? "PDF document" : "File"}
                                        {formatFileSize(previewAttachment.size) ? ` · ${formatFileSize(previewAttachment.size)}` : ""}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-sec"
                                    style={{ padding: "6px 10px", flexShrink: 0 }}
                                    onClick={() => setAttachmentPreview(null)}
                                    aria-label="Close file preview"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>
                            <div className="attachment-preview-body">
                                {isImage ? (
                                    <img src={previewAttachment.url} alt={title} />
                                ) : isPdf ? (
                                    <iframe src={previewAttachment.url} title={title} />
                                ) : (
                                    <div className="attachment-preview-fallback">
                                        <div style={{ fontSize: "2rem", marginBottom: 10 }}><FontAwesomeIcon icon={faDownload} /></div>
                                        <div style={{ fontWeight: 800, color: "#1f2937" }}>Preview is not available for this file type</div>
                                        <div style={{ marginTop: 6, fontSize: "0.84rem" }}>You can still open or download the file using the buttons below.</div>
                                    </div>
                                )}
                            </div>
                            <div className="attachment-preview-actions">
                                <a className="btn-sec" href={previewAttachment.url} download={title} onClick={(e) => e.stopPropagation()}>
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />Download
                                </a>
                                <a className="btn-prim" href={previewAttachment.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                    <FontAwesomeIcon icon={faLink} className="me-2" />Open in new tab
                                </a>
                            </div>
                        </div>
                    </div>
                );
            })()}

            <MediaViewerModal media={mediaPreview} onClose={() => setMediaPreview(null)} />

            {doorReviewModal && effectiveRole === "reviewer" && (
                <Review
                    directOpen={true}
                    directReport={doorReviewModal}
                    onDirectClose={() => setDoorReviewModal(null)}
                />
            )}

            {managerWorkModal && (
                <ReactModal
                    isOpen={true}
                    onRequestClose={() => setManagerWorkModal(null)}
                    shouldCloseOnOverlayClick={true}
                    shouldCloseOnEsc={true}
                    contentLabel="Manager Work Details"
                    style={{
                        overlay: {
                            position: "fixed",
                            inset: 0,
                            backgroundColor: "rgba(15, 23, 42, 0.55)",
                            zIndex: 9999,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                        },
                        content: {
                            position: "relative",
                            inset: "auto",
                            width: "min(900px, 96vw)",
                            minWidth: "min(720px, 96vw)",
                            maxHeight: "88vh",
                            padding: 0,
                            overflow: "hidden",
                            border: "none",
                            borderRadius: "14px",
                            background: "#fff",
                            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
                        },
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            maxHeight: "88vh",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                padding: "16px 20px",
                                borderBottom: "1px solid #e5e7eb",
                                flexShrink: 0,
                            }}
                        >
                            <div>
                                <h5
                                    style={{
                                        margin: 0,
                                        fontSize: "1.05rem",
                                        fontWeight: 700,
                                        color: "#111827",
                                    }}
                                >
                                    {doorLabel(managerWorkModal)} · Work Details
                                </h5>

                                <div
                                    style={{
                                        marginTop: 3,
                                        fontSize: "0.78rem",
                                        color: "#64748b",
                                    }}
                                >
                                    Completed remedial work information
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setManagerWorkModal(null)}
                                style={{
                                    width: 34,
                                    height: 34,
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                    background: "#fff",
                                    color: "#64748b",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div
                            style={{
                                padding: "20px",
                                overflowY: "auto",
                                flex: 1,
                            }}
                        >
                            <div
                                style={{
                                    display: "grid",
                                    gap: 18,
                                }}
                            >
                                <div
                                    style={{
                                        padding: "14px 16px",
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 10,
                                        background: "#f8fafc",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "0.72rem",
                                            fontWeight: 800,
                                            color: "#64748b",
                                            marginBottom: 6,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.04em",
                                        }}
                                    >
                                        Work Needed
                                    </div>

                                    <div
                                        style={{
                                            whiteSpace: "pre-wrap",
                                            color: "#1f2937",
                                            fontSize: "0.9rem",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {managerWorkModal.workNeeded ||
                                            "Nothing recorded."}
                                    </div>
                                </div>

                                <div
                                    style={{
                                        padding: "14px 16px",
                                        border: "1px solid #bbf7d0",
                                        borderRadius: 10,
                                        background: "#f0fdf4",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "0.72rem",
                                            fontWeight: 800,
                                            color: "#15803d",
                                            marginBottom: 6,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.04em",
                                        }}
                                    >
                                        Work Done
                                    </div>

                                    <div
                                        style={{
                                            whiteSpace: "pre-wrap",
                                            color: "#166534",
                                            fontSize: "0.9rem",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {managerWorkModal.workDone ||
                                            "Nothing recorded."}
                                    </div>
                                </div>

                                <div>
                                    <div
                                        style={{
                                            fontSize: "0.72rem",
                                            fontWeight: 800,
                                            color: "#64748b",
                                            marginBottom: 10,
                                            textTransform: "uppercase",
                                            letterSpacing: "0.04em",
                                        }}
                                    >
                                        Work Done Photos
                                    </div>

                                    {(() => {
                                        const photos = [
                                            ...(Array.isArray(
                                                managerWorkModal.workDonePhotos
                                            )
                                                ? managerWorkModal.workDonePhotos
                                                : []),

                                            ...(managerWorkModal.workDonePhoto
                                                ? [managerWorkModal.workDonePhoto]
                                                : []),
                                        ].filter(Boolean);

                                        return photos.length === 0 ? (
                                            <div
                                                style={{
                                                    padding: "18px",
                                                    border: "1px dashed #cbd5e1",
                                                    borderRadius: 10,
                                                    color: "#94a3b8",
                                                    fontSize: "0.84rem",
                                                    textAlign: "center",
                                                }}
                                            >
                                                No work-done photos.
                                            </div>
                                        ) : (
                                            <div
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns:
                                                        "repeat(auto-fill, minmax(150px, 1fr))",
                                                    gap: 12,
                                                }}
                                            >
                                                {photos.map((photo, index) => (
                                                    <button
                                                        key={`${photo}-${index}`}
                                                        type="button"
                                                        onClick={() =>
                                                            setMediaPreview({
                                                                src: photo,
                                                                label: `Work done ${index + 1}`,
                                                                isVideo: false,
                                                            })
                                                        }
                                                        style={{
                                                            padding: 0,
                                                            border: "1px solid #e5e7eb",
                                                            borderRadius: 10,
                                                            overflow: "hidden",
                                                            background: "#fff",
                                                            cursor: "pointer",
                                                        }}
                                                    >
                                                        <img
                                                            src={photo}
                                                            alt={`Work done ${index + 1}`}
                                                            style={{
                                                                display: "block",
                                                                width: "100%",
                                                                height: 130,
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 12,
                                        padding: "12px 14px",
                                        borderRadius: 9,
                                        background: "#f8fafc",
                                        border: "1px solid #e5e7eb",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "0.74rem",
                                            fontWeight: 700,
                                            color: "#64748b",
                                        }}
                                    >
                                        Work Done Updated At
                                    </div>

                                    <div
                                        style={{
                                            color: "#111827",
                                            fontSize: "0.84rem",
                                            fontWeight: 600,
                                        }}
                                    >
                                        {managerWorkModal.workDoneUpdatedAt
                                            ? fmtTimestamp(
                                                managerWorkModal.workDoneUpdatedAt
                                            )
                                            : "—"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                padding: "14px 20px",
                                borderTop: "1px solid #e5e7eb",
                                background: "#f8fafc",
                                flexShrink: 0,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => setManagerWorkModal(null)}
                                style={{
                                    padding: "8px 16px",
                                    borderRadius: 8,
                                    border: "1px solid #d1d5db",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: "0.82rem",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </ReactModal>
            )}

            {remedialInfoModal && (
                <ReactModal
                    isOpen={true}
                    onRequestClose={closeRemedialInfoModal}
                    shouldCloseOnOverlayClick={true}
                    shouldCloseOnEsc={true}
                    contentLabel="Remedial Info"
                    style={{
                        overlay: {
                            position: "fixed",
                            inset: 0,
                            backgroundColor: "rgba(15, 23, 42, 0.6)",
                            zIndex: 9999,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                            backdropFilter: "blur(2px)",
                        },
                        content: {
                            position: "relative",
                            inset: "auto",
                            width: "min(560px, 96vw)",
                            maxHeight: "88vh",
                            padding: 0,
                            overflow: "hidden",
                            border: "none",
                            borderRadius: "16px",
                            background: "#fff",
                            boxShadow: "0 24px 70px rgba(15, 23, 42, 0.3)",
                        },
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", maxHeight: "88vh" }}>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 12,
                                padding: "20px 22px",
                                flexShrink: 0,
                                background: "#fff",
                                borderBottom: "1px solid #e5e7eb",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 10,
                                        flexShrink: 0,
                                        background: "rgba(124,58,237,0.1)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#7c3aed",
                                        fontSize: "1rem",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faClipboardCheck} />
                                </div>

                                <div>
                                    <h5
                                        style={{
                                            margin: 0,
                                            fontSize: "1.05rem",
                                            fontWeight: 700,
                                            color: "#111827",
                                        }}
                                    >
                                        Remedial Info
                                    </h5>

                                    <div style={{ marginTop: 2, fontSize: "0.8rem", color: "#64748b" }}>
                                        {doorLabel(remedialInfoModal)}
                                        {remedialInfoModal.buildingName
                                            ? ` · ${remedialInfoModal.buildingName}`
                                            : ""}
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={closeRemedialInfoModal}
                                style={{
                                    width: 30,
                                    height: 30,
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                    background: "#fff",
                                    color: "#64748b",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} size="sm" />
                            </button>
                        </div>

                        <div style={{ padding: "22px", overflowY: "auto", flex: 1 }}>

                            <div style={{ marginBottom: 18 }}>
                                <div
                                    style={{
                                        fontSize: "0.7rem",
                                        fontWeight: 800,
                                        color: "#94a3b8",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        marginBottom: 6,
                                    }}
                                >
                                    Work Required
                                </div>

                                <div
                                    style={{
                                        padding: "12px 14px",
                                        borderRadius: 10,
                                        background: "#f8fafc",
                                        border: "1px solid #e2e8f0",
                                        color: "#334155",
                                        fontSize: "0.87rem",
                                        lineHeight: 1.55,
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {remedialInfoModal.workNeeded ? (
                                        <div
                                            style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "4px 16px",
                                            }}
                                        >
                                            {remedialInfoModal.workNeeded
                                                .split(/\||\n/)
                                                .map((item) => item.trim())
                                                .filter(Boolean)
                                                .map((item, idx) => (
                                                    <span
                                                        key={idx}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 6,
                                                            fontSize: "0.85rem",
                                                            color: "#374151",
                                                        }}
                                                    >
                                                        <span style={{ color: "#7c3aed", lineHeight: 1.4 }}>•</span>
                                                        <span>{item}</span>
                                                    </span>
                                                ))}
                                        </div>
                                    ) : (
                                        <p style={{ margin: 0, fontSize: "0.82rem", color: "#6b7280" }}>
                                            No work-needed notes recorded.
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="form-field mb-3">
                                <label className="form-lbl">Job Time</label>

                                <div style={{ display: "flex", gap: 8 }}>
                                    {["Half day", "Full day"].map((opt) => (
                                        <button
                                            key={opt}
                                            type="button"
                                            onClick={() => setRemedialJobTime(opt)}
                                            style={{
                                                flex: 1,
                                                padding: "10px 12px",
                                                borderRadius: 10,
                                                border:
                                                    remedialJobTime === opt
                                                        ? "1.5px solid #7c3aed"
                                                        : "1px solid #e2e8f0",
                                                background:
                                                    remedialJobTime === opt
                                                        ? "rgba(124,58,237,0.08)"
                                                        : "#fff",
                                                color:
                                                    remedialJobTime === opt
                                                        ? "#6d28d9"
                                                        : "#475569",
                                                fontSize: "0.86rem",
                                                fontWeight: 700,
                                                cursor: "pointer",
                                                transition: "all 0.15s ease",
                                            }}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>

                                {remedialJobTime && (
                                    <div
                                        style={{
                                            marginTop: 8,
                                            padding: "8px 10px",
                                            borderRadius: 8,
                                            background: "#f8fafc",
                                            border: "1px solid #e2e8f0",
                                            color: "#64748b",
                                            fontSize: "0.76rem",
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {remedialJobTime === "Half day"
                                            ? "Requires 1 booking slot. The customer will choose Morning or Afternoon when booking."
                                            : "Requires both booking slots for the day."}
                                    </div>
                                )}
                            </div>

                            <div className="form-field mb-1">
                                <label className="form-lbl">
                                    Notes <span className="form-opt">(optional)</span>
                                </label>

                                <textarea
                                    rows={3}
                                    placeholder="Any additional info for the remedial job..."
                                    value={remedialNotes}
                                    onChange={(e) => setRemedialNotes(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "10px 12px",
                                        borderRadius: 8,
                                        border: "1.5px solid #111827",
                                        fontSize: "0.87rem",
                                        color: "#1f2937",
                                        outline: "none",
                                        resize: "vertical",
                                        fontFamily: "inherit",
                                        maxHeight: "160px",
                                        overflowY: "auto",
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = "#7c3aed";
                                        e.target.style.boxShadow = "0 0 0 3px rgba(124,58,237,0.15)";
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = "#111827";
                                        e.target.style.boxShadow = "none";
                                    }}
                                />
                            </div>

                            {remedialInfoEntries.length > 0 && (
                                <div style={{ marginTop: 20 }}>
                                    <div
                                        style={{
                                            fontSize: "0.7rem",
                                            fontWeight: 800,
                                            color: "#94a3b8",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.05em",
                                            marginBottom: 8,
                                        }}
                                    >
                                        Previous Entries
                                    </div>

                                    <div
                                        style={{
                                            display: "grid",
                                            gap: 8,
                                            maxHeight: "260px",
                                            overflowY: "auto",
                                            paddingRight: 4,
                                        }}
                                    >
                                        {remedialInfoEntries.map((entry) => {
                                            const entrySlotsRequired =
                                                Number(entry.slots_required)
                                                || (entry.job_time === "Full day" ? 2 : 1);

                                            return (
                                                <div
                                                    key={entry.id}
                                                    style={{
                                                        border: "1px solid #e2e8f0",
                                                        borderRadius: 10,
                                                        padding: "11px 13px",
                                                        background: "#fff",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            gap: 8,
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 8,
                                                                flexWrap: "wrap",
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    padding: "2px 9px",
                                                                    borderRadius: 999,
                                                                    background: "rgba(124,58,237,0.1)",
                                                                    color: "#7c3aed",
                                                                    fontSize: "0.7rem",
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                {entry.job_time || "—"}
                                                            </span>

                                                            <span
                                                                style={{
                                                                    padding: "2px 8px",
                                                                    borderRadius: 999,
                                                                    background: "#f1f5f9",
                                                                    color: "#475569",
                                                                    fontSize: "0.68rem",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                {entrySlotsRequired} {entrySlotsRequired === 1 ? "slot" : "slots"}
                                                            </span>

                                                            <span
                                                                style={{
                                                                    fontSize: "0.78rem",
                                                                    fontWeight: 600,
                                                                    color: "var(--gray-700)",
                                                                }}
                                                            >
                                                                {entry.created_by_name || "Unknown"}
                                                            </span>
                                                        </div>

                                                        <span
                                                            style={{
                                                                fontSize: "0.74rem",
                                                                color: "var(--gray-400)",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {entry.created_at
                                                                ? fmtTimestamp(entry.created_at)
                                                                : "—"}
                                                        </span>
                                                    </div>

                                                    {entry.notes && (
                                                        <div
                                                            style={{
                                                                marginTop: 6,
                                                                fontSize: "0.8rem",
                                                                color: "var(--gray-600)",
                                                            }}
                                                        >
                                                            {entry.notes}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 10,
                                padding: "14px 22px",
                                borderTop: "1px solid #e5e7eb",
                                background: "#f8fafc",
                                flexShrink: 0,
                            }}
                        >
                            <button
                                type="button"
                                onClick={closeRemedialInfoModal}
                                style={{
                                    padding: "9px 16px",
                                    borderRadius: 9,
                                    border: "1px solid #d1d5db",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: "0.84rem",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                }}
                            >
                                Close
                            </button>

                            <button
                                type="button"
                                onClick={saveRemedialInfo}
                                disabled={!remedialJobTime || remedialSaving}
                                style={{
                                    padding: "9px 18px",
                                    borderRadius: 9,
                                    border: "none",
                                    background: remedialSaving ? "#a78bfa" : "#7c3aed",
                                    color: "#fff",
                                    fontSize: "0.84rem",
                                    fontWeight: 700,
                                    cursor: remedialSaving ? "not-allowed" : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 7,
                                }}
                            >
                                <FontAwesomeIcon icon={faCircleCheck} size="sm" />
                                {remedialSaving ? "Saving..." : "Save Remedial Info"}
                            </button>
                        </div>
                    </div>
                </ReactModal>
            )}

            {bookingModal && (
                <ReactModal
                    isOpen={true}
                    onRequestClose={() => setBookingModal(null)}
                    shouldCloseOnOverlayClick={true}
                    shouldCloseOnEsc={true}
                    style={{
                        overlay: {
                            position: "fixed",
                            inset: 0,
                            backgroundColor: "rgba(15, 23, 42, 0.55)",
                            zIndex: 9999,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px",
                        },
                        content: {
                            position: "relative",
                            inset: "auto",

                            width: "min(1200px, 96vw)",
                            minWidth: "900px",

                            height: "min(760px, 88vh)",
                            minHeight: "560px",
                            maxHeight: "88vh",

                            overflow: "hidden",
                            padding: 0,
                            border: "none",
                            borderRadius: "14px",
                            background: "#fff",
                            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
                        },
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",

                            width: "100%",
                            height: "100%",
                            minHeight: "560px",

                            overflow: "hidden",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                padding: "16px 20px",
                                borderBottom: "1px solid #e5e7eb",
                                flexShrink: 0,
                            }}
                        >
                            <div>
                                <h5
                                    style={{
                                        margin: 0,
                                        fontSize: "1.05rem",
                                        fontWeight: 700,
                                        color: "#111827",
                                    }}
                                >
                                    {bookingModal.bookingInfo?.buildingName ||
                                        bookingModal.ticket?.building_name ||
                                        "Resident Bookings"}
                                </h5>

                                <div
                                    style={{
                                        marginTop: 3,
                                        fontSize: "0.78rem",
                                        color: "#64748b",
                                    }}
                                >
                                    Resident bookings for attached inspection dates
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setBookingModal(null)}
                                style={{
                                    width: 34,
                                    height: 34,
                                    border: "1px solid #e5e7eb",
                                    borderRadius: 8,
                                    background: "#fff",
                                    color: "#64748b",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div
                            style={{
                                padding: "12px 20px",
                                borderBottom: "1px solid #f1f5f9",
                                background: "#f8fafc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                flexWrap: "wrap",
                                flexShrink: 0,
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    gap: 8,
                                    flexWrap: "wrap",
                                }}
                            >
                                {(bookingModal.bookingInfo?.days || []).map((day) => (
                                    <span
                                        key={`${day.inspectionUid}-${day.dateId}`}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 5,
                                            padding: "5px 9px",
                                            borderRadius: 999,
                                            background: "#fff",
                                            border: "1px solid #e2e8f0",
                                            fontSize: "0.75rem",
                                            fontWeight: 600,
                                            color: "#475569",
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faCalendar}
                                            size="xs"
                                        />

                                        {day.date || day.dateId}

                                        <strong style={{ color: "#2563eb" }}>
                                            {day.totalBookings ?? day.bookings?.length ?? 0}
                                        </strong>
                                    </span>
                                ))}
                            </div>

                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: 90,
                                    padding: "6px 12px",
                                    borderRadius: 999,
                                    background: "rgba(37,99,235,0.1)",
                                    color: "#2563eb",
                                    fontSize: "0.78rem",
                                    fontWeight: 800,
                                }}
                            >
                                {bookingModal.bookingInfo?.totalBookings ??
                                    bookingModal.bookings?.length ??
                                    0}{" "}
                                booking
                                {(bookingModal.bookingInfo?.totalBookings ??
                                    bookingModal.bookings?.length ??
                                    0) === 1
                                    ? ""
                                    : "s"}
                            </span>
                        </div>

                        <div
                            style={{
                                overflow: "auto",
                                flex: 1,
                                minHeight: 0,
                            }}
                        >
                            {!bookingModal.bookings?.length ? (
                                <div
                                    style={{
                                        minHeight: 180,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#94a3b8",
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    Nothing to show here.
                                </div>
                            ) : (
                                <table
                                    style={{
                                        width: "100%",
                                        minWidth: 1150,
                                        borderCollapse: "collapse",
                                        fontSize: "0.8rem",
                                    }}
                                >
                                    <thead
                                        style={{
                                            position: "sticky",
                                            top: 0,
                                            zIndex: 2,
                                            background: "#f8fafc",
                                        }}
                                    >
                                        <tr>
                                            {[
                                                "Flat",
                                                "Date",
                                                "Time",
                                                "Email",
                                                "Phone",
                                                "Tenant Email",
                                                "Tenant Phone",
                                                "Keys",
                                                "Terms",
                                                "Status",
                                                "Email Sent",
                                                "Submitted",
                                                "Actions",
                                            ].map((heading) => (
                                                <th
                                                    key={heading}
                                                    style={{
                                                        padding: "11px 12px",
                                                        borderBottom: "1px solid #e2e8f0",
                                                        textAlign: "left",
                                                        whiteSpace: "nowrap",
                                                        fontSize: "0.7rem",
                                                        fontWeight: 700,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.04em",
                                                        color: "#64748b",
                                                    }}
                                                >
                                                    {heading}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {bookingModal.bookings.map((booking) => {
                                            const confirmed = Boolean(booking.firstSlotUid);

                                            const emailSent =
                                                booking.emailSent === true ||
                                                booking.emailSent === "yes";

                                            const submittedAt =
                                                booking.submittedAt ||
                                                booking.createdAt;

                                            const submittedText = submittedAt
                                                ? fmtTimestamp(submittedAt)
                                                : "—";

                                            return (
                                                <tr
                                                    key={`${booking.dateId}-${booking.id}`}
                                                    style={{
                                                        background: "#fff",
                                                    }}
                                                >
                                                    <td style={bookingCellStyle}>
                                                        <strong
                                                            style={{
                                                                color: "#111827",
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {booking.flatNo || "—"}
                                                        </strong>
                                                    </td>

                                                    <td
                                                        style={{
                                                            ...bookingCellStyle,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {booking.firstDate ||
                                                            booking.date ||
                                                            booking.dateId ||
                                                            "—"}
                                                    </td>

                                                    <td
                                                        style={{
                                                            ...bookingCellStyle,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {booking.firstDate_from
                                                            ? `${booking.firstDate_from}${booking.firstDate_to
                                                                ? ` - ${booking.firstDate_to}`
                                                                : ""
                                                            }`
                                                            : "—"}
                                                    </td>

                                                    <td style={bookingCellStyle}>
                                                        {booking.email ? (
                                                            <a
                                                                href={`mailto:${booking.email}`}
                                                                style={{
                                                                    color: "#2563eb",
                                                                    textDecoration: "none",
                                                                }}
                                                            >
                                                                {booking.email}
                                                            </a>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>

                                                    <td
                                                        style={{
                                                            ...bookingCellStyle,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {booking.phone ? (
                                                            <a
                                                                href={`tel:${booking.phone}`}
                                                                style={{
                                                                    color: "#374151",
                                                                    textDecoration: "none",
                                                                }}
                                                            >
                                                                {booking.phone}
                                                            </a>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>

                                                    <td style={bookingCellStyle}>
                                                        {booking.tenantEmail ? (
                                                            <a
                                                                href={`mailto:${booking.tenantEmail}`}
                                                                style={{
                                                                    color: "#2563eb",
                                                                    textDecoration: "none",
                                                                }}
                                                            >
                                                                {booking.tenantEmail}
                                                            </a>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>

                                                    <td
                                                        style={{
                                                            ...bookingCellStyle,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {booking.tenantPhone ? (
                                                            <a
                                                                href={`tel:${booking.tenantPhone}`}
                                                                style={{
                                                                    color: "#374151",
                                                                    textDecoration: "none",
                                                                }}
                                                            >
                                                                {booking.tenantPhone}
                                                            </a>
                                                        ) : (
                                                            "—"
                                                        )}
                                                    </td>

                                                    <td
                                                        style={{
                                                            ...bookingCellStyle,
                                                            textAlign: "center",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: 26,
                                                                height: 26,
                                                                borderRadius: 999,
                                                                background: booking.keyGivenToManager
                                                                    ? "#ecfdf5"
                                                                    : "#fef2f2",
                                                                color: booking.keyGivenToManager
                                                                    ? "#059669"
                                                                    : "#dc2626",
                                                                fontSize: "0.75rem",
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {booking.keyGivenToManager ? "✓" : "✕"}
                                                        </span>
                                                    </td>

                                                    <td
                                                        style={{
                                                            ...bookingCellStyle,
                                                            textAlign: "center",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: 26,
                                                                height: 26,
                                                                borderRadius: 999,
                                                                background: booking.agreedToTerms
                                                                    ? "#ecfdf5"
                                                                    : "#fef2f2",
                                                                color: booking.agreedToTerms
                                                                    ? "#059669"
                                                                    : "#dc2626",
                                                                fontSize: "0.75rem",
                                                                fontWeight: 800,
                                                            }}
                                                        >
                                                            {booking.agreedToTerms ? "✓" : "✕"}
                                                        </span>
                                                    </td>

                                                    <td style={bookingCellStyle}>
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                padding: "4px 9px",
                                                                borderRadius: 999,
                                                                fontSize: "0.7rem",
                                                                fontWeight: 700,
                                                                whiteSpace: "nowrap",
                                                                background: confirmed
                                                                    ? "#ecfdf5"
                                                                    : "#fffbeb",
                                                                color: confirmed
                                                                    ? "#065f46"
                                                                    : "#92400e",
                                                                border: confirmed
                                                                    ? "1px solid #a7f3d0"
                                                                    : "1px solid #fde68a",
                                                            }}
                                                        >
                                                            {confirmed ? "Confirmed" : "Pending"}
                                                        </span>
                                                    </td>

                                                    <td style={bookingCellStyle}>
                                                        <span
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                padding: "4px 9px",
                                                                borderRadius: 999,
                                                                fontSize: "0.7rem",
                                                                fontWeight: 700,
                                                                background: emailSent
                                                                    ? "#ecfdf5"
                                                                    : "#fef2f2",
                                                                color: emailSent
                                                                    ? "#065f46"
                                                                    : "#991b1b",
                                                                border: emailSent
                                                                    ? "1px solid #a7f3d0"
                                                                    : "1px solid #fecaca",
                                                            }}
                                                        >
                                                            {emailSent ? "Yes" : "No"}
                                                        </span>
                                                    </td>

                                                    <td
                                                        style={{
                                                            ...bookingCellStyle,
                                                            color: "#64748b",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {submittedText}
                                                    </td>

                                                    <td style={bookingCellStyle}>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 8,
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    resendCoordinatorBookingEmail(booking)
                                                                }
                                                                disabled={resendingBookingIds.has(coordinatorBookingKey(booking))}
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    gap: 6,
                                                                    minWidth: 82,
                                                                    padding: "6px 11px",
                                                                    borderRadius: 7,
                                                                    border: "1px solid #bfdbfe",
                                                                    background: resendingBookingIds.has(coordinatorBookingKey(booking))
                                                                        ? "#eff6ff"
                                                                        : "#ffffff",
                                                                    color: "#2563eb",
                                                                    fontSize: "0.72rem",
                                                                    fontWeight: 700,
                                                                    cursor: resendingBookingIds.has(coordinatorBookingKey(booking))
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                    opacity: resendingBookingIds.has(coordinatorBookingKey(booking))
                                                                        ? 0.65
                                                                        : 1,
                                                                }}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faPaperPlane}
                                                                    size="xs"
                                                                />

                                                                {resendingBookingIds.has(coordinatorBookingKey(booking))
                                                                    ? "Sending..."
                                                                    : "Resend"}
                                                            </button>

                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    deleteCoordinatorBooking(booking)
                                                                }
                                                                disabled={deletingBookingIds.has(coordinatorBookingKey(booking))}
                                                                style={{
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    gap: 6,
                                                                    minWidth: 82,
                                                                    padding: "6px 11px",
                                                                    borderRadius: 7,
                                                                    border: "1px solid #fecaca",
                                                                    background: "#ffffff",
                                                                    color: "#dc2626",
                                                                    fontSize: "0.72rem",
                                                                    fontWeight: 700,
                                                                    cursor: deletingBookingIds.has(coordinatorBookingKey(booking))
                                                                        ? "not-allowed"
                                                                        : "pointer",
                                                                    opacity: deletingBookingIds.has(coordinatorBookingKey(booking)) ? 0.65 : 1,
                                                                }}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faTimes}
                                                                    size="xs"
                                                                />
                                                                {deletingBookingIds.has(coordinatorBookingKey(booking))
                                                                    ? "Deleting..."
                                                                    : "Delete"}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                padding: "12px 20px",
                                borderTop: "1px solid #e5e7eb",
                                flexShrink: 0,
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => setBookingModal(null)}
                                style={{
                                    padding: "7px 16px",
                                    borderRadius: 7,
                                    border: "1px solid #d1d5db",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: "0.82rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </ReactModal>
            )}

            {calendarModal && (
                <CalendarEventModal
                    directOpen={true}
                    directEvent={calendarModal.event}
                    directSlot={calendarModal.slot}
                    directPrefill={calendarModal.prefill}
                    directTicketId={calendarModal.ticketId}
                    onScheduled={handleTicketScheduled}
                    onDirectClose={() => setCalendarModal(null)}
                />
            )}

            {showDetailsModal && (
                <InspectionModal
                    directOpen={true}
                    onDirectClose={() => setShowDetailsModal(false)}
                    inspectores={inspectores}
                    assignableUsers={assignableUsers}
                    visibleTabs={['section1', 'section4']}
                />)

            }

            <EstimateModal
                show={showEstimateModal}
                onHide={closeEstimateModal}
                doorData={singleDoorForEstimate}
                doorsData={singleDoorForEstimate ? null : selectedDoors}
                buildingData={{
                    ...buildingData,
                    uid: buildingData?.originalUid || buildingData?.uid
                }}
                foundBuildingUid={estimateBuildingId}
                isResidential={isResidentialEstimate}
                onGenerate={closeEstimateModal}
            />

        </div>
    );
};

export default Tickets;